import React, { useState, useEffect, useRef, useCallback, useMemo, useDeferredValue, Suspense, lazy } from "react";
import { Navigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { isSystemAdminPersonnel } from "@/utils/roleUtils";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { DrawingMarker } from './components/location-tracking/DrawingMarker';
import { LocationMarker } from './components/location-tracking/LocationMarker';
// PERF-006: Konum harita bloğu lazy yüklenir. Leaflet + react-leaflet bundle'ı
// (~88KB) ilk Map tab açıldığında on-demand fetch edilir. Diğer tab'larda kullanıcı
// haritayı görmediği sürece bundle initial load'a dahil edilmez.
const MapDisplay = lazy(() => import('./components/MapDisplay'));
import { useWebSocket } from './hooks/useWebSocket';
import { useTeams } from './hooks/useTeams';
import { useNotificationSummary } from './hooks/useNotificationSummary';
import { useAuthorization } from './auth/AuthorizationContext';
import DynamicRolesPanel from './components/admin/DynamicRolesPanel';
import OrganizasyonAgaciPanel from './components/admin/OrganizasyonAgaciPanel';
import QuickParkDialog from './components/QuickParkDialog';
import { REVISION_TYPES, DEFAULT_REVISION_TYPE } from './constants/revisionTypes';

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { MultiSelectSearch } from "@/components/ui/MultiSelectSearch";
import DetailOrgFilterBar, {
  DetailFilterEmptyState,
  EMPTY_DETAIL_FILTERS,
} from "@/components/DetailOrgFilterBar";
import DeleteNameConfirm, { deleteNameMatches } from "@/components/DeleteNameConfirm";
import LeaveDateRangePopover from "@/components/LeaveDateRangePopover";
import ImageLightbox from "@/components/ImageLightbox";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { todayYmd } from "@/utils/dateUtils";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DraggableModal } from "@/components/ui/draggable-modal";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Trash2, Edit, Plus, Users, TreePine, Search, BarChart3, Lightbulb, Home as Bench, Trash, TreeDeciduous, Flower2, Footprints, Baby, Dumbbell, Home, Droplet, ParkingCircle, Sprout, Droplets, LayoutGrid, List, ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, UserCheck, Briefcase, Crown, Scissors, HardHat, Calendar, CalendarClock, Clock, CheckCircle, CheckCircle2, XCircle, RefreshCw, Loader2, CloudUpload, X, FileText, MapPin, User, Shield, Network } from "lucide-react";
import { toast } from "sonner";

// İptal edilen isteklerde "canceled" toast'ı hiç gösterme (tüm uygulama için)
const _toastError = toast.error.bind(toast);
toast.error = (message, ...rest) => {
  const msg = typeof message === "string" ? message : (message?.message ?? String(message));
  if (!msg) { _toastError(message, ...rest); return; }
  const lower = msg.trim().toLowerCase();
  if (lower === "canceled" || lower.endsWith(": canceled")) return;
  _toastError(message, ...rest);
};

// Custom Park Bench Icon
const BenchIcon = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="11" width="18" height="2" rx="1"/>
    <rect x="3" y="7" width="18" height="2" rx="1"/>
    <line x1="5" y1="13" x2="5" y2="20"/>
    <line x1="7" y1="9" x2="7" y2="20"/>
    <line x1="19" y1="13" x2="19" y2="20"/>
    <line x1="17" y1="9" x2="17" y2="20"/>
    <line x1="4" y1="20" x2="8" y2="20"/>
    <line x1="16" y1="20" x2="20" y2="20"/>
  </svg>
);

// Custom Light Pole Icon (Elektrik Direği) - Modern Sokak Lambası
const LightPoleIcon = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    {/* Top lamp housing */}
    <path d="M9 3 L9 5 Q9 6, 10 6 L14 6 Q15 6, 15 5 L15 3 Q15 2, 14 2 L10 2 Q9 2, 9 3 Z" 
          fill="currentColor" opacity="0.3" stroke="currentColor" strokeWidth="1.5"/>
    
    {/* Light rays */}
    <line x1="7" y1="5" x2="5" y2="7" opacity="0.4" strokeWidth="1"/>
    <line x1="17" y1="5" x2="19" y2="7" opacity="0.4" strokeWidth="1"/>
    <line x1="6" y1="8" x2="3" y2="8" opacity="0.4" strokeWidth="1"/>
    <line x1="18" y1="8" x2="21" y2="8" opacity="0.4" strokeWidth="1"/>
    
    {/* Pole arm */}
    <line x1="12" y1="6" x2="12" y2="9" strokeWidth="2"/>
    
    {/* Main vertical pole */}
    <rect x="11.2" y="9" width="1.6" height="13" fill="currentColor" opacity="0.7" rx="0.8"/>
    
    {/* Base */}
    <path d="M9 22 L9 21 L15 21 L15 22 L9 22 Z" fill="currentColor" stroke="currentColor"/>
    <rect x="8" y="22" width="8" height="0.8" fill="currentColor" rx="0.4"/>
  </svg>
);

import { Toaster } from "@/components/ui/sonner";
import LoginScreen from "./LoginScreen";
import FirstCredentialSetup from "./components/FirstCredentialSetup";
import Sidebar from "./components/Sidebar";
import ParksTab from "./components/tabs/ParksTab";
import HomeTab from "./components/tabs/HomeTab";
import TaskTrackingTab from "./components/tabs/TaskTrackingTab";
import AppHeader from "./components/app/AppHeader";
import MessagesTab from "./components/tabs/MessagesTab";
import RevisionsTab from "./components/tabs/RevisionsTab";
import ApprovalsTab from "./components/tabs/ApprovalsTab";
import ActivityManagementTab from "./components/tabs/ActivityManagementTab";
import DailyMaintenanceTab from "./components/tabs/DailyMaintenanceTab";
import PassiveAreasTab from "./components/tabs/PassiveAreasTab";
import ProtokolParklarTab from "./components/tabs/ProtokolParklarTab";
import OperasyonTab from "./components/tabs/OperasyonTab";
import ObtTab from "./components/tabs/ObtTab";
import RegionsTab from "./RegionsTab";
import FormenRegionDetails from "./components/personnel/FormenRegionDetails";
import { getFormenRegionParks } from "./utils/formenParks";
import PersonnelForm from "./components/personnel/PersonnelForm";
import LeaveRequestCard from "./components/LeaveRequestCard";
import LeaveManagementPanel from "./components/LeaveManagementPanel";
import PersonnelStatisticsCards from "./components/PersonnelStatisticsCards";
import DashboardStatisticsCards from "./components/DashboardStatisticsCards";
import ActiveDelegationsCard from "./components/ActiveDelegationsCard";
import BlockedLoginDashboardCard from "./components/BlockedLoginDashboardCard";
import InventorySummaryCards from "./components/InventorySummaryCards";
import MaterialManagementTab from "./components/MaterialManagementTab";
import DataManagementPanel from "./components/DataManagementPanel";
import PersonnelDetailsDialog from "./components/PersonnelDetailsDialog";
import LeaveRequestFormDialog from "./components/LeaveRequestFormDialog";
import LeaveActionDialog from "./components/LeaveActionDialog";
import MyLeaveRequestsCard from "./components/MyLeaveRequestsCard";
import ParkSiyonLogo from "./components/ParkSiyonLogo";
import { getNextWorkday } from "./utils/dateUtils";
import { shouldShowAnnualWorkdayHint, filterParksForSubstitute } from "./utils/leaveUtils";
import { formatTrCepStoredDisplay } from "./utils/phoneFormat";
import { normalizeSearchText, searchMatches } from "./utils/stringUtils";
import MesaiYonetimiTab from "./components/tabs/MesaiYonetimiTab";
import AuditLogTab from "./components/tabs/AuditLogTab";
import LoginManagementTab from "./components/tabs/LoginManagementTab";
import ProfileTab from "./components/tabs/ProfileTab";
import { BACKEND_URL } from "./config";
import { notifyLoginListMaybeChanged } from "./loginListBroadcast";
import apiClient from "./api/client";
import { useAuth } from "@/auth/AuthContext";
import IdleTimeoutManager from "@/auth/IdleTimeoutManager";
import BeliLauncher from "@/components/beli/BeliChatPanel";

// Runtime API base seçimi (AuthContext ile aynı mantık).
// Dev'de (page localhost/127.0.0.1 ile yüklendi) API="" → relative path → proxy
// üzerinden → same-origin → SameSite cookie OK.
// NOT: BACKEND_URL constant'ı bundle cache'lendiyse eski değeri tutabiliyor;
// window.location üzerinden runtime'da karar vererek bundle cache'inden bağımsız hale getiriyoruz.
const API = (() => {
  if (typeof window === "undefined") return BACKEND_URL;
  try {
    const host = (window.location && window.location.hostname) || "";
    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") return "";
  } catch (_) { /* ignore */ }
  return BACKEND_URL;
})();
const API_TIMEOUT_MS = 25000;
const buildApiCandidates = (baseUrl) => {
  const candidates = [baseUrl];
  // NOT: Dev'de (baseUrl="") proxy zorunlu — cookie SameSite uyumu için.
  // Eski `http://127.0.0.1:8000` fallback'i cross-origin AJAX yapıyor,
  // tarayıcı cookie göndermiyor, F5 sonrası logout oluyordu. Kaldırıldı.
  if (baseUrl) {
    if (baseUrl.includes("127.0.0.1")) {
      candidates.push(baseUrl.replace("127.0.0.1", "localhost"));
    } else if (baseUrl.includes("localhost")) {
      candidates.push(baseUrl.replace("localhost", "127.0.0.1"));
    }
  }
  return [...new Set(candidates)];
};

// Map component'leri artık ayrı dosyalarda (./components/location-tracking/)


// Role Hierarchy - Her rolün seviyesi
const roleHierarchy = {
  'park_gorevlisi': 1,  'gorevli': 1,
  'formen': 2,
  'bolge_muhendisi': 3,
  'mudur_yardimcisi': 4,
  'mudur': 5,
  'admin': 5
};

// Açıklama Görünürlüğü Kontrolü
const canSeeApprovalComment = (viewerRole, commenterRole, requestStatus, viewerId, commenterId) => {
  const viewerLevel = roleHierarchy[viewerRole] || 0;
  const commenterLevel = roleHierarchy[commenterRole] || 0;
  
  // KENDİ açıklamasını HERKES görebilir (unutmamak için)
  if (viewerId === commenterId) return true;
  
  // Müdür/Admin açıklamasını HERKES görebilir
  if (commenterRole === 'mudur' || commenterRole === 'admin') return true;
  
  // ONAYLANMIŞ veya REDDEDİLMİŞ talepler için TÜM onay geçmişi görünür
  // Bu sayede herkes kimin ne zaman onayladığını/reddettiğini görebilir
  if (requestStatus === 'approved' || requestStatus === 'rejected') {
    // Park görevlisi hariç herkes tüm geçmişi görebilir
    if (viewerLevel >= 2) return true;
    // Park görevlisi sadece müdür açıklamasını görebilir (yukarıda kontrol edildi)
    return false;
  }
  
  // Park Görevlisi özel durum:
  // İptal edilmiş taleplerde sadece Müdür'ün açıklamasını görebilir
  if (viewerLevel === 1) {
    if (requestStatus === 'cancelled') {
      return commenterRole === 'mudur';
    }
    // Devam eden taleplerde hiçbir yönetici açıklamasını görmez
    return false;
  }
  
  // Genel kural: Görüntüleyen kişi, kendinden ALTTAKİ rollerin açıklamalarını GÖREMEZ
  // Sadece kendinden ÜSTTEKİ (veya eşit seviyedeki) rollerin açıklamalarını görebilir
  return viewerLevel > commenterLevel;
};

// Approval History Filtreleme
const filterApprovalHistory = (approvalHistory, viewerRole, requestStatus, viewerId, requestOwnerId) => {
  if (!approvalHistory || approvalHistory.length === 0) return [];

  // Talep sahibi sadece final kararı görsün
  if (viewerId && requestOwnerId && viewerId === requestOwnerId) {
    if (!['approved', 'rejected', 'cancelled'].includes(requestStatus)) {
      return [];
    }
    const lastStep = approvalHistory[approvalHistory.length - 1];
    return lastStep ? [lastStep] : [];
  }
  
  return approvalHistory.filter(step => {
    return canSeeApprovalComment(viewerRole, step.approver_role, requestStatus, viewerId, step.approver_id);
  });
};

// İzinli/raporlu park görevlisinin park listesi + park-başına vekilini üretir.
// Yetkili kaynak, izin/vekalet kaydının per-park kırılımıdır (`substitute_breakdown`) —
// Aktif Vekaletler kartıyla aynı veri. park tablosunun `assigned_personnel_ids` JSON
// kolonu ile park_assignment tablosu senkron dışı kalabildiğinden (kimi personelin
// ataması yalnız park_assignment'ta olur), breakdown'ı önceliklendirir ve assigned
// parklarla birleştirir; böylece hem senkron dışı kalan parklar hem de kapsanmayan
// (vekilsiz) parklar görünür. Dönüş: [{ id, name, substituteName }].
// `plannedRequest`: yalnız BUGÜN DIŞI bir gün görüntülenirken verilir; bkz. (4).
// Bugün için null geçilir, böylece bugünkü çıktı bit bit aynı kalır.
const buildLeaveParkVekilList = (
  person, assignedParks, allActiveSubstitutions, plannedRequest = null
) => {
  const personSub = (allActiveSubstitutions || []).find(
    (s) => s && s.original_personnel_id === person.id
  );
  const breakdown = Array.isArray(personSub?.substitute_breakdown)
    ? personSub.substitute_breakdown
    : [];

  const parkMap = new Map();
  // 1) Doğrudan park atamaları (senkron olduğunda park listesinin kaynağı)
  (assignedParks || []).forEach((p) => {
    if (!p || !p.id) return;
    parkMap.set(p.id, { id: p.id, name: p.name, substituteName: null });
  });
  // 2) Per-park kırılım (yetkili park→vekil eşlemesi; assigned_personnel_ids'te
  //    olmayan parkları da yüzeye çıkarır)
  breakdown.forEach((b) => {
    if (!b || !b.id) return;
    const existing = parkMap.get(b.id) || {
      id: b.id,
      name: b.name || 'İsimsiz Park',
      substituteName: null,
    };
    existing.name = existing.name || b.name || 'İsimsiz Park';
    if (b.substitute_personnel_name) existing.substituteName = b.substitute_personnel_name;
    parkMap.set(b.id, existing);
  });
  // 3) Kırılım yoksa (tek vekil senaryosu) üst-seviye vekili tüm parklara uygula
  if (breakdown.length === 0 && personSub?.substitute_personnel_name) {
    parkMap.forEach((v) => {
      if (!v.substituteName) v.substituteName = personSub.substitute_personnel_name;
    });
  }
  // 4) GELECEK GÜN yedeği: `allActiveSubstitutions` yalnız BUGÜN aktif vekaletleri
  //    içerir (kasıtlı: start_date <= today koruması). Yarına bakarken vekaleti
  //    henüz başlamamış personelin parkları vekilsiz görünür ve kullanıcı "vekil
  //    atanmamış" sanar — oysa plan talebin üzerinde durur. Bu yüzden ileri bir gün
  //    görüntülenirken plan `leave.park_substitutes`'ten okunur.
  //    Yalnız BOŞ alanları doldurur; aktif vekalet verisi ASLA ezilmez ve bu blok
  //    yalnızca çağıran plan gönderdiğinde (bugün dışı gün) çalışır.
  if (plannedRequest) {
    (Array.isArray(plannedRequest.park_substitutes) ? plannedRequest.park_substitutes : [])
      .forEach((ps) => {
        if (!ps || !ps.park_id) return;
        const existing = parkMap.get(ps.park_id) || {
          id: ps.park_id,
          name: ps.park_name || 'İsimsiz Park',
          substituteName: null,
        };
        if (!existing.name) existing.name = ps.park_name || 'İsimsiz Park';
        if (!existing.substituteName && ps.substitute_name) {
          existing.substituteName = ps.substitute_name;
        }
        parkMap.set(ps.park_id, existing);
      });
  }
  return Array.from(parkMap.values());
};

// Helper fonksiyon: Bir yöneticinin TÜM alt personelini recursive olarak bul
// Bu fonksiyon component dışında tanımlanmıştır, böylece her render'da yeniden oluşturulmaz
const getAllSubordinates = (managerId, managerRole, personnel) => {
  const directSubordinates = [];
  
  if (managerRole === 'formen') {
    // Formen'in altındaki park görevlileri
    directSubordinates.push(
      ...personnel.filter(p => 
        p.formen_id === managerId || 
        (p.parent_id === managerId && (p.role === 'park_gorevlisi'))
      )
    );
  } else if (managerRole === 'bolge_muhendisi') {
    // Bölge Mühendisi'nin altındaki Formenler
    const formens = personnel.filter(p => 
      p.bolge_muhendisi_id === managerId ||
      (p.parent_id === managerId && p.role === 'formen')
    );
    directSubordinates.push(...formens);
    
    // Her Formen'in altındaki park görevlilerini de ekle (RECURSIVE)
    formens.forEach(formen => {
      const formenSubordinates = personnel.filter(p => 
        p.formen_id === formen.id || 
        (p.parent_id === formen.id && (p.role === 'park_gorevlisi'))
      );
      directSubordinates.push(...formenSubordinates);
    });
    
    // Doğrudan bölge mühendisine bağlı park görevlileri varsa (fallback)
    directSubordinates.push(
      ...personnel.filter(p => 
        p.bolge_muhendisi_id === managerId && (p.role === 'park_gorevlisi')
      )
    );
  } else if (managerRole === 'mudur_yardimcisi') {
    // 1. Müdür Yardımcısı'nın altındaki Bölge Mühendisleri
    const mühendisler = personnel.filter(p => 
      p.mudur_yardimcisi_id === managerId ||
      (p.parent_id === managerId && p.role === 'bolge_muhendisi')
    );
    directSubordinates.push(...mühendisler);
    
    // Her Bölge Mühendisi'nin altındaki Formenleri ve park görevlilerini ekle (RECURSIVE)
    mühendisler.forEach(mühendis => {
      const mühendisSubordinates = getAllSubordinates(mühendis.id, 'bolge_muhendisi', personnel);
      directSubordinates.push(...mühendisSubordinates);
    });
    
    // 2. Bölge Mühendisi OLMAYAN ama direkt Müdür Yardımcısı'na bağlı Formenler
    const directFormens = personnel.filter(p => 
      p.role === 'formen' &&
      p.mudur_yardimcisi_id === managerId &&
      !p.bolge_muhendisi_id // Bölge Mühendisi yok
    );
    directSubordinates.push(...directFormens);
    
    // Bu formenlerin altındaki park görevlileri
    directFormens.forEach(formen => {
      const formenSubordinates = personnel.filter(p => 
        p.formen_id === formen.id || 
        (p.parent_id === formen.id && (p.role === 'park_gorevlisi'))
      );
      directSubordinates.push(...formenSubordinates);
    });
    
    // 3. Doğrudan Müdür Yardımcısı'na bağlı park görevlileri (fallback - nadir durum)
    directSubordinates.push(
      ...personnel.filter(p => 
        p.mudur_yardimcisi_id === managerId && 
        (p.role === 'park_gorevlisi') &&
        !p.formen_id && !p.bolge_muhendisi_id
      )
    );
  } else if (managerRole === 'mudur' || managerRole === 'admin' || managerRole === 'golge_admin') {
    // Müdür / süper admin / Başkan: tüm personel (kendisi hariç)
    return personnel.filter(p => p.id !== managerId);
  }
  
  // Duplicate'leri kaldır
  const uniqueSubordinates = directSubordinates.filter((person, index, self) =>
    index === self.findIndex(p => p.id === person.id)
  );
  
  // Güvenlik: veri hatasıyla yönetici kendine bağlı görünse bile asla ast listesine alma.
  return uniqueSubordinates.filter((p) => p.id !== managerId);
};

// Türkiye Resmi Tatil Günleri (2024-2026)
const getTurkishHolidays = (year) => {
  const holidays = {
    2024: [
      '2024-01-01', // Yılbaşı
      '2024-04-23', // 23 Nisan
      '2024-05-01', // İşçi Bayramı
      '2024-05-19', // Gençlik ve Spor
      '2024-06-15', '2024-06-16', '2024-06-17', // Kurban Bayramı (tahmini)
      '2024-07-15', // Demokrasi Bayramı
      '2024-08-30', // Zafer Bayramı
      '2024-10-29', // Cumhuriyet Bayramı
    ],
    2025: [
      '2025-01-01', // Yılbaşı
      '2025-04-10', '2025-04-11', '2025-04-12', // Ramazan Bayramı (tahmini)
      '2025-04-23', // 23 Nisan
      '2025-05-01', // İşçi Bayramı
      '2025-05-19', // Gençlik ve Spor
      '2025-06-06', '2025-06-07', '2025-06-08', '2025-06-09', // Kurban Bayramı (tahmini)
      '2025-07-15', // Demokrasi Bayramı
      '2025-08-30', // Zafer Bayramı
      '2025-10-29', // Cumhuriyet Bayramı
    ],
    2026: [
      '2026-01-01', // Yılbaşı
      '2026-03-19', '2026-03-20', '2026-03-21', '2026-03-22', // Ramazan Bayramı Arife + 1-3
      '2026-04-23', // 23 Nisan
      '2026-05-01', // İşçi Bayramı
      '2026-05-19', // Gençlik ve Spor
      '2026-05-26', '2026-05-27', '2026-05-28', '2026-05-29', '2026-05-30', // Kurban Bayramı Arife + 1-4
      '2026-07-15', // Demokrasi Bayramı
      '2026-08-30', // Zafer Bayramı
      '2026-10-29', // Cumhuriyet Bayramı
    ],
  };
  return holidays[year] || [];
};

// Arife günleri: izin hakkından TAM GÜN düşer (resmi tatil sayılmaz)
const getTurkishArifeDates = (year) => {
  const arife = {
    2024: ['2024-06-14'], // Kurban arifesi (15-17 bayram)
    2025: ['2025-04-09', '2025-06-05'], // Ramazan arife, Kurban arife
    2026: ['2026-03-19', '2026-05-26'], // Ramazan arife, Kurban arife
  };
  return arife[year] || [];
};

// İzin hakkından düşüm için kullanılan tatil seti (arife hariç – arife tam gün izinden düşer)
const getTurkishHolidaysForLeaveDeduction = (year) => {
  const all = getTurkishHolidays(year) || [];
  const arifeSet = new Set(getTurkishArifeDates(year) || []);
  return all.filter((d) => !arifeSet.has(d));
};

// Yerel tarih parse/format (timezone kayması olmaması için)
const parseLocalDate = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const formatLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// İzin günü hesaplama fonksiyonu (tüm tarihler yerel gece yarısı; timezone kayması yok)
const calculateLeaveDays = (startDate, endDate, isCivilServant = false, returnDateParam = null) => {
  if (!startDate || !endDate) {
    return { weekdays: 0, weekends: 0, total: 0, workdays: 0, returnDate: '' };
  }

  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);

  if (start > end) {
    return { weekdays: 0, weekends: 0, total: 0, workdays: 0, returnDate: '' };
  }

  // İzin süresi YALNIZ seçilen [start, end] aralığıdır. return_date sonrası
  // hafta sonu/resmi tatil izne DAHİL DEĞİLDİR (cuma tek-gün izinde cmt+paz
  // sayılıp "3 gün" görünmesini önler). return_date yalnız çıktı olarak korunur.
  let returnDateStr;
  if (returnDateParam) {
    returnDateStr = returnDateParam;
  } else {
    const returnDateObj = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
    returnDateStr = formatLocalDate(returnDateObj);
  }

  let weekdays = 0;
  let weekends = 0;
  let workdays = 0;

  // İzin hakkından düşüm: arife günleri tatil sayılmaz (tam gün izinden düşer)
  const allHolidays = new Set([
    ...getTurkishHolidaysForLeaveDeduction(2024),
    ...getTurkishHolidaysForLeaveDeduction(2025),
    ...getTurkishHolidaysForLeaveDeduction(2026),
  ]);

  const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endTime = end.getTime();
  while (current.getTime() <= endTime) {
    const dateStr = formatLocalDate(current);
    const dayOfWeek = current.getDay();

    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHoliday = allHolidays.has(dateStr);

    if (isWeekend) {
      weekends++;
    } else {
      weekdays++;
      if (!isHoliday) {
        workdays++;
      }
    }

    current.setDate(current.getDate() + 1);
  }

  const total = weekdays + weekends;
  const finalWorkdays = isCivilServant ? total : workdays;

  return {
    weekdays,
    weekends,
    total,
    workdays: finalWorkdays, // Sadece bu izin hakkından düşecek
    returnDate: returnDateStr
  };
};

// Sistem Ayarları Kartı - Belediye Adı Düzenleme
function SystemSettingsCard({ API }) {
  const [municipalityName, setMunicipalityName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [originalName, setOriginalName] = useState("");
  
  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/api/system-settings`);
      setMunicipalityName(response.data?.municipality_name || "Belediye Adı");
      setOriginalName(response.data?.municipality_name || "Belediye Adı");
    } catch (error) {
      console.error("Sistem ayarları yüklenemedi:", error);
    } finally {
      setIsLoading(false);
    }
  }, [API]);
  
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);
  
  const handleSave = async () => {
    if (!municipalityName.trim()) {
      toast.error("Belediye adı boş olamaz");
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.put(`${API}/api/system-settings`, {
        municipality_name: municipalityName.trim()
      });
      setOriginalName(municipalityName.trim());
      toast.success("Belediye adı güncellendi");
    } catch (error) {
      console.error("Belediye adı güncellenemedi:", error);
      toast.error("Belediye adı güncellenemedi");
    } finally {
      setIsSaving(false);
    }
  };
  
  const hasChanges = municipalityName !== originalName;
  
  return (
    <Card className="border-green-200 mb-6" data-testid="system-settings-card">
      <CardHeader className="bg-green-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
            <TreePine className="h-5 w-5" />
          </div>
          <div>
            <CardTitle className="text-green-700">Sistem Ayarları</CardTitle>
            <CardDescription>Giriş ekranında görünecek belediye adını düzenleyin</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-green-500" />
            <span className="ml-2 text-gray-500">Yükleniyor...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Belediye Adı</Label>
              <div className="flex gap-2">
                <Input 
                  value={municipalityName}
                  onChange={(e) => setMunicipalityName(e.target.value)}
                  placeholder="Belediye adını girin..."
                  className="flex-1"
                  data-testid="municipality-name-input"
                />
                <Button 
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="save-municipality-btn"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      Kaydediliyor...
                    </>
                  ) : (
                    "Kaydet"
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Bu ad, giriş ekranındaki yeşil kutucukta görüntülenecektir.
              </p>
            </div>
            
            {/* Preview */}
            <div className="mt-4 p-4 border rounded-lg bg-gray-50">
              <p className="text-xs text-gray-500 mb-2">Önizleme:</p>
              <div 
                className="py-3 px-4 rounded-lg text-center text-white font-bold"
                style={{
                  background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 50%, #43a047 100%)'
                }}
              >
                {municipalityName || "Belediye Adı"}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Eski helper'lar — Sprint 2 frontend refactor (F2.a) ile useAuthorization()'a taşındı.
// Yeni component'ler `const { isManagerView, isManagerEdit, isGolgeAdmin } = useAuthz()` kullanmalı.
// `useAuthz()` => `frontend/src/auth/AuthorizationContext.jsx` `useAuthorization()` hook'unun
// kısayolu (App.js içinde tanımlanır). Geriye uyum için aşağıdaki sabitler kalsa da hiçbir
// yerden çağrılmamalı; yeni kontrolleri permission slug'a göre yap.
//
// LEGACY (deprecated — `userRole` parametresi alır, geriye uyum için var):
const isManagerViewRoleLegacy = (role) =>
  role === "admin" || role === "mudur" || role === "mudur_yardimcisi" || role === "bolge_muhendisi" || role === "formen" || role === "golge_admin";
const isManagerEditRoleLegacy = (role) =>
  role === "admin" || role === "mudur" || role === "mudur_yardimcisi" || role === "bolge_muhendisi" || role === "formen";

// Tesis detay modallarındaki "zemin filtresi" seçenekleri (çoklu-seçim dropdown).
// id'ler ilgili surfaceFilters state nesnesinin anahtarlarıyla birebir eşleşir.
const RUNNING_TRACK_SURFACE_OPTIONS = [
  { id: "belirtilmemis", name: "Belirtilmemiş" },
  { id: "kaucuk", name: "Kauçuk" },
  { id: "kum", name: "Kum" },
  { id: "suniCim", name: "Sunî çim" },
];
const PLAYGROUND_SURFACE_OPTIONS = [
  { id: "ahsap", name: "Ahşap" },
  { id: "belirtilmemis", name: "Belirtilmemiş" },
  { id: "dere_cakili", name: "Dere çakılı" },
  { id: "kaucuk", name: "Kauçuk" },
  { id: "kum", name: "Kum" },
  { id: "sentetik_cim", name: "Sunî çim" },
];
const SPORTS_SURFACE_OPTIONS = [
  { id: "asfalt", name: "Asfalt" },
  { id: "belirtilmemis", name: "Belirtilmemiş" },
  { id: "diger", name: "Diğer" },
  { id: "kaucuk", name: "Kauçuk" },
  { id: "kilit_tasi", name: "Kilit Taşı" },
  { id: "sentetik_cim", name: "Sunî çim" },
];
// Havuz detayında "havuz durumu" filtresi (çoklu-seçim dropdown).
const POOL_STATUS_OPTIONS = [
  { id: "calisiyor", name: "Çalışıyor" },
  { id: "arizali", name: "Arızalı" },
  { id: "yok", name: "Belirtilmemiş" },
];
// Boolean nesne <-> seçili id dizisi dönüşümü (MultiSelectSearch dizi bekler).
const surfaceObjToIds = (obj) => Object.keys(obj || {}).filter((k) => obj[k]);
const surfaceIdsToObj = (options, ids) =>
  options.reduce((acc, o) => {
    acc[o.id] = ids.includes(o.id);
    return acc;
  }, {});

// Main Dashboard Component (Admin + Employee)
function Dashboard({ userRole, personnelId, onLogout }) {
  // Sprint 2 F2.a: Dinamik yetki context'i
  const authz = useAuthorization();
  const { has, hasAny, isInChain: authzIsInChain, subordinateSet } = authz;

  // Yetki bazlı flag'ler (eski rol-tabanlı sabitlerin yerine):
  //   isManagerView : "yönetici görünümü" — park görevlisi/çim ekibi DEĞİL
  //   isManagerEdit : yönetim düzenleme yetkisi var (golge_admin hariç)
  //   isGolgeAdmin  : özel "sadece görüntüleyici" mod (audit/sistem panelleri)
  const isManagerView = has('personel.listele') || has('system.golge_admin');
  const isManagerEdit = has('personel.duzenle');
  // Personel CRUD aksiyonları — her biri kendi yetkisine bağlı (rol değil).
  const canCreatePersonnel = has('personel.olustur');
  const canDeletePersonnel = has('personel.sil');
  const isGolgeAdmin = has('system.golge_admin');

  // Spesifik rol flag'leri (intermediate step — userRole karşılaştırmaları yerine).
  // Bunlar geriye uyum için; nihai hedef her kullanım yerini doğru permission slug'a
  // çevirmek (örn. `isMudur` → `has('izin.son_onay_mudur')` veya `has('personel.tum_kapsam')`).
  const isAdmin = userRole === 'admin' || userRole === 'baskan';
  const isMudur = userRole === 'mudur';
  const isMudurYardimcisi = userRole === 'mudur_yardimcisi';
  const isBolgeMuhendisi = userRole === 'bolge_muhendisi';
  const isFormen = userRole === 'formen';
  const isParkGorevlisi = userRole === 'park_gorevlisi' || userRole === 'gorevli';
  const scrollPositionRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);
  const [personnel, setPersonnel] = useState([]);
  const [parks, setParks] = useState([]);
  const [regions, setRegions] = useState([]);
  // ParksTab paginated lightweight cards (Faz 2 — backend pagination + infinite scroll)
  const [parkCards, setParkCards] = useState([]);
  const [parkCardsTotal, setParkCardsTotal] = useState(0);
  const [parkCardsHasMore, setParkCardsHasMore] = useState(false);
  const [parkCardsLoading, setParkCardsLoading] = useState(false);
  const [parkCardsLoadingMore, setParkCardsLoadingMore] = useState(false);
  const parkCardsReqIdRef = useRef(0);
  const PARK_CARDS_PAGE_SIZE = 15;
  const [systemSettings, setSystemSettings] = useState({ mudur_social_report_override: false });
  const [personnelName, setPersonnelName] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      return stored.personnelName || "";
    } catch (error) {
      return "";
    }
  });
  const [loading, setLoading] = useState(false);
  const [isPersonnelSaving, setIsPersonnelSaving] = useState(false);
  const [isParkSaving, setIsParkSaving] = useState(false);
  const [leaveRequestLoading, setLeaveRequestLoading] = useState(false);
  const [loadingParks, setLoadingParks] = useState(true);
  const [loadingPersonnel, setLoadingPersonnel] = useState(true);
  const CORE_DATA_TTL_MS = 12000;
  const coreFetchMetaRef = useRef({
    personnel: { inFlight: null, lastFetchedAt: 0 },
    parks: { inFlight: null, lastFetchedAt: 0 },
    regions: { inFlight: null, lastFetchedAt: 0 },
  });

  useEffect(() => {
    if (personnelName || !personnelId) return;
    try {
      const stored = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      if (stored.personnelName) {
        setPersonnelName(stored.personnelName);
      }
    } catch (error) {
      // sessiz geç
    }
  }, [personnelId, personnelName]);
  
  // İzin Yönetimi states
  const [approvalTab, setApprovalTab] = useState("pending");
  const [leaveRequestViewMode, setLeaveRequestViewMode] = useState("inbox"); // inbox | touched
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [leaveStatusFilter, setLeaveStatusFilter] = useState("all"); // Park görevlisi izin filtreleme
  
  // Malzeme Yönetimi states
  const [materialRequests, setMaterialRequests] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [showMaterialRequestDialog, setShowMaterialRequestDialog] = useState(false);
  const [showMaterialDeliveryDialog, setShowMaterialDeliveryDialog] = useState(false);
  const [selectedMaterialRequest, setSelectedMaterialRequest] = useState(null);
  const [materialForm, setMaterialForm] = useState({
    material_type: "",
    quantity: "1",
    description: "",
    requested_date: "",
    for_personnel_id: null  // Formen başkası adına talep oluşturabilir
  });
  const [deliveryForm, setDeliveryForm] = useState({
    delivered_date: "",
    delivery_note: ""
  });
  const [personnelDetailDialogOpen, setPersonnelDetailDialogOpen] = useState(false);
  const [selectedPersonnelForDetail, setSelectedPersonnelForDetail] = useState(null);
  const [personnelDetailDefaultTab, setPersonnelDetailDefaultTab] = useState("overview");
  const [materialToDelete, setMaterialToDelete] = useState(null);
  const [onLeaveDialogOpen, setOnLeaveDialogOpen] = useState(false);
  const [onReportDialogOpen, setOnReportDialogOpen] = useState(false);
  /** Rapor fotoğrafı görüntüleyicisi: { src, filename, alt } | null */
  const [reportViewer, setReportViewer] = useState(null);
  /** "Bugün Raporlu Personeller" modalı: ad araması + bölge/formen/BM/MY filtresi */
  const [reportOrgFilters, setReportOrgFilters] = useState({ ...EMPTY_DETAIL_FILTERS });
  const [lowLeaveDialogOpen, setLowLeaveDialogOpen] = useState(false);
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all'); // all, Yıllık İzin, Sosyal İzin, Diğer
  /** "Bugün İzinli Personeller" modalı: ad araması + bölge/formen/BM/MY filtresi */
  const [leaveOrgFilters, setLeaveOrgFilters] = useState({ ...EMPTY_DETAIL_FILTERS });
  /** İzinli modalında görüntülenen gün: 0 = bugün, 1 = yarın … (geçmişe gidilmez) */
  const [leaveDayOffset, setLeaveDayOffset] = useState(0);
  const [leaveRequestError, setLeaveRequestError] = useState(null);
  const [activeTab, setActiveTab] = useState(isManagerView ? "dashboard" : "daily-maintenance");
  const [seenMaterialRequestIds, setSeenMaterialRequestIds] = useState([]);
  const [highlightedParkId, setHighlightedParkId] = useState(null); // Global aramadan gelen park ID'si
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [parkQuickFilter, setParkQuickFilter] = useState(null); // PG ana sayfa kartlarından parklar sekmesi hızlı filtresi
  const [filterPersonnel, setFilterPersonnel] = useState("all");
  const [filterRegion, setFilterRegion] = useState("all");
  const [filterRole, setFilterRole] = useState("all"); // Parklar için rol filtresi
  const [personnelSearchTerm, setPersonnelSearchTerm] = useState("");
  const personnelSearchDebounceRef = useRef(null);
  const personnelSearchInputRef = useRef(null);
  // Arama kutusu uncontrolled (defaultValue + debounce); state'i sıfırlamak görsel
  // metni temizlemediği için input değerini ref ile elle sıfırlarız.
  const clearPersonnelSearch = () => {
    if (personnelSearchDebounceRef.current) {
      clearTimeout(personnelSearchDebounceRef.current);
    }
    if (personnelSearchInputRef.current) {
      personnelSearchInputRef.current.value = "";
    }
    setPersonnelSearchTerm("");
  };
  const [personnelDisplayType, setPersonnelDisplayType] = useState("all"); // Gösterim türü
  // Ekipler (Migration 0064/0066): istatistik kartları ve filtre, ekiplerin
  // primary_team_id'si üzerinden dinamik çalışır.
  // `/api/teams` = `yonetim.ekip_gor`/`yonetim.ekip_yonet` yetkisi ister. Bu
  // yetkisi olmayan roller (formen, park görevlisi) için istek ATILMAZ —
  // aksi halde bootstrap'ta 403 + tarayıcı console hatası oluşur (Kapı 11).
  const {
    getTeamBySlug: getTeamBySlugForPersonnel,
    getTeamById: getTeamByIdForPersonnel,
    activeTeams: activeTeamsForPersonnel,
  } = useTeams({ enabled: hasAny(['yonetim.ekip_gor', 'yonetim.ekip_yonet']) });
  // Default olmayan aktif ekipler — üyesi olanlar için istatistik kartı üretilir.
  const nonDefaultActiveTeams = (activeTeamsForPersonnel || []).filter(
    (t) => t && !t.is_default
  );
  // Personel kartında baz rolün altında gösterilecek "ekip" rozeti: yalnızca
  // default olmayan ekipler (örn. Çim, Sulama) — default "Park Görevlisi" ekibi
  // baz rolle aynı olduğu için rozet gösterilmez.
  const getNonDefaultTeamLabel = (teamId) => {
    if (!teamId) return null;
    const t = getTeamByIdForPersonnel(teamId);
    if (!t || t.is_default) return null;
    return t.display_name || t.slug || null;
  };
  // Özel rol: müdürün açıklamalı atadığı standart dışı rol.
  const hasOzelRol = (person) =>
    Boolean(
      person?.custom_role_name && String(person.custom_role_name).trim() &&
      person?.custom_role_description && String(person.custom_role_description).trim()
    );
  // "Saf" park görevlisi: baz rolü park_gorevlisi, default dışı ekipte değil
  // (Çim/Sulama vb. hariç) ve özel rolü olmayan. Park Görevlisi kartı bunları sayar.
  const isPureParkGorevlisi = (person) =>
    person?.role === 'park_gorevlisi' &&
    !getNonDefaultTeamLabel(person?.primary_team_id) &&
    !hasOzelRol(person);
  const [personnelFilterRegion, setPersonnelFilterRegion] = useState("all"); // Bölge filtresi
  const [personnelFilterMY, setPersonnelFilterMY] = useState("all"); // Müdür Yardımcısı filtresi
  const [personnelFilterBM, setPersonnelFilterBM] = useState("all"); // Bölge Mühendisi filtresi
  const [personnelFilterFormen, setPersonnelFilterFormen] = useState("all"); // Formen filtresi
  const [personnelStatusFilter, setPersonnelStatusFilter] = useState("active"); // Durum filtresi: active | passive | all (varsayılan: yalnız aktif kadro)
  const [regionsFilterRole, setRegionsFilterRole] = useState("all"); // Bölgeler sekmesi rol filtresi
  const [regionSortOrder, setRegionSortOrder] = useState("alphabetical"); // "alphabetical" or "hierarchy"
  const [personnelViewMode, setPersonnelViewMode] = useState("cards"); // "cards" or "table"
  
  // Senkronizasyon states
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncProgress, setSyncProgress] = useState("");
  const [syncResults, setSyncResults] = useState(null);
  const [personnelSortOrder, setPersonnelSortOrder] = useState("default"); // "default", "alphabetical", or "hierarchy"
  const [unblockLoginLoadingId, setUnblockLoginLoadingId] = useState(null);
  const [parksViewMode, setParksViewMode] = useState("cards"); // "cards" or "table"
  const [parksSortOrder, setParksSortOrder] = useState("default"); // "default" or "alphabetical"
  const [expandedRegions, setExpandedRegions] = useState({}); // Formen kartlarında açık bölgeler: {personnelId_regionId: true/false}
  const [expandedPersonnelDetails, setExpandedPersonnelDetails] = useState({}); // Personel detayları göster/gizle
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0); // Okunmamış mesaj sayısı
  const [activeSurveysCount, setActiveSurveysCount] = useState(0); // Aktif anket sayısı (Mesai Yönetimi badge)
  const [pendingPlansCount, setPendingPlansCount] = useState(0); // Onay bekleyen plan sayısı (Mesai Yönetimi badge)
  const [pendingActivityPlansCount, setPendingActivityPlansCount] = useState(0); // Faaliyet Yönetimi onay bekleyen plan sayısı
  
  // İzin türü tab states
  const [leaveTypeTabs, setLeaveTypeTabs] = useState({
    "Yıllık İzin": "pending",
    "Sosyal İzin": "pending", 
    "Rapor": "pending",
    "Diğer": "pending"
  });
  
  // Hızlı ekleme dialog'ları
  const [isQuickRegionDialogOpen, setIsQuickRegionDialogOpen] = useState(false);
  const [isQuickParkDialogOpen, setIsQuickParkDialogOpen] = useState(false);
  const [quickRegionName, setQuickRegionName] = useState("");
  const [quickAddContext, setQuickAddContext] = useState(null); // "personnel" veya "park"

  useEffect(() => {
    return () => {
      if (personnelSearchDebounceRef.current) {
        clearTimeout(personnelSearchDebounceRef.current);
      }
    };
  }, []);

  // Personnel form state
  const [personnelForm, setPersonnelForm] = useState({
    id: "",
    name: "",
    custom_role_name: "",
    custom_role_description: "",
    primary_team_id: "",
    secondary_team_id: "",
    phone: "",
    email: "",
    status: "active",
    role: "park_gorevlisi",
    parent_id: null,
    assigned_parks: [], // Park ID'leri
    is_civil_servant: false,
    work_schedule: {
      morning_start: '08:15',
      morning_end: '11:45',
      afternoon_start: '13:15',
      afternoon_end: '16:45',
      work_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    }
  });
  const [isPersonnelDialogOpen, setIsPersonnelDialogOpen] = useState(false);
  const [isEditingPersonnel, setIsEditingPersonnel] = useState(false);
  const [personnelFormInitialTab, setPersonnelFormInitialTab] = useState("general");
  // Görev takipten aktarılan filtreler
  const [personnelTaskFilters, setPersonnelTaskFilters] = useState({
    startDate: null,
    endDate: null,
    parkId: null,
    parkName: null
  });

  // Park form state
  const [parkForm, setParkForm] = useState({
    id: "",
    name: "",
    neighborhood: "",
    address: "",
    coordinates: "",
    latitude: null,
    longitude: null,
    boundaries: [],
    region_id: null,
    existing_personnel_ids: [], // Mevcut personel atamalarını koru
    area: "",
    grass_area: "",
    hard_surface_area: "",
    playground_area: "",
    light_pole_count: 0,
    bench_count: 0,
    trash_bin_count: 0,
    gazebo_count: 0,
    tree_count: 0,
    flower_count: 0,
    has_running_track: false,
    running_track_length: 0,
    running_track_width: 0,
    running_track_type: "yok",
    has_playground: false,
    playground_surface_type: "yok",
    has_sports_equipment: false,
    sports_equipment_area: 0,
    sports_equipment_surface_type: "yok",
    has_wc: false,
    has_fountain: false,
    fountain_status: "yok",
    has_parking: false,
    irrigation_system: "yok",
    has_basketball_court: false,
    has_football_field: false,
    has_volleyball_court: false,
    has_tennis_court: false,
    description: "",
    assigned_park_gorevlisi_id: null,
    assigned_formen_id: null,
    assigned_bolge_muhendisi_id: null,
    assigned_mudur_yardimcisi_id: null
  });
  const [isParkDialogOpen, setIsParkDialogOpen] = useState(false);
  const [isEditingPark, setIsEditingPark] = useState(false);
  const [isFormReadOnly, setIsFormReadOnly] = useState(false); // Bölge Müh/Formen için read-only mod
  const [unassignedParksDialogOpen, setUnassignedParksDialogOpen] = useState(false); // Park görevlisi olmayan parklar listesi dialogu
  const [unassignedParksList, setUnassignedParksList] = useState([]); // Park görevlisi olmayan parklar listesi
  const [showMap, setShowMap] = useState(false);
  const [isGeocodingAddress, setIsGeocodingAddress] = useState(false);
  const [drawingMode, setDrawingMode] = useState(false);
  const [drawnBoundaries, setDrawnBoundaries] = useState([]);
  
  // Park Görevlisi Konum Takibi
  const [isTrackingLocation, setIsTrackingLocation] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState("idle"); // idle, tracking, error
  const [lastLocationUpdate, setLastLocationUpdate] = useState(null);
  const locationIntervalRef = useRef(null);
  // Faz B-8: en son backend'e bildirilen istemci konum durumu (değişimde POST)
  const lastLocationStatusRef = useRef(null);
  
  // Yönetici Konum Takibi Dashboard
  const [locationLogs, setLocationLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [tempSelectedDate, setTempSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Ara butonuna kadar bekleyecek
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const autoRefreshIntervalRef = useRef(null);
  const [showRouteFor, setShowRouteFor] = useState(null); // Rotası gösterilecek personel
  const [routeData, setRouteData] = useState([]);
  const [showHeatMap, setShowHeatMap] = useState(false); // Isı haritası toggle
  const [locationQuickFilter, setLocationQuickFilter] = useState("action_required");
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [locationFilterMY, setLocationFilterMY] = useState("all");
  const [locationFilterBM, setLocationFilterBM] = useState("all");
  const [locationFilterFormen, setLocationFilterFormen] = useState("all");
  const [locationFilterPark, setLocationFilterPark] = useState("all");
  const [locationSortBy, setLocationSortBy] = useState("status");
  const [locationPage, setLocationPage] = useState(1);
  const [locationStaleMinutes, setLocationStaleMinutes] = useState(10);
  const [locationDetailOpen, setLocationDetailOpen] = useState(false);
  const [locationDetailData, setLocationDetailData] = useState(null);
  const [locationDetailLogs, setLocationDetailLogs] = useState([]);
  const [loadingLocationDetail, setLoadingLocationDetail] = useState(false);
  
  // PWA Offline Support
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineLocationCount, setOfflineLocationCount] = useState(0);
  const [swRegistration, setSwRegistration] = useState(null);
  
  // Bildirim Tercihleri
  const [notificationPrefs, setNotificationPrefs] = useState({
    enabled: true,
    in_app_notifications: true,
    email_notifications: true,
    sms_notifications: false,
    notification_types: ["out_of_park"]
  });
  const [loadingPrefs, setLoadingPrefs] = useState(false);
  
  // Uygulama İçi Bildirimler
  const [inAppNotifications, setInAppNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Geçici Görev Atama (Temporary Assignment)
  const [tempAssignmentDialogOpen, setTempAssignmentDialogOpen] = useState(false);
  const [selectedPersonnelForAssignment, setSelectedPersonnelForAssignment] = useState(null);
  const [tempAssignmentForm, setTempAssignmentForm] = useState({
    task_type: "Başka Parkta Yardım",
    task_description: "",
    location: "",
    duration_hours: 2
  });
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Aktif Vekaletler (İzin yerine bakma)
  const [mySubstitutions, setMySubstitutions] = useState({ as_substitute: [], on_leave: [] });
  const [allActiveSubstitutions, setAllActiveSubstitutions] = useState([]);
  const [activeDelegations, setActiveDelegations] = useState([]);
  const [handoverLoadingId, setHandoverLoadingId] = useState(null);
  const [isReturnFlowOpen, setIsReturnFlowOpen] = useState(false);
  const [selectedReturnSub, setSelectedReturnSub] = useState(null);
  const [returnFlowSaving, setReturnFlowSaving] = useState(false);
  const [returnFlowForm, setReturnFlowForm] = useState({
    actual_return_date: new Date().toISOString().slice(0, 10),
    return_note: "",
    handover_mode: "immediate", // immediate | end_of_day | custom
    handover_until: "",
  });
  const [isReturnActionDialogOpen, setIsReturnActionDialogOpen] = useState(false);
  const [selectedReturnApprovalRequest, setSelectedReturnApprovalRequest] = useState(null);
  const [returnApprovalAction, setReturnApprovalAction] = useState("approve"); // approve | reject
  const [returnApprovalComment, setReturnApprovalComment] = useState("");
  const [returnApprovalSaving, setReturnApprovalSaving] = useState(false);
  
  // Vekalet Yönetim Dialog
  const [vekaletDialogOpen, setVekaletDialogOpen] = useState(false);
  const [selectedSubstitution, setSelectedSubstitution] = useState(null);
  const [newSubstituteId, setNewSubstituteId] = useState("");
  const [vekaletLoading, setVekaletLoading] = useState(false);

  // Raporlama (Reporting)
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportType, setReportType] = useState("location-logs");
  const [reportStartDate, setReportStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportEndDate, setReportEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Revize Yönetimi States
  const [revisions, setRevisions] = useState([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [revisionForm, setRevisionForm] = useState({
    park_id: "",
    park_name: "",
    category: DEFAULT_REVISION_TYPE,
    title: "",
    description: "",
    initial_comment: "",
    initial_photos: []
  });
  const [addEntryPhotos, setAddEntryPhotos] = useState([]);
  const [sendingRevisionEntry, setSendingRevisionEntry] = useState(false);
  const revisionEntrySubmitLockRef = useRef(false);
  
  // Erteleme dialog state'leri
  const [showPostponeDialog, setShowPostponeDialog] = useState(false);
  const [postponeDate, setPostponeDate] = useState("");
  const [postponingRevision, setPostponingRevision] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingRevision, setRejectingRevision] = useState(null);

  // Delete confirmation states
  const [parkToDelete, setParkToDelete] = useState(null);
  // Park silme ÜÇÜNCÜ onayı: kullanıcının elle yazdığı park adı. Tek tıklık
  // "Evet, Sil" diyaloğu kazara silmeyi engelleyemiyordu (13 EKİM BAŞKENT PARKI
  // 26.08.2026'da böyle silinip veritabanından elle geri alınmak zorunda kaldı).
  const [parkDeleteConfirmText, setParkDeleteConfirmText] = useState("");
  const [personnelToDelete, setPersonnelToDelete] = useState(null);
  // Personel silme üçüncü onayı — park silmedeki ile aynı kural (ad birebir yazılır).
  const [personnelDeleteConfirmText, setPersonnelDeleteConfirmText] = useState("");
  const [revisionToDelete, setRevisionToDelete] = useState(null);
  const [revisionDeleteConfirmText, setRevisionDeleteConfirmText] = useState("");
  const [deletingRevision, setDeletingRevision] = useState(false);
  const [selectedUrgency, setSelectedUrgency] = useState("ORTA");
  const normalizeRevisionRole = useCallback((role) => {
    const raw = String(role || "").trim().toLowerCase();
    const roleMap = {
      "müdür": "mudur",
      "mudur": "mudur",
      "müdür yardımcısı": "mudur_yardimcisi",
      "mudur yardimcisi": "mudur_yardimcisi",
      "mudur_yardimcisi": "mudur_yardimcisi",
      "bölge mühendisi": "bolge_muhendisi",
      "bolge muhendisi": "bolge_muhendisi",
      "bolge_muhendisi": "bolge_muhendisi",
      "formen": "formen",
      "park görevlisi": "park_gorevlisi",
      "park gorevlisi": "park_gorevlisi",
      "park_gorevlisi": "park_gorevlisi",
      // Migration 0064: tüm çim ekibi varyantları park_gorevlisi'ye yönlendirilir
      "çim ekibi": "park_gorevlisi",
      "çim_ekibi": "park_gorevlisi",
      "cim ekibi": "park_gorevlisi",
      "cim_ekibi": "park_gorevlisi",
    };
    return roleMap[raw] || raw;
  }, []);
  const revisionEntryGate = useMemo(() => {
    if (!selectedRevision) {
      return { canSubmit: false, message: null };
    }
    const finalStatuses = ["Tamamlandı", "Reddedildi"];
    if (finalStatuses.includes(selectedRevision.status)) {
      return { canSubmit: false, message: null };
    }
    const myRole = normalizeRevisionRole(userRole);
    const currentLevel = normalizeRevisionRole(selectedRevision.current_level);
    const hasSubmittedAtMyLevel = (selectedRevision.entries || []).some((entry) =>
      String(entry?.person_id || "") === String(personnelId || "") &&
      normalizeRevisionRole(entry?.level) === myRole
    );
    if (hasSubmittedAtMyLevel) {
      return {
        canSubmit: false,
        message: "Görüşünüz üst makama iletildi. Bu aşamada tekrar görüş gönderemezsiniz.",
      };
    }
    if (currentLevel !== myRole) {
      return {
        canSubmit: false,
        message: "Talep şu anda sizin değerlendirme seviyenizde değil.",
      };
    }
    return { canSubmit: true, message: null };
  }, [selectedRevision, userRole, personnelId, normalizeRevisionRole]);
  const openRevisionPhotoGallery = useCallback((photoUrls, startIndex = 0, title = "Revize Fotoğrafları") => {
    const photos = (photoUrls || []).filter(Boolean);
    if (photos.length === 0) return;

    const safeIndex = Math.max(0, Math.min(startIndex, photos.length - 1));
    const galleryWindow = window.open("", "_blank");
    if (!galleryWindow) {
      toast.error("Yeni sekme açılamadı. Tarayıcı popup engelliyor olabilir.");
      return;
    }

    const photosJson = JSON.stringify(photos);
    const titleJson = JSON.stringify(title);
    const initialIndex = String(safeIndex);

    galleryWindow.document.write(`<!doctype html>
<html lang="tr">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Revize Fotoğraf Galerisi</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      background: #0f172a;
      color: #e2e8f0;
      font-family: Arial, sans-serif;
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    }
    .topbar {
      padding: 12px 16px;
      border-bottom: 1px solid #334155;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
    .title {
      font-size: 14px;
      font-weight: 600;
      color: #f8fafc;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .counter {
      font-size: 12px;
      color: #94a3b8;
      white-space: nowrap;
    }
    .stage {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 16px;
    }
    .nav {
      width: 44px;
      height: 44px;
      border-radius: 999px;
      border: 1px solid #475569;
      background: #1e293b;
      color: #f8fafc;
      font-size: 22px;
      cursor: pointer;
    }
    .nav:disabled {
      opacity: 0.5;
      cursor: default;
    }
    .image-wrap {
      width: min(1100px, 92vw);
      height: min(80vh, 800px);
      display: flex;
      align-items: center;
      justify-content: center;
      background: #020617;
      border-radius: 10px;
      border: 1px solid #334155;
      overflow: hidden;
    }
    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      display: block;
    }
    .help {
      text-align: center;
      font-size: 12px;
      color: #94a3b8;
      padding: 0 16px 12px;
    }
  </style>
</head>
<body>
  <div class="topbar">
    <div id="title" class="title"></div>
    <div id="counter" class="counter"></div>
  </div>
  <div class="stage">
    <button id="prevBtn" class="nav" aria-label="Önceki fotoğraf">&#8592;</button>
    <div class="image-wrap">
      <img id="galleryImage" alt="Revize fotoğrafı" />
    </div>
    <button id="nextBtn" class="nav" aria-label="Sonraki fotoğraf">&#8594;</button>
  </div>
  <div class="help">Klavye kısayolları: Sol/Sağ ok ile gez, Esc ile kapat.</div>
  <script>
    const photos = ${photosJson};
    const titleText = ${titleJson};
    let currentIndex = ${initialIndex};

    const titleEl = document.getElementById("title");
    const counterEl = document.getElementById("counter");
    const imageEl = document.getElementById("galleryImage");
    const prevBtn = document.getElementById("prevBtn");
    const nextBtn = document.getElementById("nextBtn");

    const normalizeIndex = (index) => {
      if (!photos.length) return 0;
      return (index + photos.length) % photos.length;
    };

    const render = () => {
      if (!photos.length) return;
      currentIndex = normalizeIndex(currentIndex);
      imageEl.src = photos[currentIndex];
      titleEl.textContent = titleText;
      counterEl.textContent = (currentIndex + 1) + " / " + photos.length;
      prevBtn.disabled = photos.length <= 1;
      nextBtn.disabled = photos.length <= 1;
    };

    const showPrev = () => {
      currentIndex -= 1;
      render();
    };

    const showNext = () => {
      currentIndex += 1;
      render();
    };

    prevBtn.addEventListener("click", showPrev);
    nextBtn.addEventListener("click", showNext);
    document.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") showPrev();
      if (event.key === "ArrowRight") showNext();
      if (event.key === "Escape") window.close();
    });

    render();
  </script>
</body>
</html>`);
    galleryWindow.document.close();
  }, []);

  // Detail dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailDialogTitle, setDetailDialogTitle] = useState("");
  const [detailDialogParks, setDetailDialogParks] = useState([]);
  /** Envanter/tesis detay modalı filtreleri: park adı + bölge / formen / bölge müh. / müdür yrd. */
  const [invDetailFilters, setInvDetailFilters] = useState({ ...EMPTY_DETAIL_FILTERS });
  /** Havuz detayında "havuz durumu" filtresi (çalışıyor / arızalı / belirtilmemiş) */
  const [poolStatusFilters, setPoolStatusFilters] = useState({
    calisiyor: false,
    arizali: false,
    yok: false,
  });
  /** Yürüme/koşu yolu detayında zemin kutucuk filtresi */
  const [runningTrackSurfaceFilters, setRunningTrackSurfaceFilters] = useState({
    suniCim: false,
    belirtilmemis: false,
    kum: false,
    kaucuk: false,
  });
  /** Çocuk oyun grubu detayında zemin kutucuk filtresi (etiket sırası alfabetik) */
  const [playgroundSurfaceFilters, setPlaygroundSurfaceFilters] = useState({
    ahsap: false,
    belirtilmemis: false,
    dere_cakili: false,
    kaucuk: false,
    kum: false,
    sentetik_cim: false,
  });
  /** Spor aleti detayında zemin kutucuk filtresi (etiket sırası alfabetik) */
  const [sportsEquipmentSurfaceFilters, setSportsEquipmentSurfaceFilters] = useState({
    asfalt: false,
    belirtilmemis: false,
    diger: false,
    kaucuk: false,
    kilit_tasi: false,
    sentetik_cim: false,
  });
  
  // Leave request states
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [isLeaveRequestDialogOpen, setIsLeaveRequestDialogOpen] = useState(false);
  const [leaveRequestForm, setLeaveRequestForm] = useState({
    start_date: "",
    end_date: "",
    return_date: "",
    reason_type: "",  // Boş başlasın, kullanıcı seçmeli
    reason_detail: "",
    substitute_change_reason: "",
    substitute_personnel_id: null,
    park_substitutes: null,
    delegation_scope: null,
    report_file: null,
    report_filename: null
  });
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [isLeaveActionDialogOpen, setIsLeaveActionDialogOpen] = useState(false);
  const [leaveAction, setLeaveAction] = useState("");
  
  // Göster/Gizle state'leri
  const [showMudurYardimcisi, setShowMudurYardimcisi] = useState(true);
  const [showBolgeMuhendisi, setShowBolgeMuhendisi] = useState(true);
  const [showFormen, setShowFormen] = useState(true);
  const [showParkGorevlileri, setShowParkGorevlileri] = useState(false);

  // Rol badge renkleri ve isimler
  const getRoleBadge = (role) => {
    const roleMap = {
      admin: { label: 'Yönetici', className: 'bg-red-500 text-white border-red-600' },
      mudur_yardimcisi: { label: 'Müdür Yardımcısı', className: 'bg-orange-500 text-white border-orange-600' },
      bolge_muhendisi: { label: 'Bölge Mühendisi', className: 'bg-blue-500 text-white border-blue-600' },
      formen: { label: 'Formen', className: 'bg-yellow-500 text-white border-yellow-600' },
      park_gorevlisi: { label: 'Park Görevlisi', className: 'bg-purple-500 text-white border-purple-600' },
      çim_ekibi: { label: 'Çim Ekibi', className: 'bg-teal-500 text-white border-teal-600' }
    };
    
    // Eğer standart rol değilse, custom rol olarak göster
    if (roleMap[role]) {
      return roleMap[role];
    }
    
    // Custom rol - okunabilir hale getir (çim_ekibi -> Çim Ekibi)
    const readableRole = role ? role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Özel Rol';
    return { label: readableRole, className: 'bg-cyan-500 text-white border-cyan-600' };
  };

  // Fetch data - fonksiyonlar altta tanımlandığı için bu useEffect'i aşağı taşıyacağız
  // Geçici olarak yoruma alındı

  // PWA Service Worker Registration
  useEffect(() => {
    const isDev = process.env.NODE_ENV === "development";
    if (isDev) {
      // Dev modunda SW, eski bundle'ı cache'leyip yeni fixleri gölgeleyebiliyor.
      // Bu yüzden geliştirmede her açılışta unregister ediyoruz.
      serviceWorkerRegistration.unregister();
      return;
    }

    serviceWorkerRegistration.register({
      onSuccess: (registration) => {
        console.log('✅ Service Worker registered successfully');
        setSwRegistration(registration);
        toast.success('Offline desteği aktif!', { duration: 2000 });
      },
      onUpdate: (registration) => {
        console.log('🔄 New service worker available');
        toast.info('Yeni güncelleme mevcut. Sayfayı yenileyebilirsiniz.');
      }
    });
    
    // Online/Offline event listeners
    const handleOnline = async () => {
      setIsOnline(true);
      toast.success('İnternet bağlantısı kuruldu!');
      
      // Offline location'ları sync et
      const count = await serviceWorkerRegistration.getOfflineLocationCount();
      if (count > 0) {
        toast.info(`${count} offline konum kaydı senkronize ediliyor...`);
        serviceWorkerRegistration.requestSync();
      }
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('İnternet bağlantısı yok. Veriler offline kaydedilecek.');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Service Worker mesajlarını dinle
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'SYNC_COMPLETE') {
          toast.success(`${event.data.count} konum kaydı başarıyla senkronize edildi!`);
          updateOfflineCount();
        }
      });
    }
    
    // İlk yüklemede offline count'u al
    updateOfflineCount();
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Offline location count güncelle
  const updateOfflineCount = async () => {
    const count = await serviceWorkerRegistration.getOfflineLocationCount();
    setOfflineLocationCount(count);
  };
  
  // Refetch leave requests when personnelId changes (user switch)
  useEffect(() => {
    if (personnelId) {
      fetchLeaveRequests();
      fetchMySubstitutions(); // Aktif vekaletleri de çek
      fetchAllActiveSubstitutions(); // Tüm vekaletleri çek
      fetchActiveDelegations();
    }
  }, [personnelId]);

  // Scroll position restore effect
  useEffect(() => {
    if (shouldRestoreScrollRef.current && scrollPositionRef.current > 0) {
      const savedPosition = scrollPositionRef.current;
      
      // Use setTimeout to ensure DOM is fully rendered
      const timeoutId = setTimeout(() => {
        // Main content container için scroll restore
        const mainContent = document.querySelector('.flex-1.overflow-auto');
        if (mainContent) {
          mainContent.scrollTop = savedPosition;
        } else {
          window.scrollTo({
            top: savedPosition,
            behavior: 'instant'
          });
        }
        // Reset the flag after restoring
        shouldRestoreScrollRef.current = false;
        scrollPositionRef.current = 0;
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [parks, personnel]);

  // Senkronizasyon fonksiyonu - Backend üzerinden Production'a veri aktarır
  const handleSyncToProduction = async () => {
    setSyncLoading(true);
    setSyncProgress("Veriler alınıyor...");
    setSyncResults(null);
    
    try {
      // Backend'deki sync-to-production endpoint'ini çağır
      setSyncProgress("Production'a senkronize ediliyor...");
      const response = await axios.post(`${API}/api/sync/to-production`);
      
      if (!response.data.success) {
        throw new Error(response.data.error || "Senkronizasyon başarısız");
      }
      
      setSyncResults(response.data.results);
      setSyncProgress("Senkronizasyon tamamlandı!");
      toast.success("Veriler Production'a başarıyla aktarıldı!");
      
    } catch (error) {
      console.error("Senkronizasyon hatası:", error);
      const errorMsg = error.response?.data?.detail || error.message;
      setSyncProgress(`Hata: ${errorMsg}`);
      toast.error("Senkronizasyon başarısız: " + errorMsg);
    } finally {
      setSyncLoading(false);
    }
  };

  // ==================== WEBSOCKET HANDLER ====================
  // Anlık güncellemeler için WebSocket mesaj işleyicisi
  const handleWebSocketMessage = useCallback((message) => {
    console.log('WebSocket mesajı alındı:', message);
    const payload = message?.data || message?.payload || {};
    const action = message?.action || payload?.action;
    
    switch (message.type) {
      case 'LEAVE_QUOTA_UPDATE':
        // İzin kotası güncellendi - personel listesini ve stats'ı yenile
        toast.info('İzin kotanız güncellendi!');
        fetchPersonnel({ force: true, silent: true });
        break;
        
      case 'LEAVE_REQUEST_UPDATE':
        // İzin talebi güncellendi
        if (action === 'approved') {
          toast.success('İzin talebiniz onaylandı! ✅');
        } else if (action === 'rejected') {
          toast.error('İzin talebiniz reddedildi. ❌');
        }
        fetchLeaveRequests();
        fetchPersonnel({ force: true, silent: true });
        fetchMySubstitutions();
        fetchAllActiveSubstitutions();
        window.dispatchEvent(new CustomEvent('maintenanceSummaryRefresh'));
        break;
        
      case 'leave_request_deleted':
        if (payload?.request_id) {
          setLeaveRequests(prev => prev.filter(lr => lr.id !== payload.request_id));
          window.__allLeaveRequests = (window.__allLeaveRequests || []).filter(lr => lr.id !== payload.request_id);
        } else {
          fetchLeaveRequests();
        }
        // Onaylı izin silinince personel izin sayaçları (kullanılan/kalan) anında güncellensin.
        fetchPersonnel({ force: true, silent: true });
        fetchMySubstitutions();
        fetchAllActiveSubstitutions();
        break;
        
      case 'DELEGATION_UPDATE':
        // Vekalet güncellendi
        fetchMySubstitutions();
        fetchAllActiveSubstitutions();
        fetchActiveDelegations();
        break;
        
      case 'NEW_MESSAGE':
        // Yeni mesaj geldi
        toast.info(`Yeni mesaj: ${payload?.sender_name || 'Bilinmeyen'} - ${payload?.subject || ''}`);
        // Sidebar rozetini anında güncelle (sekme değişimini bekleme)
        setUnreadMessagesCount(prev => prev + 1);
        // Mesaj ekranı açıksa listeyi de anlık yenile
        window.dispatchEvent(new CustomEvent('messagesRefresh'));
        break;

      case 'operation_assigned':
        // Yeni operasyon emri atandı — Operasyon Emirleri sekmesini tazele
        toast.info(`Yeni görev: ${payload?.title || ''}`);
        window.dispatchEvent(new CustomEvent('operation_assigned', { detail: payload }));
        break;

      case 'operation_updated':
        // Operasyon emri durumu değişti — listeyi/detayı tazele
        window.dispatchEvent(new CustomEvent('operation_updated', { detail: payload }));
        break;

      case 'operation_deleted':
        // Operasyon emri silindi — listeden kaldır, açık detayı kapat
        window.dispatchEvent(new CustomEvent('operation_deleted', { detail: payload }));
        break;

      case 'MESSAGE_DELETED':
        // Gönderilen mesaj silindi; mesaj ekranı açıksa listeyi yenilesin
        window.dispatchEvent(new CustomEvent('messagesRefresh'));
        break;
        
      case 'TASK_UPDATE':
        // Görev güncellendi; Park Bakım Yönetimi özeti anında yenilensin (çıkış yapmadan görünsün)
        window.dispatchEvent(new CustomEvent('maintenanceSummaryRefresh'));
        toast.info(`Görev güncellendi: ${payload?.park_name || ''}`);
        break;

      case 'PARK_TASK_COMPLETED_BY_SUBSTITUTE':
        // Vekil park görevini tamamladı/geri aldı; park sahibi (izinli) listeyi anında yenilesin
        window.dispatchEvent(new CustomEvent('parkTasksRefresh'));
        if (payload?.completed_by_name && payload?.park_name) {
          const actionText = payload?.completed === false ? 'geri aldı' : 'tamamladı';
          toast.info(`${payload.completed_by_name} park görevini ${actionText}: ${payload.park_name}`);
        }
        break;
        
      case 'NOTIFICATION':
        // Bildirim geldi (mesai atama vb.)
        toast.info(payload?.message || 'Yeni bildirim');
        if (typeof fetchInAppNotifications === 'function') fetchInAppNotifications();
        break;
        
      case 'PARK_ASSIGNMENT':
        // Park ataması
        if (action === 'assigned') {
          toast.success(`Yeni parka atandınız: ${payload?.name || ''}`);
        } else {
          toast.info(`Park atamanız kaldırıldı: ${payload?.name || ''}`);
        }
        // TTL cache beklemeden anlık veri çek.
        fetchParks({ force: true, silent: true });
        fetchPersonnel({ force: true, silent: true });
        window.dispatchEvent(new CustomEvent('parkTasksRefresh'));
        window.dispatchEvent(new CustomEvent('maintenanceSummaryRefresh'));
        break;

      case 'PARKS_UPDATED':
        // Parklar eklendi/güncellendi/silindi: özellikle formen ekranında anlık görünürlük için.
        fetchParks({ force: true, silent: true });
        // Park bakım görevleri park ayarlarından üretildiği için görev listelerini de anında tazele.
        window.dispatchEvent(new CustomEvent('parkTasksRefresh'));
        window.dispatchEvent(new CustomEvent('maintenanceSummaryRefresh'));
        break;
        
      case 'MATERIAL_REQUEST_UPDATE':
        // Malzeme talebi güncellendi
        if (action === 'approved') {
          toast.success('Malzeme talebiniz onaylandı!');
        } else if (action === 'delivered') {
          toast.success('Malzemeniz teslim edildi!');
        }
        fetchMaterialRequests();
        break;

      case 'REVISION_UPDATE':
        if (action === 'deleted' && payload?.revision_id) {
          setRevisions((prev) => (prev || []).filter((r) => r?.id !== payload.revision_id));
          if (selectedRevision?.id === payload.revision_id) {
            setSelectedRevision(null);
          }
        } else {
          fetchRevisions();
        }
        break;
        
      case 'PERSONNEL_UPDATE':
        // Personel bilgisi/hiyerarşisi güncellendi
        if (!['subordinate_added', 'subordinate_removed', 'subordinate_updated'].includes(action)) {
          toast.info('Bilgileriniz güncellendi');
        }
        fetchPersonnel({ force: true, silent: true });
        break;

      case 'PERSONNEL_LIST_UPDATED':
        // Herkes için personel listesini sessizce yenile (panel kapat-aç gerekmesin)
        fetchPersonnel({ force: true, silent: true });
        break;

      case 'LOGIN_BLOCKED': {
        const msg = message?.data?.message || 'Hesabınız giriş için engellendi. Oturum sonlandırıldı.';
        toast.error(msg);
        sessionStorage.removeItem('parkBakimAuth');
        delete axios.defaults.headers.common['Authorization'];
        window.dispatchEvent(new Event('parkBakimAuthRequired'));
        break;
      }
        
      default:
        console.log('Bilinmeyen WebSocket mesaj tipi:', message.type);
    }
  }, []);

  // WebSocket bağlantısı
  const { isConnected: wsConnected, connectionError: wsError, reconnect: wsReconnect } = useWebSocket(personnelId, handleWebSocketMessage);
  // Not: Operasyon emirleri olayları (SSE) AuthorizationContext'teki tek merkezi
  // SSE tüketicisinden window event'lerine köprüleniyor (lider yarışını önlemek için).

  const normalizePersonnelList = useCallback((items) => {
    const normalizeRoleKey = (role) => {
      const raw = String(role || "").trim().toLowerCase();
      const normalized = raw
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ç/g, "c")
        .replace(/ğ/g, "g")
        .replace(/ı/g, "i")
        .replace(/ö/g, "o")
        .replace(/ş/g, "s")
        .replace(/ü/g, "u")
        .replace(/-/g, "_")
        .replace(/\s+/g, "_");

            if (normalized === "park_gorevlisi" || normalized === "parkgorevlisi") {
        return "park_gorevlisi";
      }
      if (normalized === "mudur_yardimcisi" || normalized === "muduryardimcisi") {
        return "mudur_yardimcisi";
      }
      if (normalized === "bolge_muhendisi" || normalized === "bolgemuhendisi") {
        return "bolge_muhendisi";
      }
      return raw;
    };

    const list = Array.isArray(items) ? items : [];
    return list
      .filter((p) => p && p.id)
      // Sistem admini hiyerarşi/personel listesinden hariçtir. Admin kendi
      // yönetim arayüzünü (/admin/*) kullanır; ana uygulamada görünmesi
      // gereken bir personel kategorisi değildir.
      .filter((p) => !isSystemAdminPersonnel(p))
      .map((p) => ({
        ...p,
        role: normalizeRoleKey(p.role),
        name: typeof p.name === "string" && p.name.trim() ? p.name : "İsimsiz",
      }));
  }, []);

  const resolveHierarchyIds = useCallback((person) => {
    if (!person) return { myId: null, bmId: null, formenId: null };

    const byId = (id) => personnel.find((p) => p.id === id);
    const role = person.role;

    let myId = person.mudur_yardimcisi_id || null;
    let bmId = person.bolge_muhendisi_id || null;
    let formenId = person.formen_id || null;

    if (role === "mudur_yardimcisi") {
      myId = person.id;
    } else if (role === "bolge_muhendisi") {
      bmId = person.id;
      if (!myId && person.parent_id) {
        const parent = byId(person.parent_id);
        if (parent?.role === "mudur_yardimcisi") myId = parent.id;
      }
    } else if (role === "formen") {
      formenId = person.id;
      if (!bmId && person.parent_id) {
        const parent = byId(person.parent_id);
        if (parent?.role === "bolge_muhendisi") bmId = parent.id;
      }
      if (!myId && bmId) {
        const bm = byId(bmId);
        myId = bm?.mudur_yardimcisi_id || myId;
      }
    } else if (role === "park_gorevlisi") {
      if (!formenId && person.parent_id) {
        const parent = byId(person.parent_id);
        if (parent?.role === "formen") formenId = parent.id;
      }
      if (!bmId && formenId) {
        const formen = byId(formenId);
        bmId = formen?.bolge_muhendisi_id || bmId;
      }
      if (!myId && bmId) {
        const bm = byId(bmId);
        myId = bm?.mudur_yardimcisi_id || myId;
      }
    }

    return { myId, bmId, formenId };
  }, [personnel]);

  const sortByName = useCallback(
    (arr) => [...arr].sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "tr")),
    []
  );

  // Sprint 2 F2.b — 3 paralel useMemo birleşik mantığa indirgendi.
  // Eski: her rol için 3-4 dallı if/else, statik formen_id/BM_id/MY_id FK okuması.
  // Yeni: personel.tum_kapsam yetkisi varsa hepsini göster, aksi halde kendisi +
  //       alt zincirinde olanları göster (useAuthorization().isInChain).
  // FK cascade'i (personnelFilterMY/BM) hala eski denorm FK'lerle çalışır;
  // Sprint 4'te parent_personnel_id'ye tam geçildikten sonra o da güncellenir.
  const myFilterOptions = useMemo(() => {
    let list = personnel.filter((p) => p.role === "mudur_yardimcisi");
    if (!has('personel.tum_kapsam')) {
      list = list.filter((p) => p.id === personnelId || authzIsInChain(p.id));
    }
    return sortByName(list);
  }, [personnel, has, authzIsInChain, personnelId, sortByName]);

  const bmFilterOptions = useMemo(() => {
    let list = personnel.filter((p) => p.role === "bolge_muhendisi");
    if (personnelFilterMY !== "all") {
      list = list.filter((p) => p.mudur_yardimcisi_id === personnelFilterMY);
    }
    if (!has('personel.tum_kapsam')) {
      list = list.filter((p) => p.id === personnelId || authzIsInChain(p.id));
    }
    return sortByName(list);
  }, [personnel, personnelFilterMY, has, authzIsInChain, personnelId, sortByName]);

  const formenFilterOptions = useMemo(() => {
    let list = personnel.filter((p) => p.role === "formen");
    if (personnelFilterBM !== "all") {
      list = list.filter((p) => p.bolge_muhendisi_id === personnelFilterBM);
    }
    if (!has('personel.tum_kapsam')) {
      list = list.filter((p) => p.id === personnelId || authzIsInChain(p.id));
    }
    return sortByName(list);
  }, [personnel, personnelFilterBM, has, authzIsInChain, personnelId, sortByName]);

  useEffect(() => {
    if (personnelFilterMY !== "all" && !myFilterOptions.some((p) => p.id === personnelFilterMY)) {
      setPersonnelFilterMY("all");
    }
  }, [personnelFilterMY, myFilterOptions]);

  useEffect(() => {
    if (personnelFilterBM !== "all" && !bmFilterOptions.some((p) => p.id === personnelFilterBM)) {
      setPersonnelFilterBM("all");
    }
  }, [personnelFilterBM, bmFilterOptions]);

  useEffect(() => {
    if (personnelFilterFormen !== "all" && !formenFilterOptions.some((p) => p.id === personnelFilterFormen)) {
      setPersonnelFilterFormen("all");
    }
  }, [personnelFilterFormen, formenFilterOptions]);

  const fetchPersonnel = useCallback(async (options = {}) => {
    const { force = false, silent = false } = options;
    const meta = coreFetchMetaRef.current.personnel;
    const now = Date.now();
    const RETRY_DELAY_MS = 1200;
    const MAX_ATTEMPTS_PER_CANDIDATE = 2;

    if (!force && meta.inFlight) return meta.inFlight;
    if (!force && meta.lastFetchedAt && now - meta.lastFetchedAt < CORE_DATA_TTL_MS) {
      return true;
    }

    const run = (async () => {
      if (!silent) setLoadingPersonnel(true);
      try {
        const apiCandidates = buildApiCandidates(API);
        let response = null;
        let lastError = null;
        for (const apiBase of apiCandidates) {
          for (let attempt = 1; attempt <= MAX_ATTEMPTS_PER_CANDIDATE; attempt += 1) {
            try {
              response = await axios.get(`${apiBase}/api/personnel`, { timeout: API_TIMEOUT_MS });
              break;
            } catch (err) {
              lastError = err;
              if (attempt < MAX_ATTEMPTS_PER_CANDIDATE) {
                await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
              }
            }
          }
          if (response) break;
        }
        if (!response) throw lastError || new Error("Personel listesi alınamadı");
        const raw = response.data;
        const safePersonnel = normalizePersonnelList(Array.isArray(raw) ? raw : []);
        setPersonnel(safePersonnel);
        meta.lastFetchedAt = Date.now();

        // Yönetici olmayan kullanıcılar için kendi adını set et (müdür ad bilgisini başka yerden alıyor)
        // ESKİ: `!isMudur && !isMudur` (duplicate bug — `personel.tum_kapsam` yoksa)
        if (!has('personel.tum_kapsam') && personnelId) {
          const currentUser = safePersonnel.find(p => p.id === personnelId);
          if (currentUser) {
            setPersonnelName(currentUser.name);
          }
        }
        return true;
      } catch (error) {
        console.error("Personel yüklenirken hata:", error);
        // Liste daha önce yüklendiyse geçici hatada kullanıcıyı gereksiz alarm etme.
        if (!personnel || personnel.length === 0) {
          toast.error("Personel listesi yüklenemedi");
        }
        return false;
      } finally {
        meta.inFlight = null;
        if (!silent) setLoadingPersonnel(false);
      }
    })();

    meta.inFlight = run;
    return run;
  }, [has, personnelId, normalizePersonnelList]);

  const loginBlockedPersonnel = useMemo(() => {
    if (!has('personel.login_yonet')) return [];
    return personnel.filter((p) => p && p.login_blocked);
  }, [personnel, has]);

  const handleUnblockPersonnelLogin = useCallback(
    async (personId) => {
      setUnblockLoginLoadingId(personId);
      try {
        await axios.put(`${API}/api/personnel/${personId}/login-blocked`, { blocked: false });
        toast.success("Giriş engeli kaldırıldı.");
        notifyLoginListMaybeChanged();
        await fetchPersonnel({ force: true, silent: true });
      } catch (error) {
        const d = error.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "İşlem başarısız.");
      } finally {
        setUnblockLoginLoadingId(null);
      }
    },
    [fetchPersonnel]
  );

  const fetchParks = async (options = {}) => {
    const { force = false, silent = false } = options;
    const meta = coreFetchMetaRef.current.parks;
    const now = Date.now();

    if (!force && meta.inFlight) return meta.inFlight;
    if (!force && meta.lastFetchedAt && now - meta.lastFetchedAt < CORE_DATA_TTL_MS) {
      return true;
    }

    const run = (async () => {
      if (!silent) setLoadingParks(true);
      try {
        const apiCandidates = buildApiCandidates(API);
        let response = null;
        let lastError = null;
        for (const apiBase of apiCandidates) {
          try {
            response = await axios.get(`${apiBase}/api/parks`, { timeout: API_TIMEOUT_MS });
            break;
          } catch (err) {
            lastError = err;
          }
        }
        if (!response) throw lastError || new Error("Park listesi alınamadı");
        setParks(response.data);
        meta.lastFetchedAt = Date.now();
        return true;
      } catch (error) {
        console.error("Parklar yüklenirken hata:", error);
        // Sessiz arka plan yenilemesinde toast yok; liste zaten varsa geçici ağ/timeout için alarm yok.
        if (!silent && (!parks || parks.length === 0)) {
          toast.error("Park listesi yüklenemedi");
        }
        return false;
      } finally {
        meta.inFlight = null;
        if (!silent) setLoadingParks(false);
      }
    })();

    meta.inFlight = run;
    return run;
  };

  // Faz 2: paginated + filtered + lightweight kart listesi
  // Mevcut `parks` state'ini etkilemez — sadece ParksTab kart/tablo görünümünü besler.
  const computeRoleScope = useCallback(() => {
    const role = (userRole || "").toLowerCase();
    if (!role || role === "admin" || role === "mudur" || role === "baskan") {
      return null; // tüm parkları görür, filtre yok
    }
    // park.tum_kapsam ("Tüm Parkları Gör"): rolü hiyerarşik scoped olsa bile
    // bu yetkiye sahip kullanıcı tüm müdürlüğün parklarını görür (scope by-pass).
    // Süper admin yetki matrisinden bu kutu açıldığında/kapandığında etki eder.
    if (has('park.tum_kapsam')) {
      return null;
    }
    if (!personnelId) return null;
    if (role === "park_gorevlisi" || role === "gorevli") {
      return `park_gorevlisi:${personnelId}`;
    }
    if (role === "formen" || role === "bolge_muhendisi" || role === "mudur_yardimcisi") {
      return `${role}:${personnelId}`;
    }
    return null;
  }, [userRole, personnelId, has]);

  const fetchParkCards = useCallback(async ({ append = false, skipOverride = null } = {}) => {
    const reqId = ++parkCardsReqIdRef.current;
    if (append) setParkCardsLoadingMore(true); else setParkCardsLoading(true);

    const currentLen = append ? parkCards.length : 0;
    const skip = skipOverride != null ? skipOverride : currentLen;

    const params = {
      skip,
      limit: PARK_CARDS_PAGE_SIZE,
    };
    if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
    if (filterRegion && filterRegion !== "all") params.region_id = filterRegion;
    if (filterPersonnel && filterPersonnel !== "all") params.personnel_id = filterPersonnel;
    const scope = computeRoleScope();
    if (scope) params.role_scope = scope;

    try {
      const apiCandidates = buildApiCandidates(API);
      let response = null;
      let lastError = null;
      for (const apiBase of apiCandidates) {
        try {
          response = await axios.get(`${apiBase}/api/parks/cards`, {
            params,
            timeout: API_TIMEOUT_MS,
          });
          break;
        } catch (err) {
          lastError = err;
        }
      }
      if (!response) throw lastError || new Error("Park kart listesi alınamadı");

      // Race condition: sadece en son istek state'e yazsın
      if (reqId !== parkCardsReqIdRef.current) return;

      const { items = [], total = 0, has_more = false } = response.data || {};
      setParkCards((prev) => (append ? [...prev, ...items] : items));
      setParkCardsTotal(total);
      setParkCardsHasMore(Boolean(has_more));
    } catch (error) {
      if (reqId !== parkCardsReqIdRef.current) return;
      console.error("Park kartları yüklenirken hata:", error);
      if (!append) {
        setParkCards([]);
        setParkCardsTotal(0);
        setParkCardsHasMore(false);
        toast.error("Park kart listesi yüklenemedi");
      }
    } finally {
      if (reqId === parkCardsReqIdRef.current) {
        if (append) setParkCardsLoadingMore(false); else setParkCardsLoading(false);
      }
    }
  }, [searchTerm, filterRegion, filterPersonnel, computeRoleScope, parkCards.length, API]);

  const loadMoreParkCards = useCallback(() => {
    if (parkCardsLoadingMore || !parkCardsHasMore) return;
    fetchParkCards({ append: true });
  }, [parkCardsLoadingMore, parkCardsHasMore, fetchParkCards]);

  // Filtre/arama değişince ilk sayfayı yeniden yükle
  // useDeferredValue: arama her tuşta backend'e gitmesin (App.js:701'de zaten var)
  useEffect(() => {
    fetchParkCards({ append: false, skipOverride: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferredSearchTerm, filterRegion, filterPersonnel, userRole, personnelId, has('park.tum_kapsam')]);

  // Faz 2 — Dashboard / harita / region / personel için yeni fetcher altyapısı
  // Yeni state'ler: filter değişimleri burada da otomatik refetch tetiklemez (App.js içi
  // diğer alt paneller hâlâ parks state'inden besleniyor; aşağıdaki fetcher'lar
  // ihtiyaç anında manuel veya useEffect ile çağrılır).
  const [parkAggregates, setParkAggregates] = useState(null);
  const [parkBoundaries, setParkBoundaries] = useState([]);
  const [regionsWithAggregates, setRegionsWithAggregates] = useState([]);
  const parkAggregatesReqIdRef = useRef(0);
  const parkBoundariesReqIdRef = useRef(0);

  const fetchParkAggregates = useCallback(async () => {
    const reqId = ++parkAggregatesReqIdRef.current;
    const params = {};
    if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
    if (filterRegion && filterRegion !== "all") params.region_id = filterRegion;
    if (filterPersonnel && filterPersonnel !== "all") params.personnel_id = filterPersonnel;
    const scope = computeRoleScope();
    if (scope) params.role_scope = scope;
    try {
      const apiCandidates = buildApiCandidates(API);
      let response = null, lastError = null;
      for (const apiBase of apiCandidates) {
        try {
          response = await axios.get(`${apiBase}/api/parks/aggregates`, { params, timeout: API_TIMEOUT_MS });
          break;
        } catch (err) { lastError = err; }
      }
      if (!response) throw lastError || new Error("Aggregates alınamadı");
      if (reqId === parkAggregatesReqIdRef.current) setParkAggregates(response.data);
    } catch (e) {
      console.error("parkAggregates fetch:", e);
    }
  }, [searchTerm, filterRegion, filterPersonnel, computeRoleScope, API]);

  const fetchParkBoundaries = useCallback(async () => {
    const reqId = ++parkBoundariesReqIdRef.current;
    const params = {};
    if (filterRegion && filterRegion !== "all") params.region_id = filterRegion;
    if (filterPersonnel && filterPersonnel !== "all") params.personnel_id = filterPersonnel;
    const scope = computeRoleScope();
    if (scope) params.role_scope = scope;
    try {
      const apiCandidates = buildApiCandidates(API);
      let response = null, lastError = null;
      for (const apiBase of apiCandidates) {
        try {
          response = await axios.get(`${apiBase}/api/parks/boundaries`, { params, timeout: API_TIMEOUT_MS });
          break;
        } catch (err) { lastError = err; }
      }
      if (!response) throw lastError || new Error("Boundaries alınamadı");
      if (reqId === parkBoundariesReqIdRef.current) setParkBoundaries(response.data || []);
    } catch (e) {
      console.error("parkBoundaries fetch:", e);
    }
  }, [filterRegion, filterPersonnel, computeRoleScope, API]);

  const fetchRegionsWithAggregates = useCallback(async () => {
    try {
      const apiCandidates = buildApiCandidates(API);
      let response = null, lastError = null;
      for (const apiBase of apiCandidates) {
        try {
          response = await axios.get(`${apiBase}/api/regions/with-aggregates`, { timeout: API_TIMEOUT_MS });
          break;
        } catch (err) { lastError = err; }
      }
      if (!response) throw lastError || new Error("regionsWithAggregates alınamadı");
      setRegionsWithAggregates(response.data || []);
    } catch (e) {
      console.error("regionsWithAggregates fetch:", e);
    }
  }, [API]);

  // Personel parkları özet cache (PersonnelDetailsDialog/Form/FormenRegionDetails için)
  const personnelParksSummaryCacheRef = useRef({});
  const fetchPersonnelParksSummary = useCallback(async (personId, { force = false } = {}) => {
    if (!personId) return null;
    const cache = personnelParksSummaryCacheRef.current[personId];
    const now = Date.now();
    if (!force && cache && now - cache.fetchedAt < 30000) return cache.data;
    try {
      const apiCandidates = buildApiCandidates(API);
      let response = null, lastError = null;
      for (const apiBase of apiCandidates) {
        try {
          response = await axios.get(`${apiBase}/api/personnel/${personId}/parks-summary`, { timeout: API_TIMEOUT_MS });
          break;
        } catch (err) { lastError = err; }
      }
      if (!response) throw lastError || new Error("Personel parklar özeti alınamadı");
      personnelParksSummaryCacheRef.current[personId] = { data: response.data, fetchedAt: Date.now() };
      return response.data;
    } catch (e) {
      console.error("personnelParksSummary fetch:", e);
      return null;
    }
  }, [API]);

  // Tek park detay cache (ParkDetailModal + handleParkEdit için)
  const parkDetailCacheRef = useRef({});
  const fetchParkDetail = useCallback(async (parkId, { force = false } = {}) => {
    if (!parkId) return null;
    const cache = parkDetailCacheRef.current[parkId];
    const now = Date.now();
    if (!force && cache && now - cache.fetchedAt < 30000) return cache.data;
    try {
      const apiCandidates = buildApiCandidates(API);
      let response = null, lastError = null;
      for (const apiBase of apiCandidates) {
        try {
          response = await axios.get(`${apiBase}/api/parks/${parkId}`, { timeout: API_TIMEOUT_MS });
          break;
        } catch (err) { lastError = err; }
      }
      if (!response) throw lastError || new Error("Park detayı alınamadı");
      parkDetailCacheRef.current[parkId] = { data: response.data, fetchedAt: Date.now() };
      return response.data;
    } catch (e) {
      console.error("parkDetail fetch:", e);
      return null;
    }
  }, [API]);

  // Tamamlanmış revize geçmişi (RevisionsTab için)
  const fetchCompletedRevisions = useCallback(async ({ skip = 0, limit = 1000 } = {}) => {
    try {
      const apiCandidates = buildApiCandidates(API);
      let response = null, lastError = null;
      for (const apiBase of apiCandidates) {
        try {
          response = await axios.get(`${apiBase}/api/parks/completed-revisions`, {
            params: { skip, limit }, timeout: API_TIMEOUT_MS,
          });
          break;
        } catch (err) { lastError = err; }
      }
      if (!response) throw lastError || new Error("Tamamlanmış revizeler alınamadı");
      return response.data;
    } catch (e) {
      console.error("completedRevisions fetch:", e);
      return { items: [], total: 0, has_more: false };
    }
  }, [API]);

  // Aggregates filtre değişince otomatik refetch (Dashboard kartları için)
  useEffect(() => {
    fetchParkAggregates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferredSearchTerm, filterRegion, filterPersonnel, userRole, personnelId]);

  // Boundaries: sadece filter (search hariç) değişince refetch
  useEffect(() => {
    fetchParkBoundaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRegion, filterPersonnel, userRole, personnelId]);

  // Regions with aggregates: bir kere yükle
  useEffect(() => {
    fetchRegionsWithAggregates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRegions = async (options = {}) => {
    const { force = false } = options;
    const meta = coreFetchMetaRef.current.regions;
    const now = Date.now();

    if (!force && meta.inFlight) return meta.inFlight;
    if (!force && meta.lastFetchedAt && now - meta.lastFetchedAt < CORE_DATA_TTL_MS) {
      return true;
    }

    const run = (async () => {
      try {
        const apiCandidates = buildApiCandidates(API);
        let response = null;
        let lastError = null;
        for (const apiBase of apiCandidates) {
          try {
            response = await axios.get(`${apiBase}/api/regions`, { timeout: API_TIMEOUT_MS });
            break;
          } catch (err) {
            lastError = err;
          }
        }
        if (!response) throw lastError || new Error("Bölge listesi alınamadı");
        setRegions(response.data);
        meta.lastFetchedAt = Date.now();
        return true;
      } catch (error) {
        console.error("Bölgeler yüklenirken hata:", error);
        toast.error("Bölge listesi yüklenemedi");
        return false;
      } finally {
        meta.inFlight = null;
      }
    })();

    meta.inFlight = run;
    return run;
  };

  const fetchSystemSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/system-settings`);
      setSystemSettings(response.data || { mudur_social_report_override: false });
    } catch (error) {
      console.error("Sistem ayarları yüklenemedi:", error);
    }
  }, [API]);

  const getLeaveRequestParams = useCallback(() => {
    const rawRole = String(userRole || "").trim().toLowerCase();
    const roleMap = {
      "admin": "mudur",
      "müdür": "mudur",
      "mudur": "mudur",
      "golge_admin": "golge_admin",
      "müdür yardımcısı": "mudur_yardimcisi",
      "mudur yardimcisi": "mudur_yardimcisi",
      "mudur_yardimcisi": "mudur_yardimcisi",
      "bölge mühendisi": "bolge_muhendisi",
      "bolge muhendisi": "bolge_muhendisi",
      "bolge_muhendisi": "bolge_muhendisi",
      "formen": "formen",
      "park görevlisi": "park_gorevlisi",
      "park gorevlisi": "park_gorevlisi",
      "park_gorevlisi": "park_gorevlisi",
      // Migration 0064: tüm çim ekibi varyantları park_gorevlisi'ye yönlendirilir
      "çim ekibi": "park_gorevlisi",
      "cim ekibi": "park_gorevlisi",
      "çim_ekibi": "park_gorevlisi",
      "cim_ekibi": "park_gorevlisi",
    };
    const role = roleMap[rawRole] || rawRole;
    if (!personnelId) return { limit: 300 };
    const isManager = ["admin", "mudur", "golge_admin", "mudur_yardimcisi", "bolge_muhendisi", "formen"].includes(role);
    if (!role) return { limit: 300 };
    if (isManager) {
      // Müdür / gölge admin: sosyal/rapor dahil tüm hiyerarşi akışını takip edebilmeli (golge_admin sadece görüntüleme).
      if (role === "mudur" || role === "golge_admin") {
        if (leaveRequestViewMode === "touched") {
          return {
            viewer_id: personnelId,
            viewer_role: role,
            touched_by: personnelId,
            limit: 300,
          };
        }
        return {
          viewer_id: personnelId,
          viewer_role: role,
          scope: "visible",
          limit: 300,
        };
      }
      if (leaveRequestViewMode === "touched") {
        return {
          viewer_id: personnelId,
          viewer_role: role,
          touched_by: personnelId,
          limit: 300,
        };
      }
      // MY / BM / Formen: yalnız "onay bekleyenlerim (gelen kutusu)" değil,
      // kendi hiyerarşik kapsamındaki (astlarına ait) TÜM izinler görünmeli
      // (onaylanan/reddedilen dahil). Backend scope:"visible" bunu her rol için
      // kendi görünür-personel kümesiyle sınırlar; müdür gibi kısıtsız DEĞİL.
      return {
        viewer_id: personnelId,
        viewer_role: role,
        scope: "visible",
        limit: 300,
      };
    }
    return {
      viewer_id: personnelId,
      viewer_role: role,
      scope: "mine",
      limit: 300
    };
  }, [personnelId, userRole, leaveRequestViewMode]);

  const fetchLeaveRequests = async () => {
    try {
      console.log("fetchLeaveRequests called - fetching from API...");
      const params = getLeaveRequestParams();
      const response = await apiClient.get(`/leave-requests`, {
        params,
        timeout: API_TIMEOUT_MS
      });
      // PERF: setState'i YALNIZCA veri gerçekten değiştiyse yap. Aksi halde her
      // sessiz yenileme (polling) yeni dizi referansı atayıp gereksiz re-render'a,
      // o da dependency'sinde `leaveRequests` olan effect'lerin yeniden kurulmasına
      // ve refetch SELİNE yol açıyordu (backend'i saniyede bir döverek DELETE'i
      // bağlantı havuzu açlığına sokuyordu). hasListChanged ile referans çalkantısı
      // durdurulur.
      window.__allLeaveRequests = response.data;
      setLeaveRequests(prev =>
        hasListChanged(response.data, prev) ? response.data : prev
      );
      return true;
    } catch (error) {
      console.error("İzin talepleri yüklenirken hata:", error);
      return false;
    }
  };

  /** İzin test verilerini temizle: tüm izin talepleri + personel izin sayaçları sıfırlanır */
  const handleClearLeaveTestData = async (confirmText) => {
    try {
      await axios.post(`${API}/api/data-management/clear-leave-test-data`, null, {
        params: { confirm_text: confirmText }
      });
      toast.success("İzin test verileri temizlendi. Liste yenileniyor.");
      await fetchLeaveRequests();
      await fetchPersonnel();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Temizlenemedi";
      toast.error(msg);
      throw error;
    }
  };

  // Aktif vekaletleri çek (kullanıcının vekil olarak atandığı veya izinde olduğu durumlar)
  const fetchMySubstitutions = async () => {
    if (!personnelId) return;
    try {
      const response = await axios.get(`${API}/api/active-substitutions/personnel/${personnelId}`);
      setMySubstitutions(response.data);
    } catch (error) {
      console.error("Vekaletler yüklenirken hata:", error);
    }
  };

  // Tüm aktif vekaletleri çek (rol bazlı filtrelenmiş; parklar ve personel görünümü için)
  const fetchAllActiveSubstitutions = async () => {
    try {
      const params = new URLSearchParams();
      if (userRole != null && userRole !== "") params.set("user_role", String(userRole));
      if (personnelId != null && personnelId !== "") params.set("user_personnel_id", String(personnelId));
      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(`${API}/api/active-substitutions${query}`);
      setAllActiveSubstitutions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Tüm vekaletler yüklenirken hata:", error);
    }
  };

  const fetchActiveDelegations = useCallback(async () => {
    if (!personnelId) return;
    try {
      const response = await axios.get(`${API}/api/delegations/active/${personnelId}`);
      setActiveDelegations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Aktif delegation kayıtları yüklenemedi:", error);
      setActiveDelegations([]);
    }
  }, [API, personnelId]);

  // Vekaleti kaldır
  const handleRemoveSubstitution = async (substitutionId) => {
    // Yetki kontrolü - sadece yetkililer kaldırabilir
    const currentUser = personnel.find(p => p.id === personnelId);
    // Değişken adı "Name" kalmıştı; aşağıdaki istek `currentUserName` okuyor ve
    // tanımsız isim try içinde ReferenceError fırlatıp "bir hata oluştu" olarak
    // yutuluyordu → vekalet kaldırma isteği sunucuya HİÇ gitmiyordu.
    const currentUserName = currentUser?.name || '';

    setVekaletLoading(true);
    try {
      await axios.delete(`${API}/api/substitutions/${substitutionId}?removed_by_id=${personnelId}&removed_by_name=${encodeURIComponent(currentUserName)}`);
      toast.success("Vekalet başarıyla kaldırıldı");
      fetchAllActiveSubstitutions();
      fetchParks();
      setVekaletDialogOpen(false);
      setSelectedSubstitution(null);
    } catch (error) {
      console.error("Vekalet kaldırılırken hata:", error);
      toast.error(error.response?.data?.detail || "Vekalet kaldırılırken bir hata oluştu");
    } finally {
      setVekaletLoading(false);
    }
  };

  // Vekaleti başka birine devret
  const handleTransferSubstitution = async (substitutionId, newSubstitutePersonnelId) => {
    if (!newSubstitutePersonnelId) {
      toast.error("Lütfen yeni vekil seçin");
      return;
    }
    
    // Yetki kontrolü - sadece yetkililer değiştirebilir
    const currentUser = personnel.find(p => p.id === personnelId);
    const currentUserName = currentUser?.name || '';
    
    setVekaletLoading(true);
    try {
      await axios.put(`${API}/api/substitutions/${substitutionId}/transfer`, {
        new_substitute_personnel_id: newSubstitutePersonnelId,
        changed_by_id: personnelId,
        changed_by_name: currentUserName
      });
      toast.success("Vekalet başarıyla devredildi");
      fetchAllActiveSubstitutions();
      fetchParks();
      setVekaletDialogOpen(false);
      setSelectedSubstitution(null);
      setNewSubstituteId("");
    } catch (error) {
      console.error("Vekalet devredilirken hata:", error);
      toast.error(error.response?.data?.detail || "Vekalet devredilirken bir hata oluştu");
    } finally {
      setVekaletLoading(false);
    }
  };

  const fetchMaterialRequests = async (silent = false) => {
    if (!personnelId || !userRole) return;
    if (!silent) setLoadingMaterials(true);
    try {
      const params = new URLSearchParams();
      params.append('role', isParkGorevlisi ? 'Park Görevlisi' : isFormen ? 'Formen' : isBolgeMuhendisi ? 'Bölge Mühendisi' : isMudurYardimcisi ? 'Müdür Yardımcısı' : 'Müdür');
      if (isParkGorevlisi) {
        params.append('personnel_id', personnelId);
      } else if (isFormen) {
        params.append('formen_id', personnelId);
      }
      const response = await axios.get(`${API}/api/material-requests?${params.toString()}`);
      setMaterialRequests(response.data);
    } catch (error) {
      console.error("Malzeme talepleri yüklenirken hata:", error);
      if (!silent) toast.error("Malzeme talepleri yüklenemedi");
    } finally {
      if (!silent) setLoadingMaterials(false);
    }
  };

  // Mesai Yönetimi - Aktif anket sayısını çek (kullanıcının görebildiği ve görmediği)
  const fetchActiveSurveysCount = async () => {
    try {
      const response = await axios.get(`${API}/api/overtime-surveys?status=active`);
      const personnelResponse = await axios.get(`${API}/api/personnel`);
      const allPersonnel = personnelResponse.data;
      const currentPerson = allPersonnel.find(p => p.id === personnelId);
      
      // Müdür TÜM anketleri görebilir (gri badge)
      if (isMudur) {
        const unseenSurveys = response.data.filter(survey => 
          !survey.viewed_by || !survey.viewed_by.includes(personnelId)
        );
        setActiveSurveysCount(unseenSurveys.length);
        return;
      }
      
      // Kullanıcının görebildiği anketleri filtrele (HİYERARŞİK HEDEFLEME DAHİL)
      const visibleSurveys = response.data.filter(survey => {
        // Kendi oluşturduğu anketi görebilir
        if (survey.created_by === personnelId) return true;
        
        // Anketi oluşturan kişiyi bul
        const creator = allPersonnel.find(p => p.id === survey.created_by);
        if (!creator) return false;
        
        const creatorRole = survey.created_by_role || creator.role;
        
        // === HİYERARŞİK HEDEFLEME KONTROLÜ ===
        // Eğer anket belirli bir formen veya BM'ye hedeflenmiş ise
        
        // Hedef Formen belirlenmişse
        if (survey.target_formen_id) {
          // Ben hedef formen miyim?
          if (personnelId === survey.target_formen_id) return true;
          // Ben hedef formenin ekibinden miyim?
          if (currentPerson?.formen_id === survey.target_formen_id) return true;
          // Hedef BM de belirlenmişse, ben o BM miyim?
          if (survey.target_bolge_muhendisi_id && personnelId === survey.target_bolge_muhendisi_id) return true;
          // Müdür Yardımcısı için badge gösterme
          if (isMudurYardimcisi) {
            return false;
          }
          return false;
        }
        
        // Hedef BM belirlenmişse ama formen belirlenmemişse
        if (survey.target_bolge_muhendisi_id && !survey.target_formen_id) {
          if (personnelId === survey.target_bolge_muhendisi_id) return true;
          if (currentPerson?.bolge_muhendisi_id === survey.target_bolge_muhendisi_id) return true;
          if (isMudurYardimcisi) return false;
          return false;
        }
        
        // === ESKİ MANTIK: Hedef belirlenmemişse oluşturana göre ===
        if (creatorRole === 'formen' && currentPerson?.formen_id === survey.created_by) return true;
        if (creatorRole === 'bolge_muhendisi' && currentPerson?.bolge_muhendisi_id === survey.created_by) return true;
        if (creatorRole === 'mudur_yardimcisi' && currentPerson?.mudur_yardimcisi_id === survey.created_by) return true;
        if (creatorRole === 'mudur') return true;
        
        // === OLUŞTURANIN AMİRLERİ ===
        if (creatorRole === 'formen') {
          if (creator.bolge_muhendisi_id === personnelId) return true;
          if (creator.mudur_yardimcisi_id === personnelId) return true;
        }
        if (creatorRole === 'bolge_muhendisi') {
          if (creator.mudur_yardimcisi_id === personnelId) return true;
        }
        
        return false;
      });
      
      // Görülebilir anketlerden henüz görülmemiş olanları say
      const unseenSurveys = visibleSurveys.filter(survey => 
        !survey.viewed_by || !survey.viewed_by.includes(personnelId)
      );
      setActiveSurveysCount(unseenSurveys.length);
    } catch (error) {
      console.error("Aktif anket sayısı yüklenirken hata:", error);
    }
  };

  const fetchPendingPlansCount = async () => {
    try {
      const [plansResponse, surveysResponse] = await Promise.all([
        axios.get(`${API}/api/overtime-draft-plans`),
        axios.get(`${API}/api/overtime-surveys`),
      ]);
      const personnelResponse = await axios.get(`${API}/api/personnel`);
      const allPersonnel = personnelResponse.data;
      const currentPerson = allPersonnel.find(p => p.id === personnelId);
      const existingSurveyIds = new Set((surveysResponse.data || []).map((s) => s?.id).filter(Boolean));
      
      // Hedef filtresi ile planları filtrele
      const visiblePlans = plansResponse.data.filter(plan => {
        // Silinmiş ankete bağlı planları badge hesabından çıkar
        if (plan?.survey_id && !existingSurveyIds.has(plan.survey_id)) return false;

        const creatorRole = plan.created_by_role;
        const isApproved = plan.status === 'onaylandi';
        
        // Onaylanan planlar badge'de sayılmaz
        if (isApproved) return false;
        
        // Kendi oluşturduğu planı görebilir
        if (plan.created_by === personnelId) return true;
        
        // MÜDÜR YARDIMCISI OLUŞTURDU → Sadece Müdür görebilir (badge için)
        if (creatorRole === 'mudur_yardimcisi') {
          if (isMudur) return true;
          return false;
        }
        
        // MÜDÜR OLUŞTURDU → Başkaları göremez
        if (creatorRole === 'mudur') {
          return false;
        }
        
        // BM OLUŞTURDU → BM + Müdür Yardımcısı görebilir (badge)
        if (creatorRole === 'bolge_muhendisi') {
          const creator = allPersonnel.find(p => p.id === plan.created_by);
          if (creator?.mudur_yardimcisi_id === personnelId) return true;
          if (isMudur) return true;
          return false;
        }
        
        // FORMEN OLUŞTURDU → Hiyerarşik görünürlük
        if (creatorRole === 'formen') {
          const creator = allPersonnel.find(p => p.id === plan.created_by);
          if (creator) {
            if (creator.bolge_muhendisi_id === personnelId) return true;
            if (creator.mudur_yardimcisi_id === personnelId) return true;
            if (isMudur) return true;
          }
          return false;
        }
        
        return false;
      });
      
      // Kullanıcının onaylaması gereken ve henüz görmediği planları say
      const pendingStatuses = ['onay_bekliyor', 'bolge_muhendisi_onayladi', 'mudur_yardimcisi_onayladi'];
      const pendingPlans = visiblePlans.filter(plan => 
        pendingStatuses.includes(plan.status) && 
        (!plan.viewed_by || !plan.viewed_by.includes(personnelId))
      );
      setPendingPlansCount(pendingPlans.length);
    } catch (error) {
      console.error("Onay bekleyen plan sayısı yüklenirken hata:", error);
    }
  };

  const fetchPendingActivityPlansCount = async () => {
    try {
      if (!personnelId || !userRole) {
        setPendingActivityPlansCount(0);
        return;
      }

      let status = "";
      if (isBolgeMuhendisi) status = "pending_bolge";
      else if (isMudurYardimcisi) status = "pending_mudur_yard";
      else if (isMudur || isAdmin) status = "pending_mudur";
      else {
        setPendingActivityPlansCount(0);
        return;
      }

      const response = await axios.get(`${API}/api/activity-plans?status=${status}`);
      const plans = response?.data?.success ? (response.data.plans || []) : [];

      const myPendingPlans = plans.filter((plan) => {
        if (plan.current_approver_id) {
          return plan.current_approver_id === personnelId;
        }

        const creator = personnel.find((p) => p.id === plan.created_by_id);
        if (!creator) return false;

        if (isBolgeMuhendisi) {
          return creator.bolge_muhendisi_id === personnelId;
        }
        if (isMudurYardimcisi) {
          return creator.mudur_yardimcisi_id === personnelId;
        }
        if (isMudur || isAdmin) {
          return true;
        }
        return false;
      });

      setPendingActivityPlansCount(myPendingPlans.length);
    } catch (error) {
      console.error("Faaliyet onay bekleyen plan sayısı yüklenirken hata:", error);
    }
  };

  // Revize Yönetimi API Fonksiyonları
  const fetchRevisions = async () => {
    setLoadingRevisions(true);
    try {
      const params = {};
      if (personnelId) params.personnel_id = personnelId;
      if (userRole) params.role = isAdmin ? "mudur" : userRole;
      
      const response = await axios.get(`${API}/api/revisions`, { params });
      setRevisions(response.data);
    } catch (error) {
      console.error("Revize talepleri yüklenirken hata:", error);
      toast.error("Revize talepleri yüklenemedi");
    } finally {
      setLoadingRevisions(false);
    }
  };

  const handleRevisionSubmit = async (e) => {
    e.preventDefault();
    setLeaveRequestLoading(true);
    try {
      await axios.post(`${API}/api/revisions`, {
        ...revisionForm,
        created_by_id: personnelId,
        created_by_name: personnel.find(p => p.id === personnelId)?.name || "Unknown",
        created_by_role: userRole
      });
      
      toast.success("Revize talebi oluşturuldu!");
      setRevisionDialogOpen(false);
      fetchRevisions();
      
      // Formu sıfırla
      setRevisionForm({
        park_id: "",
        park_name: "",
        category: DEFAULT_REVISION_TYPE,
        title: "",
        description: "",
        initial_comment: "",
        initial_photos: []
      });
    } catch (error) {
      console.error("Revize talebi oluşturulurken hata:", error);
      toast.error("Revize talebi oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  const handleRevisionDelete = async () => {
    // Üçüncü onay: başlık birebir yazılmadan istek gitmez.
    if (!revisionToDelete || !revisionDeleteConfirmed) return;

    setDeletingRevision(true);
    try {
      await axios.delete(`${API}/api/revisions/${revisionToDelete.id}`);
      toast.success("Revize talebi silindi!");
      fetchRevisions();
      setSelectedRevision(null);
      setRevisionToDelete(null);
    } catch (error) {
      console.error("Silme hatası:", error);
      toast.error("Revize talebi silinemedi. Lütfen tekrar deneyin.");
    } finally {
      setDeletingRevision(false);
    }
  };

  // Tamamlanmış aktif revizeyi sil - Çift onay sistemi
  const handleDeleteCompletedRevision = async (revisionId) => {
    if (!revisionId) return;
    
    // 1. Onay
    if (!window.confirm("⚠️ Bu revize talebini silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!")) {
      return;
    }
    
    // 2. Onay
    if (!window.confirm("🛑 SON ONAY: Revize kalıcı olarak silinecek.\n\nDevam etmek istiyor musunuz?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/revisions/${revisionId}`);
      toast.success("Revize silindi!");
      fetchRevisions();
      fetchParks(); // Park listesini de güncelle
    } catch (error) {
      console.error("Revize silme hatası:", error);
      toast.error("Revize silinemedi. Lütfen tekrar deneyin.");
    }
  };

  // Park geçmişinden tamamlanmış revize kaydını sil - Çift onay sistemi
  const handleDeleteCompletedRevisionHistory = async (parkId, revisionId, options = {}) => {
    if (!parkId || !revisionId) return;
    const skipBrowserConfirm = Boolean(options?.skipBrowserConfirm);
    
    if (!skipBrowserConfirm) {
      // 1. Onay
      if (!window.confirm("⚠️ Bu geçmiş revize kaydını silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!")) {
        return;
      }
      
      // 2. Onay
      if (!window.confirm("🛑 SON ONAY: Geçmiş kayıt kalıcı olarak silinecek.\n\nDevam etmek istiyor musunuz?")) {
        return;
      }
    }
    
    try {
      const q = personnelId ? `?personnel_id=${encodeURIComponent(personnelId)}` : "";
      await axios.delete(`${API}/api/parks/${parkId}/completed-revision-history/${revisionId}${q}`);
      toast.success("Geçmiş revize kaydı silindi!");
      fetchParks(); // Park listesini güncelle
    } catch (error) {
      console.error("Geçmiş revize silme hatası:", error);
      toast.error("Geçmiş revize kaydı silinemedi. Lütfen tekrar deneyin.");
    }
  };

  // Initial data fetch - tüm fetch fonksiyonları tanımlandıktan sonra
  useEffect(() => {
    fetchPersonnel();
    fetchParks();
    fetchRegions();
    fetchSystemSettings();
    fetchLeaveRequests();
    fetchActiveSurveysCount(); // Aktif anket sayısını çek
    fetchPendingPlansCount(); // Onay bekleyen plan sayısını çek
    fetchPendingActivityPlansCount(); // Faaliyet Yönetimi onay bekleyen sayısını çek
  }, []); // Sadece component mount olduğunda çalışsın

  // Liste karşılaştırma (JSON.stringify yerine daha hafif)
  const hasListChanged = useCallback((next = [], prev = []) => {
    if (next.length !== prev.length) return true;
    for (let i = 0; i < next.length; i += 1) {
      const nextItem = next[i];
      const prevItem = prev[i];
      if (!prevItem) return true;
      if (nextItem.id !== prevItem.id) return true;
      const nextUpdated = nextItem.updated_at || nextItem.updatedAt || nextItem.created_at || nextItem.createdAt;
      const prevUpdated = prevItem.updated_at || prevItem.updatedAt || prevItem.created_at || prevItem.createdAt;
      if (nextUpdated && prevUpdated && nextUpdated !== prevUpdated) return true;
      if (nextItem.status && prevItem.status && nextItem.status !== prevItem.status) return true;
      // Onay akışı adım tablosunda ilerlediğinden (status 'in_progress' kalır,
      // leave_request.updated_at bump olmayabilir) SIRADAKİ ONAYCI değişimini de
      // izle; aksi halde "Şu an … da" satırı talep bir üst makama geçince stale
      // kalıyordu (ör. BM→MY). current_approver_id leave dışı listelerde yoktur →
      // undefined===undefined, yanlış-pozitif yapmaz.
      if ((nextItem.current_approver_id || null) !== (prevItem.current_approver_id || null)) return true;
    }
    return false;
  }, []);

  // Veri yenileme fonksiyonu - child component'lerden çağrılabilir
  const refreshData = useCallback(async (silent = false) => {
    try {
      const leaveParams = getLeaveRequestParams();
      const apiCandidates = buildApiCandidates(API);
      const primaryApi = apiCandidates[0];
      const [parksRes, regionsRes, personnelRes, leaveRes, materialRes] = await Promise.all([
        axios.get(`${primaryApi}/api/parks`, { timeout: API_TIMEOUT_MS }),
        axios.get(`${primaryApi}/api/regions`, { timeout: API_TIMEOUT_MS }),
        axios.get(`${primaryApi}/api/personnel`, { timeout: API_TIMEOUT_MS }),
        apiClient.get(`/leave-requests`, { params: leaveParams, timeout: API_TIMEOUT_MS }),
        axios.get(`${primaryApi}/api/material-requests`, { timeout: API_TIMEOUT_MS })
      ]);
      setParks(parksRes.data);
      setRegions(regionsRes.data);
      setPersonnel(normalizePersonnelList(Array.isArray(personnelRes.data) ? personnelRes.data : []));
      setLeaveRequests(leaveRes.data);
      setMaterialRequests(materialRes.data);

      // Vekalet: 30 sn'lik genel yenilemede de güncel kalsın (BM dışı roller / WS kapalıyken)
      if (personnelId) {
        try {
          const subParams = new URLSearchParams();
          if (userRole != null && userRole !== "") subParams.set("user_role", String(userRole));
          subParams.set("user_personnel_id", String(personnelId));
          const subQuery = `?${subParams.toString()}`;
          const [subPersRes, delegRes, allSubRes] = await Promise.all([
            axios.get(`${primaryApi}/api/active-substitutions/personnel/${personnelId}`, { timeout: API_TIMEOUT_MS }),
            axios.get(`${primaryApi}/api/delegations/active/${personnelId}`, { timeout: API_TIMEOUT_MS }),
            axios.get(`${primaryApi}/api/active-substitutions${subQuery}`, { timeout: API_TIMEOUT_MS }),
          ]);
          setMySubstitutions(subPersRes.data);
          setActiveDelegations(Array.isArray(delegRes.data) ? delegRes.data : []);
          setAllActiveSubstitutions(Array.isArray(allSubRes.data) ? allSubRes.data : []);
        } catch (subErr) {
          console.error("Vekalet yenileme (refreshData):", subErr);
        }
      }

      if (!silent) {
        console.log('Data refreshed');
      }
    } catch (error) {
      console.error('Data refresh error:', error);
    }
  }, [API, getLeaveRequestParams, normalizePersonnelList, personnelId, userRole]);
  
  // Badge'leri güncelle: bu sekme görünür olduğunda ve 60 sn'de bir (çoklu sekme: her sekme kendi verisini görünürken çeker, lider şartı yok)
  useEffect(() => {
    if (!personnelId) return;
    
    const refreshBadges = async () => {
      if (document.hidden) return;
      await Promise.all([
        fetchActiveSurveysCount(),
        fetchPendingPlansCount(),
        fetchPendingActivityPlansCount()
      ]);
    };
    
    refreshBadges(); // İlk yüklemede
    const onVisible = () => { if (!document.hidden) refreshBadges(); };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(refreshBadges, 60000);
    
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [personnelId, userRole, wsConnected]);

  // Global otomatik veri yenileme - 30 saniyede bir (sadece bu sekme görünürken)
  useEffect(() => {
    if (wsConnected) return;
    const interval = setInterval(() => {
      if (document.hidden || isParkDialogOpen) return;
      refreshData(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [refreshData, wsConnected, isParkDialogOpen]);

  // Sekme tekrar görünür/odakta olunca taze veri çek — SSE event kaçırılırsa fallback.
  // Çıkış-girişe gerek kalmadan başka bir kullanıcının (müdür/admin) yaptığı değişiklik anında yansır.
  useEffect(() => {
    if (!personnelId) return;
    const onActive = () => {
      if (document.hidden || isParkDialogOpen) return;
      fetchParks({ force: true, silent: true });
      fetchPersonnel({ force: true, silent: true });
      fetchRegions({ force: true, silent: true });
    };
    document.addEventListener('visibilitychange', onActive);
    window.addEventListener('focus', onActive);
    return () => {
      document.removeEventListener('visibilitychange', onActive);
      window.removeEventListener('focus', onActive);
    };
  }, [personnelId, isParkDialogOpen]);

  const fetchUnreadMessages = useCallback(async () => {
    if (!personnelId) return;
    try {
      const response = await axios.get(`${API}/api/messages/unread-count/${personnelId}`);
      setUnreadMessagesCount(response.data.unread_count);
    } catch (error) {
      console.error('Okunmamış mesaj sayısı alınamadı:', error);
    }
  }, [personnelId]);

  // Okunmamış mesaj sayısını güncelle (bu sekme görünürken; çoklu sekme için lider şartı yok)
  // PERF-008: 5s → 30s + WebSocket bağlıyken polling kapalı.
  // WebSocket canlı update sağladığında polling yedek olarak gereksiz; bağlantı
  // koparsa 30s'lik fallback poll devreye girer. Görünür davranış: badge sayacı
  // anında WS event ile veya 30s içinde poll ile güncellenir.
  useEffect(() => {
    if (!personnelId) return;
    const run = () => { if (!document.hidden && !isParkDialogOpen) fetchUnreadMessages(); };
    run();
    const onVisible = () => run();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', run);
    // WebSocket bağlıyken polling kapansın — sadece focus/visibility event'leriyle yenile.
    const interval = wsConnected ? null : setInterval(run, 30000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', run);
      if (interval) clearInterval(interval);
    };
  }, [personnelId, fetchUnreadMessages, wsConnected, isParkDialogOpen]);
  
  // Malzeme taleplerini ve revizeleri yükle - personnelId ve userRole hazır olduğunda
  useEffect(() => {
    if (personnelId && userRole) {
      fetchMaterialRequests();
      fetchRevisions();
    }
  }, [personnelId, userRole]);
  
  // activeTab'ı userRole'e göre güncelle
  useEffect(() => {
    if (userRole) {
      setActiveTab(isManagerView ? "dashboard" : "daily-maintenance");
    }
  }, [userRole]);

  useEffect(() => {
    if (isLeaveRequestDialogOpen) {
      setLeaveRequestError(null);
      setLeaveRequestForm({
        start_date: "",
        end_date: "",
        return_date: "",
        reason_type: "",
        reason_detail: "",
        substitute_change_reason: "",
        report_file: null,
        report_filename: null,
        substitute_personnel_id: null,
        park_substitutes: null,
        delegation_scope: null
      });
    }
  }, [isLeaveRequestDialogOpen]);

  const handleLeaveRequestSubmit = async (e) => {
    e.preventDefault();
    if (isDelegatorReadOnly && !isGolgeAdmin) {
      toast.warning(READ_ONLY_MESSAGE);
      return;
    }
    if (isGolgeAdmin) return; // Başkan/GKAGÖLGE sadece görüntüleme
    
    const currentPerson = personnel.find(p => p.id === personnelId);

    // return_date kontrolü
    if (!leaveRequestForm.return_date) {
      toast.error("Lütfen İş Başı tarihini seçiniz");
      return;
    }
    const expectedReturn = getNextWorkday(leaveRequestForm.end_date);
    if (expectedReturn && leaveRequestForm.return_date !== expectedReturn) {
      toast.error(`İşe başlama tarihi ${expectedReturn} olmalıdır.`);
      return;
    }

    // Rapor (istirahat) izni için rapor fotoğrafı zorunlu
    if (leaveRequestForm.reason_type === "Rapor" && !leaveRequestForm.report_file) {
      toast.error("Rapor fotoğrafını yüklemediğiniz için talep oluşturulamıyor. Lütfen rapor fotoğrafını ekleyin.");
      return;
    }

    const formSnapshot = { ...leaveRequestForm };
    setLeaveRequestLoading(true);
    setLeaveRequestError(null);
    // UX: kullanıcı beklemesin; dialog anında kapansın.
    setIsLeaveRequestDialogOpen(false);
    let optimisticId = null;
    try {
      const calc = calculateLeaveDays(
        leaveRequestForm.start_date,
        leaveRequestForm.end_date,
        Boolean(currentPerson?.is_civil_servant),
        leaveRequestForm.return_date || null
      );
      
      console.log('📤 İzin talebi gönderiliyor:', {
        return_date: leaveRequestForm.return_date,
        start_date: leaveRequestForm.start_date,
        end_date: leaveRequestForm.end_date
      });
      
      const payload = {
        personnel_id: personnelId,
        start_date: leaveRequestForm.start_date,
        end_date: leaveRequestForm.end_date,
        return_date: leaveRequestForm.return_date,
        reason_type: leaveRequestForm.reason_type,
        reason_detail: leaveRequestForm.reason_detail || null,
        report_file: leaveRequestForm.report_file || null,
        report_filename: leaveRequestForm.report_filename || null,
        total_days: calc.total, // Takvim aralığı = weekdays + weekends (DB kısıtı: lr_weekdays_consistent)
        weekdays: calc.weekdays,
        weekends: calc.weekends,
        workdays_used: calc.workdays, // İzin hakkından düşülen gün (hafta sonu/tatil hariç; memurda dahil)
        substitute_personnel_id: leaveRequestForm.substitute_personnel_id || null,
        substitute_change_reason: leaveRequestForm.substitute_change_reason || null,
        delegation_scope: leaveRequestForm.delegation_scope || null,
        park_substitutes: (leaveRequestForm.park_substitutes && leaveRequestForm.park_substitutes.length > 0)
          ? leaveRequestForm.park_substitutes.filter(ps => ps.park_id && ps.substitute_id)
          : null
      };
      const logCreateRoutingSnapshot = (created) => {
        const chain = Array.isArray(created?.approval_chain) ? created.approval_chain : [];
        const findRoleId = (roleName) => {
          const p = (personnel || []).find((x) => (x?.role || "").toLowerCase() === roleName && chain.includes(x?.id));
          return p?.id || null;
        };
        console.log("[leave-create][routing]", {
          status: created?.status || null,
          currentApproverRole: ((created?.current_approver_role || "").toUpperCase()) || null,
          currentApproverUserId: created?.current_approver_id || null,
          chainSnapshot: {
            formenId: findRoleId("formen"),
            bmId: findRoleId("bolge_muhendisi"),
            myId: findRoleId("mudur_yardimcisi"),
            mudurId: findRoleId("mudur"),
          },
          routed_to_delegate: Boolean(created?.current_approver_delegated_from_id),
          targetUserId: created?.current_approver_id || null,
        });
      };
      optimisticId = `temp-${Date.now()}`;
      const optimisticRequest = {
        id: optimisticId,
        personnel_id: personnelId,
        personnel_name: currentPerson?.name || personnelName || "Bilinmiyor",
        personnel_role: currentPerson?.role || userRole || "park_gorevlisi",
        start_date: payload.start_date,
        end_date: payload.end_date,
        return_date: payload.return_date,
        reason_type: payload.reason_type,
        reason_detail: payload.reason_detail,
        status: "in_progress",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      // Kullanıcı backend cevabını beklemeden talebi listede görsün.
      setLeaveRequests((prev) => [optimisticRequest, ...(Array.isArray(prev) ? prev : [])]);

      const existsOnServer = async () => {
        try {
          const checkResp = await apiClient.get(`/leave-requests`, { timeout: 2000 });
          const all = Array.isArray(checkResp?.data) ? checkResp.data : [];
          const exists = all.some((lr) =>
            String(lr?.personnel_id || "") === String(payload.personnel_id || "") &&
            String(lr?.start_date || "") === String(payload.start_date || "") &&
            String(lr?.end_date || "") === String(payload.end_date || "") &&
            String(lr?.reason_type || "") === String(payload.reason_type || "")
          );
          return exists;
        } catch {
          // bağlantı veya timeout durumunda false dön
        }
        return false;
      };

      // Kullanıcıyı bekletmemek için önce hızlı bir deneme yap.
      let submitted = false;
      let submitError = null;
      let createdRequestData = null;
      try {
        const createResp = await apiClient.post(`/leave-requests`, payload, {
          timeout: 3000,
          headers: { "Content-Type": "application/json" }
        });
        logCreateRoutingSnapshot(createResp?.data);
        createdRequestData = createResp?.data || null;
        submitted = true;
      } catch (err) {
        submitError = err;
      }

      if (!submitted) {
        submitted = await existsOnServer();
      }

      if (submitted) {
        if (createdRequestData?.status === "rejected") {
          const rejectReason = createdRequestData?.rejection_reason || "Talep kurallar nedeniyle otomatik reddedildi.";
          toast.error(rejectReason);
        } else if (["special_review", "exception_review"].includes(createdRequestData?.review_mode)) {
          toast.info(
            createdRequestData?.review_notice ||
            "Talep, standart zaman kuralı dışında açıldığı için yönetim tarafından talebiniz incelemeye alınmıştır."
          );
        } else {
          toast.success("İzin talebi oluşturuldu");
        }
        fetchLeaveRequests();
      } else {
        const isRetryableSubmitError =
          !submitError ||
          !submitError.response ||
          submitError.code === "ECONNABORTED" ||
          submitError.code === "ERR_NETWORK";

        if (!isRetryableSubmitError) {
          throw submitError;
        }

        // Arka planda tamamla: kullanıcıya anında geri bildirim ver.
        toast.success("Talep gönderildi, işleniyor...");
        (async () => {
          const bgResp = await apiClient.post(`/leave-requests`, payload, {
            timeout: 5000,
            headers: { "Content-Type": "application/json" }
          });
          logCreateRoutingSnapshot(bgResp?.data);
          if (await existsOnServer()) {
            toast.success("İzin talebi oluşturuldu");
            fetchLeaveRequests();
            return;
          }
          throw submitError || new Error("Talep oluşturulamadı");
        })().catch((bgErr) => {
          setLeaveRequests((prev) => (Array.isArray(prev) ? prev.filter((lr) => lr?.id !== optimisticId) : prev));
          const detail = bgErr?.response?.data?.detail || bgErr?.message || "İzin talebi oluşturulamadı";
          setLeaveRequestError(detail);
          setLeaveRequestForm(formSnapshot);
          setIsLeaveRequestDialogOpen(true);
          toast.error(`İzin talebi oluşturulamadı: ${detail}`);
        });
      }
      setLeaveRequestForm({ 
        start_date: "", 
        end_date: "", 
        return_date: "", 
        reason_type: "",  // Boş başlasın
        reason_detail: "",
        report_file: null,
        report_filename: null,
        substitute_personnel_id: null,
        park_substitutes: null,
        delegation_scope: null
      });
    } catch (error) {
      setLeaveRequests((prev) => (Array.isArray(prev) ? prev.filter((lr) => lr?.id !== optimisticId) : prev));
      console.error("İzin talebi oluşturulamadı:", error);
      const detail = error.response?.data?.detail || error.message;
      const message = detail || "İzin talebi oluşturulamadı";
      setLeaveRequestError(message);
      // Hata olursa dialog geri açılsın ve kullanıcı tekrar denesin.
      setLeaveRequestForm(formSnapshot);
      setIsLeaveRequestDialogOpen(true);
      toast.error(detail ? `İzin talebi oluşturulamadı: ${detail}` : "İzin talebi oluşturulamadı");
    } finally {
      setLeaveRequestLoading(false);
    }
  };

  // Material Request CRUD
  const handleMaterialRequestSubmit = async (e) => {
    e.preventDefault();
    
    const quantityValue = parseInt(materialForm.quantity, 10);
    if (!materialForm.material_type || !materialForm.requested_date) {
      toast.error("Lütfen malzeme türü ve talep tarihini seçiniz");
      return;
    }
    if (!quantityValue || quantityValue < 1) {
      toast.error("Lütfen geçerli bir adet giriniz");
      return;
    }
    
    setLoading(true);
    try {
      // Formen başkası adına talep oluşturabilir
      const targetPersonnelId = materialForm.for_personnel_id || personnelId;
      const createdByFormen = isFormen && materialForm.for_personnel_id;
      
      await axios.post(`${API}/api/material-requests`, {
        personnel_id: targetPersonnelId,
        material_type: materialForm.material_type,
        quantity: quantityValue,
        description: materialForm.description || null,
        requested_date: materialForm.requested_date,
        created_by_id: personnelId,
        created_by_role: userRole
      });
      
      const targetName = materialForm.for_personnel_id 
        ? personnel.find(p => p.id === materialForm.for_personnel_id)?.name 
        : null;
      
      toast.success(targetName 
        ? `${targetName} için malzeme talebi oluşturuldu` 
        : "Malzeme talebi başarıyla oluşturuldu"
      );
      setShowMaterialRequestDialog(false);
      setMaterialForm({
        material_type: "",
        quantity: "1",
        description: "",
        requested_date: "",
        for_personnel_id: null
      });
      fetchMaterialRequests(); // Listeyi yenile
    } catch (error) {
      console.error("Malzeme talebi oluşturulamadı:", error);
      toast.error("Malzeme talebi oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialDelivery = async (e) => {
    e.preventDefault();
    
    if (!deliveryForm.delivered_date) {
      toast.error("Lütfen teslim tarihini seçiniz");
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/api/material-requests/${selectedMaterialRequest.id}/deliver?personnel_id=${personnelId}`,
        {
          delivered_date: deliveryForm.delivered_date,
          delivery_note: deliveryForm.delivery_note || null
        }
      );
      
      toast.success("Malzeme başarıyla teslim edildi");
      setShowMaterialDeliveryDialog(false);
      setSelectedMaterialRequest(null);
      setDeliveryForm({
        delivered_date: "",
        delivery_note: ""
      });
      fetchMaterialRequests(); // Listeyi yenile
    } catch (error) {
      console.error("Malzeme teslim edilemedi:", error);
      toast.error(error.response?.data?.error?.message || error.response?.data?.detail || "Malzeme teslim edilemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectMaterialRequest = async (requestId, rejectionReason) => {
    try {
      await axios.put(
        `${API}/api/material-requests/${requestId}/reject?personnel_id=${personnelId}`,
        { rejection_reason: rejectionReason }
      );
      fetchMaterialRequests(); // Listeyi yenile
    } catch (error) {
      console.error("Malzeme talebi reddedilemedi:", error);
      throw error;
    }
  };

  const handleDeleteMaterialRequest = async () => {
    if (!materialToDelete) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/api/material-requests/${materialToDelete.id}`);
      toast.success("Malzeme talebi silindi");
      setMaterialToDelete(null);
      fetchMaterialRequests(); // Listeyi yenile
    } catch (error) {
      console.error("Malzeme talebi silinemedi:", error);
      toast.error(error.response?.data?.detail || "Malzeme talebi silinemedi");
    } finally {
      setLoading(false);
    }
  };

  // Personnel CRUD
  const handlePersonnelSubmit = async (e) => {
    e.preventDefault();
    setIsPersonnelSaving(true);
    
    // Scroll pozisyonunu kaydet - main content container için
    const mainContent = document.querySelector('.flex-1.overflow-auto');
    if (mainContent) {
      scrollPositionRef.current = mainContent.scrollTop;
      shouldRestoreScrollRef.current = true;
    }
    
    try {
      let personnelId = personnelForm.id;
      const requestWithFallback = async (method, endpoint, data = null) => {
        // Önce mevcut ana API yolunu dene (dev'de proxy /api en hızlı yol).
        // Sadece başarısız olursa kısa timeout ile fallback'lere geç.
        const apiCandidates = Array.from(new Set(buildApiCandidates(API)));
        const WRITE_PRIMARY_TIMEOUT_MS = 20000;
        const WRITE_FALLBACK_TIMEOUT_MS = 8000;
        let lastError = null;
        for (let i = 0; i < apiCandidates.length; i++) {
          const apiBase = apiCandidates[i];
          const timeoutMs = i === 0 ? WRITE_PRIMARY_TIMEOUT_MS : WRITE_FALLBACK_TIMEOUT_MS;
          try {
            const response = await axios({
              method,
              url: `${apiBase}${endpoint}`,
              data,
              timeout: timeoutMs,
            });
            return response;
          } catch (err) {
            lastError = err;
          }
        }
        throw lastError || new Error("API isteği başarısız");
      };
      
      // Standart rol slug'ı; özel rol artık ayrı alanlarda (custom_role_name/description) saklanır
      const finalRole = personnelForm.role;
      const shouldSyncParkAssignments = ["park_gorevlisi"].includes(
        String(finalRole || "").trim().toLowerCase()
      );
      const shouldClearFormenParkAssignments =
        String(finalRole || "").trim().toLowerCase() === "formen";
      
      const wasEditing = isEditingPersonnel;
      
      if (isEditingPersonnel) {
        console.log('🔍 Güncelleme verisi:', {
          name: personnelForm.name,
          region_ids: personnelForm.region_ids
        });
        await requestWithFallback("put", `/api/personnel/${personnelForm.id}`, {
          name: personnelForm.name,
          custom_role_name: personnelForm.custom_role_name?.trim() || null,
          custom_role_description: personnelForm.custom_role_description?.trim() || null,
          primary_team_id: personnelForm.primary_team_id || null,
          secondary_team_id: personnelForm.secondary_team_id || null,
          phone: personnelForm.phone,
          email: personnelForm.email,
          status: personnelForm.status,
          role: finalRole,
          role_id: personnelForm.role_id || null,
          parent_id: personnelForm.parent_id,
          parent_personnel_id: personnelForm.parent_personnel_id || null,
          assignment_type: personnelForm.parent_personnel_id
            ? (personnelForm.assignment_type || 'NORMAL')
            : 'NORMAL',
          formen_id: personnelForm.formen_id || null,
          bolge_muhendisi_id: personnelForm.bolge_muhendisi_id || null,
          mudur_yardimcisi_id: personnelForm.mudur_yardimcisi_id || null,
          region_id: personnelForm.region_id || null,
          region_ids: personnelForm.region_ids || [],
          is_civil_servant: personnelForm.is_civil_servant || false,
          work_schedule: personnelForm.work_schedule || null,
          hire_date: personnelForm.hire_date || null,
          annual_leave_days: personnelForm.annual_leave_days ?? 20,
          used_leave_days: personnelForm.used_leave_days ?? 0
        });
      } else {
        const response = await requestWithFallback("post", "/api/personnel", {
          name: personnelForm.name,
          custom_role_name: personnelForm.custom_role_name?.trim() || null,
          custom_role_description: personnelForm.custom_role_description?.trim() || null,
          primary_team_id: personnelForm.primary_team_id || null,
          secondary_team_id: personnelForm.secondary_team_id || null,
          phone: personnelForm.phone,
          email: personnelForm.email,
          status: personnelForm.status || "active",
          role: finalRole || "park_gorevlisi",
          role_id: personnelForm.role_id || null,
          parent_id: personnelForm.parent_id,
          parent_personnel_id: personnelForm.parent_personnel_id || null,
          assignment_type: personnelForm.parent_personnel_id
            ? (personnelForm.assignment_type || 'NORMAL')
            : 'NORMAL',
          formen_id: personnelForm.formen_id || null,
          bolge_muhendisi_id: personnelForm.bolge_muhendisi_id || null,
          mudur_yardimcisi_id: personnelForm.mudur_yardimcisi_id || null,
          region_id: personnelForm.region_id || null,
          region_ids: personnelForm.region_ids || [],
          is_civil_servant: personnelForm.is_civil_servant || false,
          work_schedule: personnelForm.work_schedule || null,
          hire_date: personnelForm.hire_date || null,
          annual_leave_days: personnelForm.annual_leave_days ?? 20,
          used_leave_days: personnelForm.used_leave_days ?? 0
        });
        personnelId = response.data.id;
      }
      
      if (shouldSyncParkAssignments) {
        // Park atamalarını toplu/paralel güncelle (tek tek beklemede takılmayı önler)
        const currentParks = parks.filter((p) =>
          p.assigned_personnel_ids && p.assigned_personnel_ids.includes(personnelForm.id)
        );
        const removeOps = currentParks
          .filter((park) => !personnelForm.assigned_park_ids.includes(park.id))
          .map((park) => {
            const newPersonnelIds = park.assigned_personnel_ids.filter((id) => id !== personnelForm.id);
            return requestWithFallback(
              "put",
              `/api/parks/${park.id}?personnel_id=${encodeURIComponent(personnelId)}`,
              { assigned_personnel_ids: newPersonnelIds }
            );
          });

        const addOps = (personnelForm.assigned_park_ids || [])
          .map((parkId) => {
            const park = parks.find((p) => p.id === parkId);
            if (!park) return null;
            const currentIds = park.assigned_personnel_ids || [];
            if (currentIds.includes(personnelId)) return null;
            return requestWithFallback(
              "put",
              `/api/parks/${parkId}?personnel_id=${encodeURIComponent(personnelId)}`,
              { assigned_personnel_ids: [...currentIds, personnelId] }
            );
          })
          .filter(Boolean);

        const parkResults = await Promise.allSettled([...removeOps, ...addOps]);
        const failedParkOps = parkResults.filter((r) => r.status === "rejected");
        if (failedParkOps.length > 0) {
          toast.warning(`Personel kaydedildi, ${failedParkOps.length} park ataması güncellenemedi.`);
        }
      } else if (shouldClearFormenParkAssignments) {
        // Formen için park bazlı atama tutulmamalı; varsa temizle.
        const currentParks = parks.filter((p) =>
          p.assigned_personnel_ids && p.assigned_personnel_ids.includes(personnelForm.id)
        );
        if (currentParks.length > 0) {
          const clearOps = currentParks.map((park) => {
            const newPersonnelIds = (park.assigned_personnel_ids || []).filter((id) => id !== personnelForm.id);
            return requestWithFallback(
              "put",
              `/api/parks/${park.id}?personnel_id=${encodeURIComponent(personnelId)}`,
              { assigned_personnel_ids: newPersonnelIds }
            );
          });
          void Promise.allSettled(clearOps);
        }
      }
      
      toast.success(isEditingPersonnel ? "Personel güncellendi" : "Personel eklendi");
      resetPersonnelForm();
      setIsPersonnelDialogOpen(false);
      
      // Dialog'un kapanması için kısa bir bekleme
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Data yenilemeyi arka planda yap; kaydet akışını gereksiz uzatma.
      void fetchPersonnel({ force: true, silent: true });
      void fetchParks({ force: true, silent: true });
    } catch (error) {
      console.error("Personel kaydedilirken hata:", error);
      const detail = error?.response?.data?.detail;
      const detailMsg = Array.isArray(detail)
        ? detail.map(d => d?.msg || JSON.stringify(d)).join("; ")
        : (typeof detail === "string" ? detail : null);
      const msg = detailMsg || error?.message || "Personel kaydedilemedi";
      toast.error(`Personel kaydedilemedi: ${msg}`);
    } finally {
      setIsPersonnelSaving(false);
    }
  };

  const handlePersonnelEdit = (person) => {
    // Bu personele atanmış parkları bul
    const assignedParkIds = parks
      .filter(p => p.assigned_personnel_ids && p.assigned_personnel_ids.includes(person.id))
      .map(p => p.id);

    setPersonnelForm({
      ...person,
      status: person.status || "active",
      role: person.role || "park_gorevlisi",
      role_id: person.role_id || '',
      parent_id: person.parent_id || null,
      parent_personnel_id: person.parent_personnel_id || null,
      assignment_type: person.assignment_type || 'NORMAL',
      formen_id: person.formen_id || null,
      bolge_muhendisi_id: person.bolge_muhendisi_id || null,
      mudur_yardimcisi_id: person.mudur_yardimcisi_id || null,
      region_id: person.region_id || null,
      region_ids: person.region_ids || [],
      assigned_park_ids: String(person.role || "").trim().toLowerCase() === "formen" ? [] : assignedParkIds,
      custom_role_name: person.custom_role_name || '',
      custom_role_description: person.custom_role_description || '',
      primary_team_id: person.primary_team_id || '',
      secondary_team_id: person.secondary_team_id || '',
      hire_date: person.hire_date || '',
      annual_leave_days: person.annual_leave_days ?? 20,
      used_leave_days: person.used_leave_days ?? 0,
      is_civil_servant: person.is_civil_servant ?? false
    });
    
    // Bölge mühendisi ve formen için read-only mod
    const readOnlyRoles = ['bolge_muhendisi', 'formen', 'park_gorevlisi', 'golge_admin'];
    setIsFormReadOnly(readOnlyRoles.includes(userRole));
    
    setIsEditingPersonnel(true);
    setIsPersonnelDialogOpen(true);
  };
  
  // Personel Kartını açmak için ayrı fonksiyon
  const handleOpenPersonnelCard = (person) => {
    setSelectedPersonnelForDetail(person);
    setPersonnelDetailDialogOpen(true);
  };

  const handlePersonnelDelete = async (idOrEvent) => {
    // Dialog kapanınca state null olabiliyor; id'yi parametre veya state'ten al
    const idToDelete = typeof idOrEvent === "string" ? idOrEvent : personnelToDelete?.id;
    if (!idToDelete) return;
    try {
      await axios.delete(`${API}/api/personnel/${idToDelete}`);
      toast.success("Personel silindi");
      fetchPersonnel();
      fetchParks();
      setPersonnelToDelete(null);
    } catch (error) {
      console.error("Personel silinirken hata:", error);
      toast.error(error.response?.data?.detail || "Personel silinemedi");
      setPersonnelToDelete(null);
    }
  };

  const resetPersonnelForm = () => {
    setPersonnelForm({
      id: "",
      name: "",
      custom_role_name: "",
      custom_role_description: "",
      primary_team_id: "",
      secondary_team_id: "",
      phone: "",
      email: "",
      status: "active",
      // Dinamik rol/yetki sistemi: rol boş başlar — kullanıcı seçmek zorunda
      role: "",
      role_id: "",
      parent_personnel_id: null,
      assignment_type: "NORMAL",
      region_id: null,
      region_ids: [],
      assigned_park_ids: [],
      is_civil_servant: false
    });
    setIsEditingPersonnel(false);
  };

  // Park CRUD
  const handleParkSubmit = async (e, submittedBoundaries = null) => {
    e.preventDefault();

    // --- Formen değişimi tespiti: formen değişiyorsa parka bağlı park görevlileri
    // de yeni formene taşınır (personnel.formen_id + BM/MY zinciri). ---
    const newFormenId = parkForm.assigned_formen_id || null;
    const isFormenChanged =
      isEditingPark &&
      parkForm.original_formen_id &&
      newFormenId &&
      parkForm.original_formen_id !== newFormenId;
    // Yalnızca ESKİ formene bağlı, bu parktaki park görevlileri taşınır.
    const boundGorevliler = isFormenChanged
      ? (parkForm.existing_personnel_ids || [])
          .map((id) => personnel.find((p) => p.id === id))
          .filter(
            (p) =>
              p &&
              p.role === "park_gorevlisi" &&
              p.formen_id === parkForm.original_formen_id
          )
      : [];
    if (isFormenChanged && boundGorevliler.length > 0) {
      const _newFormen = personnel.find((p) => p.id === newFormenId);
      const onay = window.confirm(
        `🔄 Formen değişiyor\n\n` +
          `Bu parka bağlı ${boundGorevliler.length} park görevlisi de yeni formene ` +
          `(${_newFormen?.name || "-"}) taşınacak:\n\n` +
          boundGorevliler.map((p) => `• ${p.name}`).join("\n") +
          `\n\nNot: Bu görevliler başka parklarda da çalışıyorsa, formenleri her yerde değişir.\n\n` +
          `Devam edilsin mi? (İptal: hiçbir şey değişmez)`
      );
      if (!onay) return;
    }

    setIsParkSaving(true);
    
    // Scroll pozisyonunu kaydet - main content container için
    const mainContent = document.querySelector('.flex-1.overflow-auto');
    if (mainContent) {
      scrollPositionRef.current = mainContent.scrollTop;
      shouldRestoreScrollRef.current = true;
    }
    
    try {
      // Çizim modundan yeni çıkıldığında boundaries state'i parkForm'a henüz
      // yansımamış olabilir; submit'te en güncel çizimi esas al.
      const effectiveBoundaries = (submittedBoundaries && submittedBoundaries.length >= 3)
        ? submittedBoundaries
        : (drawnBoundaries && drawnBoundaries.length >= 3)
          ? drawnBoundaries
        : (parkForm.boundaries && parkForm.boundaries.length >= 3 ? parkForm.boundaries : null);

      const parkData = {
        name: parkForm.name,
        neighborhood: parkForm.neighborhood || null,
        address: parkForm.address || null,
        coordinates: parkForm.coordinates || null,
        latitude: parkForm.latitude || null,
        longitude: parkForm.longitude || null,
        boundaries: effectiveBoundaries,
        region_id: parkForm.region_id || null,
        area: parkForm.area ? parseFloat(parkForm.area) : null,
        grass_area: parkForm.grass_area ? parseFloat(parkForm.grass_area) : null,
        hard_surface_area: parkForm.hard_surface_area ? parseFloat(parkForm.hard_surface_area) : null,
        playground_area: parkForm.playground_area ? parseFloat(parkForm.playground_area) : null,
        light_pole_count: parseInt(parkForm.light_pole_count) || 0,
        bench_count: parseInt(parkForm.bench_count) || 0,
        trash_bin_count: parseInt(parkForm.trash_bin_count) || 0,
        gazebo_count: parseInt(parkForm.gazebo_count) || 0,
        tree_count: parseInt(parkForm.tree_count) || 0,
        flower_count: parseInt(parkForm.flower_count) || 0,
        has_running_track: parkForm.has_running_track,
        running_track_length: parseFloat(parkForm.running_track_length) || 0,
        running_track_width: parseFloat(parkForm.running_track_width) || 0,
        running_track_type: parkForm.running_track_type || "yok",
        has_playground: parkForm.has_playground,
        playground_surface_type: parkForm.playground_surface_type || "yok",
        has_sports_equipment: parkForm.has_sports_equipment,
        sports_equipment_area: parseFloat(parkForm.sports_equipment_area) || 0,
        sports_equipment_surface_type: parkForm.sports_equipment_surface_type || "yok",
        has_wc: parkForm.has_wc,
        has_fountain: parkForm.has_fountain,
        fountain_status: parkForm.fountain_status || "yok",
        has_parking: parkForm.has_parking,
        irrigation_system: parkForm.irrigation_system,
        has_basketball_court: parkForm.has_basketball_court,
        has_football_field: parkForm.has_football_field,
        has_volleyball_court: parkForm.has_volleyball_court,
        has_tennis_court: parkForm.has_tennis_court,
        description: parkForm.description,
        assigned_personnel_ids: (() => {
          // MEVCUT personel atamalarını koru
          const existingIds = parkForm.existing_personnel_ids || [];
          const newPersonnelId = parkForm.assigned_park_gorevlisi_id;
          const newFormenId = parkForm.assigned_formen_id;
          
          // Düzenlemede: SADECE formen kaydı listeden çıkarılır (eski formen
          // gitsin, yenisi gelsin). Park görevlileri KORUNUR — aksi halde park
          // mevcut görevlilerini kaybediyordu. Formen değiştiyse bu görevliler
          // ayrıca yeni formene taşınır (aşağıdaki cascade).
          if (isEditingPark) {
            const resultIds = existingIds.filter(id => {
              const role = personnel.find(p => p.id === id)?.role;
              return role !== 'formen';
            });

            if (newFormenId && !resultIds.includes(newFormenId)) {
              resultIds.push(newFormenId);
            }

            if (newPersonnelId && !resultIds.includes(newPersonnelId)) {
              resultIds.push(newPersonnelId);
            }

            return resultIds;
          }
          
          // Yeni park ekleme durumunda
          const newIds = [];
          if (newFormenId) newIds.push(newFormenId);
          if (newPersonnelId) newIds.push(newPersonnelId);
          return newIds;
        })()
      };

      const parkSaveTimeout = 60000;
      const personnelParam = personnelId ? `?personnel_id=${encodeURIComponent(personnelId)}` : "";
      if (isEditingPark) {
        await axios.put(`${API}/api/parks/${parkForm.id}${personnelParam}`, parkData, { timeout: parkSaveTimeout });
        toast.success("Park güncellendi");
      } else {
        await axios.post(`${API}/api/parks${personnelParam}`, parkData, { timeout: parkSaveTimeout });
        toast.success("Park eklendi");
      }

      // Formen değiştiyse (ve onaylandıysa): bağlı park görevlilerini yeni formene
      // taşı — personnel.formen_id + bağlı oldukları BM/MY zincirini güncelle.
      if (isFormenChanged && boundGorevliler.length > 0) {
        const _newFormen = personnel.find((p) => p.id === newFormenId);
        const moveResults = await Promise.allSettled(
          boundGorevliler.map((g) =>
            axios.put(
              `${API}/api/personnel/${g.id}`,
              {
                formen_id: newFormenId,
                bolge_muhendisi_id: _newFormen?.bolge_muhendisi_id || null,
                mudur_yardimcisi_id: _newFormen?.mudur_yardimcisi_id || null,
              },
              { timeout: 30000 }
            )
          )
        );
        const moved = moveResults.filter((r) => r.status === "fulfilled").length;
        const failed = moveResults.length - moved;
        if (failed > 0) {
          toast.warning(`${moved} görevli taşındı, ${failed} görevli taşınamadı`);
        } else {
          toast.success(
            `${moved} park görevlisi ${_newFormen?.name || "yeni formene"} taşındı`
          );
        }
        fetchPersonnel({ force: true, silent: true });
      }

      resetParkForm();
      setIsParkDialogOpen(false);
      
      // Dialog'un kapanması için kısa bir bekleme
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Data'yı güncelle - scroll restore useEffect tarafından yapılacak
      await fetchParks({ force: true });
      fetchRegions({ force: true });
      // Faz 2: paginated kart listesini de invalidate et (ilk sayfaya dön)
      fetchParkCards({ append: false, skipOverride: 0 });
    } catch (error) {
      console.error("Park kaydedilirken hata:", error);
      toast.error(error?.response?.data?.detail || error?.message || "Park kaydedilemedi");
    } finally {
      setIsParkSaving(false);
    }
  };

  const handleParkEdit = async (park) => {
    console.log("=== handleParkEdit called ===", park?.name);
    // Scroll pozisyonunu kaydet - dialog açılmadan önce
    const mainContent = document.querySelector('.flex-1.overflow-auto');
    if (mainContent) {
      scrollPositionRef.current = mainContent.scrollTop;
      shouldRestoreScrollRef.current = true;
    }

    // Faz 2: park lightweight kaynaktan geldiyse (parkCards) tam veriyi backend'den çek
    // Lightweight obje: area/has_*/boundaries gibi alanlar yok.
    const isLightweight = park && park.id && (park.area === undefined && park.has_running_track === undefined);
    if (isLightweight) {
      const full = await fetchParkDetail(park.id);
      if (full) {
        park = full;
      }
    }

    // Mevcut personel atamalarını koru
    const existingPersonnelIds = park.assigned_personnel_ids || [];
    
    // Boundaries'i yükle
    if (park.boundaries && park.boundaries.length > 0) {
      setDrawnBoundaries(park.boundaries);
    } else {
      setDrawnBoundaries([]);
    }
    
    // assigned_personnel_ids'den Formen ve Park Görevlisi ID'lerini türet
    let derivedFormenId = null;
    let derivedParkGorevlisiId = null;
    let derivedBolgeMuhendisiId = null;
    let derivedMudurYardimcisiId = null;
    
    if (existingPersonnelIds.length > 0 && personnel.length > 0) {
      for (const pid of existingPersonnelIds) {
        const person = personnel.find(p => p.id === pid);
        if (person) {
          if (person.role === 'formen') {
            derivedFormenId = person.id;
            // Formen'in üst hiyerarşisini de al
            derivedBolgeMuhendisiId = person.bolge_muhendisi_id || null;
            derivedMudurYardimcisiId = person.mudur_yardimcisi_id || null;
          } else if (person.role === 'park_gorevlisi') {
            derivedParkGorevlisiId = person.id;
            // Park görevlisinin formen'ini de al (eğer formen henüz bulunmadıysa)
            if (!derivedFormenId && person.formen_id) {
              derivedFormenId = person.formen_id;
              // Formen'in üst hiyerarşisini bul
              const formenPerson = personnel.find(p => p.id === person.formen_id);
              if (formenPerson) {
                derivedBolgeMuhendisiId = formenPerson.bolge_muhendisi_id || null;
                derivedMudurYardimcisiId = formenPerson.mudur_yardimcisi_id || null;
              }
            }
          }
        }
      }
    }
    
    setParkForm({
      ...park,
      neighborhood: park.neighborhood || "",
      address: park.address || "",
      coordinates: park.coordinates || "",
      latitude: park.latitude || null,
      longitude: park.longitude || null,
      boundaries: park.boundaries || [],
      region_id: park.region_id || null,
      // Personel atamaları - assigned_personnel_ids'den türetildi
      assigned_personnel_ids: existingPersonnelIds,
      assigned_formen_id: derivedFormenId,
      original_formen_id: derivedFormenId, // Formen değişimini tespit etmek için (görevlileri taşıma)
      assigned_park_gorevlisi_id: derivedParkGorevlisiId,
      assigned_bolge_muhendisi_id: derivedBolgeMuhendisiId,
      assigned_mudur_yardimcisi_id: derivedMudurYardimcisiId,
      existing_personnel_ids: existingPersonnelIds, // Mevcut atamaları sakla
      area: park.area || "",
      grass_area: park.grass_area || "",
      hard_surface_area: park.hard_surface_area || "",
      playground_area: park.playground_area || "",
      light_pole_count: park.light_pole_count || 0,
      bench_count: park.bench_count || 0,
      trash_bin_count: park.trash_bin_count || 0,
      gazebo_count: park.gazebo_count || 0,
      tree_count: park.tree_count || 0,
      flower_count: park.flower_count || 0,
      has_running_track: park.has_running_track || false,
      running_track_length: park.running_track_length || 0,
      running_track_width: park.running_track_width || 0,
      running_track_type: park.running_track_type || "yok",
      has_playground: park.has_playground || false,
      playground_surface_type: park.playground_surface_type || "yok",
      has_sports_equipment: park.has_sports_equipment || false,
      sports_equipment_area: park.sports_equipment_area || 0,
      sports_equipment_surface_type: park.sports_equipment_surface_type || "yok",
      has_wc: park.has_wc || false,
      has_fountain: park.has_fountain || false,
      fountain_status: park.fountain_status || "yok",
      has_parking: park.has_parking || false,
      irrigation_system: park.irrigation_system || "yok",
      has_basketball_court: park.has_basketball_court || false,
      has_football_field: park.has_football_field || false,
      has_volleyball_court: park.has_volleyball_court || false,
      has_tennis_court: park.has_tennis_court || false
    });
    
    // Yeni unified akış: modal her zaman read-only başlar.
    // Yetkisi olan kullanıcı (canEditPark) modal içindeki "Düzenle" butonuyla editable'a geçer.
    setIsFormReadOnly(true);

    setIsEditingPark(true);
    setIsParkDialogOpen(true);

    // PERF: Detay modalında harita otomatik açılır; ancak Leaflet + dış
    // OpenStreetMap tile yüklemesi ağır olduğu için, eskiden modal kartının
    // GÖRÜNMESİNİ saniyelerce bloklayabiliyordu (özellikle yavaş ağda ~15 sn).
    // Çözüm: önce modal paint olsun (kart anında açılsın), haritayı bir sonraki
    // frame'de mount et. Böylece kart anında, harita arkadan yüklenir.
    setShowMap(false);
    setTimeout(() => setShowMap(true), 0);
  };

  // Silme diyaloğu her açılıp kapandığında yazılan onay metnini sıfırla —
  // aksi hâlde bir önceki parkın adı kutuda kalıp onayı anlamsızlaştırırdı.
  useEffect(() => {
    setParkDeleteConfirmText("");
  }, [parkToDelete]);

  useEffect(() => {
    setPersonnelDeleteConfirmText("");
  }, [personnelToDelete]);

  useEffect(() => {
    setRevisionDeleteConfirmText("");
  }, [revisionToDelete]);

  const parkDeleteConfirmed =
    !!parkToDelete && deleteNameMatches(parkDeleteConfirmText, parkToDelete.name);

  const personnelDeleteConfirmed =
    !!personnelToDelete &&
    deleteNameMatches(personnelDeleteConfirmText, personnelToDelete.name);

  // Revize talebinde ad yerine talebin BAŞLIĞI yazılır (park adı tek başına
  // ayırt edici değil — aynı parkta birden çok revize talebi olabilir).
  const revisionDeleteConfirmed =
    !!revisionToDelete &&
    deleteNameMatches(revisionDeleteConfirmText, revisionToDelete.title);

  const handleParkDelete = async () => {
    if (!parkToDelete || !parkDeleteConfirmed) return;

    // Scroll pozisyonunu kaydet (hem window hem de main container için)
    const mainContent = document.querySelector('.flex-1.overflow-auto');
    const containerScrollTop = mainContent ? mainContent.scrollTop : 0;
    const windowScrollY = window.scrollY;
    
    try {
      const apiCandidates = buildApiCandidates(API);
      let deleteOk = false;
      let lastError = null;
      for (const apiBase of apiCandidates) {
        try {
          const delQ = personnelId ? `?personnel_id=${encodeURIComponent(personnelId)}` : "";
          await axios.delete(`${apiBase}/api/parks/${parkToDelete.id}${delQ}`, { timeout: API_TIMEOUT_MS });
          deleteOk = true;
          break;
        } catch (err) {
          lastError = err;
        }
      }
      if (!deleteOk) throw lastError || new Error("Park silme isteği başarısız");
      toast.success("Park silindi");
      await fetchParks();
      fetchRegions(); // Bölge istatistiklerini güncelle
      // Faz 2: paginated kart listesini de invalidate et
      fetchParkCards({ append: false, skipOverride: 0 });
      setParkToDelete(null);
      
      // Scroll pozisyonunu geri yükle - React render sonrası için gecikme
      setTimeout(() => {
        const mainContentAfter = document.querySelector('.flex-1.overflow-auto');
        if (mainContentAfter) {
          mainContentAfter.scrollTop = containerScrollTop;
        }
        window.scrollTo(0, windowScrollY);
      }, 150);
    } catch (error) {
      console.error("Park silinirken hata:", error);
      const detail = error?.response?.data?.detail || error?.message || "";
      toast.error(detail ? `Park silinemedi: ${detail}` : "Park silinemedi");
      setParkToDelete(null);
    }
  };

  // Hızlı bölge ekleme
  const handleQuickRegionAdd = async (e) => {
    e.preventDefault();
    if (!quickRegionName.trim()) {
      toast.error("Bölge adı giriniz");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/regions`, {
        name: quickRegionName.trim()
      });
      
      toast.success("Bölge eklendi");
      await fetchRegions(); // Bölgeleri yenile
      
      const newRegionId = response.data.id;
      
      // Context'e göre uygun forma ekle
      if (quickAddContext === "personnel") {
        // Personel formu - çoklu seçim
        const currentRegions = personnelForm.region_ids || [];
        setPersonnelForm({
          ...personnelForm,
          region_ids: [...currentRegions, newRegionId]
        });
      } else if (quickAddContext === "park") {
        // Park formu - tekli seçim
        setParkForm({
          ...parkForm,
          region_id: newRegionId
        });
      }
      
      setQuickRegionName("");
      setIsQuickRegionDialogOpen(false);
      setQuickAddContext(null);
    } catch (error) {
      console.error("Bölge eklenirken hata:", error);
      toast.error("Bölge eklenemedi");
    } finally {
      setLoading(false);
    }
  };

  // Hızlı park ekleme
  const handleQuickParkAdd = async (parkName) => {
    if (!parkName.trim()) {
      toast.error("Park adı giriniz");
      return false;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/parks`, {
        name: parkName.trim()
      });
      
      // Önce parkları yenile
      await fetchParks();
      
      // Yeni eklenen parkı otomatik olarak seç
      const newParkId = response.data.id;
      setPersonnelForm({
        ...personnelForm,
        assigned_park_ids: [...(personnelForm.assigned_park_ids || []), newParkId]
      });
      
      // Başarı mesajı ve dialog kapatma
      toast.success(`${parkName.trim()} parkı eklendi ve seçildi`);
      setIsQuickParkDialogOpen(false);
      return true;
    } catch (error) {
      console.error("Park eklenirken hata:", error);
      toast.error(`Park eklenemedi: ${error.response?.data?.detail || error.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Bölge → sorumlu personel çözümleme (formen / bölge mühendisi / müdür yardımcısı).
  // Envanter detay modalındaki filtre ve "formen kim" etiketinde kullanılır.
  const getRegionMeta = (regionId) => {
    const empty = {
      formenIds: [],
      formenNames: [],
      bolgeMuhId: null,
      bolgeMuhName: "",
      mudurYrdId: null,
      mudurYrdName: "",
    };
    if (!regionId || regionId === "none") return empty;
    const region = regions.find((r) => String(r.id) === String(regionId));
    if (!region) return empty;
    const formenIds = (region.assigned_formen_ids || []).map(String);
    const formenNames = formenIds
      .map((id) => personnel.find((p) => String(p.id) === id)?.name)
      .filter(Boolean);
    let bolgeMuhId = region.assigned_bolge_muhendisi_id
      ? String(region.assigned_bolge_muhendisi_id)
      : null;
    // Bölgeye doğrudan mühendis atanmadıysa formenin bağlı olduğu mühendisi kullan
    if (!bolgeMuhId && formenIds.length) {
      const firstFormen = personnel.find((p) => String(p.id) === formenIds[0]);
      if (firstFormen?.bolge_muhendisi_id) bolgeMuhId = String(firstFormen.bolge_muhendisi_id);
    }
    const bolgeMuh = bolgeMuhId ? personnel.find((p) => String(p.id) === bolgeMuhId) : null;
    const mudurYrdId = bolgeMuh?.mudur_yardimcisi_id
      ? String(bolgeMuh.mudur_yardimcisi_id)
      : null;
    const mudurYrd = mudurYrdId ? personnel.find((p) => String(p.id) === mudurYrdId) : null;
    return {
      formenIds,
      formenNames,
      bolgeMuhId,
      bolgeMuhName: bolgeMuh?.name || "",
      mudurYrdId,
      mudurYrdName: mudurYrd?.name || "",
    };
  };

  // Bir park, detay modalı filtrelerinden (park adı + bölge/formen/BM/MY) geçiyor mu?
  // `skip`: o dropdown'un kendi seçimini hesaba katma (kademeli seçenek üretimi).
  // Park adı araması `skip` DIŞINDADIR: her zaman uygulanır, böylece kullanıcı ad
  // yazdıkça dropdown seçenekleri de yalnızca eşleşen parkların bölge/formen/BM/MY
  // değerlerine daralır (dropdown'ların birbirini daraltmasıyla aynı mantık).
  const parkPassesOrgFilter = (park, f, skip) => {
    const m = getRegionMeta(park.regionId);
    if (!searchMatches(park.name, f.q)) return false;
    if (skip !== "region" && f.region !== "all" && String(park.regionId) !== String(f.region)) return false;
    if (skip !== "formen" && f.formen !== "all" && !m.formenIds.includes(String(f.formen))) return false;
    if (skip !== "bolgeMuh" && f.bolgeMuh !== "all" && String(m.bolgeMuhId) !== String(f.bolgeMuh)) return false;
    if (skip !== "mudurYrd" && f.mudurYrd !== "all" && String(m.mudurYrdId) !== String(f.mudurYrd)) return false;
    return true;
  };

  // Kademeli filtre seçenekleri: her dropdown'un seçenekleri, diğer aktif
  // filtrelerden geçen parklardan toplanır. (Örn. BM "Gökhan" seçiliyken formen
  // listesi yalnızca Gökhan'a bağlı bölgelerin formenlerini gösterir.)
  const buildOrgFilterOptions = (parksList, f) => {
    const regionOptMap = new Map();
    const formenOptMap = new Map();
    const bolgeMuhOptMap = new Map();
    const mudurYrdOptMap = new Map();
    parksList.forEach((park) => {
      const meta = getRegionMeta(park.regionId);
      if (parkPassesOrgFilter(park, f, "region") && park.regionId && park.regionId !== "none") {
        regionOptMap.set(park.regionId, park.regionName || "Bölge");
      }
      if (parkPassesOrgFilter(park, f, "formen")) {
        meta.formenIds.forEach((id, i) => {
          if (meta.formenNames[i]) formenOptMap.set(id, meta.formenNames[i]);
        });
      }
      if (parkPassesOrgFilter(park, f, "bolgeMuh") && meta.bolgeMuhId && meta.bolgeMuhName) {
        bolgeMuhOptMap.set(meta.bolgeMuhId, meta.bolgeMuhName);
      }
      if (parkPassesOrgFilter(park, f, "mudurYrd") && meta.mudurYrdId && meta.mudurYrdName) {
        mudurYrdOptMap.set(meta.mudurYrdId, meta.mudurYrdName);
      }
    });
    // SEÇİLİ değer, diğer filtreler (özellikle park adı araması) yüzünden hiçbir
    // parkta kalmasa bile listede TUTULUR. Aksi hâlde SearchableSelect eşleşen
    // seçeneği bulamayıp "Tümü" etiketini gösteriyordu: filtre hâlâ açıkken kapalı
    // görünüyor, kullanıcı da aradığı parkı "sistemde yok" sanıyordu.
    const keepSelected = (map, id, resolveName) => {
      if (!id || id === "all") return;
      const already = Array.from(map.keys()).some((k) => String(k) === String(id));
      if (already) return;
      const name = resolveName();
      if (name) map.set(id, name);
    };
    const personName = (id) =>
      personnel.find((p) => String(p.id) === String(id))?.name;
    keepSelected(regionOptMap, f.region, () =>
      regions.find((r) => String(r.id) === String(f.region))?.name
    );
    keepSelected(formenOptMap, f.formen, () => personName(f.formen));
    keepSelected(bolgeMuhOptMap, f.bolgeMuh, () => personName(f.bolgeMuh));
    keepSelected(mudurYrdOptMap, f.mudurYrd, () => personName(f.mudurYrd));

    const toSorted = (m) =>
      Array.from(m.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name, "tr"));
    return {
      regionOptions: toSorted(regionOptMap),
      formenOptions: toSorted(formenOptMap),
      bolgeMuhOptions: toSorted(bolgeMuhOptMap),
      mudurYrdOptions: toSorted(mudurYrdOptMap),
    };
  };

  // =====================================================================
  // İzinli personel modalı filtreleri (park tarafının personel karşılığı)
  // =====================================================================
  // Park tarafındaki getRegionMeta park → bölge yönünde çalışır. Burada
  // listelenen şey park değil PERSONEL olduğu için kişi → bölge yönü gerekir:
  // bir kişi bölgeye hem kendi FK'siyle, hem bölge denorm'undaki atamayla,
  // hem de sorumlu olduğu parklar üzerinden bağlı olabilir. Üçü de toplanır,
  // aksi hâlde park görevlileri (kendi region FK'si çoğu zaman boş) bölge
  // filtresinde hiç görünmezdi.
  const getPersonnelOrgMeta = (person) => {
    const empty = {
      regionIds: [],
      formenId: null, formenName: "",
      bolgeMuhId: null, bolgeMuhName: "",
      mudurYrdId: null, mudurYrdName: "",
    };
    if (!person) return empty;
    const pid = String(person.id);
    const nameOf = (id) =>
      id ? personnel.find((p) => String(p.id) === String(id))?.name || "" : "";

    const formenId = person.formen_id ? String(person.formen_id) : null;
    const bolgeMuhId = person.bolge_muhendisi_id ? String(person.bolge_muhendisi_id) : null;
    const mudurYrdId = person.mudur_yardimcisi_id ? String(person.mudur_yardimcisi_id) : null;

    const regionIds = new Set();
    (Array.isArray(person.region_ids) ? person.region_ids : []).forEach(
      (r) => r && regionIds.add(String(r))
    );
    [person.primary_region_id, person.region_id, person.formen_region_id,
     person.bolge_muhendisi_region_id].forEach((r) => r && regionIds.add(String(r)));
    (Array.isArray(regions) ? regions : []).forEach((r) => {
      if (String(r.assigned_bolge_muhendisi_id || "") === pid) regionIds.add(String(r.id));
      if (Array.isArray(r.assigned_formen_ids) &&
          r.assigned_formen_ids.some((fid) => String(fid) === pid)) {
        regionIds.add(String(r.id));
      }
    });
    (Array.isArray(parks) ? parks : []).forEach((p) => {
      if (p.region_id && Array.isArray(p.assigned_personnel_ids) &&
          p.assigned_personnel_ids.some((x) => String(x) === pid)) {
        regionIds.add(String(p.region_id));
      }
    });

    return {
      regionIds: Array.from(regionIds),
      formenId, formenName: person.formen_name || nameOf(formenId),
      bolgeMuhId, bolgeMuhName: person.bolge_muhendisi_name || nameOf(bolgeMuhId),
      mudurYrdId, mudurYrdName: person.mudur_yardimcisi_name || nameOf(mudurYrdId),
    };
  };

  // `skip`: o dropdown'un kendi seçimini hesaba katma (kademeli seçenek üretimi).
  // Ad araması skip DIŞINDADIR: yazdıkça dropdown'lar da daralır.
  const personnelPassesOrgFilter = (person, f, skip) => {
    if (!searchMatches(person?.name, f.q)) return false;
    const m = getPersonnelOrgMeta(person);
    if (skip !== "region" && f.region !== "all" && !m.regionIds.includes(String(f.region))) return false;
    if (skip !== "formen" && f.formen !== "all" && String(m.formenId) !== String(f.formen)) return false;
    if (skip !== "bolgeMuh" && f.bolgeMuh !== "all" && String(m.bolgeMuhId) !== String(f.bolgeMuh)) return false;
    if (skip !== "mudurYrd" && f.mudurYrd !== "all" && String(m.mudurYrdId) !== String(f.mudurYrd)) return false;
    return true;
  };

  const buildPersonnelOrgFilterOptions = (people, f) => {
    const regionOptMap = new Map();
    const formenOptMap = new Map();
    const bolgeMuhOptMap = new Map();
    const mudurYrdOptMap = new Map();
    const regionName = (id) =>
      (Array.isArray(regions) ? regions : []).find((r) => String(r.id) === String(id))?.name;

    people.forEach((person) => {
      const meta = getPersonnelOrgMeta(person);
      if (personnelPassesOrgFilter(person, f, "region")) {
        meta.regionIds.forEach((rid) => {
          const nm = regionName(rid);
          if (nm) regionOptMap.set(rid, nm);
        });
      }
      if (personnelPassesOrgFilter(person, f, "formen") && meta.formenId && meta.formenName) {
        formenOptMap.set(meta.formenId, meta.formenName);
      }
      if (personnelPassesOrgFilter(person, f, "bolgeMuh") && meta.bolgeMuhId && meta.bolgeMuhName) {
        bolgeMuhOptMap.set(meta.bolgeMuhId, meta.bolgeMuhName);
      }
      if (personnelPassesOrgFilter(person, f, "mudurYrd") && meta.mudurYrdId && meta.mudurYrdName) {
        mudurYrdOptMap.set(meta.mudurYrdId, meta.mudurYrdName);
      }
    });

    // Seçili değer kalan kayıtlarda kalmasa bile listede TUTULUR; aksi hâlde
    // dropdown "Tümü" etiketine düşüp açık filtreyi gizler (park tarafındaki
    // ile aynı tuzak).
    const keepSelected = (map, id, resolve) => {
      if (!id || id === "all") return;
      if (Array.from(map.keys()).some((k) => String(k) === String(id))) return;
      const nm = resolve();
      if (nm) map.set(id, nm);
    };
    const personName = (id) =>
      personnel.find((p) => String(p.id) === String(id))?.name;
    keepSelected(regionOptMap, f.region, () => regionName(f.region));
    keepSelected(formenOptMap, f.formen, () => personName(f.formen));
    keepSelected(bolgeMuhOptMap, f.bolgeMuh, () => personName(f.bolgeMuh));
    keepSelected(mudurYrdOptMap, f.mudurYrd, () => personName(f.mudurYrd));

    const toSorted = (m) =>
      Array.from(m.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name, "tr"));
    return {
      regionOptions: toSorted(regionOptMap),
      formenOptions: toSorted(formenOptMap),
      bolgeMuhOptions: toSorted(bolgeMuhOptMap),
      mudurYrdOptions: toSorted(mudurYrdOptMap),
    };
  };

  // İzinli modalında görüntülenen günün tarihi + etiketi.
  // Gün ötelemesi gün SAYISI olarak tutulur; tarih her render'da yerel saate göre
  // türetilir, böylece gece yarısını geçen açık bir sekmede "bugün" kaymaz.
  const LEAVE_DAY_MAX_OFFSET = 30;
  const getLeaveViewDay = (offset) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + offset);
    return d;
  };
  const leaveViewDate = getLeaveViewDay(leaveDayOffset);
  const leaveDayLabel =
    leaveDayOffset === 0 ? "Bugün" : leaveDayOffset === 1 ? "Yarın" : null;

  // "İşe başlama" çipi — izinli/raporlu modallarındaki kart başlığında.
  // İzin kartıyla aynı kaynak (`return_date`): bitiş tarihi izinin SON günü,
  // işe dönüş ise ertesi iş günü olduğundan ikisi karışabiliyordu; yönetici
  // "ne zaman geri gelecek" sorusunu tarih hesaplamadan görsün diye ayrı yazılır.
  const renderReturnDateChip = (returnDate) => {
    if (!returnDate) return null;
    const d = new Date(returnDate);
    if (Number.isNaN(d.getTime())) return null;
    return (
      <span
        className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-800"
        title="Personelin işe başlayacağı gün"
      >
        ↩ İşe başlama: {d.toLocaleDateString('tr-TR')}
      </span>
    );
  };

  // Bölge başlığında "formen kim" etiketi (çok belli olmayacak şekilde).
  const renderRegionForemenLabel = (regionId) => {
    const meta = getRegionMeta(regionId);
    const label = meta.formenNames.join(", ");
    if (!label) return null;
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-normal text-slate-400"
        title={`Formen: ${label}`}
      >
        <HardHat className="h-3 w-3" />
        {label}
      </span>
    );
  };

  // Detail dialog handlers - rol bazlı filtrelenmiş parkları kullanır
  const showInventoryDetail = (title, field, filteredParksList = null) => {
    setInvDetailFilters({ ...EMPTY_DETAIL_FILTERS });
    const parksToUse = filteredParksList || parks;
    const parksWithItem = parksToUse.filter(p => p[field] && p[field] > 0);
    const parkDetails = parksWithItem.map(p => {
      const parkRegion = regions.find(r => r.id === p.region_id);
      return {
        name: p.name,
        count: p[field],
        regionName: parkRegion?.name || 'Bölge Atanmamış',
        regionId: p.region_id || 'none'
      };
    });
    setDetailDialogTitle(title);
    setDetailDialogParks(parkDetails);
    setDetailDialogOpen(true);
  };

  const showFacilityDetail = (title, field, filteredParksList = null) => {
    setInvDetailFilters({ ...EMPTY_DETAIL_FILTERS });
    const parksToUse = filteredParksList || parks;
    const parksWithFacility = parksToUse.filter(p => p[field] === true);
    const parkDetails = parksWithFacility.map(p => {
      const parkRegion = regions.find(r => r.id === p.region_id);
      return {
        name: p.name,
        count: null,
        regionName: parkRegion?.name || 'Bölge Atanmamış',
        regionId: p.region_id || 'none'
      };
    });
    setDetailDialogTitle(title);
    setDetailDialogParks(parkDetails);
    setDetailDialogOpen(true);
  };

  const showIrrigationDetail = (title, irrigationType, filteredParksList = null) => {
    setInvDetailFilters({ ...EMPTY_DETAIL_FILTERS });
    const parksToUse = filteredParksList || parks;
    const parksWithIrrigation = parksToUse.filter(p => p.irrigation_system === irrigationType);
    const parkDetails = parksWithIrrigation.map(p => {
      const parkRegion = regions.find(r => r.id === p.region_id);
      return {
        name: p.name,
        count: null,
        regionName: parkRegion?.name || 'Bölge Atanmamış',
        regionId: p.region_id || 'none'
      };
    });
    setDetailDialogTitle(title);
    setDetailDialogParks(parkDetails);
    setDetailDialogOpen(true);
  };

  const showRunningTrackDetail = (filteredParksList = null) => {
    const parksToUse = filteredParksList || parks;
    const parksWithRunningTrack = parksToUse.filter(p => p.has_running_track === true);
    const parkDetailsByType = parksWithRunningTrack.map(p => {
      // Bölge bilgisini bul
      const parkRegion = regions.find(r => r.id === p.region_id);
      return {
        name: p.name,
        length: p.running_track_length || 0,
        width: p.running_track_width || 0,
        area: (p.running_track_length || 0) * (p.running_track_width || 0),
        type: p.running_track_type || 'yok',
        regionName: parkRegion?.name || 'Bölge Atanmamış',
        regionId: p.region_id || 'none'
      };
    });
    setDetailDialogTitle("Yürüme/Koşu Yolu Detayları");
    setInvDetailFilters({ ...EMPTY_DETAIL_FILTERS });
    setRunningTrackSurfaceFilters({
      suniCim: false,
      belirtilmemis: false,
      kum: false,
      kaucuk: false,
    });
    setPlaygroundSurfaceFilters({
      ahsap: false,
      belirtilmemis: false,
      dere_cakili: false,
      kaucuk: false,
      kum: false,
      sentetik_cim: false,
    });
    setSportsEquipmentSurfaceFilters({
      asfalt: false,
      belirtilmemis: false,
      diger: false,
      kaucuk: false,
      kilit_tasi: false,
      sentetik_cim: false,
    });
    setDetailDialogParks(parkDetailsByType);
    setDetailDialogOpen(true);
  };

  const showPlaygroundDetail = (filteredParksList = null) => {
    const parksToUse = filteredParksList || parks;
    const parksWithPlayground = parksToUse.filter(p => p.has_playground === true);
    const parkDetailsByType = parksWithPlayground.map(p => {
      const parkRegion = regions.find(r => r.id === p.region_id);
      return {
        name: p.name,
        area: p.playground_area || 0,
        type: p.playground_surface_type || 'yok',
        regionName: parkRegion?.name || 'Bölge Atanmamış',
        regionId: p.region_id || 'none'
      };
    });
    setDetailDialogTitle("Çocuk Oyun Grubu Detayları");
    setInvDetailFilters({ ...EMPTY_DETAIL_FILTERS });
    setPlaygroundSurfaceFilters({
      ahsap: false,
      belirtilmemis: false,
      dere_cakili: false,
      kaucuk: false,
      kum: false,
      sentetik_cim: false,
    });
    setRunningTrackSurfaceFilters({
      suniCim: false,
      belirtilmemis: false,
      kum: false,
      kaucuk: false,
    });
    setSportsEquipmentSurfaceFilters({
      asfalt: false,
      belirtilmemis: false,
      diger: false,
      kaucuk: false,
      kilit_tasi: false,
      sentetik_cim: false,
    });
    setDetailDialogParks(parkDetailsByType);
    setDetailDialogOpen(true);
  };

  const showSportsEquipmentDetail = (filteredParksList = null) => {
    const parksToUse = filteredParksList || parks;
    const parksWithSportsEquipment = parksToUse.filter(p => p.has_sports_equipment === true);
    const parkDetailsByType = parksWithSportsEquipment.map(p => {
      const parkRegion = regions.find(r => r.id === p.region_id);
      return {
        name: p.name,
        area: p.sports_equipment_area || 0,
        type: p.sports_equipment_surface_type || 'yok',
        regionName: parkRegion?.name || 'Bölge Atanmamış',
        regionId: p.region_id || 'none'
      };
    });
    setDetailDialogTitle("Spor Aleti Detayları");
    setInvDetailFilters({ ...EMPTY_DETAIL_FILTERS });
    setSportsEquipmentSurfaceFilters({
      asfalt: false,
      belirtilmemis: false,
      diger: false,
      kaucuk: false,
      kilit_tasi: false,
      sentetik_cim: false,
    });
    setRunningTrackSurfaceFilters({
      suniCim: false,
      belirtilmemis: false,
      kum: false,
      kaucuk: false,
    });
    setPlaygroundSurfaceFilters({
      ahsap: false,
      belirtilmemis: false,
      dere_cakili: false,
      kaucuk: false,
      kum: false,
      sentetik_cim: false,
    });
    setDetailDialogParks(parkDetailsByType);
    setDetailDialogOpen(true);
  };

  const showFountainDetail = (filteredParksList = null) => {
    const parksToUse = filteredParksList || parks;
    const parksWithFountain = parksToUse.filter(p => p.has_fountain === true);
    const parkDetailsByStatus = parksWithFountain.map(p => {
      const parkRegion = regions.find(r => r.id === p.region_id);
      return {
        name: p.name,
        status: p.fountain_status || 'yok',
        regionName: parkRegion?.name || 'Bölge Atanmamış',
        regionId: p.region_id || 'none'
      };
    });
    setDetailDialogTitle("Havuz Detayları");
    setInvDetailFilters({ ...EMPTY_DETAIL_FILTERS });
    setPoolStatusFilters({ calisiyor: false, arizali: false, yok: false });
    setDetailDialogParks(parkDetailsByStatus);
    setDetailDialogOpen(true);
  };

  // ==================== KONUM TAKİBİ FONKSİYONLARI ====================
  
  // Faz B-8 / Madde 3: istemci konum durumunu backend'e bildir (yalnız DEĞİŞİMDE).
  // status: granted | permission_denied | gps_off | unavailable | timeout
  const reportLocationClientStatus = (status, message) => {
    if (!personnelId) return;
    if (lastLocationStatusRef.current === status) return; // gereksiz POST'u önle
    lastLocationStatusRef.current = status;
    axios.post(`${API}/api/location/status`, {
      personnel_id: personnelId,
      status,
      message: message || null,
    }).catch(() => { /* sessiz: durum bildirimi best-effort */ });
  };

  const startLocationTracking = () => {
    if (!navigator.geolocation) {
      console.error("Tarayıcı konum servisleri desteklemiyor");
      return;
    }
    if (locationIntervalRef.current) return; // zaten çalışıyor

    setIsTrackingLocation(true);
    setLocationStatus("tracking");
    console.log("🟢 Otomatik konum takibi başlatıldı");

    // İlk konumu hemen gönder
    sendCurrentLocation();

    // Her 2 dakikada bir konum gönder (120000 ms)
    locationIntervalRef.current = setInterval(() => {
      sendCurrentLocation();
    }, 120000); // 2 dakika
  };

  const stopLocationTracking = () => {
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
    }
    setIsTrackingLocation(false);
    setLocationStatus("idle");
    console.log("⏸️ Konum takibi durduruldu");
  };

  const sendCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        setCurrentLocation({ latitude, longitude });
        setLastLocationUpdate(new Date());
        // GPS başarıyla okundu → izin/GPS açık. Önceki "kapalı" durumunu temizle.
        reportLocationClientStatus("granted");

        try {
          const response = await axios.post(`${API}/api/location/log`, {
            personnel_id: personnelId,
            latitude,
            longitude
          });

          // Madde 1: backend mesai dışı/ara ise (success:false) GPS'i hemen durdur.
          // Mesai tekrar başlayınca work-status zamanlayıcısı yeniden başlatır.
          if (response.data && response.data.success === false) {
            console.log("⏸️ Mesai dışı/ara — GPS durduruluyor:", response.data.message);
            stopLocationTracking();
          }
        } catch (error) {
          console.error("❌ Konum gönderme hatası:", error);
          // Mesai dışı (403) ise durdur; zamanlayıcı mesai başlayınca açar.
          if (error.response?.data?.period === "off_duty" || error.response?.status === 403) {
            stopLocationTracking();
          }
        }
      },
      (error) => {
        // Faz B-8 / Madde 3: GPS/konum izni durumunu ayırt et.
        console.warn("❌ Konum alma hatası:", error?.code, error?.message);
        setLocationStatus("error");
        const code = error?.code;
        if (code === 1) {
          // PERMISSION_DENIED
          toast.error("Konum izni kapalı. Mesai takibi için konum iznini açınız.");
          reportLocationClientStatus("permission_denied", "Konum izni kapalı");
        } else if (code === 2) {
          // POSITION_UNAVAILABLE (genelde GPS kapalı)
          reportLocationClientStatus("gps_off", "Konum alınamıyor (GPS kapalı olabilir)");
        } else if (code === 3) {
          // TIMEOUT — geçici; alarm verme
          reportLocationClientStatus("timeout", "Konum zaman aşımı");
        } else {
          reportLocationClientStatus("unavailable", "Konum alınamıyor");
        }
        // GPS okumasını durdur; work-status zamanlayıcısı mesai içindeyse
        // sonraki turda yeniden dener (izin kapalıyken tarayıcı tekrar sormaz).
        stopLocationTracking();
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Park Görevlisi için MESAİ-DURUMU ZAMANLAYICISI (Faz B-8 / Madde 1).
  // GPS'i yalnızca mesai içinde çalıştırır: mesai dışıysa durdurur, mesai
  // başlayınca otomatik başlatır. Mesai durumu GPS gerektirmeyen hafif bir
  // uçtan (work-status) okunur → mesai dışında telefon GPS okumaz (pil + KVKK).
  useEffect(() => {
    if (!isParkGorevlisi || !personnelId) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await axios.get(`${API}/api/location/work-status/${personnelId}`);
        if (cancelled) return;
        const onDuty = !!res.data?.is_work_time;
        if (onDuty) {
          if (!locationIntervalRef.current) startLocationTracking();
        } else {
          if (locationIntervalRef.current) stopLocationTracking();
        }
      } catch (e) {
        // Ağ hatası: mevcut durumu değiştirme (bağlantı gelince düzelir).
      }
    };

    const initialTimer = setTimeout(tick, 2000); // sayfa yüklensin
    const schedId = setInterval(tick, 180000);   // her 3 dk mesai durumunu denetle

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      clearInterval(schedId);
      stopLocationTracking(); // sekme/oturum kapanınca GPS'i bırak
    };
  }, [userRole, personnelId]);
  
  // Yönetici konum takibi sekmesinde konum loglarını otomatik yükle
  // Konum takibi sekmesine geçildiğinde verileri yükle
  const personnelLength = personnel.length; // Sadece uzunluk değişince tetiklensin
  useEffect(() => {
    if (
      isManagerView && 
      activeTab === "location-tracking" && 
      personnelLength > 0
    ) {
      fetchLocationLogs(selectedDate);
      fetchNotificationPreferences(); // Bildirim tercihlerini de yükle
    }
    // NOT: selectedDate değiştiğinde otomatik yenileme YAPMA
    // Kullanıcı "Ara" butonuna basmalı
  }, [activeTab, userRole, personnelLength]); // selectedDate kaldırıldı - Ara butonu kullanılacak

  // Görev Alanı Yönetimi hiyerarşi filtreleri (MY/BM/Formen): giriş yapan KİMLİK
  // (personnelId) veya ROL değişince role-uygun varsayılana ayarlanır. Bu filtreler
  // kimliğe özgü olduğu için HİÇBİR ZAMAN localStorage'a kaydedilmez (aksi halde
  // aynı tarayıcıda başka kullanıcıya geçince sızıyordu):
  //   • Formen   → kendi formen filtresinde seçili; MY/BM = "Tümü"
  //   • BM       → kendi BM filtresinde seçili; formeni alttan kendi seçer; MY = "Tümü"
  //   • MY       → kendi MY filtresinde seçili; BM/Formen = "Tümü"
  //   • Müdür/Başkan → hepsi "Tümü" (tüm kapsamı görür)
  useEffect(() => {
    setLocationFilterMY(isMudurYardimcisi ? personnelId : "all");
    setLocationFilterBM(isBolgeMuhendisi ? personnelId : "all");
    setLocationFilterFormen(isFormen ? personnelId : "all");
    setLocationPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personnelId, userRole]);

  // Sekme değişince personel listesini yenile (BM/Formen atanmış bölge-park güncellemesini görsün, çıkıp girmeden)
  useEffect(() => {
    const tabsThatNeedFreshPersonnel = ["parks", "personnel", "regions", "dashboard"];
    if (tabsThatNeedFreshPersonnel.includes(activeTab) && personnelId) {
      fetchPersonnel({ force: true, silent: true });
    }
  }, [activeTab, personnelId, fetchPersonnel]);

  // Bildirimleri otomatik çek (tüm kullanıcılar: yönetici + park görevlisi – mesai atama bildirimleri için)
  useEffect(() => {
    if (!personnelId) return;
    fetchInAppNotifications(); // İlk yükleme (park görevlisi de mesai bildirimi alır)
    
    if (wsConnected) return;
    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchInAppNotifications();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [personnelId, wsConnected]);

  // Malzeme taleplerini otomatik yenile (Malzeme Yönetimi sekmesinde)
  // NOT: Sekme kimliği "materials" (bkz. TabsContent value="materials"); eskiden
  // burada yanlışlıkla "material-management" kontrol ediliyordu ve efekt hiç
  // çalışmıyordu (WS düştüğünde yedek yenileme ölü koddu).
  useEffect(() => {
    if (activeTab === "materials" && personnelId) {
      // Her 15 saniyede bir sessizce yenile (WS bağlıyken zaten canlı gelir)
      if (wsConnected) return;
      const interval = setInterval(() => {
        if (document.hidden) return;
        // Kanonik yükleyiciyi kullan: role/scope parametrelerini doğru kurar
        // (park görevlisi için personnel_id kapsamı) ve sessizce yeniler.
        fetchMaterialRequests(true);
      }, 15000);

      return () => clearInterval(interval);
    }
  }, [activeTab, personnelId, userRole, wsConnected]);

  // Sidebar badge'leri için global malzeme ve izin talepleri yenilemesi (golge_admin: müdür gibi veri görür)
  useEffect(() => {
    if (personnelId && (isAdmin || isFormen || isBolgeMuhendisi || isMudurYardimcisi || isMudur || isGolgeAdmin)) {
      const refreshManagerSideData = async () => {
        try {
          if (document.hidden || isParkDialogOpen) return;
          // Malzeme talepleri
          const matParams = { personnel_id: personnelId };
          if (userRole) matParams.role = userRole;
          const matResponse = await axios.get(`${API}/api/material-requests`, { params: matParams });
          if (hasListChanged(matResponse.data, materialRequests)) {
            setMaterialRequests(matResponse.data);
          }
          
          // İzin talepleri — DİĞER fetch'lerle AYNI kapsamlı param'ları kullan.
          // Önceden kapsamsız (bare) çekiyordu: rol-kapsamlı görünürlük → 7 kayıt,
          // fetchLeaveRequests/refreshData ise scope=approvals&inbox=1 → 1 kayıt
          // döndürüyordu. İkisi de leaveRequests'e yazınca ekran 7↔1 arası SÜREKLİ
          // GEÇİŞ yapıyordu (İzin Yönetimi'nde "sürekli değişiklik"). Aynı param'la
          // tutarlı hale getirildi.
          const leaveResponse = await apiClient.get(`/leave-requests`, { params: getLeaveRequestParams() });
          if (hasListChanged(leaveResponse.data, leaveRequests)) {
            setLeaveRequests(leaveResponse.data);
          }
          
          // Revize talepleri
          const revParams = { personnel_id: personnelId };
          if (userRole) revParams.role = isAdmin ? "mudur" : userRole;
          const revResponse = await axios.get(`${API}/api/revisions`, { params: revParams });
          // Revize durum etiketi gecikmesin diye yönetsel tarafta doğrudan güncelle.
          setRevisions(revResponse.data);

          // Vekalet / yetki devri: yalnızca girişte değil, bu döngüde de güncellensin (F12'de de görünür)
          await Promise.all([
            fetchMySubstitutions(),
            fetchActiveDelegations(),
            fetchAllActiveSubstitutions(),
          ]);
        } catch (error) {
          console.error("Badge verileri yenilenirken hata:", error);
        }
      };

      // Sekmeye girmeyi beklemeden ilk yüklemede de anlık çek
      refreshManagerSideData();

      // Yönetsel akışta (MY -> Müdür) anlık görünürlük için daha sık yenile.
      const interval = setInterval(async () => {
        await refreshManagerSideData();
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [personnelId, userRole, isParkDialogOpen]);

  // Park/Personel sekmelerinde canlı senkron için fallback yenileme:
  // SSE mesajı kaçsa bile yönetici ekranları birkaç sn içinde güncellenir.
  useEffect(() => {
    if (!personnelId) return;
    const managerRoles = ["admin", "formen", "bolge_muhendisi", "mudur_yardimcisi", "mudur", "golge_admin"];
    if (!managerRoles.includes(userRole)) return;
    if (!["personnel", "parks", "dashboard"].includes(activeTab)) return;

    const intervalMs = wsConnected ? 5000 : 8000;
    const interval = setInterval(() => {
      if (document.hidden || isParkDialogOpen) return;
      fetchParks({ force: true, silent: true });
      fetchPersonnel({ force: true, silent: true });
    }, intervalMs);

    return () => clearInterval(interval);
  }, [activeTab, personnelId, userRole, wsConnected, isParkDialogOpen, fetchPersonnel]);

  // Revize taleplerini otomatik yenile (Revize Yönetimi sekmesinde)
  useEffect(() => {
    if (activeTab === "revisions" && personnelId) {
      const refreshRevisions = async () => {
        try {
          if (document.hidden) return;
          const params = {};
          if (personnelId) params.personnel_id = personnelId;
          if (userRole) params.role = isAdmin ? "mudur" : userRole;
          
          const response = await axios.get(`${API}/api/revisions`, { params });
          // Sadece veri değiştiyse güncelle
          if (hasListChanged(response.data, revisions)) {
            setRevisions(response.data);
          }
        } catch (error) {
          console.error("Revize talepleri yenilenirken hata:", error);
        }
      };

      // Sekmeye girince anında güncelle
      refreshRevisions();

      // Her 15 saniyede bir sessizce yenile (loading göstermeden)
      const interval = setInterval(async () => {
        await refreshRevisions();
      }, 15000);
      
      return () => clearInterval(interval);
    }
  }, [activeTab, personnelId, userRole]);

  // İzin taleplerini otomatik yenile (İzin Yönetimi sekmesinde)
  useEffect(() => {
    if (activeTab === "approvals" && personnelId) {
      // Her durumda sessiz yenile: WS açık olsa da kaçan event'lerde ekran stale kalmasın
      const intervalMs = wsConnected ? 5000 : 15000;
      const interval = setInterval(async () => {
        try {
          if (document.hidden) return;
          const ok = await fetchLeaveRequests();
          if (!ok) return;
          return;
        } catch (error) {
          console.error("İzin talepleri yenilenirken hata:", error);
        }
      }, intervalMs);
      
      return () => clearInterval(interval);
    }
    // PERF: `leaveRequests`/`hasListChanged`/`getLeaveRequestParams` deps'ten
    // ÇIKARILDI. Bunlar her fetch/render'da değişip effect'i yeniden kuruyor,
    // interval'i sürekli sıfırlayıp refetch SELİNE yol açıyordu (scroll sıçraması
    // + backend'i döverek DELETE 16s/500). Interval yalnız fetchLeaveRequests()
    // çağırır; onun kendi closure'ı güncel state'i okur. leaveRequestViewMode
    // gerçek anlamlı bağımlılık olduğu için tutulur.
  }, [activeTab, personnelId, wsConnected, leaveRequestViewMode]);

  // İzin taleplerini arka planda da yenile (rozetler sekmeye girmeden güncellensin)
  useEffect(() => {
    if (!personnelId) return;
    if (activeTab === "approvals") return;

    const role = String(userRole || "").trim().toLowerCase();
    const managerRoles = ["mudur", "müdür", "mudur_yardimcisi", "müdür yardımcısı", "bolge_muhendisi", "bölge mühendisi", "formen"];
    if (!managerRoles.includes(role)) return;

    const intervalMs = wsConnected ? 7000 : 15000;
    const interval = setInterval(async () => {
      try {
        if (document.hidden) return;
        await fetchLeaveRequests();
      } catch (error) {
        console.error("Arka plan izin rozet yenileme hatası:", error);
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [activeTab, personnelId, userRole, wsConnected, leaveRequestViewMode]);

  useEffect(() => {
    if (activeTab === "approvals" && personnelId) {
      fetchLeaveRequests();
    }
  }, [activeTab, personnelId, leaveRequestViewMode]);
  
  // Bildirim tercihlerini getir
  const fetchNotificationPreferences = async () => {
    if (!personnelId) return;
    
    setLoadingPrefs(true);
    try {
      const response = await axios.get(`${API}/api/notifications/preferences/${personnelId}`);
      setNotificationPrefs(response.data);
    } catch (error) {
      console.error("Bildirim tercihleri yüklenemedi:", error);
    } finally {
      setLoadingPrefs(false);
    }
  };
  
  // Bildirim tercihlerini güncelle
  const updateNotificationPreferences = async (newPrefs) => {
    setLoadingPrefs(true);
    try {
      await axios.post(`${API}/api/notifications/preferences`, {
        personnel_id: personnelId,
        ...newPrefs
      });
      
      setNotificationPrefs(newPrefs);
      toast.success("Bildirim tercihleri kaydedildi");
    } catch (error) {
      console.error("Bildirim tercihleri güncellenemedi:", error);
      toast.error("Bildirim tercihleri güncellenemedi");
    } finally {
      setLoadingPrefs(false);
    }
  };
  
  // Uygulama içi bildirimleri getir
  const fetchInAppNotifications = async () => {
    if (!personnelId) return;
    
    try {
      const response = await axios.get(`${API}/api/notifications/in-app/${personnelId}`);
      if (response.data.success) {
        setInAppNotifications(response.data.notifications);
        setUnreadNotificationCount(response.data.unread_count);
      }
    } catch (error) {
      console.error("Bildirimler yüklenemedi:", error);
    }
  };
  
  // Bildirimi okundu işaretle
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/api/notifications/mark-read/${notificationId}`, null, {
        params: { personnel_id: personnelId }
      });
      fetchInAppNotifications(); // Listeyi yenile
    } catch (error) {
      console.error("Bildirim işaretlenemedi:", error);
    }
  };
  
  // Tüm bildirimleri okundu işaretle
  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/api/notifications/mark-all-read/${personnelId}`);
      fetchInAppNotifications(); // Listeyi yenile
      toast.success("Tüm bildirimler okundu olarak işaretlendi");
    } catch (error) {
      console.error("Bildirimler işaretlenemedi:", error);
    }
  };
  
  // Konum loglarını çek (Yönetici için)
  const fetchLocationLogs = async (date = selectedDate) => {
    setLoadingLocations(true);
    try {
      // Hiyerarşik filtreleme: Sadece altındaki park görevlilerini getir (golge_admin = müdür gibi tümünü görür)
      const myParkGorevlileri = personnel.filter(p => {
        if (p.role !== "park_gorevlisi") return false;
        // Yalnızca sabit park görevlileri haritada/sayaçta gösterilir.
        // Çim/Boya/Budama/Sulama gibi mobil ekipler ve özel rollüler hariç —
        // sabit bir parkta durmadıkları için konum haritasına dahil edilmez.
        // (Mobil ekip takibi ileride ayrı katman + filtre ile ele alınacak.)
        if (!isPureParkGorevlisi(p)) return false;

        if (isMudur || isGolgeAdmin) {
          // Müdür / Başkan (gölge admin): tüm park görevlilerini görür
          return true;
        } else if (isMudurYardimcisi) {
          // Müdür Yardımcısı kontrolü
          return p.mudur_yardimcisi_id === personnelId;
        } else if (isBolgeMuhendisi) {
          // Bölge Müh: bolge_muhendisi_id eşit olanlar
          return p.bolge_muhendisi_id === personnelId;
        } else if (isFormen) {
          // Formen: formen_id eşit olanlar
          return p.formen_id === personnelId;
        }
        return false;
      });
      
      // Aktif geçici görevleri çek
      let activeAssignmentsMap = {};
      try {
        const assignResponse = await axios.get(`${API}/api/temporary-assignment/active`);
        if (assignResponse.data.success) {
          assignResponse.data.assignments.forEach(a => {
            activeAssignmentsMap[a.personnel_id] = a;
          });
          setActiveAssignments(assignResponse.data.assignments);
        }
      } catch (error) {
        console.error("Aktif görevler yüklenemedi:", error);
      }

      // Faz B-8 / Madde 3: İstemci konum durumları (GPS/izin kapalı vb.).
      // Son 30 dk içinde bildirilenler; aşağıda her log'a personel bazında iliştirilir.
      let clientStatusMap = {};
      try {
        const csResp = await axios.get(`${API}/api/location/client-statuses`, {
          params: { within_minutes: 30 },
        });
        (csResp.data?.statuses || []).forEach((s) => {
          clientStatusMap[s.personnel_id] = s;
        });
      } catch (error) {
        // sessiz: durum bilgisi best-effort
      }

      // Her park görevlisi için konum loglarını PARALEL çek (daha hızlı)
      const logPromises = myParkGorevlileri.map(async (pg) => {
        try {
          const response = await axios.get(`${API}/api/location/history/${pg.id}`, {
            params: { date, limit: 100 }
          });
          
          const hasAssignment = activeAssignmentsMap[pg.id];
          
          if (response.data.success && response.data.logs.length > 0) {
            // En son logu al (gerçek zamanlı için)
            const latestLog = response.data.logs[0];
            return {
              ...latestLog,
              personnel_name: pg.name,
              personnel_role: pg.role,
              temporary_assignment: hasAssignment || null
            };
          } else if (hasAssignment) {
            // Konum logu yok AMA geçici görev var - yine de göster
            return {
              personnel_id: pg.id,
              personnel_name: pg.name,
              personnel_role: pg.role,
              latitude: null,
              longitude: null,
              timestamp: new Date().toISOString(),
              in_park: false,
              temporary_assignment: hasAssignment
            };
          }
          // Konum kaydı yoksa da personeli listede göster
          return {
            personnel_id: pg.id,
            personnel_name: pg.name,
            personnel_role: pg.role,
            latitude: null,
            longitude: null,
            timestamp: new Date().toISOString(),
            in_park: null,
            temporary_assignment: null,
            no_location_data: true
          };
        } catch (error) {
          console.error(`${pg.name} için konum alınamadı:`, error);
          // Hata olsa bile geçici görev varsa göster
          const hasAssignment = activeAssignmentsMap[pg.id];
          if (hasAssignment) {
            return {
              personnel_id: pg.id,
              personnel_name: pg.name,
              personnel_role: pg.role,
              latitude: null,
              longitude: null,
              timestamp: new Date().toISOString(),
              in_park: false,
              temporary_assignment: hasAssignment
            };
          }
          // API hatası olsa da personeli listede kaybetme
          return {
            personnel_id: pg.id,
            personnel_name: pg.name,
            personnel_role: pg.role,
            latitude: null,
            longitude: null,
            timestamp: new Date().toISOString(),
            in_park: null,
            temporary_assignment: null,
            no_location_data: true
          };
        }
      });
      
      // Tüm promise'leri bekle ve null olmayanları filtrele
      const results = await Promise.all(logPromises);
      // İstemci konum durumunu (izin/GPS kapalı) her log'a iliştir (personel bazında).
      const allLogs = results
        .filter(log => log !== null)
        .map((log) => {
          const cs = clientStatusMap[log.personnel_id];
          return cs
            ? { ...log, client_status: cs.status, client_status_at: cs.updated_at }
            : log;
        });

      setLocationLogs(allLogs);
    } catch (error) {
      console.error("Konum logları yüklenirken hata:", error);
      toast.error("Konum verileri yüklenemedi");
    } finally {
      setLoadingLocations(false);
    }
  };
  
  // UTC zamanını Türkiye saatine çevir
  const formatTurkeyTime = (utcDateString) => {
    if (!utcDateString) return '';
    const date = new Date(utcDateString);
    return date.toLocaleString('tr-TR', { 
      timeZone: 'Europe/Istanbul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  // Rota Yükleme
  const handleShowRoute = async (personnelId, personnelName) => {
    try {
      const response = await axios.get(`${API}/api/location/history/${personnelId}`, {
        params: { date: selectedDate, limit: 1000 }
      });
      
      if (response.data.success && response.data.logs.length > 0) {
        // Zaman sırasına göre sırala
        const sorted = response.data.logs.sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        );
        
        setRouteData(sorted);
        setShowRouteFor({ id: personnelId, name: personnelName });
        toast.success(`${personnelName} için ${sorted.length} noktalık rota gösteriliyor`);
      } else {
        toast.error(`${personnelName} için konum verisi bulunamadı`);
      }
    } catch (error) {
      console.error('Rota yükleme hatası:', error);
      toast.error('Rota yüklenemedi');
    }
  };
  
  const handleHideRoute = () => {
    setShowRouteFor(null);
    setRouteData([]);
  };
  
  // Rapor İndirme
  const handleDownloadReport = async () => {
    setLoadingReport(true);
    try {
      let endpoint = '';
      let reportName = '';
      
      switch(reportType) {
        case 'location-logs':
          endpoint = `/reports/location-logs?start_date=${reportStartDate}&end_date=${reportEndDate}`;
          reportName = 'Konum Logları';
          break;
        case 'work-hours':
          endpoint = `/reports/work-hours?start_date=${reportStartDate}&end_date=${reportEndDate}`;
          reportName = 'Çalışma Saatleri';
          break;
        case 'temporary-assignments':
          endpoint = `/reports/temporary-assignments?start_date=${reportStartDate}&end_date=${reportEndDate}`;
          reportName = 'Geçici Görevler';
          break;
        case 'out-of-park-analysis':
          endpoint = `/reports/out-of-park-analysis?start_date=${reportStartDate}&end_date=${reportEndDate}`;
          reportName = 'Park Dışı Analiz';
          break;
        default:
          toast.error('Geçersiz rapor türü');
          return;
      }
      
      const response = await axios.get(`${API}${endpoint}`, {
        responseType: 'blob'
      });
      
      // Excel dosyasını indir
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportName}_${reportStartDate}_${reportEndDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${reportName} raporu indirildi`);
      setReportDialogOpen(false);
    } catch (error) {
      console.error('Rapor indirme hatası:', error);
      if (error.response?.status === 404) {
        toast.error('Belirtilen tarih aralığında veri bulunamadı');
      } else {
        toast.error('Rapor indirilemedi: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoadingReport(false);
    }
  };
  
  // Geçici Görev İşlemleri
  const fetchActiveAssignments = async () => {
    try {
      const response = await axios.get(`${API}/api/temporary-assignment/active`);
      if (response.data.success) {
        setActiveAssignments(response.data.assignments);
      }
    } catch (error) {
      console.error("Aktif görevler yüklenirken hata:", error);
    }
  };
  
  const openTempAssignmentDialog = (personnel) => {
    setSelectedPersonnelForAssignment(personnel);
    setTempAssignmentForm({
      task_type: "Başka Parkta Yardım",
      task_description: "",
      location: "",
      duration_hours: 2
    });
    setTempAssignmentDialogOpen(true);
  };
  
  const handleCreateTempAssignment = async () => {
    if (!selectedPersonnelForAssignment) {
      toast.error("Lütfen bir park görevlisi seçin");
      return;
    }
    
    setLoadingAssignments(true);
    try {
      // Mevcut kullanıcının bilgisini bul
      const currentUser = personnel.find(p => p.id === personnelId);
      
      const response = await axios.post(`${API}/api/temporary-assignment`, {
        personnel_id: selectedPersonnelForAssignment.id,
        task_type: tempAssignmentForm.task_type,
        task_description: tempAssignmentForm.task_description,
        location: tempAssignmentForm.location,
        duration_hours: tempAssignmentForm.duration_hours,
        assigned_by_id: personnelId,
        assigned_by_name: currentUser?.name || 'Formen'
      });
      
      if (response.data.success) {
        toast.success(`${selectedPersonnelForAssignment.name} için geçici görev oluşturuldu`);
        setTempAssignmentDialogOpen(false);
        setSelectedPersonnelForAssignment(null); // Reset
        fetchActiveAssignments();
        fetchLocationLogs(); // Haritayı güncelle
      }
    } catch (error) {
      console.error("Geçici görev oluşturma hatası:", error);
      let msg = error.response?.data?.detail ?? error.message ?? "Geçici görev oluşturulamadı";
      if (Array.isArray(msg)) msg = msg.map((m) => m.msg || m).join(". ");
      toast.error(typeof msg === "string" ? msg : "Geçici görev oluşturulamadı");
    } finally {
      setLoadingAssignments(false);
    }
  };
  
  const handleCancelTempAssignment = async (assignmentId) => {
    try {
      const response = await axios.delete(`${API}/api/temporary-assignment/${assignmentId}`);
      if (response.data.success) {
        toast.success("Geçici görev iptal edildi");
        fetchActiveAssignments();
        fetchLocationLogs();
      }
    } catch (error) {
      console.error("Geçici görev iptal hatası:", error);
      let msg = error.response?.data?.detail ?? error.message ?? "Geçici görev iptal edilemedi";
      if (Array.isArray(msg)) msg = msg.map((m) => m.msg || m).join(". ");
      toast.error(typeof msg === "string" ? msg : "Geçici görev iptal edilemedi");
    }
  };
  
  // Component unmount olduğunda interval'i temizle
  useEffect(() => {
    return () => {
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, []);
  
  // Otomatik yenileme
  useEffect(() => {
    if (autoRefresh) {
      // Her 30 saniyede bir yenile
      autoRefreshIntervalRef.current = setInterval(() => {
        fetchLocationLogs(selectedDate);
        // Toast kaldırıldı - sessiz güncelleme
      }, 30000);
    } else {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    }
    
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, [autoRefresh, selectedDate]);

  const LOCATION_STALE_MINUTES = Number(locationStaleMinutes) > 0 ? Number(locationStaleMinutes) : 10;
  const LOCATION_PAGE_SIZE = 50;

  // Konum takibi UI tercihleri (hiyerarşi filtreleri HARİÇ) kullanıcıya özel
  // anahtarda saklanır — böylece bir kullanıcının tercihi başka kullanıcıya sızmaz.
  // MY/BM/Formen filtreleri burada YÜKLENMEZ; onlar yukarıdaki effect'te role göre
  // belirlenir.
  useEffect(() => {
    if (!personnelId) return;
    try {
      // Eski paylaşılan (kullanıcılar arası sızdıran) anahtarı temizle.
      localStorage.removeItem("locationTrackingPrefs");
      const raw = localStorage.getItem(`locationTrackingPrefs_${personnelId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        if (typeof parsed.quickFilter === "string") setLocationQuickFilter(parsed.quickFilter);
        if (typeof parsed.searchQuery === "string") setLocationSearchQuery(parsed.searchQuery);
        if (typeof parsed.filterPark === "string") setLocationFilterPark(parsed.filterPark);
        if (typeof parsed.sortBy === "string") setLocationSortBy(parsed.sortBy);
        if (Number.isFinite(Number(parsed.staleMinutes))) setLocationStaleMinutes(Number(parsed.staleMinutes));
      }
    } catch (e) {
      console.warn("Location tracking prefs load failed:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personnelId]);

  useEffect(() => {
    if (!personnelId) return;
    try {
      // Hiyerarşi filtreleri (MY/BM/Formen) bilinçli olarak KAYDEDİLMEZ — kimliğe
      // özgüdürler ve her girişte role göre yeniden belirlenir.
      localStorage.setItem(
        `locationTrackingPrefs_${personnelId}`,
        JSON.stringify({
          quickFilter: locationQuickFilter,
          searchQuery: locationSearchQuery,
          filterPark: locationFilterPark,
          sortBy: locationSortBy,
          staleMinutes: locationStaleMinutes
        })
      );
    } catch (e) {
      console.warn("Location tracking prefs save failed:", e);
    }
  }, [
    personnelId,
    locationQuickFilter,
    locationSearchQuery,
    locationFilterPark,
    locationSortBy,
    locationStaleMinutes
  ]);

  const locationPersonnelById = useMemo(() => {
    const map = {};
    (personnel || []).forEach((p) => {
      if (p?.id) map[p.id] = p;
    });
    return map;
  }, [personnel]);

  const locationPersonNameById = useMemo(() => {
    const map = {};
    (personnel || []).forEach((p) => {
      if (p?.id) map[p.id] = p?.name || "";
    });
    return map;
  }, [personnel]);

  // Personel -> atandığı (sorumlu olduğu) park adları. Park atamaları
  // parks[].assigned_personnel_ids üzerinden tutulur. Arama ve harita park
  // filtresi, görevlinin o an park dışında olsa bile "baktığı" parkı baz alır.
  const personnelAssignedParkNames = useMemo(() => {
    const map = {};
    (Array.isArray(parks) ? parks : []).forEach((park) => {
      const name = (park?.name || "").trim();
      if (!name) return;
      (park?.assigned_personnel_ids || []).forEach((pid) => {
        if (!map[pid]) map[pid] = [];
        if (!map[pid].includes(name)) map[pid].push(name);
      });
    });
    return map;
  }, [parks]);

  // Park -> { parkGorevlileri, formenler } adları. Park popup'ında "park görevlisi
  // kim, formeni kim" göstermek için parks[].assigned_personnel_ids çözümlenir.
  // Doğrudan atanmış formen varsa o; yoksa park görevlisinin formen_id'sinden türetilir.
  const parkStaffInfoById = useMemo(() => {
    const info = {};
    (Array.isArray(parks) ? parks : []).forEach((park) => {
      if (!park?.id) return;
      const gorevliler = [];
      const formenMap = new Map(); // id -> ad (tekilleştirme)
      (park?.assigned_personnel_ids || []).forEach((pid) => {
        const p = locationPersonnelById[pid];
        if (!p) return;
        if (p.role === "park_gorevlisi") {
          if (p.name) gorevliler.push(p.name);
          if (p.formen_id) {
            formenMap.set(p.formen_id, locationPersonNameById[p.formen_id] || "");
          }
        } else if (p.role === "formen") {
          formenMap.set(p.id, p.name || "");
        }
      });
      info[park.id] = {
        parkGorevlileri: gorevliler,
        formenler: Array.from(formenMap.values()).filter(Boolean),
      };
    });
    return info;
  }, [parks, locationPersonnelById, locationPersonNameById]);

  const isLocationStale = useCallback((log) => {
    if (!log || log.no_location_data) return false;
    if (!log.timestamp) return true;
    const ts = new Date(log.timestamp);
    if (Number.isNaN(ts.getTime())) return true;
    return (Date.now() - ts.getTime()) > LOCATION_STALE_MINUTES * 60 * 1000;
  }, [LOCATION_STALE_MINUTES]);

  const openLocationDetail = useCallback(async (log) => {
    if (!log?.personnel_id) return;
    setLocationDetailData(log);
    setLocationDetailOpen(true);
    setLoadingLocationDetail(true);
    try {
      const response = await axios.get(`${API}/api/location/history/${log.personnel_id}`, {
        params: { date: selectedDate, limit: 5 }
      });
      if (response?.data?.success && Array.isArray(response.data.logs)) {
        setLocationDetailLogs(response.data.logs);
      } else {
        setLocationDetailLogs([]);
      }
    } catch (err) {
      console.error("Location detail load error:", err);
      setLocationDetailLogs([]);
    } finally {
      setLoadingLocationDetail(false);
    }
  }, [selectedDate]);

  const getLocationStatusKey = useCallback((log) => {
    if (log?.temporary_assignment?.is_active) return "temporary";
    if (log?.in_park === false) return "out_of_park";
    if (log?.in_park === true) return "in_park";
    return "no_location";
  }, []);

  const locationSummary = useMemo(() => {
    const all = Array.isArray(locationLogs) ? locationLogs : [];
    return {
      total: all.length,
      inPark: all.filter((l) => l?.in_park === true && !l?.temporary_assignment?.is_active).length,
      temporary: all.filter((l) => l?.temporary_assignment?.is_active).length,
      out: all.filter((l) => l?.in_park === false && !l?.temporary_assignment?.is_active).length,
      noLocation: all.filter((l) => l?.in_park !== true && l?.in_park !== false && !l?.temporary_assignment?.is_active).length,
      stale: all.filter((l) => isLocationStale(l) && !l?.temporary_assignment?.is_active).length,
      actionRequired: all.filter((l) => {
        const key = getLocationStatusKey(l);
        return key === "temporary" || key === "out_of_park" || key === "no_location" || isLocationStale(l);
      }).length
    };
  }, [locationLogs, isLocationStale, getLocationStatusKey]);

  const locationFilterOptions = useMemo(() => {
    const logs = Array.isArray(locationLogs) ? locationLogs : [];
    const myMap = new Map();
    const bmMap = new Map();
    const formenMap = new Map();
    const parkMap = new Map();

    logs.forEach((log) => {
      const p = locationPersonnelById[log?.personnel_id];
      if (p?.mudur_yardimcisi_id) {
        myMap.set(p.mudur_yardimcisi_id, locationPersonNameById[p.mudur_yardimcisi_id] || "MY");
      }
      if (p?.bolge_muhendisi_id) {
        bmMap.set(p.bolge_muhendisi_id, locationPersonNameById[p.bolge_muhendisi_id] || "BM");
      }
      if (p?.formen_id) {
        formenMap.set(p.formen_id, locationPersonNameById[p.formen_id] || "Formen");
      }
      if (log?.park_name) {
        parkMap.set(log.park_name, log.park_name);
      }
    });

    // Park seçeneklerini ana park listesinden de doldur — ANCAK yalnızca
    // yüklü (hiyerarşiye göre filtrelenmiş) personele atanmış parklar. Böylece
    // bir formen yalnızca kendi parklarını filtre dropdown'unda görür, müdür
    // ise tüm parkları görür.
    const loadedPersonnelIds = new Set(logs.map((l) => l?.personnel_id));
    (Array.isArray(parks) ? parks : []).forEach((park) => {
      const name = (park?.name || "").trim();
      if (!name) return;
      const assigned = park?.assigned_personnel_ids || [];
      if (assigned.some((pid) => loadedPersonnelIds.has(pid))) {
        parkMap.set(name, name);
      }
    });

    const byName = (a, b) => (a?.name || "").localeCompare(b?.name || "", "tr", { sensitivity: "base" });
    return {
      my: Array.from(myMap.entries()).map(([id, name]) => ({ id, name })).sort(byName),
      bm: Array.from(bmMap.entries()).map(([id, name]) => ({ id, name })).sort(byName),
      formen: Array.from(formenMap.entries()).map(([id, name]) => ({ id, name })).sort(byName),
      park: Array.from(parkMap.entries()).map(([id, name]) => ({ id, name })).sort(byName),
    };
  }, [locationLogs, locationPersonnelById, locationPersonNameById, parks]);

  const filteredLocationLogs = useMemo(() => {
    let list = Array.isArray(locationLogs) ? [...locationLogs] : [];

    list = list.filter((log) => {
      const p = locationPersonnelById[log?.personnel_id] || {};
      if (locationFilterMY !== "all" && p?.mudur_yardimcisi_id !== locationFilterMY) return false;
      if (locationFilterBM !== "all" && p?.bolge_muhendisi_id !== locationFilterBM) return false;
      if (locationFilterFormen !== "all" && p?.formen_id !== locationFilterFormen) return false;
      if (locationFilterPark !== "all" && (log?.park_name || "") !== locationFilterPark) return false;
      return true;
    });

    const q = normalizeSearchText(locationSearchQuery);

    // Arama aktifken varsayılan "Aksiyon Gerekenler" filtresi, aranan kişiyi
    // (örn. parkında sağlıklı duran personeli) gizlemesin — arama her durumdaki
    // eşleşmeyi göstermeli ki "personel hangi parkta" görülebilsin. Kullanıcı
    // bilinçli başka bir durum filtresi seçtiyse (Park Dışı vb.) ona dokunulmaz.
    const effectiveQuickFilter =
      q && locationQuickFilter === "action_required" ? "all" : locationQuickFilter;

    list = list.filter((log) => {
      switch (effectiveQuickFilter) {
        case "temporary":
          return !!log?.temporary_assignment?.is_active;
        case "out_of_park":
          return log?.in_park === false && !log?.temporary_assignment?.is_active;
        case "no_location":
          return log?.in_park !== true && log?.in_park !== false && !log?.temporary_assignment?.is_active;
        case "stale":
          return isLocationStale(log) && !log?.temporary_assignment?.is_active;
        case "action_required": {
          const key = getLocationStatusKey(log);
          return key === "temporary" || key === "out_of_park" || key === "no_location" || isLocationStale(log);
        }
        case "all":
        default:
          return true;
      }
    });

    if (q) {
      list = list.filter((log) => {
        const p = locationPersonnelById[log?.personnel_id] || {};
        const haystack = normalizeSearchText([
          log?.personnel_name || "",
          log?.park_name || "",
          ...(personnelAssignedParkNames[log?.personnel_id] || []),
          log?.temporary_assignment?.task_type || "",
          log?.temporary_assignment?.task_description || "",
          locationPersonNameById[p?.formen_id] || "",
          locationPersonNameById[p?.bolge_muhendisi_id] || "",
          locationPersonNameById[p?.mudur_yardimcisi_id] || "",
        ].join(" "));
        return haystack.includes(q);
      });
    }

    const statusOrder = {
      temporary: 0,
      out_of_park: 1,
      no_location: 2,
      in_park: 3
    };

    list.sort((a, b) => {
      if (locationSortBy === "name") {
        return (a?.personnel_name || "").localeCompare((b?.personnel_name || ""), "tr", { sensitivity: "base" });
      }
      if (locationSortBy === "updated_desc" || locationSortBy === "updated_asc") {
        const at = new Date(a?.timestamp || 0).getTime() || 0;
        const bt = new Date(b?.timestamp || 0).getTime() || 0;
        return locationSortBy === "updated_desc" ? bt - at : at - bt;
      }
      if (locationSortBy === "park") {
        return (a?.park_name || "").localeCompare((b?.park_name || ""), "tr", { sensitivity: "base" });
      }
      // default: status
      const ar = statusOrder[getLocationStatusKey(a)] ?? 99;
      const br = statusOrder[getLocationStatusKey(b)] ?? 99;
      if (ar !== br) return ar - br;
      return (a?.personnel_name || "").localeCompare((b?.personnel_name || ""), "tr", { sensitivity: "base" });
    });

    return list;
  }, [
    locationLogs,
    locationFilterMY,
    locationFilterBM,
    locationFilterFormen,
    locationFilterPark,
    locationQuickFilter,
    locationSearchQuery,
    locationSortBy,
    locationPersonnelById,
    locationPersonNameById,
    personnelAssignedParkNames,
    isLocationStale,
    getLocationStatusKey
  ]);

  // Haritada gösterilecek park sınırları. Coğrafi bir filtre (arama, park,
  // formen/BM/MY) aktifse yalnızca ilgili parkları çiz; aksi halde tüm parkları
  // bağlam (kontekst) olarak göster. Personel ile park ilişkisi
  // parks[].assigned_personnel_ids üzerinden kurulur.
  const mapVisibleParks = useMemo(() => {
    const allParks = Array.isArray(parks) ? parks : [];
    const q = normalizeSearchText(locationSearchQuery);
    const hasGeoFilter =
      Boolean(q) ||
      locationFilterPark !== "all" ||
      locationFilterFormen !== "all" ||
      locationFilterBM !== "all" ||
      locationFilterMY !== "all";
    if (!hasGeoFilter) return allParks;

    const loadedIds = new Set(
      (Array.isArray(locationLogs) ? locationLogs : []).map((l) => l?.personnel_id)
    );
    const matchHierarchy = (p) => {
      if (locationFilterMY !== "all" && p?.mudur_yardimcisi_id !== locationFilterMY) return false;
      if (locationFilterBM !== "all" && p?.bolge_muhendisi_id !== locationFilterBM) return false;
      if (locationFilterFormen !== "all" && p?.formen_id !== locationFilterFormen) return false;
      return true;
    };

    return allParks.filter((park) => {
      const name = park?.name || "";
      // 1) Belirli bir park seçiliyse yalnızca o parkı göster.
      if (locationFilterPark !== "all") return name === locationFilterPark;
      // 2) Park adı aramayla eşleşiyorsa göster.
      if (q && normalizeSearchText(name).includes(q)) return true;
      // 3) Parkın atanmış (ve yüklü) personeli hiyerarşi + arama filtresine uyuyor mu?
      const assigned = park?.assigned_personnel_ids || [];
      return assigned.some((pid) => {
        if (!loadedIds.has(pid)) return false;
        const p = locationPersonnelById[pid];
        if (!p || !matchHierarchy(p)) return false;
        if (q) {
          const hay = normalizeSearchText([name, p?.name || ""].join(" "));
          if (!hay.includes(q)) return false;
        }
        return true;
      });
    });
  }, [
    parks,
    locationLogs,
    locationPersonnelById,
    locationSearchQuery,
    locationFilterPark,
    locationFilterFormen,
    locationFilterBM,
    locationFilterMY,
  ]);

  // Harita otomatik odaklanması için "filtre imzası". Yalnızca bir coğrafi filtre
  // (arama/park/formen/BM/MY) aktifken dolu olur; bu değer değiştiğinde harita
  // ilgili parklara + personele zoom/pan yapar. Filtre yokken null → otomatik
  // odaklama yapılmaz (canlı veri yenilemesi kullanıcının manuel zoom'unu bozmaz).
  const mapFitSignature = useMemo(() => {
    const q = (locationSearchQuery || "").trim();
    const hasGeoFilter =
      Boolean(q) ||
      locationFilterPark !== "all" ||
      locationFilterFormen !== "all" ||
      locationFilterBM !== "all" ||
      locationFilterMY !== "all";
    if (!hasGeoFilter) return null;
    return [q, locationFilterPark, locationFilterFormen, locationFilterBM, locationFilterMY].join("|");
  }, [
    locationSearchQuery,
    locationFilterPark,
    locationFilterFormen,
    locationFilterBM,
    locationFilterMY,
  ]);

  const exportFilteredLocationCsv = useCallback(() => {
    const rows = (filteredLocationLogs || []).map((log) => {
      const status = log?.temporary_assignment?.is_active
        ? "Geçici Görevde"
        : log?.in_park === true
          ? "Parkta"
          : log?.in_park === false
            ? "Park Dışı"
            : "Konum verisi yok";
      return {
        "Personel": log?.personnel_name || "",
        "Durum": status,
        "Ana Park": log?.park_name || "",
        "Son Güncelleme": formatTurkeyTime(log?.timestamp || ""),
        "Geçici Görev": log?.temporary_assignment?.task_type || "",
        "Görev Bitiş": formatTurkeyTime(log?.temporary_assignment?.end_time || ""),
      };
    });
    if (rows.length === 0) {
      toast.error("Dışa aktarılacak kayıt bulunamadı");
      return;
    }
    const headers = Object.keys(rows[0]);
    const escapeCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = [
      headers.map(escapeCell).join(","),
      ...rows.map((r) => headers.map((h) => escapeCell(r[h])).join(","))
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `gorev_alani_${selectedDate || "rapor"}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Filtrelenmiş liste dışa aktarıldı");
  }, [filteredLocationLogs, selectedDate]);

  const locationTotalPages = Math.max(1, Math.ceil(filteredLocationLogs.length / LOCATION_PAGE_SIZE));

  const paginatedLocationLogs = useMemo(() => {
    const page = Math.min(locationPage, locationTotalPages);
    const start = (page - 1) * LOCATION_PAGE_SIZE;
    return filteredLocationLogs.slice(start, start + LOCATION_PAGE_SIZE);
  }, [filteredLocationLogs, locationPage, locationTotalPages]);

  useEffect(() => {
    setLocationPage(1);
  }, [
    locationQuickFilter,
    locationSearchQuery,
    locationFilterMY,
    locationFilterBM,
    locationFilterFormen,
    locationFilterPark,
    locationSortBy,
    selectedDate
  ]);

  useEffect(() => {
    if (locationPage > locationTotalPages) {
      setLocationPage(locationTotalPages);
    }
  }, [locationPage, locationTotalPages]);

  const resetParkForm = () => {
    setParkForm({
      id: "",
      name: "",
      neighborhood: "",
      address: "",
      coordinates: "",
      latitude: null,
      longitude: null,
      existing_personnel_ids: [],
      area: "",
      grass_area: "",
      hard_surface_area: "",
      playground_area: "",
      light_pole_count: 0,
      bench_count: 0,
      trash_bin_count: 0,
      gazebo_count: 0,
      tree_count: 0,
      flower_count: 0,
      has_running_track: false,
      has_playground: false,
      has_sports_equipment: false,
      has_wc: false,
      has_fountain: false,
      fountain_status: "yok",
      has_parking: false,
      irrigation_system: "yok",
      has_basketball_court: false,
      has_football_field: false,
      has_volleyball_court: false,
      has_tennis_court: false,
      description: ""
    });
    setIsEditingPark(false);
    // Yeni park ekleme her zaman editable başlamalı (handleParkEdit'in default true'su ezilir)
    setIsFormReadOnly(false);
  };

  // Filter parks (parks yoksa çökme önlenir)
  const filteredParks = useMemo(() => {
    const list = Array.isArray(parks) ? parks : [];
    const normalize = normalizeSearchText;
    const searchNormalized = normalize(deferredSearchTerm);
    const searchTokens = searchNormalized ? searchNormalized.split(/\s+/) : [];
    return list.filter(park => {
      // Çoklu kelime araması: her token, name/neighborhood/address birleşiminde
      // bulunmalı. Böylece "beysu cumhuriyet" gibi bir sorgu, kelimeler farklı
      // alanlara dağılmış olsa bile parkı bulur.
      let matchesSearch = true;
      if (searchTokens.length > 0) {
        const haystack = [park.name, park.neighborhood, park.address]
          .filter(Boolean)
          .map(normalize)
          .join(" ");
        matchesSearch = searchTokens.every(tok => haystack.includes(tok));
      }
      
      // HİYERARŞİK PERSONEL FİLTRESİ
      let matchesPersonnel = filterPersonnel === "all";
      
      if (!matchesPersonnel && filterPersonnel) {
        const personnelList = Array.isArray(personnel) ? personnel : [];
        const selectedPerson = personnelList.find(p => p.id === filterPersonnel);
        
        if (selectedPerson) {
          // Seçilen personelin kendisi bu parkta mı?
          const isDirectlyAssigned = park.assigned_personnel_ids && 
                                     park.assigned_personnel_ids.includes(filterPersonnel);
          
          // Seçilen personel Formen, Bölge Mühendisi veya Müdür Yardımcısı ise, altındaki personelin parklarını da göster
          if (selectedPerson.role === 'formen' || selectedPerson.role === 'bolge_muhendisi' || selectedPerson.role === 'mudur_yardimcisi') {
            // Altında çalışan TÜM personeli bul (recursive olarak)
            const subordinates = getAllSubordinates(filterPersonnel, selectedPerson.role, personnelList);
            
            // Bu parkta altındaki personelden biri var mı?
            const hasSubordinate = subordinates.some(sub => 
              park.assigned_personnel_ids && park.assigned_personnel_ids.includes(sub.id)
            );
            
            matchesPersonnel = isDirectlyAssigned || hasSubordinate;
          } else {
            // Normal personel - sadece kendi parkları
            matchesPersonnel = isDirectlyAssigned;
          }
        }
      }
      
      const matchesRegion = filterRegion === "all" || 
                           (filterRegion === "none" && !park.region_id) ||
                           (filterRegion === "assigned" && park.region_id) ||
                           park.region_id === filterRegion;
      
      // NOT: Rol filtresi sadece personel dropdown'ını daraltmak için kullanılıyor
      // Park filtreleme için "matchesPersonnel" zaten hiyerarşik çalışıyor
      return matchesSearch && matchesPersonnel && matchesRegion;
    });
  }, [parks, deferredSearchTerm, filterPersonnel, personnel, filterRegion]);

  // Filter parks based on role hierarchy - filteredParks'tan başla!
  const displayedParks = useMemo(() => {
    const safeFilteredParks = Array.isArray(filteredParks) ? filteredParks : [];
    const safePersonnel = Array.isArray(personnel) ? personnel : [];
    let scopedParks = safeFilteredParks;

    // Faz 4-G: Dashboard park kapsamı BELİ ile HİZALI (yetki tutarsızlığı düzeltmesi).
    // Tam park kapsamı = park.tum_kapsam VEYA personel.tum_kapsam VEYA system.golge_admin
    // VEYA admin/başkan → tüm parkları gör. BELİ'nin park kapsamıyla (_full_scope +
    // park.tum_kapsam) BİREBİR aynı küme. Böylece personel.tum_kapsam olan Müdür/Müdür
    // Yardımcısı/BM, BELİ 202 derken dashboard'da da 202 görür. Süper admin yetki
    // matrisinden açılınca/kapanınca (SSE permission_updated → refresh) canlı etki eder.
    // Bu küme DIŞINDAKİLER yine yalnız kendi hiyerarşik/bölge kapsamını görür (item 4).
    const hasFullParkScope =
      has('park.tum_kapsam') || has('personel.tum_kapsam') ||
      has('system.golge_admin') || isAdmin;
    if (hasFullParkScope) {
      scopedParks = safeFilteredParks;
    } else if (isParkGorevlisi) {
      scopedParks = safeFilteredParks.filter(p =>
        p.assigned_personnel_ids && p.assigned_personnel_ids.includes(personnelId)
      );
    } else if (isFormen) {
      const currentUser = safePersonnel.find(p => p.id === personnelId);
      const managedRegionIds = (Array.isArray(regions) ? regions : [])
        .filter(r => Array.isArray(r.assigned_formen_ids) && r.assigned_formen_ids.includes(personnelId))
        .map(r => r.id);
      const formenRegions = [
        ...(Array.isArray(currentUser?.region_ids) ? currentUser.region_ids : []),
        currentUser?.formen_region_id,
        ...managedRegionIds
      ].filter(Boolean);
      const subordinateIds = safePersonnel
        .filter((p) => p.formen_id === personnelId)
        .map((p) => p.id);
      scopedParks = safeFilteredParks.filter((p) => {
        const assignedIds = Array.isArray(p.assigned_personnel_ids) ? p.assigned_personnel_ids : [];
        const regionMatch = !!p.region_id && formenRegions.includes(p.region_id);
        const directlyAssigned = assignedIds.includes(personnelId);
        const subordinateAssigned = assignedIds.some((id) => subordinateIds.includes(id));
        return regionMatch || directlyAssigned || subordinateAssigned;
      });
    } else if (isBolgeMuhendisi) {
      // BM: personel region_ids + region tablosundaki BM/Formen atamaları + doğrudan atamalar
      const currentPersonnel = safePersonnel.find(p => p.id === personnelId);
      const myRegionIds = Array.isArray(currentPersonnel?.region_ids) ? currentPersonnel.region_ids : [];
      const managedBmRegionIds = (Array.isArray(regions) ? regions : [])
        .filter(r => r.assigned_bolge_muhendisi_id === personnelId)
        .map(r => r.id);
      const subordinateFormens = safePersonnel.filter(p =>
        p.role === "formen" && p.bolge_muhendisi_id === personnelId
      );
      const subordinateFormenIds = subordinateFormens.map(f => f.id);
      const regionAssignedFormenIds = (Array.isArray(regions) ? regions : [])
        .filter(r => r.assigned_bolge_muhendisi_id === personnelId)
        .flatMap(r => Array.isArray(r.assigned_formen_ids) ? r.assigned_formen_ids : []);
      const myFormenIds = [...new Set([...subordinateFormenIds, ...regionAssignedFormenIds])];
      const formenRegionIds = subordinateFormens.flatMap(f => f.region_ids || []);
      const formenManagedRegionIds = (Array.isArray(regions) ? regions : [])
        .filter(r => Array.isArray(r.assigned_formen_ids) && r.assigned_formen_ids.some(fid => myFormenIds.includes(fid)))
        .map(r => r.id);
      const allRegionIds = [...new Set([...myRegionIds, ...managedBmRegionIds, ...formenRegionIds, ...formenManagedRegionIds])];
      const formenSubordinates = safePersonnel.filter(p => myFormenIds.includes(p.formen_id));
      const myAllPersonnelIds = [personnelId, ...myFormenIds, ...formenSubordinates.map(s => s.id)];
      scopedParks = safeFilteredParks.filter(p =>
        allRegionIds.includes(p.region_id) ||
        (p.assigned_personnel_ids && p.assigned_personnel_ids.some(pid => myAllPersonnelIds.includes(pid)))
      );
    } else if (isMudurYardimcisi) {
      // MY: kendi region_ids + kendine bağlı BM/Formen atamalı bölgeler
      const currentPersonnel = safePersonnel.find(p => p.id === personnelId);
      const myRegionIds = Array.isArray(currentPersonnel?.region_ids) ? currentPersonnel.region_ids : [];
      const mySubordinates = safePersonnel.filter(
        p =>
          p.mudur_yardimcisi_id === personnelId &&
          p.role !== "mudur_yardimcisi" &&
          p.role !== "mudur"
      );
      const myBolgeMuhIds = mySubordinates.filter(p => p.role === "bolge_muhendisi").map(p => p.id);
      const myFormenIds = mySubordinates.filter(p => p.role === "formen").map(p => p.id);
      const managedRegionIds = (Array.isArray(regions) ? regions : [])
        .filter(r =>
          (r.assigned_bolge_muhendisi_id && myBolgeMuhIds.includes(r.assigned_bolge_muhendisi_id)) ||
          (Array.isArray(r.assigned_formen_ids) && r.assigned_formen_ids.some(fid => myFormenIds.includes(fid)))
        )
        .map(r => r.id);
      const allRegionIds = [...new Set([...myRegionIds, ...managedRegionIds])];
      scopedParks = safeFilteredParks.filter(p => allRegionIds.includes(p.region_id));
    }
    
    // Parkları alfabetik sırala (scopedParks array olmalı)
    return [...(Array.isArray(scopedParks) ? scopedParks : [])].sort((a, b) => (a?.name || '').localeCompare(b?.name || '', 'tr'));
  }, [filteredParks, userRole, personnel, personnelId, regions, has]);

  // Stats - Genel İstatistikler
  // Bugün tarihini al
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Bugün izinli personelleri hesapla (onaylanmış ve tarihi bugünü kapsayan)
  const todayOnLeave = leaveRequests.filter(lr => {
    if (lr.status !== 'approved') return false;
    const startDate = new Date(lr.start_date);
    const endDate = new Date(lr.end_date);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return today >= startDate && today <= endDate;
  });
  
  // Bugün raporlu personelleri hesapla (Rapor türü ve onaylanmış)
  const todayOnReport = todayOnLeave.filter(lr => lr.reason_type === 'Rapor');
  const todayOnLeaveOnly = todayOnLeave.filter(lr => lr.reason_type !== 'Rapor');
  
  // Benzersiz personel ID'lerini al
  const onLeavePersonnelIds = [...new Set(todayOnLeaveOnly.map(lr => lr.personnel_id))];
  const onReportPersonnelIds = [...new Set(todayOnReport.map(lr => lr.personnel_id))];
  
  // Rol bazlı filtreleme için yardımcı veriler (memoize)
  const mySubordinates = useMemo(() => {
    if (isAdmin || isMudur || isGolgeAdmin) {
      return personnel;
    }
    return getAllSubordinates(personnelId, userRole, personnel);
  }, [userRole, personnelId, personnel]);

  const myRegionIds = useMemo(() => {
    if (isAdmin || isMudur || isGolgeAdmin) {
      return regions.map(r => r.id);
    }
    
    const currentPersonnel = personnel.find(p => p.id === personnelId);
    const ownRegionIds = [
      ...(Array.isArray(currentPersonnel?.region_ids) ? currentPersonnel.region_ids : []),
      currentPersonnel?.region_id,
      currentPersonnel?.formen_region_id,
    ].filter(Boolean);

    const mySubordinateIds = [personnelId, ...mySubordinates.map(s => s.id)];
    
    // Kendime ve astlarıma atanmış parkların bölgelerini bul
    const myParkRegionIds = parks
      .filter(p => p.assigned_personnel_ids?.some(pid => mySubordinateIds.includes(pid)))
      .map(p => p.region_id)
      .filter(Boolean);
    
    // Ayrıca regions tablosunda manager olarak atandığım bölgeleri de ekle
    const managedRegionIds = regions
      .filter(r => {
        if (isMudurYardimcisi) {
          const myBolgeMuhIds = mySubordinates
            .filter(s => s.role === 'bolge_muhendisi')
            .map(s => s.id);
          const myFormenIds = mySubordinates
            .filter(s => s.role === 'formen')
            .map(s => s.id);

          const hasDirectRegionAssignment = Array.isArray(r.assigned_formen_ids)
            ? r.assigned_formen_ids.some(fid => myFormenIds.includes(fid))
            : false;

          return (
            (r.assigned_bolge_muhendisi_id && myBolgeMuhIds.includes(r.assigned_bolge_muhendisi_id)) ||
            hasDirectRegionAssignment
          );
        } else if (isBolgeMuhendisi) {
          return r.assigned_bolge_muhendisi_id === personnelId;
        }
        return false;
      })
      .map(r => r.id);
    
    return [...new Set([...ownRegionIds, ...myParkRegionIds, ...managedRegionIds])];
  }, [userRole, personnelId, personnel, mySubordinates, parks, regions]);

  // Dashboard ve Parklar sekmesi aynı park kapsamını kullansın.
  const myFilteredParks = useMemo(() => {
    return Array.isArray(displayedParks) ? displayedParks : [];
  }, [displayedParks]);

  const myFilteredPersonnel = useMemo(() => {
    const personnelList = Array.isArray(personnel) ? personnel : [];
    if (isAdmin || isMudur || isGolgeAdmin) {
      return personnelList;
    }
    return [personnelList.find(p => p.id === personnelId), ...mySubordinates].filter(Boolean);
  }, [userRole, personnel, personnelId, mySubordinates]);

  const myFilteredRegions = useMemo(() => {
    const regionsList = Array.isArray(regions) ? regions : [];
    return regionsList.filter(r => myRegionIds.includes(r.id));
  }, [regions, myRegionIds]);
  
  // Filtrelenmiş personel için izin/rapor hesapla
  const myFilteredPersonnelIds = myFilteredPersonnel.map(p => p.id);
  const filteredOnLeaveIds = onLeavePersonnelIds.filter(id => myFilteredPersonnelIds.includes(id));
  const filteredOnReportIds = onReportPersonnelIds.filter(id => myFilteredPersonnelIds.includes(id));

  // "Park Atama Durumu" widget'ı için dar/strict bölge kapsamı.
  // displayedParks personel-temelli sızıntılar (çapraz BM atamaları, formen region_ids genişliği)
  // içerebildiği için, widget hesabı yalnızca kullanıcının GERÇEKTEN atandığı bölgelerdeki
  // parkları saymalı. Parklar sekmesi ve envanter alanları myFilteredParks ile devam ediyor.
  const widgetStrictRegionParks = useMemo(() => {
    const parksList = Array.isArray(myFilteredParks) ? myFilteredParks : [];
    if (isAdmin || isMudur || isGolgeAdmin) return parksList;
    if (isParkGorevlisi) return parksList;

    const safePersonnel = Array.isArray(personnel) ? personnel : [];
    const safeRegions = Array.isArray(regions) ? regions : [];
    const currentPersonnel = safePersonnel.find(p => p.id === personnelId);
    const ownRegionIds = [
      ...(Array.isArray(currentPersonnel?.region_ids) ? currentPersonnel.region_ids : []),
      currentPersonnel?.region_id,
      currentPersonnel?.formen_region_id,
    ].filter(Boolean);

    let managedRegionIds = [];
    if (isBolgeMuhendisi) {
      managedRegionIds = safeRegions
        .filter(r => r.assigned_bolge_muhendisi_id === personnelId)
        .map(r => r.id);
    } else if (isFormen) {
      managedRegionIds = safeRegions
        .filter(r => Array.isArray(r.assigned_formen_ids) && r.assigned_formen_ids.includes(personnelId))
        .map(r => r.id);
    } else if (isMudurYardimcisi) {
      const subs = Array.isArray(mySubordinates) ? mySubordinates : [];
      const myBolgeMuhIds = subs.filter(s => s.role === 'bolge_muhendisi').map(s => s.id);
      const myFormenIds = subs.filter(s => s.role === 'formen').map(s => s.id);
      managedRegionIds = safeRegions
        .filter(r =>
          (r.assigned_bolge_muhendisi_id && myBolgeMuhIds.includes(r.assigned_bolge_muhendisi_id)) ||
          (Array.isArray(r.assigned_formen_ids) && r.assigned_formen_ids.some(fid => myFormenIds.includes(fid)))
        )
        .map(r => r.id);
    }

    const strictRegionIdSet = new Set([...ownRegionIds, ...managedRegionIds]);
    return parksList.filter(p => p.region_id && strictRegionIdSet.has(p.region_id));
  }, [
    myFilteredParks, personnel, personnelId, regions, mySubordinates,
    isAdmin, isMudur, isGolgeAdmin, isBolgeMuhendisi, isFormen, isMudurYardimcisi, isParkGorevlisi,
  ]);

  const stats = useMemo(() => ({
    totalParks: myFilteredParks.length,
    totalPersonnel: myFilteredPersonnel.length,
    activePersonnel: myFilteredPersonnel.length - filteredOnLeaveIds.length - filteredOnReportIds.length,
    onLeavePersonnel: filteredOnLeaveIds.length,
    onReportPersonnel: filteredOnReportIds.length,
    // Park Atama Durumu - Park Görevlisi bazlı
    // Atanmış = En az bir park görevlisi atanmış
    // Atanmamış = Hiç park görevlisi yok (sadece formen olabilir)
    // Bu üç alan widgetStrictRegionParks üzerinden hesaplanır: sadece kullanıcının
    // gerçekten atandığı bölgelerdeki parklar. (Diğer alanlar myFilteredParks ile devam.)
    assignedParks: widgetStrictRegionParks.filter(p => {
      if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return false;
      // Atanan personellerden en az biri park_gorevlisi mi?
      return p.assigned_personnel_ids.some(pid => {
        const person = myFilteredPersonnel.find(per => per.id === pid);
        return person && person.role === 'park_gorevlisi';
      });
    }).length,
    unassignedParks: widgetStrictRegionParks.filter(p => {
      if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return true;
      // Atanan personellerden hiçbiri park_gorevlisi değilse = atanmamış
      const hasParkGorevlisi = p.assigned_personnel_ids.some(pid => {
        const person = myFilteredPersonnel.find(per => per.id === pid);
        return person && person.role === 'park_gorevlisi';
      });
      return !hasParkGorevlisi;
    }).length,
    // Park görevlisi olmayan parkların listesi (atanmamış park tıklandığında kullanılacak)
    unassignedParksList: widgetStrictRegionParks.filter(p => {
      if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return true;
      const hasParkGorevlisi = p.assigned_personnel_ids.some(pid => {
        const person = myFilteredPersonnel.find(per => per.id === pid);
        return person && person.role === 'park_gorevlisi';
      });
      return !hasParkGorevlisi;
    }),
    myParks: isParkGorevlisi ? parks.filter(p => p.assigned_personnel_ids && p.assigned_personnel_ids.includes(personnelId)).length : 0,
    
    // Envanter İstatistikleri
    totalArea: Math.round(myFilteredParks.reduce((sum, p) => sum + (p.area || 0), 0)),
    totalGrassArea: Math.round(myFilteredParks.reduce((sum, p) => sum + (p.grass_area || 0), 0)),
    totalLightPoles: myFilteredParks.reduce((sum, p) => sum + (p.light_pole_count || 0), 0),
    totalBenches: myFilteredParks.reduce((sum, p) => sum + (p.bench_count || 0), 0),
    totalTrashBins: myFilteredParks.reduce((sum, p) => sum + (p.trash_bin_count || 0), 0),
    totalGazebos: myFilteredParks.reduce((sum, p) => sum + (p.gazebo_count || 0), 0),
    totalTrees: myFilteredParks.reduce((sum, p) => sum + (p.tree_count || 0), 0),
    totalFlowers: myFilteredParks.reduce((sum, p) => sum + (p.flower_count || 0), 0),
    
    // Tesis İstatistikleri
    parksWithRunningTrack: myFilteredParks.filter(p => p.has_running_track).length,
    parksWithPlayground: myFilteredParks.filter(p => p.has_playground).length,
    parksWithSportsEquipment: myFilteredParks.filter(p => p.has_sports_equipment).length,
    parksWithWC: myFilteredParks.filter(p => p.has_wc).length,
    parksWithFountain: myFilteredParks.filter(p => p.has_fountain).length,
    parksWithFountainWorking: myFilteredParks.filter(p => p.has_fountain && p.fountain_status === "calisiyor").length,
    parksWithFountainBroken: myFilteredParks.filter(p => p.has_fountain && p.fountain_status === "arizali").length,
    parksWithParking: myFilteredParks.filter(p => p.has_parking).length,
    
    // Sulama Sistemi İstatistikleri
    automaticIrrigation: myFilteredParks.filter(p => p.irrigation_system === "otomatik").length,
    manualIrrigation: myFilteredParks.filter(p => p.irrigation_system === "manuel").length,
    hoseIrrigation: myFilteredParks.filter(p => p.irrigation_system === "hortumla").length,
    noIrrigation: myFilteredParks.filter(p => !p.irrigation_system || p.irrigation_system === "yok").length,
    
    // Spor Sahaları İstatistikleri
    parksWithBasketball: myFilteredParks.filter(p => p.has_basketball_court).length,
    parksWithFootball: myFilteredParks.filter(p => p.has_football_field).length,
    parksWithVolleyball: myFilteredParks.filter(p => p.has_volleyball_court).length,
    parksWithTennis: myFilteredParks.filter(p => p.has_tennis_court).length,
    
    // Bölge İstatistikleri
    totalRegions: myFilteredRegions.length,
    parksWithRegion: myFilteredParks.filter(p => p.region_id).length,
    parksWithoutRegion: myFilteredParks.filter(p => !p.region_id).length,
    
    // İzni Azalan Personeller (5 günden az kalan)
    lowLeavePersonnel: myFilteredPersonnel.filter(p => {
      const annualLeave = p.annual_leave_days || 20;
      const usedLeave = p.used_leave_days || 0;
      const remaining = annualLeave - usedLeave;
      return remaining <= 5 && remaining >= 0 && p.role === 'park_gorevlisi';
    }),
    
    // Rol bazlı filtreleme için orijinal parkları da sakla
    _allParks: parks,
    _myFilteredParks: myFilteredParks,
  }), [
    myFilteredParks,
    widgetStrictRegionParks,
    myFilteredPersonnel,
    myFilteredRegions,
    filteredOnLeaveIds,
    filteredOnReportIds,
    userRole,
    parks,
    personnelId
  ]);

  const handleSearchSelect = useCallback((type, item) => {
    // Sonuç türüne göre ilgili sekmeye/dialog'a yönlendir
    switch (type) {
      case 'personnel': {
        const selectedPerson = personnel.find(p => p.id === item.id);
        if (selectedPerson) {
          handlePersonnelEdit(selectedPerson);
        }
        break;
      }
      case 'parks': {
        setHighlightedParkId(item.id);
        setActiveTab("parks");
        setTimeout(() => {
          const parkElement = document.getElementById(`park-card-${item.id}`);
          if (parkElement) {
            parkElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            parkElement.classList.add('ring-2', 'ring-green-500', 'ring-offset-2');
            setTimeout(() => {
              parkElement.classList.remove('ring-2', 'ring-green-500', 'ring-offset-2');
              setHighlightedParkId(null);
            }, 3000);
          }
        }, 500);
        toast.success(`${item.name} parkına yönlendiriliyorsunuz...`);
        break;
      }
      case 'leave_requests': {
        setActiveTab("leave-management");
        const leavePersonnel = personnel.find(p => p.id === item.personnel_id);
        if (leavePersonnel) {
          setTimeout(() => {
            setSelectedPersonnelForDetail(leavePersonnel);
            setPersonnelDetailDefaultTab("leaves");
            setPersonnelDetailDialogOpen(true);
          }, 100);
        }
        break;
      }
      case 'tasks':
        setActiveTab("task-tracking");
        break;
      case 'messages':
        setActiveTab("messages");
        break;
      default:
        console.log('Bilinmeyen sonuç türü:', type);
    }
  }, [
    personnel,
    handlePersonnelEdit,
    setHighlightedParkId,
    setActiveTab,
    setSelectedPersonnelForDetail,
    setPersonnelDetailDefaultTab,
    setPersonnelDetailDialogOpen
  ]);

  const handleOpenSyncDialog = useCallback(() => {
    setSyncDialogOpen(true);
  }, []);

  const visiblePendingMaterialRequestIds = useMemo(() => {
    if (!(isFormen || isBolgeMuhendisi || isMudurYardimcisi || isMudur || isGolgeAdmin)) {
      return [];
    }

    const currentPersonnel = personnel.find(p => p.id === personnelId);
    let pendingRequests = [];

    if (isFormen) {
      // Formen kendi bölgelerindeki bekleyen talepleri görür
      pendingRequests = materialRequests.filter(mr =>
        mr.status === "pending" && mr.formen_id === personnelId
      );
    } else if (isBolgeMuhendisi) {
      // Bölge mühendisi altındaki formenlerin bekleyen taleplerini görür
      const myFormens = personnel.filter(p => p.bolge_muhendisi_id === personnelId && p.role === "formen");
      const formenIds = myFormens.map(f => f.id);
      pendingRequests = materialRequests.filter(mr =>
        mr.status === "pending" && formenIds.includes(mr.formen_id)
      );
    } else if (isMudurYardimcisi) {
      // Müdür yardımcısı kendi bölgelerindeki bekleyen talepleri görür
      const myRegionIds = currentPersonnel?.region_ids || [];
      const myFormens = personnel.filter(p =>
        p.role === "formen" && p.region_ids?.some(rid => myRegionIds.includes(rid))
      );
      const formenIds = myFormens.map(f => f.id);
      pendingRequests = materialRequests.filter(mr =>
        mr.status === "pending" && formenIds.includes(mr.formen_id)
      );
    } else if (isMudur || isGolgeAdmin) {
      pendingRequests = materialRequests.filter(mr => mr.status === "pending");
    }

    return pendingRequests.map(mr => mr.id);
  }, [userRole, personnel, personnelId, materialRequests]);

  useEffect(() => {
    // Kullanıcı/rol değişiminde önceki "görüldü" kayıtlarını temizle
    setSeenMaterialRequestIds([]);
  }, [personnelId, userRole]);

  useEffect(() => {
    // Malzeme sekmesi açıldığında o anki bekleyen talepleri görüldü kabul et
    if (activeTab !== "materials" || visiblePendingMaterialRequestIds.length === 0) return;
    setSeenMaterialRequestIds(prevSeenIds => {
      const merged = new Set(prevSeenIds);
      visiblePendingMaterialRequestIds.forEach(id => merged.add(id));
      return Array.from(merged);
    });
  }, [activeTab, visiblePendingMaterialRequestIds]);

  const sidebarBadges = useMemo(() => {
    // Badge hesaplamaları - kullanıcının görmesi gereken bekleyen talepler
    const badges = {};

    const seenSet = new Set(seenMaterialRequestIds);
    const pendingMaterialsCount = visiblePendingMaterialRequestIds.length;
    const unseenPendingMaterials = visiblePendingMaterialRequestIds.filter(id => !seenSet.has(id)).length;
    const isManagerInfoRole = isAdmin || isMudur || isMudurYardimcisi || isBolgeMuhendisi;

    if (isManagerInfoRole) {
      // Müdür/MY/BM: bilgi amaçlı gri rozet, sekme görüldüğünde kalkar.
      if (unseenPendingMaterials > 0) {
        badges['materials'] = { count: unseenPendingMaterials, muted: true };
      }
    } else if (isFormen) {
      // Formen: işlem yapması gerektiği için kırmızı rozet, bekleyen talep oldukça görünür.
      if (pendingMaterialsCount > 0) {
        badges['materials'] = pendingMaterialsCount;
      }
    } else if (unseenPendingMaterials > 0) {
      // Diğer roller: önceki davranış (görülmemişler kırmızı).
      badges['materials'] = unseenPendingMaterials;
    }
    
    // İzin Yönetimi - Onay bekleyen izin talepleri
    if (!isParkGorevlisi && !isParkGorevlisi && !isParkGorevlisi) {
      if (isMudur || isAdmin) {
        const pendingAnnualOtherCount = leaveRequests.filter(lr =>
          lr.status === "in_progress" &&
          lr.current_approver_id === personnelId &&
          (lr.reason_type === "Yıllık İzin" || lr.reason_type === "Diğer")
        ).length;
        const pendingReportInfoCount = leaveRequests.filter(lr =>
          lr.status === "in_progress" &&
          lr.reason_type === "Rapor"
        ).length;

        if (pendingAnnualOtherCount > 0 || pendingReportInfoCount > 0) {
          badges['approvals'] = {
            primary: { count: pendingAnnualOtherCount, muted: false },
            secondary: { count: pendingReportInfoCount, muted: true }
          };
        }
      } else {
        const pendingApprovals = leaveRequests.filter(lr =>
          lr.current_approver_id === personnelId && lr.status === "in_progress"
        ).length;
        
        if (pendingApprovals > 0) badges['approvals'] = pendingApprovals;
      }
    }
    
    // Revize Yönetimi - Üzerimde bekleyen revize talepleri (FORMEN dahil)
    if (["formen", "bolge_muhendisi", "mudur_yardimcisi", "mudur"].includes(userRole)) {
      const pendingRevisions = revisions.filter((r) => {
        const status = String(r?.status || "");
        const currentLevel = String(r?.current_level || r?.current_approval_level || "").trim().toLowerCase();
        const isFinalized = ["Onaylandı", "Reddedildi", "Tamamlandı", "Ertelendi"].includes(status);
        return !isFinalized && currentLevel === userRole;
      }).length;

      if (pendingRevisions > 0) badges['revisions'] = pendingRevisions;
    }
    
    // Mesajlar - Okunmamış mesaj sayısı
    if (unreadMessagesCount > 0) badges['messages'] = unreadMessagesCount;

    // Faaliyet Yönetimi - Kullanıcının onayını bekleyen haftalık faaliyet planları
    if (pendingActivityPlansCount > 0) badges['activity-management'] = pendingActivityPlansCount;
    
    // Mesai Yönetimi - Aktif anket + onay bekleyen plan sayısı (toplam)
    const totalOvertimeNotifications = activeSurveysCount + pendingPlansCount;
    if (totalOvertimeNotifications > 0) {
      if (isMudur) {
        badges['overtime-management'] = { count: totalOvertimeNotifications, muted: true };
      } else {
        badges['overtime-management'] = totalOvertimeNotifications;
      }
    }
    
    return badges;
  }, [
    userRole,
    personnel,
    personnelId,
    visiblePendingMaterialRequestIds,
    seenMaterialRequestIds,
    leaveRequests,
    revisions,
    unreadMessagesCount,
    pendingActivityPlansCount,
    activeSurveysCount,
    pendingPlansCount
  ]);

  // -------------------------------------------------------------------------
  // Bildirim rozetleri — okunmamış bildirimleri sekme üzerinde göster
  // -------------------------------------------------------------------------
  // Mevcut rozetler (bekleyen onay, talep vb.) İŞLEM gerektiren şeyleri
  // gösterir; bildirim rozeti ise sadece "haber var" der. Bu yüzden birleştirme
  // kuralı: sekmede rozet yoksa bildirim sayısı ana rozet (kırmızı) olur; ana
  // rozet doluysa ikincil (gri) yuvaya düşer; ikisi de doluysa eklenmez —
  // böylece hiçbir mevcut rozet ezilmez.
  const { byTab: notificationBadgesByTab, refresh: refreshNotificationSummary, markTabRead } =
    useNotificationSummary({ personnelId });

  const mergedSidebarBadges = useMemo(() => {
    const merged = { ...sidebarBadges };
    Object.entries(notificationBadgesByTab).forEach(([tabId, count]) => {
      if (!count) return;
      const existing = merged[tabId];
      if (!existing) {
        merged[tabId] = { count, muted: false };
        return;
      }
      const normalized = typeof existing === 'number'
        ? { primary: { count: existing, muted: false }, secondary: null }
        : (existing.primary || existing.secondary)
          ? existing
          : { primary: { count: existing.count || 0, muted: existing.muted || false }, secondary: null };
      if (!normalized.secondary) {
        merged[tabId] = { primary: normalized.primary, secondary: { count, muted: true } };
      }
      // Her iki yuva doluysa dokunma (mevcut bilgi daha önceliklidir).
    });
    return merged;
  }, [sidebarBadges, notificationBadgesByTab]);

  // Sekme ziyaretinde otomatik okundu işaretleme.
  // Bu davranış beğenilmezse tek yapılacak: aşağıdaki sabiti `false` yap.
  // false → rozetler kalır, bildirimler yalnız zil panelinden okununca söner.
  const SEKME_ZIYARETINDE_OKUNDU_ISARETLE = true;
  const OKUNDU_GECIKMESI_MS = 2000; // sekmeler arası hızlı gezinmede tetiklenmesin

  // Sekme başına tek deneme: istek sessizce başarısız olursa her poll'da
  // yeniden denenip istek yağmuruna dönüşmesin. Sayaç sıfırlanınca kayıt silinir.
  const autoReadAttemptedRef = useRef(new Set());

  useEffect(() => {
    if (!SEKME_ZIYARETINDE_OKUNDU_ISARETLE) return undefined;
    const attempted = autoReadAttemptedRef.current;
    if (!activeTab) return undefined;
    if (!notificationBadgesByTab[activeTab]) {
      attempted.delete(activeTab); // sayaç boşaldı → yeni bildirimde tekrar dene
      return undefined;
    }
    if (attempted.has(activeTab)) return undefined;
    const timer = setTimeout(() => {
      attempted.add(activeTab);
      markTabRead(activeTab);
    }, OKUNDU_GECIKMESI_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, notificationBadgesByTab, markTabRead]);

  const READ_ONLY_MESSAGE = "Vekalet verdiğiniz için bu dönemde sadece görüntüleme yapabilirsiniz.";
  const todayStr = new Date().toISOString().slice(0, 10);
  const isLeaveRowActiveToday = useCallback((row) => {
    if (!row) return false;
    const start = String(row.start_date || "").slice(0, 10);
    const end = String(row.end_date || "").slice(0, 10);
    if (!start || !end) return false;
    return start <= todayStr && end >= todayStr;
  }, [todayStr]);

  const delegateActiveDelegations = useMemo(() => {
    const me = String(personnelId || "").trim();
    return (activeDelegations || []).filter((d) => {
      if (d?.is_active === false) return false;
      const toId = String(d?.to_person_id || "").trim();
      return Boolean(me && toId && toId === me);
    });
  }, [activeDelegations, personnelId]);
  const delegatorActiveDelegations = useMemo(() => {
    const me = String(personnelId || "").trim();
    return (activeDelegations || []).filter((d) => {
      if (d?.is_active === false) return false;
      const fromId = String(d?.from_person_id || "").trim();
      return Boolean(me && fromId && fromId === me);
    });
  }, [activeDelegations, personnelId]);
  const currentPersonForReadonly = useMemo(
    () => personnel.find(p => p.id === personnelId),
    [personnel, personnelId]
  );
  // Backend ile aynı mantık: delegasyon aktifken, kişi gerçekten unavailable ise read-only.
  const isPersonUnavailableToday = useMemo(() => {
    if (!personnelId) return false;
    // 1) Personel status
    if (currentPersonForReadonly?.status === "on_leave" || currentPersonForReadonly?.status === "on_report") {
      return true;
    }
    // 2) Onaylanmış izin aralığı bugünü kapsıyor mu?
    const hasApprovedLeaveToday = (leaveRequests || []).some(lr =>
      lr?.personnel_id === personnelId &&
      lr?.status === "approved" &&
      lr?.start_date && lr?.end_date &&
      lr.start_date <= todayStr &&
      lr.end_date >= todayStr
    );
    if (hasApprovedLeaveToday) return true;
    // 3) Aktif substitution (orijinal kişi) bugünü kapsıyor mu?
    const hasActiveSubstitutionToday = (allActiveSubstitutions || []).some(sub =>
      sub?.original_personnel_id === personnelId &&
      (sub?.status || "active") === "active" &&
      sub?.start_date && sub?.end_date &&
      sub.start_date <= todayStr &&
      sub.end_date >= todayStr
    );
    return Boolean(hasActiveSubstitutionToday);
  }, [allActiveSubstitutions, currentPersonForReadonly?.status, leaveRequests, personnelId, todayStr]);

  const isDelegatorReadOnly = Boolean(
    !isMudur &&
    delegatorActiveDelegations.length > 0 &&
    isPersonUnavailableToday
  );
  const pendingReturnApprovals = useMemo(
    () => (leaveRequests || []).filter(lr =>
      lr?.return_request?.status === "pending" &&
      lr?.return_request?.current_approver_id === personnelId
    ),
    [leaveRequests, personnelId]
  );

  const isDelegationReturnEligible = useCallback((delegation) => {
    if (!delegation || delegation.is_active === false) return false;
    const leaveId = delegation.leave_request_id;
    if (!leaveId) return false;
    const leave = (leaveRequests || []).find(lr => lr.id === leaveId);
    if (!leave) return false;
    if (leave.actual_return_date) {
      const mode = (leave.handover_mode || "immediate").toLowerCase();
      const until = leave.handover_until || leave.actual_return_date;
      if (mode === "immediate") return true;
      return Boolean(until && todayStr >= until);
    }
    return Boolean(leave.return_date && todayStr >= leave.return_date);
  }, [leaveRequests, todayStr]);

  const getEffectiveSubstitutionEndDate = useCallback((leave, fallbackEnd) => {
    if (!leave) return fallbackEnd || null;
    const rr = leave.return_request || {};
    const rrStatus = String(rr.status || "").trim().toLowerCase();
    const actualReturn = leave.actual_return_date || rr.actual_return_date;
    const handoverMode = String(leave.handover_mode || rr.handover_mode || "immediate").trim().toLowerCase();
    const handoverUntil = leave.handover_until || rr.handover_until;
    if (rrStatus === "approved" && actualReturn) {
      if (handoverMode === "end_of_day" || handoverMode === "custom") {
        return handoverUntil || actualReturn;
      }
      const actualDate = new Date(actualReturn);
      if (Number.isNaN(actualDate.getTime())) return actualReturn;
      actualDate.setDate(actualDate.getDate() - 1);
      return actualDate.toISOString().slice(0, 10);
    }
    return leave.end_date || leave.return_date || fallbackEnd || null;
  }, []);

  const isSubstitutionActiveToday = useCallback((start, end) => {
    const s = String(start || "").slice(0, 10);
    const e = String(end || "").slice(0, 10);
    return Boolean(s && e && s <= todayStr && e >= todayStr);
  }, [todayStr]);

  const findRelatedLeaveForSubstitution = useCallback((sub) => {
    if (!sub) return null;
    const leaveId = sub.leave_request_id || sub.leave_id;
    if (leaveId) {
      const direct = (leaveRequests || []).find((lr) => lr.id === leaveId);
      if (direct) return direct;
    }
    const originalId = sub.original_personnel_id;
    const substituteId = sub.substitute_personnel_id;
    if (!originalId || !substituteId) return null;
    const subStart = String(sub.start_date || "").slice(0, 10);
    const subEnd = String(sub.end_date || "").slice(0, 10);
    const candidates = (leaveRequests || []).filter((lr) => {
      if (lr?.status !== "approved") return false;
      if (String(lr.personnel_id || "") !== String(originalId)) return false;
      const start = String(lr.start_date || "").slice(0, 10);
      const end = String(lr.end_date || lr.return_date || "").slice(0, 10);
      if (subStart && end && subStart > end) return false;
      if (subEnd && start && subEnd < start) return false;
      const directSubMatch = String(lr.substitute_personnel_id || "") === String(substituteId);
      const planSingle = lr.delegationPlan?.singleDelegateUserId;
      const planMatch = String(planSingle || "") === String(substituteId);
      const parkSubMatch = Array.isArray(lr.park_substitutes) && lr.park_substitutes.some((ps) => {
        const sid = ps?.substitute_id || ps?.substitute_personnel_id;
        return String(sid || "") === String(substituteId);
      });
      return directSubMatch || parkSubMatch || planMatch;
    });
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => String(b.start_date || "").localeCompare(String(a.start_date || "")));
    return candidates[0];
  }, [leaveRequests]);

  const visibleAsSubstitute = useMemo(() => {
    return (mySubstitutions?.as_substitute || []).filter((sub) => {
      const me = String(personnelId || "").trim();
      const subId = String(sub?.substitute_personnel_id || "").trim();
      if (me && subId && subId !== me) return false;

      const leave = findRelatedLeaveForSubstitution(sub);
      if (leave && leave.status !== "approved") return false;
      const start = String(leave?.start_date || sub.start_date || "").slice(0, 10);
      const end = String(
        (leave ? getEffectiveSubstitutionEndDate(leave, sub.end_date) : null) ||
          getEffectiveSubstitutionEndDate(null, sub.end_date) ||
          ""
      ).slice(0, 10);
      // leaveRequests'ta kayıt yoksa bile (eski API filtresi) personnel endpoint'ten gelen satır geçerli
      return Boolean(start && end && end >= todayStr);
    });
  }, [mySubstitutions?.as_substitute, findRelatedLeaveForSubstitution, getEffectiveSubstitutionEndDate, todayStr, personnelId]);

  /** Üst vekalet bandı: personnel endpoint + global aktif liste (Ana Sayfa kartı ile aynı kaynak) + delegations */
  const substituteBannerItems = useMemo(() => {
    const me = String(personnelId || "").trim();
    const currentPerson = (personnel || []).find((p) => p.id === personnelId);
    const myNameNorm = String(currentPerson?.name || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

    const fromSubs = visibleAsSubstitute || [];
    const seen = new Set(
      fromSubs.map((s) => s.leave_request_id).filter(Boolean).map(String)
    );

    const isSubstituteForMe = (sub) => {
      const sid = String(sub?.substitute_personnel_id || "").trim();
      if (me && sid && sid === me) return true;
      const n = String(sub?.substitute_personnel_name || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
      if (myNameNorm && n && n === myNameNorm) return true;
      // PER_PARK: global /active-substitutions çöktürülmüş satırın top-level vekilini
      // "ilk park vekili"ne sabitliyor (_enrich_breakdown_from_park_substitutes →
      // entries[0], ör. BAŞAK→ERDİNÇ). Bu yüzden diğer per-park vekiller (Ali/Devrim)
      // top-level ile eşleşmez ve banner'ları hiç görünmüyordu. substitute_breakdown'da
      // HERHANGİ bir parkın vekili bensem de bu vekalet bana aittir.
      const breakdown = Array.isArray(sub?.substitute_breakdown) ? sub.substitute_breakdown : [];
      return breakdown.some((b) => {
        const bsid = String(b?.substitute_personnel_id || b?.substitute_id || "").trim();
        if (me && bsid && bsid === me) return true;
        const bname = String(b?.substitute_personnel_name || b?.substitute_name || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ");
        return Boolean(myNameNorm && bname && bname === myNameNorm);
      });
    };

    const inDateWindow = (sub) => {
      const start = String(sub?.start_date || "").slice(0, 10);
      const lr = sub?.leave_request_id
        ? (leaveRequests || []).find((l) => l.id === sub.leave_request_id)
        : null;
      const end = String(
        (lr ? getEffectiveSubstitutionEndDate(lr, sub.end_date) : null) ||
          getEffectiveSubstitutionEndDate(null, sub?.end_date) ||
          ""
      ).slice(0, 10);
      return Boolean(start && end && end >= todayStr);
    };

    const fromGlobalList = (allActiveSubstitutions || [])
      .filter((sub) => isSubstituteForMe(sub) && inDateWindow(sub))
      .filter((sub) => sub?.leave_request_id && !seen.has(String(sub.leave_request_id)))
      .map((sub) => ({
        ...sub,
        id: sub.id || `banner_global_${sub.leave_request_id}_${sub.original_personnel_id || ""}`,
        substitute_personnel_id: sub.substitute_personnel_id || personnelId,
      }));

    for (const g of fromGlobalList) {
      seen.add(String(g.leave_request_id));
    }

    // Sil/yeniden onay gibi akışlarda delegation/substitution kaydı gecikirse,
    // banner'ı doğrudan onaylı izin kaydından da üret.
    const fromLeaveFallback = (leaveRequests || [])
      .filter((lr) => lr?.id && lr?.status === "approved")
      .filter((lr) => {
        const end = String(getEffectiveSubstitutionEndDate(lr, lr?.end_date) || "").slice(0, 10);
        return Boolean(end && end >= todayStr);
      })
      .filter((lr) => !seen.has(String(lr.id)))
      .filter((lr) => {
        const direct = String(lr?.substitute_personnel_id || "").trim();
        if (me && direct && direct === me) return true;

        const req = String(lr?.requestedSubstituteUserId || "").trim();
        if (me && req && req === me) return true;

        const plan = String(lr?.delegationPlan?.singleDelegateUserId || "").trim();
        if (me && plan && plan === me) return true;

        const byPark = (lr?.park_substitutes || []).some((ps) => {
          const sid = String(ps?.substitute_id || ps?.substitute_personnel_id || "").trim();
          if (me && sid && sid === me) return true;
          const sname = String(ps?.substitute_name || ps?.substitute_personnel_name || "")
            .trim()
            .toLowerCase()
            .replace(/\s+/g, " ");
          return Boolean(myNameNorm && sname && sname === myNameNorm);
        });
        if (byPark) return true;

        const directName = String(lr?.substitute_personnel_name || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ");
        return Boolean(myNameNorm && directName && directName === myNameNorm);
      })
      .map((lr) => {
        const fromP = (personnel || []).find((p) => p.id === lr.personnel_id);
        const start = String(lr?.start_date || "").slice(0, 10);
        const end = String(getEffectiveSubstitutionEndDate(lr, lr?.end_date) || "").slice(0, 10);
        const parksFromSubstitutes = (lr?.park_substitutes || [])
          .filter((ps) => ps?.park_id || ps?.park_name)
          .map((ps) => ({ id: ps?.park_id || null, name: ps?.park_name || "" }));
        const parksFromSubstituteParks = (lr?.substitute_parks || [])
          .filter((p) => p?.id || p?.name)
          .map((p) => ({ id: p?.id || null, name: p?.name || "" }));
        const parks = parksFromSubstitutes.length > 0 ? parksFromSubstitutes : parksFromSubstituteParks;
        return {
          id: `banner_leave_${lr.id}_${personnelId}`,
          leave_request_id: lr.id,
          original_personnel_id: lr.personnel_id,
          original_personnel_name: fromP?.name || lr?.personnel_name || "İzinli personel",
          substitute_personnel_id: personnelId,
          start_date: start,
          end_date: end,
          parks,
        };
      })
      .filter((item) => item.start_date && item.end_date && item.end_date >= todayStr);

    for (const f of fromLeaveFallback) {
      if (f?.leave_request_id) seen.add(String(f.leave_request_id));
    }

    const fromDeleg = (delegateActiveDelegations || [])
      .filter((d) => {
        const lid = d?.leave_request_id ? String(d.leave_request_id) : "";
        if (!lid) return true;
        return !seen.has(lid);
      })
      .map((d) => {
        const lr = (leaveRequests || []).find((l) => l.id === d.leave_request_id);
        const fromP = (personnel || []).find((p) => p.id === d.from_person_id);
        const start = String((lr?.start_date || d.start_date || "") || "").slice(0, 10);
        const endRaw = lr ? getEffectiveSubstitutionEndDate(lr, lr.end_date) : d.end_date;
        const end = String(endRaw || "").slice(0, 10);
        return {
          id: `deleg_banner_${d.id}`,
          leave_request_id: d.leave_request_id || null,
          original_personnel_id: d.from_person_id,
          original_personnel_name: fromP?.name || lr?.personnel_name || 'İzinli personel',
          substitute_personnel_id: personnelId,
          start_date: start,
          end_date: end,
          parks: [],
        };
      })
      .filter((item) => item.start_date && item.end_date && item.end_date >= todayStr);

    const rawList = [...fromSubs, ...fromGlobalList, ...fromLeaveFallback, ...fromDeleg];
    const byDedupeKey = new Map();
    for (const item of rawList) {
      const lid = item?.leave_request_id != null && String(item.leave_request_id).trim() !== ""
        ? String(item.leave_request_id)
        : "";
      const dedupeKey = lid
        ? `lr_${lid}`
        : `fp_${item?.original_personnel_id || ""}_${item?.start_date || ""}_${item?.end_date || ""}_${item?.substitute_personnel_id || ""}`;
      const prev = byDedupeKey.get(dedupeKey);
      if (!prev) {
        byDedupeKey.set(dedupeKey, { ...item });
        continue;
      }
      const parkMap = new Map();
      [...(prev.parks || []), ...(item.parks || [])].forEach((p) => {
        if (!p) return;
        const pk = p.id != null && String(p.id) !== "" ? String(p.id) : String(p.name || "");
        if (pk) parkMap.set(pk, p);
      });
      byDedupeKey.set(dedupeKey, {
        ...prev,
        ...item,
        original_personnel_name: prev.original_personnel_name || item.original_personnel_name,
        original_personnel_id: prev.original_personnel_id || item.original_personnel_id,
        substitute_personnel_id: prev.substitute_personnel_id || item.substitute_personnel_id,
        start_date: prev.start_date || item.start_date,
        end_date: prev.end_date || item.end_date,
        parks: Array.from(parkMap.values()),
        id: prev.id || item.id,
      });
    }

    // KAPSAM: Banner her zaman "ben vekilim" bağlamıdır. Bir izin talebinde park
    // bazlı vekiller varsa, göstereceğim parklar YALNIZ bana atanmış olanlar olmalı
    // (aksi halde izin sahibinin tüm parkları sızar — Erdinç yalnız Başak'ı görmeli).
    // Per-park yoksa (tek vekil) tüm izin parkları bana aittir → dokunma.
    // Not: Backend uçları (get_personnel_substitutions / active-substitutions) da
    // artık substitute-filtreli park döndürür; bu, defense-in-depth son kapıdır.
    const scopeParksToMe = (item) => {
      // 1) EN GÜVENİLİR KAYNAK: global /active-substitutions satırının kendi
      //    taşıdığı `substitute_breakdown` (park→vekil). Bu, izin kaydına ERİŞİM
      //    GEREKTİRMEZ — park görevlisi vekilin (ör. Erdinç) leaveRequests'i
      //    scope:"mine" olduğundan Reyfet'in iznini içermez; o yüzden lr null olur
      //    ve aşağıdaki filterParksForSubstitute park_substitutes'i bulamayıp TÜM
      //    parkları geçiriyordu (çöktürülmüş satırın `parks` alanı union=tüm parklar
      //    + substitute_name=None). breakdown ise self-contained ve per-park doğru.
      const breakdown = Array.isArray(item?.substitute_breakdown) ? item.substitute_breakdown : null;
      if (breakdown && breakdown.length > 0) {
        const mineParks = breakdown
          .filter((b) => {
            const sid = String(b?.substitute_personnel_id || b?.substitute_id || "").trim();
            if (me && sid && sid === me) return true;
            const sname = String(b?.substitute_personnel_name || b?.substitute_name || "")
              .trim()
              .toLowerCase()
              .replace(/\s+/g, " ");
            return Boolean(myNameNorm && sname && sname === myNameNorm);
          })
          .map((b) => ({ id: b?.id || b?.park_id || null, name: b?.name || b?.park_name || "" }))
          .filter((p) => p.id || p.name);
        // breakdown'da bana atanmış park varsa YALNIZ onları göster.
        if (mineParks.length > 0) return { ...item, parks: mineParks };
        // Yoksa (breakdown var ama bana atanmış park yok) aşağıdaki lr-tabanlı yola düş.
      }
      const lr = item?.leave_request_id
        ? (leaveRequests || []).find((l) => l.id === item.leave_request_id)
        : null;
      // Ortak helper (leaveUtils): per-park varsa yalnız bana atanmışlar; yoksa tümü.
      const scoped = filterParksForSubstitute(lr?.park_substitutes, item.parks || [], {
        id: me,
        name: currentPerson?.name,
      });
      return { ...item, parks: scoped };
    };

    const afterLid = Array.from(byDedupeKey.values()).map(scopeParksToMe);
    const byPersonPeriod = new Map();
    for (const item of afterLid) {
      const oid = String(item?.original_personnel_id || "");
      const sd = String(item?.start_date || "").slice(0, 10);
      const ed = String(item?.end_date || "").slice(0, 10);
      const sid = String(item?.substitute_personnel_id || "");
      if (!oid || !sd || !ed) {
        byPersonPeriod.set(`_${byPersonPeriod.size}_${item?.id || ""}`, item);
        continue;
      }
      const periodKey = `${oid}|${sd}|${ed}|${sid}`;
      const prev = byPersonPeriod.get(periodKey);
      if (!prev) {
        byPersonPeriod.set(periodKey, { ...item });
        continue;
      }
      const parkMap2 = new Map();
      [...(prev.parks || []), ...(item.parks || [])].forEach((p) => {
        if (!p) return;
        const pk = p.id != null && String(p.id) !== "" ? String(p.id) : String(p.name || "");
        if (pk) parkMap2.set(pk, p);
      });
      byPersonPeriod.set(periodKey, {
        ...prev,
        ...item,
        leave_request_id: prev.leave_request_id || item.leave_request_id,
        original_personnel_name: prev.original_personnel_name || item.original_personnel_name,
        original_personnel_id: prev.original_personnel_id || item.original_personnel_id,
        substitute_personnel_id: prev.substitute_personnel_id || item.substitute_personnel_id,
        parks: Array.from(parkMap2.values()),
        id: prev.id || item.id,
      });
    }
    return Array.from(byPersonPeriod.values());
  }, [
    visibleAsSubstitute,
    allActiveSubstitutions,
    delegateActiveDelegations,
    leaveRequests,
    personnel,
    personnelId,
    todayStr,
    getEffectiveSubstitutionEndDate,
  ]);

  const regionNameById = useMemo(() => {
    const map = new Map();
    for (const r of regions || []) {
      const rid = String(r?.id || "").trim();
      const rname = String(r?.name || "").trim();
      if (rid && rname) map.set(rid, rname);
    }
    return map;
  }, [regions]);

  const getFormenDelegationRegionNames = useCallback((sub) => {
    const names = [];
    const seen = new Set();
    const addByRegionId = (rid) => {
      const key = String(rid || "").trim();
      if (!key || seen.has(key)) return;
      const rname = regionNameById.get(key);
      if (!rname) return;
      seen.add(key);
      names.push(rname);
    };

    const originalId = String(sub?.original_personnel_id || "").trim();
    const originalPerson = originalId
      ? (personnel || []).find((p) => String(p?.id || "").trim() === originalId)
      : null;

    // Öncelik: izinli personelin tüm bölge atamaları (park satırları eksik/tekil gelebilir).
    const personRegionIds = Array.isArray(originalPerson?.region_ids) ? originalPerson.region_ids : [];
    for (const rid of personRegionIds) addByRegionId(rid);
    addByRegionId(originalPerson?.region_id);
    addByRegionId(originalPerson?.formen_region_id);

    // Bölge tablosundan bağlı atanmış bölgeleri de ekle (formen/BM için güvenli kaynak).
    if (originalId) {
      for (const r of regions || []) {
        if (String(r?.assigned_bolge_muhendisi_id || "").trim() === originalId) {
          addByRegionId(r?.id);
        }
        const assignedFormenIds = Array.isArray(r?.assigned_formen_ids) ? r.assigned_formen_ids : [];
        if (assignedFormenIds.some((fid) => String(fid || "").trim() === originalId)) addByRegionId(r?.id);
      }
    }

    // Parklardan türeyen bölgeleri de birleştir.
    const parkById = new Map();
    for (const p of parks || []) {
      const pid = String(p?.id || "").trim();
      if (pid) parkById.set(pid, p);
    }
    for (const sp of sub?.parks || []) {
      const directRid = String(sp?.region_id || "").trim();
      if (directRid) {
        addByRegionId(directRid);
        continue;
      }
      const pid = String(sp?.id || "").trim();
      if (!pid) continue;
      const fullPark = parkById.get(pid);
      addByRegionId(fullPark?.region_id);
    }

    return names;
  }, [parks, personnel, regionNameById, regions]);

  const openReturnFlow = useCallback((sub) => {
    if (!sub?.leave_request_id) {
      toast.error("Bu kayıt için izin talebi bulunamadı.");
      return;
    }
    const leave = (leaveRequests || []).find(lr => lr.id === sub.leave_request_id);
    if (leave?.return_request?.status === "pending") {
      toast.info("Bu izin için bekleyen bir işbaşı talebi zaten var.");
      return;
    }
    if (leave?.return_request?.status === "approved") {
      toast.info("Bu izin için işbaşı talebi zaten onaylanmış.");
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    setSelectedReturnSub(sub);
    setReturnFlowForm({
      actual_return_date: today,
      return_note: "",
      handover_mode: "immediate",
      handover_until: today,
    });
    setIsReturnFlowOpen(true);
  }, [leaveRequests]);

  const handlePgQuickParkFilter = useCallback((quickFilter) => {
    setParkQuickFilter(quickFilter || null);
    setSearchTerm("");
    setFilterPersonnel("all");
    setFilterRegion("all");
    setFilterRole("all");
    setCurrentPage(1);
    setActiveTab("parks");
  }, []);

  const clearPgQuickParkFilter = useCallback(() => {
    setParkQuickFilter(null);
  }, []);

  const handleMarkReturn = useCallback(async ({
    leaveRequestId,
    actualReturnDate,
    returnNote,
    handoverMode,
    handoverUntil,
  }) => {
    if (!leaveRequestId || !personnelId) return;
    try {
      const response = await apiClient.put(`/leave-requests/${leaveRequestId}/mark-return`, {
        personnel_id: personnelId,
        actual_return_date: actualReturnDate,
        return_note: returnNote || null,
        handover_mode: handoverMode,
        handover_until: handoverMode === "custom" ? handoverUntil : null,
      }, { timeout: 30000 });
      const rr = response?.data?.return_request;
      if (rr?.status === "pending") {
        toast.success("İşbaşı talebi oluşturuldu ve onay zincirine gönderildi.");
      } else if (rr?.status === "approved") {
        toast.success("İşbaşı talebi doğrudan onaylandı.");
      } else {
        toast.success("İşbaşı talebi kaydedildi.");
      }
      fetchLeaveRequests();
      fetchMySubstitutions();
      fetchActiveDelegations();
      fetchAllActiveSubstitutions();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || error.message || "İş başı kaydı alınamadı";
      toast.error(msg);
    }
  }, [API, personnelId, fetchActiveDelegations]);

  const handleReturnRequestAction = useCallback((leaveReq, actionType) => {
    if (!leaveReq?.id || !personnelId) return;
    if (isDelegatorReadOnly || isGolgeAdmin) {
      if (!isGolgeAdmin) toast.warning(READ_ONLY_MESSAGE);
      return;
    }
    setSelectedReturnApprovalRequest(leaveReq);
    setReturnApprovalAction(actionType === "reject" ? "reject" : "approve");
    setReturnApprovalComment("");
    setIsReturnActionDialogOpen(true);
  }, [personnelId, isDelegatorReadOnly, userRole]);

  const handleSubmitReturnRequestAction = useCallback(async () => {
    if (!selectedReturnApprovalRequest?.id || !personnelId) return;
    setReturnApprovalSaving(true);
    try {
      const response = await apiClient.put(`/leave-requests/${selectedReturnApprovalRequest.id}/return-request/action`, {
        action: returnApprovalAction,
        approver_id: personnelId,
        comment: returnApprovalComment?.trim() || null
      }, { timeout: 30000 });

      const rrStatus = response?.data?.return_request?.status;
      if (returnApprovalAction === "approve") {
        toast.success(rrStatus === "pending"
          ? "İşbaşı talebi onaylanarak üst makama sevk edildi."
          : "İşbaşı talebi onaylandı.");
      } else {
        toast.success(rrStatus === "pending"
          ? "İşbaşı talebi reddet notu ile üst makama sevk edildi."
          : "İşbaşı talebi reddedildi.");
      }

      setIsReturnActionDialogOpen(false);
      setSelectedReturnApprovalRequest(null);
      setReturnApprovalComment("");

      fetchLeaveRequests();
      fetchMySubstitutions();
      fetchActiveDelegations();
      fetchAllActiveSubstitutions();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || error.message || "İşlem başarısız";
      toast.error(msg);
    } finally {
      setReturnApprovalSaving(false);
    }
  }, [
    API,
    personnelId,
    returnApprovalAction,
    returnApprovalComment,
    selectedReturnApprovalRequest,
    fetchActiveDelegations
  ]);

  const handleReturnDelegation = useCallback(async (delegation) => {
    if (!delegation?.id || !personnelId) return;
    setHandoverLoadingId(delegation.id);
    try {
      await axios.put(`${API}/api/delegations/${delegation.id}/return`, {
        actor_id: personnelId,
      }, { timeout: 30000 });
      toast.success("Vekalet iade edildi. Yetkiler asıl kullanıcıya devredildi.");
      fetchActiveDelegations();
      fetchMySubstitutions();
      fetchAllActiveSubstitutions();
      fetchLeaveRequests();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || error.message || "Vekalet iade edilemedi";
      toast.error(msg);
    } finally {
      setHandoverLoadingId(null);
    }
  }, [API, personnelId, fetchActiveDelegations]);

  useEffect(() => {
    const interceptorId = axios.interceptors.request.use(
      (config) => {
        if (!isDelegatorReadOnly) return config;
        const method = (config?.method || "get").toLowerCase();
        if (["get", "head", "options"].includes(method)) return config;
        const url = config?.url || "";
        if (url.includes("/leave-requests/") && url.includes("/mark-return")) return config;

        const err = new Error("DELEGATION_READ_ONLY");
        err.isDelegationReadonly = true;
        if (!isGolgeAdmin) toast.warning(READ_ONLY_MESSAGE);
        return Promise.reject(err);
      },
      (error) => Promise.reject(error)
    );
    return () => axios.interceptors.request.eject(interceptorId);
  }, [isDelegatorReadOnly, userRole]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <Toaster />
      
      <AppHeader
        userRole={userRole}
        personnelName={personnelName}
        personnelId={personnelId}
        onLogout={onLogout}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        notificationBadgesByTab={notificationBadgesByTab}
        onNotificationSummaryChanged={refreshNotificationSummary}
      />

      {/* Main Layout with Sidebar */}
      <div className="flex">
        {/* Sidebar Navigation */}
        <Sidebar 
          userRole={userRole} 
          activeTab={activeTab} 
          onTabChange={setActiveTab}
          badges={mergedSidebarBadges}
        />
        
        {/* Main Content Area */}
        <div className="flex-1 overflow-auto" style={{ height: "calc(100vh - var(--app-header-h))" }}>
          <div className="p-6">
            
            {/* Admin (sadece görüntüleme) – değişiklik yapılamaz */}
            {isGolgeAdmin && (
              <div className="mb-4 rounded-lg border-2 border-slate-400 bg-slate-100 p-4 text-slate-800">
                <p className="font-semibold">Admin – Sadece Görüntüleme</p>
                <p className="text-sm">Tüm verileri görüntüleyebilirsiniz; onay, düzenleme veya silme yapılamaz.</p>
              </div>
            )}

            {/* Delegator read-only uyarısı (Başkan/GKAGÖLGE için gösterme – üstte "Admin – Sadece Görüntüleme" var) */}
            {isDelegatorReadOnly && !isGolgeAdmin && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
                <p className="font-semibold">Salt Okunur Mod Aktif</p>
                <p className="text-sm">{READ_ONLY_MESSAGE}</p>
              </div>
            )}

            {/* Mesai göreviniz var – Vekalet gibi sayfa üstünde, mesai süresince görünsün */}
            {Array.isArray(inAppNotifications) && inAppNotifications.filter(n => (n.notification_type || n.type) === 'mesai_assigned').length > 0 && (
              <div className="mb-4 space-y-2">
                {inAppNotifications
                  .filter(n => (n.notification_type || n.type) === 'mesai_assigned')
                  .map((n) => (
                    <div key={n.id || n.target_date} className="rounded-lg p-4 shadow-sm border-2 border-green-300 bg-green-50">
                      <div className="flex items-start gap-3">
                        <div className="rounded-full p-2 bg-green-200">
                          <CalendarClock className="h-6 w-6 text-green-700" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-green-900">
                            📅 Mesai Göreviniz Var
                          </h3>
                          <p className="mt-1 text-green-800">{n.message || n.title}</p>
                          {n.target_date && (
                            <p className="text-sm text-green-700 mt-1">
                              Tarih: {new Date(n.target_date).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Aktif Vekalet Bildirimi - Kullanıcı başkasının yerine bakıyorsa */}
            {substituteBannerItems.length > 0 && (
              <div className="mb-4 space-y-2">
                {substituteBannerItems.map((sub) => {
                  const formenRegionNames = isFormen ? getFormenDelegationRegionNames(sub) : [];
                  const formenRegionsText = formenRegionNames.length > 0 ? formenRegionNames.join(", ") : "atanmış bölgeler";
                  return (
                    <div key={sub.id} className="vekalet-card rounded-lg p-4 shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="vekalet-card-icon rounded-full p-2">
                          <Users className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-lg flex items-center gap-2">
                            📋 Vekalet Göreviniz Var!
                          </h3>
                          <p className="mt-1">
                            <strong>{sub.original_personnel_name}</strong> izinli olduğu süre boyunca ({new Date(sub.start_date).toLocaleDateString('tr-TR')} / {new Date(sub.end_date).toLocaleDateString('tr-TR')})
                            {isBolgeMuhendisi
                              ? " siz sorumlusunuz."
                              : isFormen
                                ? (
                                  <>
                                    {" "}aşağıdaki bölgelerdeki (<strong>{formenRegionsText}</strong>) parklara sizin bakmanız gerekmektedir:
                                  </>
                                )
                                : " aşağıdaki parklara sizin bakmanız gerekmektedir:"}
                          </p>
                          {!isBolgeMuhendisi && !isFormen && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {sub.parks?.map((park) => (
                                <span key={park.id} className="vekalet-card-pill px-3 py-1 rounded-full text-sm font-medium">
                                  🌳 {park.name}
                                </span>
                              ))}
                            </div>
                          )}
                          {(() => {
                            const delegation = delegateActiveDelegations.find(d =>
                              d.leave_request_id &&
                              sub.leave_request_id &&
                              String(d.leave_request_id) === String(sub.leave_request_id)
                            );
                            if (!delegation) return null;
                            const canReturn = isDelegationReturnEligible(delegation);
                            const leaveForDelegation = (leaveRequests || []).find(lr => lr.id === delegation.leave_request_id);
                            const handoverUntil = leaveForDelegation?.handover_until;
                            return (
                              <div className="mt-3 flex items-center gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => handleReturnDelegation(delegation)}
                                  disabled={!canReturn || handoverLoadingId === delegation.id}
                                >
                                  {handoverLoadingId === delegation.id ? "İade ediliyor..." : "Vekaleti İade Et"}
                                </Button>
                                {!canReturn && (
                                  <span className="text-xs text-gray-600">
                                    {handoverUntil ? `İade ${new Date(handoverUntil).toLocaleDateString('tr-TR')} sonrası açılır.` : "İş başı bilgisi bekleniyor."}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Vekalet veren kullanıcı için erken dönüş aksiyonu */}
            {mySubstitutions.on_leave && mySubstitutions.on_leave.length > 0 && (
              <div className="mb-4 space-y-2">
                {(() => {
                  // Aynı izin için birden fazla park vekili seçilirse on_leave tekrar edebilir.
                  // UI'da tek bir "iş başı" bildirimi göstermek için leave_request_id bazında tekilleştir.
                  const formatTrDate = (dateStr) =>
                    dateStr ? new Date(dateStr).toLocaleDateString('tr-TR').replaceAll('.', ',') : "";

                  const genitiveLeaveLabel = (reasonType) => {
                    const r = (reasonType || "").toLowerCase();
                    if (r.includes("yıllık")) return "yıllık izninizden";
                    if (r.includes("sosyal")) return "sosyal izninizden";
                    if (r.includes("rapor")) return "raporunuzdan";
                    return "izninizden";
                  };

                  const leaveRequestIds = new Set((leaveRequests || []).map((lr) => lr.id));
                  const uniq = new Map();
                  (mySubstitutions.on_leave || []).forEach((s) => {
                    const lid = s?.leave_request_id;
                    if (!lid) return;
                    if (!leaveRequestIds.has(lid)) return;
                    if (!uniq.has(lid)) {
                      uniq.set(lid, s);
                      return;
                    }
                    const prev = uniq.get(lid);
                    const mergedParks = [...(prev?.parks || []), ...(s?.parks || [])];
                    const byId = new Map();
                    mergedParks.forEach((p) => {
                      if (p?.id) byId.set(p.id, p);
                    });
                    uniq.set(lid, { ...prev, parks: Array.from(byId.values()) });
                  });

                  return Array.from(uniq.values()).map((sub) => {
                    const leave = (leaveRequests || []).find(lr => lr.id === sub.leave_request_id);
                    const rangeText = (leave?.start_date && leave?.end_date)
                      ? `${formatTrDate(leave.start_date)}-${formatTrDate(leave.end_date)}`
                      : "";
                    const infoText = rangeText
                      ? `${rangeText} tarihleri arası ${genitiveLeaveLabel(leave?.reason_type)} erken döndüyseniz iş başı talebi oluşturun.`
                      : `Bu ${genitiveLeaveLabel(leave?.reason_type)} erken döndüyseniz iş başı talebi oluşturun.`;

                    const originalPerson = (personnel || []).find(
                      (p) => String(p?.id || "").trim() === String(sub?.original_personnel_id || "").trim()
                    );
                    const originalRole = String(originalPerson?.role || "").trim().toLowerCase();
                    const isRegionDelegation = ["formen", "bolge_muhendisi", "mudur_yardimcisi"].includes(originalRole);
                    const fallbackVekilName = (
                      sub?.substitute_personnel_name ||
                      leave?.substitute_personnel_name ||
                      "Vekil"
                    ).trim();

                    let delegationItems = [];
                    if (isRegionDelegation) {
                      delegationItems = getFormenDelegationRegionNames(sub).map((regionName) => ({
                        name: regionName,
                        vekil: fallbackVekilName,
                      }));
                    } else {
                      delegationItems = (sub.parks || []).map((p) => ({
                        name: p?.name || p?.park_name || '',
                        vekil: (p?.substitute_name || p?.substitute_personnel_name || sub?.substitute_personnel_name || 'Vekil').trim()
                      })).filter((x) => x.name);
                      if (!delegationItems.length && leave) {
                        const psList = leave.park_substitutes || [];
                        if (psList.length > 0) {
                          delegationItems = psList.map((p) => ({
                            name: (p.park_name || p.park_id || '').trim(),
                            vekil: (p.substitute_name || p.substitute_personnel_name || leave.substitute_personnel_name || 'Vekil').trim()
                          })).filter((x) => x.name);
                        }
                        if (!delegationItems.length && (leave.substitute_parks || []).length > 0 && leave.substitute_personnel_name) {
                          delegationItems = (leave.substitute_parks || []).map((p) => ({
                            name: (typeof p === 'string' ? p : (p?.name || p?.park_name || p?.id || '')).trim(),
                            vekil: (leave.substitute_personnel_name || 'Vekil').trim()
                          })).filter((x) => x.name);
                        }
                      }
                    }

                    return (
                  <div key={`on_leave_${sub.id}`} className="rounded-lg border border-emerald-300 bg-emerald-50 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-emerald-900">İş başı bildirimi</p>
                        <p className="text-sm text-emerald-800">
                          {infoText}
                        </p>
                        {rangeText && leave?.reason_type && (
                          <p className="text-xs mt-1.5 font-medium text-emerald-700">
                            {rangeText} — {leave.reason_type}
                          </p>
                        )}
                        {delegationItems.length > 0 && (
                          <div className="mt-2 text-xs text-emerald-800">
                            <span className="font-semibold">{isRegionDelegation ? "Vekalet Bölgeleri:" : "Vekaletler:"}</span>
                            <ul className="mt-1 space-y-0.5 list-none pl-0">
                              {delegationItems.map((item, idx) => (
                                <li key={`vekalet_${item.name}_${idx}`}>{isRegionDelegation ? "🗺️" : "🌳"} {item.name} → {item.vekil}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {(() => {
                          const rr = leave?.return_request;
                          if (!rr?.status) return null;
                          return (
                            <p className="text-xs mt-1 text-emerald-700">
                              Talep durumu: {rr.status === "pending" ? "Dogrulama bekliyor" : rr.status === "approved" ? "Onaylandı" : rr.status === "rejected" ? "Reddedildi" : rr.status}
                            </p>
                          );
                        })()}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => openReturnFlow(sub)}
                        disabled={!sub.leave_request_id}
                      >
                        İş Başı Talebi Oluştur
                      </Button>
                    </div>
                  </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* Bu kullanıcıda bekleyen işbaşı talebi onayları */}
            {pendingReturnApprovals.length > 0 && (
              <div className="mb-4 space-y-2">
                {pendingReturnApprovals.map((lr) => (
                  <div key={`return_approval_${lr.id}`} className="rounded-lg border border-blue-300 bg-blue-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-blue-900">İşbaşı Talebi Onayı Bekliyor</p>
                        <p className="text-sm text-blue-800">
                          <strong>{lr.personnel_name}</strong> için {lr.reason_type || "izin"} talebi
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          Planlanan dönüş: {lr.return_date ? new Date(lr.return_date).toLocaleDateString('tr-TR') : "-"} | Fiili: {lr.return_request?.actual_return_date ? new Date(lr.return_request.actual_return_date).toLocaleDateString('tr-TR') : "-"}
                        </p>
                        {lr.return_request?.return_note && (
                          <p className="text-xs text-blue-700 mt-1 italic">Not: {lr.return_request.return_note}</p>
                        )}
                        {(() => {
                          const rrHistory = lr.return_request?.approval_history || [];
                          const visible = rrHistory.filter((step) =>
                            canSeeApprovalComment(
                              userRole,
                              step?.approver_role,
                              lr.return_request?.status || "pending",
                              personnelId,
                              step?.approver_id
                            )
                          );
                          if (!visible.length) return null;
                          const actionLabel = (stepAction) => {
                            if (stepAction === "approve") return "✅ Onaylayarak Üst Makama Sevk Etti";
                            if (stepAction === "reject_and_forward") return "❌ Reddet ile Üst Makama Sevk Etti.";
                            if (stepAction === "reject") return "❌ Reddetti";
                            return "⬆️ Üst Makama Sevk Etti";
                          };
                          return (
                            <div className="mt-2 space-y-1">
                              {visible.map((step, idx) => (
                                <div key={`${step?.approver_id || "x"}_${step?.timestamp || idx}`} className="text-xs text-blue-800">
                                  <span className="font-semibold">{step?.approver_name || "Bilinmiyor"}:</span>{" "}
                                  <span>{actionLabel(step?.action)}</span>
                                  {step?.comment ? (
                                    <span className="italic"> - {step.comment}</span>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleReturnRequestAction(lr, "approve")}>
                          Onayla
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleReturnRequestAction(lr, "reject")}>
                          {(() => {
                            const chain = lr.return_request?.approval_chain || [];
                            const idx = chain.findIndex((id) => String(id) === String(personnelId));
                            const hasNext = idx >= 0 && idx + 1 < chain.length;
                            return hasNext ? "Reddet + Üst Makama Sevk" : "Reddet";
                          })()}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

          {/* PARK GÖREVLİSİ - Ana Sayfa sekmesi (sidebar’dan Ana Sayfa tıklanınca) */}
          {isParkGorevlisi && activeTab === "dashboard" && (
            <TabsContent value="dashboard" className="space-y-6">
              <HomeTab
                displayedParks={displayedParks}
                inAppNotifications={inAppNotifications}
                unreadNotificationCount={unreadNotificationCount}
                onMarkAsRead={markAsRead}
                onMarkAllRead={markAllAsRead}
                onRefreshNotifications={fetchInAppNotifications}
                onQuickParkFilter={handlePgQuickParkFilter}
                onInventoryDetail={(title, field) => showInventoryDetail(title, field, displayedParks)}
                onFacilityDetail={(title, field) => showFacilityDetail(title, field, displayedParks)}
                onIrrigationDetail={(title, type) => showIrrigationDetail(title, type, displayedParks)}
              />
            </TabsContent>
          )}

          {/* Dashboard Tab - Ana Sayfa (Müdür, MY, BM, Formen) */}
          {isManagerView && activeTab === "dashboard" && (() => {
            // Filtrelenmiş istatistikler hesapla (stats yoksa varsayılan ile çökme önlenir)
            let displayStats = stats ?? {
              totalParks: 0, totalArea: 0, totalGrassArea: 0, totalRegions: 0,
              totalPersonnel: 0, activePersonnel: 0, onLeavePersonnel: 0, onReportPersonnel: 0,
              assignedParks: 0, unassignedParks: 0, parksWithRegion: 0, parksWithoutRegion: 0,
              unassignedParksList: []
            };
            
            if (isMudurYardimcisi) {
              // Müdür Yardımcısı: Kendine atanmış bölgelerdeki parkları ve personelleri görür
              const myRegionCount = Array.isArray(myFilteredRegions) ? myFilteredRegions.length : 0;
              
              // Parklar sekmesiyle birebir aynı kapsam
              const myParks = myFilteredParks;
              
              // Atanmış ve atanmamış park sayıları (bölge bazlı - eski)
              const parksWithRegion = myParks.filter(p => p.region_id).length;
              // Diğer rollerle (BM/Formen) ve global stats ile tutarlı: bölgesiz parklar
              // MY'nin KENDİ kapsamından (myParks) sayılır — global `parks` başka
              // yöneticilerin bölgesiz parklarını bu istatistiğe sızdırıyordu.
              const parksWithoutRegion = myParks.filter(p => !p.region_id).length;
              
              // Müdür Yardımcısına bağlı personeller
              const myPersonnel = personnel.filter(p => 
                p.mudur_yardimcisi_id === personnelId && 
                p.role !== "mudur" && 
                p.role !== "mudur_yardimcisi"
              );
              
              // Park Görevlisi bazlı atama hesaplaması
              // Widget üç alanı (assignedParks/unassignedParks) widgetStrictRegionParks
              // üzerinden — çapraz atama veya geçici görev nedeniyle başka bölgenin parkı
              // bu hesaba sızmasın. Diğer alanlar myParks (= myFilteredParks) ile devam eder.
              const parkGorevlisiIds = new Set(myPersonnel.filter(p => p.role === 'park_gorevlisi').map(p => p.id));
              const assignedParks = widgetStrictRegionParks.filter(p => {
                if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return false;
                return p.assigned_personnel_ids.some(pid => parkGorevlisiIds.has(pid));
              }).length;
              const unassignedParks = widgetStrictRegionParks.filter(p => {
                if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return true;
                return !p.assigned_personnel_ids.some(pid => parkGorevlisiIds.has(pid));
              }).length;
              const unassignedParksList = widgetStrictRegionParks.filter(p => {
                if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return true;
                return !p.assigned_personnel_ids.some(pid => parkGorevlisiIds.has(pid));
              });

              // Bu yöneticinin altındaki personellerin bugünkü izin/rapor durumlarını hesapla
              const myPersonnelIds = myPersonnel.map(p => p.id);
              const myPersonnelOnLeave = todayOnLeaveOnly.filter(lr => myPersonnelIds.includes(lr.personnel_id));
              const myPersonnelOnReport = todayOnReport.filter(lr => myPersonnelIds.includes(lr.personnel_id));
              const myOnLeaveIds = [...new Set(myPersonnelOnLeave.map(lr => lr.personnel_id))];
              const myOnReportIds = [...new Set(myPersonnelOnReport.map(lr => lr.personnel_id))];

              displayStats = {
                totalParks: myParks.length,
                totalArea: Math.round(myParks.reduce((sum, p) => sum + (parseFloat(p.area) || 0), 0)),
                totalGrassArea: Math.round(myParks.reduce((sum, p) => sum + (parseFloat(p.grass_area) || 0), 0)),
                // Bölgeler sekmesiyle aynı kapsam: görünür/erişilebilir bölgeler
                totalRegions: myRegionCount,
                parksWithRegion: parksWithRegion,
                parksWithoutRegion: parksWithoutRegion,
                assignedParks: assignedParks,
                unassignedParks: unassignedParks,
                unassignedParksList: unassignedParksList,
                totalPersonnel: myPersonnel.length,
                activePersonnel: myPersonnel.length - myOnLeaveIds.length - myOnReportIds.length,
                onLeavePersonnel: myOnLeaveIds.length,
                onReportPersonnel: myOnReportIds.length
              };
            } else if (isBolgeMuhendisi) {
              const currentPersonnel = personnel.find(p => p.id === personnelId);
              const myRegionIds = currentPersonnel?.region_ids || [];
              
              // Bölge mühendisine bağlı Formen'lerin region_ids'lerini de ekle
              const myFormenler = personnel.filter(p => p.bolge_muhendisi_id === personnelId && p.role === "formen");
              const formenRegionIds = myFormenler.flatMap(f => f.region_ids || []);
              const allRegionIds = [...new Set([...myRegionIds, ...formenRegionIds])];
              
              // Bölge mühendisine bağlı tüm personel ID'leri (kendisi dahil formenler ve altı)
              const myFormenIds = myFormenler.map(f => f.id);
              const myAllPersonnelIds = [personnelId, ...myFormenIds];
              // Formenlere bağlı park görevlileri de ekle
              const formenSubordinates = personnel.filter(p => myFormenIds.includes(p.formen_id));
              myAllPersonnelIds.push(...formenSubordinates.map(s => s.id));
              
              // Parklar sekmesiyle birebir aynı kapsam
              const myParks = myFilteredParks;
              
              // Bölge mühendisine bağlı personelleri filtrele (formen ve altı)
              const myPersonnel = personnel.filter(p => 
                p.bolge_muhendisi_id === personnelId && 
                p.role !== "mudur" && 
                p.role !== "mudur_yardimcisi" &&
                p.role !== "bolge_muhendisi"
              );
              
              // Bölge Mühendisi'nin sorumlu olduğu toplam bölge sayısı
              // Sadece bölge mühendisinin kendisine atanan bölgeler (assigned_bolge_muhendisi_id)
              const myTotalRegions = regions.filter(r => r.assigned_bolge_muhendisi_id === personnelId).length;
              
              // Park'lara atanmış ve atanmamış sayıları
              const parksWithRegion = myParks.filter(p => p.region_id).length;
              const parksWithoutRegion = myParks.filter(p => !p.region_id).length;
              
              // Park Görevlisi bazlı atama hesaplaması
              // Widget alanları widgetStrictRegionParks üzerinden: yalnızca BM'nin
              // gerçekten atandığı bölgelerdeki parklar sayılır. Çapraz BM atamaları veya
              // alt-personel sızıntıları "görevlisiz" listesine girmez.
              const parkGorevlisiIds = new Set(myPersonnel.filter(p => p.role === 'park_gorevlisi').map(p => p.id));
              const assignedParks = widgetStrictRegionParks.filter(p => {
                if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return false;
                return p.assigned_personnel_ids.some(pid => parkGorevlisiIds.has(pid));
              }).length;
              const unassignedParks = widgetStrictRegionParks.filter(p => {
                if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return true;
                return !p.assigned_personnel_ids.some(pid => parkGorevlisiIds.has(pid));
              }).length;

              // Park görevlisi olmayan parkların listesi
              const unassignedParksList = widgetStrictRegionParks.filter(p => {
                if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return true;
                return !p.assigned_personnel_ids.some(pid => parkGorevlisiIds.has(pid));
              });

              // Bu yöneticinin altındaki personellerin bugünkü izin/rapor durumlarını hesapla
              const myPersonnelIds = myPersonnel.map(p => p.id);
              const myPersonnelOnLeave = todayOnLeaveOnly.filter(lr => myPersonnelIds.includes(lr.personnel_id));
              const myPersonnelOnReport = todayOnReport.filter(lr => myPersonnelIds.includes(lr.personnel_id));
              const myOnLeaveIds = [...new Set(myPersonnelOnLeave.map(lr => lr.personnel_id))];
              const myOnReportIds = [...new Set(myPersonnelOnReport.map(lr => lr.personnel_id))];

              displayStats = {
                totalParks: myParks.length,
                totalArea: Math.round(myParks.reduce((sum, p) => sum + (parseFloat(p.area) || 0), 0)),
                totalGrassArea: Math.round(myParks.reduce((sum, p) => sum + (parseFloat(p.grass_area) || 0), 0)),
                totalRegions: myTotalRegions,
                parksWithRegion: parksWithRegion,
                parksWithoutRegion: parksWithoutRegion,
                assignedParks: assignedParks,
                unassignedParks: unassignedParks,
                unassignedParksList: unassignedParksList,
                totalPersonnel: myPersonnel.length,
                activePersonnel: myPersonnel.length - myOnLeaveIds.length - myOnReportIds.length,
                onLeavePersonnel: myOnLeaveIds.length,
                onReportPersonnel: myOnReportIds.length
              };
            } else if (isFormen) {
              const currentPersonnel = personnel.find(p => p.id === personnelId);
              const myRegionIds = currentPersonnel?.region_ids || [];
              
              // Formene bağlı personel ID'leri (kendisi dahil)
              const mySubordinates = personnel.filter(p => p.formen_id === personnelId);
              const myAllPersonnelIds = [personnelId, ...mySubordinates.map(s => s.id)];
              
              // Parklar sekmesiyle birebir aynı kapsam
              const myParks = myFilteredParks;
              
              // Formen'e bağlı personelleri filtrele (park görevlisi ve çim ekibi)
              const myPersonnel = personnel.filter(p => 
                p.formen_id === personnelId &&
                (p.role === "park_gorevlisi")
              );
              
              // Formen'in sorumlu olduğu bölge sayısı (region_ids'ten)
              const myTotalRegions = myRegionIds.length;
              
              // Atanmış ve atanmamış park sayıları (sorumlu olduğu bölgelerdeki parklar)
              const parksWithRegion = myParks.filter(p => p.region_id).length;
              const parksWithoutRegion = myParks.filter(p => !p.region_id).length;
              
              // Park Görevlisi bazlı atama hesaplaması
              // Widget alanları widgetStrictRegionParks üzerinden: yalnızca Formen'in
              // gerçekten atandığı bölgelerdeki parklar.
              const parkGorevlisiIds = new Set(myPersonnel.filter(p => p.role === 'park_gorevlisi').map(p => p.id));
              const assignedParks = widgetStrictRegionParks.filter(p => {
                if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return false;
                return p.assigned_personnel_ids.some(pid => parkGorevlisiIds.has(pid));
              }).length;
              const unassignedParks = widgetStrictRegionParks.filter(p => {
                if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return true;
                return !p.assigned_personnel_ids.some(pid => parkGorevlisiIds.has(pid));
              }).length;

              // Park görevlisi olmayan parkların listesi
              const unassignedParksList = widgetStrictRegionParks.filter(p => {
                if (!p.assigned_personnel_ids || p.assigned_personnel_ids.length === 0) return true;
                return !p.assigned_personnel_ids.some(pid => parkGorevlisiIds.has(pid));
              });

              // Bu yöneticinin altındaki personellerin bugünkü izin/rapor durumlarını hesapla
              const myPersonnelIds = myPersonnel.map(p => p.id);
              const myPersonnelOnLeave = todayOnLeaveOnly.filter(lr => myPersonnelIds.includes(lr.personnel_id));
              const myPersonnelOnReport = todayOnReport.filter(lr => myPersonnelIds.includes(lr.personnel_id));
              const myOnLeaveIds = [...new Set(myPersonnelOnLeave.map(lr => lr.personnel_id))];
              const myOnReportIds = [...new Set(myPersonnelOnReport.map(lr => lr.personnel_id))];

              displayStats = {
                totalParks: myParks.length,
                totalArea: Math.round(myParks.reduce((sum, p) => sum + (parseFloat(p.area) || 0), 0)),
                totalGrassArea: Math.round(myParks.reduce((sum, p) => sum + (parseFloat(p.grass_area) || 0), 0)),
                totalRegions: myTotalRegions,
                parksWithRegion: parksWithRegion,
                parksWithoutRegion: parksWithoutRegion,
                assignedParks: assignedParks,
                unassignedParks: unassignedParks,
                unassignedParksList: unassignedParksList,
                totalPersonnel: myPersonnel.length,
                activePersonnel: myPersonnel.length - myOnLeaveIds.length - myOnReportIds.length,
                onLeavePersonnel: myOnLeaveIds.length,
                onReportPersonnel: myOnReportIds.length
              };
            }
            
            return (
            <TabsContent value="dashboard" className="space-y-6">
              <DashboardStatisticsCards
                displayStats={displayStats}
                userRole={userRole}
                personnelId={personnelId}
                leaveRequests={leaveRequests}
                activeSearchTerm={searchTerm}
                onClearSearch={() => setSearchTerm("")}
                setOnLeaveDialogOpen={setOnLeaveDialogOpen}
                setOnReportDialogOpen={setOnReportDialogOpen}
                setLowLeaveDialogOpen={setLowLeaveDialogOpen}
                setActiveTab={setActiveTab}
                onParkFilterClick={(filterType) => {
                  if (filterType === "assigned") {
                    // Atanmış parkları göster - bölgesi olan parklar
                    setFilterRegion("assigned");
                  } else if (filterType === "unassigned") {
                    // Atanmamış parkları göster - bölgesi olmayan parklar
                    setFilterRegion("none");
                  }
                  setActiveTab("parks");
                }}
                onUnassignedParkGorevlisiClick={() => {
                  // Park görevlisi olmayan parkları bul
                  const parksList = displayStats.unassignedParksList || stats.unassignedParksList || [];
                  
                  if (parksList.length === 0) return;
                  
                  if (isAdmin || isMudur || isMudurYardimcisi) {
                    // Müdür/Yardımcı: Atama yapabilir
                    if (parksList.length === 1) {
                      // Tek park varsa direkt aç
                      setActiveTab("parks");
                      setTimeout(() => {
                        handleParkEdit(parksList[0]);
                      }, 100);
                    } else {
                      // Birden fazla park varsa seçim dialogu aç
                      setUnassignedParksList(parksList);
                      setUnassignedParksDialogOpen(true);
                    }
                  } else {
                    // Bölge Mühendisi/Formen: Sadece görüntüleme
                    if (parksList.length === 1) {
                      setSearchTerm(parksList[0].name);
                      setActiveTab("parks");
                    } else {
                      // Birden fazla park varsa seçim dialogu aç (görüntüleme modunda)
                      setUnassignedParksList(parksList);
                      setUnassignedParksDialogOpen(true);
                    }
                  }
                }}
              >
                {/* Küçük kartlar istatistik ızgarasının SON HÜCRESİNDE durur.
                    Ayrı satırdayken 220px'lik kart satırın kalanını boş
                    bırakıyor, sayfa boşuna uzuyordu. Admin'de iki mini kart
                    aynı hücreyi paylaşır ki hücre sayısı yine 6 kalsın. */}
                <div className={`grid gap-4 ${isAdmin ? "grid-cols-2" : "grid-cols-1"}`}>
                  <ActiveDelegationsCard
                    activeSubstitutions={allActiveSubstitutions}
                    title="🔄 Aktif Vekaletler"
                    className="h-full flex flex-col justify-center"
                    personnel={personnel}
                    regions={regions}
                    parks={parks}
                  />
                  {isAdmin && (
                    <BlockedLoginDashboardCard
                      blockedPersonnel={loginBlockedPersonnel}
                      onUnblock={handleUnblockPersonnelLogin}
                      onEditPerson={handlePersonnelEdit}
                      unblockLoginLoadingId={unblockLoginLoadingId}
                      className="h-full flex flex-col justify-center"
                    />
                  )}
                </div>
              </DashboardStatisticsCards>

              <InventorySummaryCards 
                stats={stats}
                filteredParks={stats._myFilteredParks}
                showInventoryDetail={(title, field) => showInventoryDetail(title, field, stats._myFilteredParks)}
                showFacilityDetail={(title, field) => showFacilityDetail(title, field, stats._myFilteredParks)}
                showIrrigationDetail={(title, type) => showIrrigationDetail(title, type, stats._myFilteredParks)}
                showRunningTrackDetail={() => showRunningTrackDetail(stats._myFilteredParks)}
                showPlaygroundDetail={() => showPlaygroundDetail(stats._myFilteredParks)}
                showSportsEquipmentDetail={() => showSportsEquipmentDetail(stats._myFilteredParks)}
                showFountainDetail={() => showFountainDetail(stats._myFilteredParks)}
                onUnassignedParkClick={() => {
                  // Park görevlisi olmayan parkları bul
                  const parksList = stats.unassignedParksList || [];
                  
                  if (parksList.length === 0) return;
                  
                  if (isMudur || isMudurYardimcisi) {
                    // Müdür/Yardımcı: Atama yapabilir
                    if (parksList.length === 1) {
                      // Tek park varsa direkt aç
                      setActiveTab("parks");
                      setTimeout(() => {
                        handleParkEdit(parksList[0]);
                      }, 100);
                    } else {
                      // Birden fazla park varsa seçim dialogu aç
                      setUnassignedParksList(parksList);
                      setUnassignedParksDialogOpen(true);
                    }
                  } else {
                    // Bölge Mühendisi/Formen: Sadece görüntüleme
                    if (parksList.length === 1) {
                      setSearchTerm(parksList[0].name);
                      setActiveTab("parks");
                    } else {
                      // Birden fazla park varsa seçim dialogu aç (görüntüleme modunda)
                      setUnassignedParksList(parksList);
                      setUnassignedParksDialogOpen(true);
                    }
                  }
                }}
              />
            </TabsContent>
            );
          })()}

          {/* Parks Tab - Refactored WITH ENVANTER */}
          {activeTab === "parks" && (
            <ParksTab
              userRole={userRole}
              personnelId={personnelId}
              parks={parks}
              personnel={personnel}
              regions={regions}
              loadingParks={loadingParks}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterPersonnel={filterPersonnel}
              setFilterPersonnel={setFilterPersonnel}
              filterRegion={filterRegion}
              setFilterRegion={setFilterRegion}
              filterRole={filterRole}
              setFilterRole={setFilterRole}
              parksViewMode={parksViewMode}
              setParksViewMode={setParksViewMode}
              parksSortOrder={parksSortOrder}
              setParksSortOrder={setParksSortOrder}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              handleParkEdit={handleParkEdit}
              handleParkDelete={handleParkDelete}
              setIsParkDialogOpen={setIsParkDialogOpen}
              getAllSubordinates={getAllSubordinates}
              getRoleBadge={getRoleBadge}
              displayedParks={displayedParks}
              parkCards={parkCards}
              parkCardsTotal={parkCardsTotal}
              parkCardsHasMore={parkCardsHasMore}
              parkCardsLoading={parkCardsLoading}
              parkCardsLoadingMore={parkCardsLoadingMore}
              loadMoreParkCards={loadMoreParkCards}
              fetchParkDetail={fetchParkDetail}
              parkAggregates={parkAggregates}
              parkBoundaries={parkBoundaries}
              highlightedParkId={highlightedParkId}
              isParkDialogOpen={isParkDialogOpen}
              parkForm={parkForm}
              setParkForm={setParkForm}
              isEditingPark={isEditingPark}
              isFormReadOnly={isFormReadOnly}
              setIsFormReadOnly={setIsFormReadOnly}
              resetParkForm={resetParkForm}
              handleParkSubmit={handleParkSubmit}
              isGeocodingAddress={isGeocodingAddress}
              setIsGeocodingAddress={setIsGeocodingAddress}
              showMap={showMap}
              setShowMap={setShowMap}
              drawingMode={drawingMode}
              setDrawingMode={setDrawingMode}
              drawnBoundaries={drawnBoundaries}
              setDrawnBoundaries={setDrawnBoundaries}
              setQuickAddContext={setQuickAddContext}
              setIsQuickRegionDialogOpen={setIsQuickRegionDialogOpen}
              setParkToDelete={setParkToDelete}
              API={API}
              loading={loading}
              isParkSaving={isParkSaving}
              revisions={revisions}
              onDeleteRevision={handleDeleteCompletedRevision}
              onDeleteCompletedRevisionHistory={handleDeleteCompletedRevisionHistory}
              activeSubstitutions={allActiveSubstitutions}
              quickParkFilter={isParkGorevlisi ? parkQuickFilter : null}
            />
          )}

          {/* Passive Areas Tab */}
          {activeTab === "passive-areas" && (
            <PassiveAreasTab
              userRole={userRole}
              personnelId={personnelId}
              personnel={personnel}
              regions={regions}
              API={API}
            />
          )}

          {activeTab === "protokol-parks" && (
            <ProtokolParklarTab
              userRole={userRole}
              personnelId={personnelId}
              personnel={personnel}
              regions={regions}
              API={API}
              showInventoryDetail={showInventoryDetail}
              facilityDetailHandlers={{
                has_running_track: showRunningTrackDetail,
                has_playground: showPlaygroundDetail,
                has_sports_equipment: showSportsEquipmentDetail,
                has_fountain: showFountainDetail,
              }}
              showFacilityDetail={showFacilityDetail}
              showIrrigationDetail={showIrrigationDetail}
            />
          )}

          {/* Operasyon Emirleri Tab */}
          {activeTab === "operations" && (
            <OperasyonTab
              API={API}
              personnelId={personnelId}
              personnel={personnel}
              parks={parks}
            />
          )}

          {/* OBT — Operasyonel Bilgi Toplama Tab
              Yetki kapısı bileşenin KENDİ içindedir (ObtAccessGuard); burada
              rol koşulu tekrarlanmaz, tek doğruluk kaynağı korunur. */}
          {activeTab === "obt" && <ObtTab />}

          {/* Activity Management Tab */}
          {activeTab === "activity-management" && (
            <ActivityManagementTab
              userRole={userRole}
              personnelId={personnelId}
              personnelName={personnelName}
              parks={parks}
              personnel={personnel}
              API={API}
            />
          )}

          {/* Daily Maintenance Tab */}
          {activeTab === "daily-maintenance" && (
            <DailyMaintenanceTab
              userRole={userRole}
              personnelId={personnelId}
              personnelName={personnelName}
              parks={parks}
              personnel={personnel}
              regions={regions}
              userRegionIds={personnel.find(p => p.id === personnelId)?.region_ids || []}
              API={API}
              activeSubstitutions={allActiveSubstitutions}
              isOnLeave={Boolean((mySubstitutions.on_leave || []).some(isLeaveRowActiveToday))}
              onLeaveSubstitutions={(mySubstitutions.on_leave || []).filter(isLeaveRowActiveToday)}
              onRefreshData={refreshData}
              onNavigateToPark={(parkId) => {
                const park = parks.find(p => p.id === parkId);
                clearPgQuickParkFilter();
                setHighlightedParkId(parkId);
                setActiveTab("parks");
                // 500ms sonra scroll yap (sekme değişimi için bekle)
                setTimeout(() => {
                  const parkElement = document.getElementById(`park-card-${parkId}`);
                  if (parkElement) {
                    parkElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Vurgulama animasyonu için
                    parkElement.classList.add('ring-2', 'ring-green-500', 'ring-offset-2');
                    setTimeout(() => {
                      parkElement.classList.remove('ring-2', 'ring-green-500', 'ring-offset-2');
                      setHighlightedParkId(null);
                    }, 5000); // 5 saniye vurgulu kalsın
                  }
                }, 500);
                if (park) {
                  toast.success(`"${park.name}" parkına yönlendiriliyorsunuz. Koordinat eklemek için düzenle butonuna tıklayın.`);
                }
              }}
            />
          )}

          {/* Task Tracking Tab - Görev Takip */}
          {isManagerView && activeTab === "task-tracking" && (
            <TabsContent value="task-tracking" className="space-y-6">
              <TaskTrackingTab
                userRole={userRole}
                personnelId={personnelId}
                parks={parks}
                personnel={personnel}
                regions={regions}
                onPersonnelClick={(clickData) => {
                  const person = personnel.find(p => p.id === clickData.personnelId);
                  if (person) {
                    setPersonnelFormInitialTab("tasks"); // Görevler sekmesinde aç
                    // Görev takip filtrelerini aktar
                    setPersonnelTaskFilters({
                      startDate: clickData.startDate,
                      endDate: clickData.endDate,
                      parkId: clickData.parkId,
                      parkName: clickData.parkName
                    });
                    handlePersonnelEdit(person);
                  }
                }}
              />
            </TabsContent>
          )}

          {/* Personnel Tab */}
          {activeTab === "personnel" && (
          <TabsContent value="personnel" className="space-y-6">
            {/* İstatistik Kartları - Yönetici, Müdür Yardımcısı, Bölge Mühendisi, Formen İçin */}
            {isManagerView && (() => {
              let filteredPersonnel = personnel;
              if (!isMudur && !isAdmin && !isGolgeAdmin) {
                // Kartlar MY/BM/Formen için tüm ast hiyerarşiyi kapsasın (özel roller dahil)
                filteredPersonnel = getAllSubordinates(personnelId, userRole, personnel);
              }
              
              return (
                <PersonnelStatisticsCards
                  personnel={filteredPersonnel}
                  allPersonnel={personnel}
                  defaultTeamId={getTeamBySlugForPersonnel('park_gorevlisi')?.id || null}
                  teamCards={nonDefaultActiveTeams}
                  userRole={userRole}
                  personnelId={personnelId}
                  allActiveSubstitutions={allActiveSubstitutions}
                  regions={regions}
                  parks={parks}
                  leaveRequests={leaveRequests}
                  loginBlockedPersonnel={loginBlockedPersonnel}
                  onUnblockPersonnelLogin={handleUnblockPersonnelLogin}
                  onEditPersonnel={handlePersonnelEdit}
                  unblockLoginLoadingId={unblockLoginLoadingId}
                  onFilterByRole={(role) => {
                    setPersonnelDisplayType(role);
                    // Karttan filtrelenirken ek filtreler sonucu daraltmasin.
                    clearPersonnelSearch();
                    setPersonnelFilterRegion("all");
                    setPersonnelFilterMY("all");
                    setPersonnelFilterBM("all");
                    setPersonnelFilterFormen("all");
                  }}
                />
              );
            })()}

            {/* İzin Taleplerim - Türüne Göre Kategorize Edilmiş Kartlar */}
            {isParkGorevlisi && (() => {
              const myLeaveRequests = leaveRequests.filter(lr => lr.personnel_id === personnelId);
              
              // İzin türlerine göre grupla
              const leaveTypes = [
                { 
                  type: "Yıllık İzin", 
                  color: "blue", 
                  icon: "🏖️",
                  bgGradient: "from-blue-50 to-blue-100",
                  borderColor: "border-blue-300",
                  textColor: "text-blue-900"
                },
                { 
                  type: "Sosyal İzin", 
                  color: "purple", 
                  icon: "👨‍👩‍👧‍👦",
                  bgGradient: "from-purple-50 to-purple-100",
                  borderColor: "border-purple-300",
                  textColor: "text-purple-900"
                },
                { 
                  type: "Rapor", 
                  color: "red", 
                  icon: "🏥",
                  bgGradient: "from-red-50 to-red-100",
                  borderColor: "border-red-300",
                  textColor: "text-red-900"
                },
                { 
                  type: "Diğer", 
                  color: "gray", 
                  icon: "📋",
                  bgGradient: "from-gray-50 to-gray-100",
                  borderColor: "border-gray-300",
                  textColor: "text-gray-900"
                }
              ];

              return (
                <div className="space-y-4">
                  {/* Başlık */}
                  <div className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-lg shadow-lg">
                    <Calendar className="h-6 w-6" />
                    <h2 className="text-xl font-bold">İzin Taleplerim</h2>
                    <Badge variant="outline" className="ml-auto bg-white text-blue-900">
                      {myLeaveRequests.length} Toplam
                    </Badge>
                  </div>

                  {/* İzin Türlerine Göre Kartlar */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {leaveTypes.map((leaveType) => {
                      const typeRequests = myLeaveRequests.filter(lr => lr.reason_type === leaveType.type);
                      
                      return (
                        <Card key={leaveType.type} className={`bg-gradient-to-br ${leaveType.bgGradient} border-2 ${leaveType.borderColor}`}>
                          <CardHeader>
                            <CardTitle className={`flex items-center gap-2 ${leaveType.textColor}`}>
                              <span className="text-2xl">{leaveType.icon}</span>
                              <span>{leaveType.type}</span>
                              <Badge variant="secondary" className="ml-auto">
                                {typeRequests.length}
                              </Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {typeRequests.length === 0 ? (
                              <div className="text-center py-6 text-gray-500">
                                <Clock className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                                <p className="text-sm">Bu türde izin talebiniz yok</p>
                              </div>
                            ) : (
                              <div className="space-y-3 max-h-96 overflow-y-auto">
                                {typeRequests
                                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                  .map(request => (
                                    <LeaveRequestCard
                                      key={request.id}
                                      request={request}
                                      personnelId={personnelId}
                                      userRole={userRole}
                                      filterApprovalHistory={filterApprovalHistory}
                                      showPersonnelName={false}
                                      showCancelButton={true}
                                      showActionButtons={false}
                                      onUpdate={async () => {
                                        await fetchLeaveRequests();
                                        await fetchPersonnel({ force: true, silent: true });
                                        await fetchAllActiveSubstitutions();
                                      }}
                                      personnel={personnel}
                                      regions={regions}
                                    />
                                  ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* İzin Talep Et Butonu - Sadece Park Görevlisi ve Çim Ekibi İçin */}
            {/* Yöneticiler için İzin Yönetimi sekmesinde buton var */}
            {isParkGorevlisi && (
              <div className="flex justify-end">
                <Button 
                  onClick={() => {
                    if (isDelegatorReadOnly && !isGolgeAdmin) {
                      toast.warning(READ_ONLY_MESSAGE);
                      return;
                    }
                    if (isGolgeAdmin) return;
                    setIsLeaveRequestDialogOpen(true);
                  }}
                  disabled={isDelegatorReadOnly}
                  className="flex items-center gap-2"
                >
                  <Calendar className="h-4 w-4" />
                  İzin Talep Et
                </Button>
              </div>
            )}

            {/* Arama, Filtreleme ve Görünüm Kontrolü - Yönetici, Müdür Yardımcısı, Bölge Mühendisi, Formen İçin */}
            {isManagerView && (<>
              <div className="flex flex-col gap-4 bg-white p-4 rounded-lg shadow">
              {/* Arama ve Sıralama/Görünüm */}
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="relative flex-1 w-full sm:w-auto">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    ref={personnelSearchInputRef}
                    placeholder="Personel ara..."
                    defaultValue={personnelSearchTerm}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      if (personnelSearchDebounceRef.current) {
                        clearTimeout(personnelSearchDebounceRef.current);
                      }
                      personnelSearchDebounceRef.current = setTimeout(() => {
                        setPersonnelSearchTerm(nextValue);
                      }, 180);
                    }}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  {/* Yeni Personel Ekle Butonu — personel.olustur yetkisi gerekir */}
                  {canCreatePersonnel && (
                    <Button 
                      onClick={() => {
                        resetPersonnelForm();
                        setIsPersonnelDialogOpen(true);
                      }}
                      className="flex items-center gap-2 flex-1 sm:flex-none"
                    >
                      <Plus className="h-4 w-4" />
                      Yeni Personel Ekle
                    </Button>
                  )}
                  
                  {/* Sıralama Dropdown Menü */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant={personnelSortOrder !== "default" ? "default" : "outline"}
                        className="flex-1 sm:flex-none"
                      >
                        <ArrowUpDown className="h-4 w-4 mr-2" />
                        {personnelSortOrder === "alphabetical" 
                          ? "A-Z" 
                          : personnelSortOrder === "hierarchy" 
                          ? "🔷 Hiyerarşi" 
                          : "Sırala"}
                        <ChevronDown className="h-4 w-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setPersonnelSortOrder("default")}>
                        <span className={personnelSortOrder === "default" ? "font-bold" : ""}>
                          Varsayılan
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setPersonnelSortOrder("alphabetical")}>
                        <span className={personnelSortOrder === "alphabetical" ? "font-bold" : ""}>
                          A-Z (Alfabetik)
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setPersonnelSortOrder("hierarchy")}>
                        <span className={personnelSortOrder === "hierarchy" ? "font-bold" : ""}>
                          🔷 Hiyerarşi
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {/* Görünüm Değiştirme Butonları */}
                  <div className="flex gap-1 border rounded-md p-1">
                    <Button
                      variant={personnelViewMode === "cards" ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setPersonnelViewMode("cards")}
                      className="px-3"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={personnelViewMode === "table" ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setPersonnelViewMode("table")}
                      className="px-3"
                    >
                      <List className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Filtreler */}
              <div className="flex flex-wrap gap-3">
                {/* Gösterim Türü */}
                <SearchableSelect
                  value={personnelDisplayType}
                  onChange={setPersonnelDisplayType}
                  allLabel="Tümü"
                  searchPlaceholder="Gösterim türü ara..."
                  className="w-full sm:w-[200px]"
                  options={[
                    { id: "mudur_yardimcisi", name: "Sadece Müdür Yardımcısı" },
                    { id: "bolge_muhendisi", name: "Sadece Bölge Mühendisi" },
                    { id: "formen", name: "Sadece Formen" },
                    { id: "park_gorevlisi", name: "Sadece Park Görevlisi" },
                    { id: "ozel_rol", name: "Sadece Özel Rol" },
                    // Dinamik ekipler (Boya/Budama/Elektrik/Sulama...) — üyesi
                    // 0'dan büyük olanlar, istatistik kartlarıyla aynı mantıkla
                    // filtre listesine de eklenir.
                    ...nonDefaultActiveTeams
                      .map((team) => ({
                        team,
                        count: personnel.filter(
                          (p) => String(p.primary_team_id || "") === String(team.id)
                        ).length,
                      }))
                      .filter((x) => x.count > 0)
                      .map(({ team }) => ({
                        id: `team:${team.id}`,
                        name: team.display_name,
                      })),
                  ]}
                />

                {/* Bölge Filtresi */}
                <SearchableSelect
                  value={personnelFilterRegion}
                  onChange={setPersonnelFilterRegion}
                  allLabel="Tüm Bölgeler"
                  searchPlaceholder="Bölge ara..."
                  className="w-full sm:w-[200px]"
                  options={[
                    { id: "none", name: "Bölge Atanmamış" },
                    ...regions
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name, "tr"))
                      .map((region) => ({ id: region.id, name: region.name })),
                  ]}
                />

                {/* Müdür Yardımcısı Filtresi */}
                <SearchableSelect
                  value={personnelFilterMY}
                  onChange={(value) => {
                    setPersonnelFilterMY(value);
                    setPersonnelFilterBM("all");
                    setPersonnelFilterFormen("all");
                  }}
                  allLabel="Tüm MY"
                  searchPlaceholder="Müdür yardımcısı ara..."
                  className="w-full sm:w-[200px]"
                  options={myFilterOptions.map((p) => ({ id: p.id, name: p.name }))}
                />

                {/* Bölge Mühendisi Filtresi */}
                <SearchableSelect
                  value={personnelFilterBM}
                  onChange={(value) => {
                    setPersonnelFilterBM(value);
                    setPersonnelFilterFormen("all");
                  }}
                  allLabel="Tüm BM"
                  searchPlaceholder="Bölge mühendisi ara..."
                  className="w-full sm:w-[220px]"
                  options={bmFilterOptions.map((p) => ({ id: p.id, name: p.name }))}
                />

                {/* Formen Filtresi */}
                <SearchableSelect
                  value={personnelFilterFormen}
                  onChange={setPersonnelFilterFormen}
                  allLabel="Tüm Formen"
                  searchPlaceholder="Formen ara..."
                  className="w-full sm:w-[220px]"
                  options={formenFilterOptions.map((p) => ({ id: p.id, name: p.name }))}
                />

                {/* Durum Filtresi (Aktif / Pasif / Tümü) */}
                <SearchableSelect
                  value={personnelStatusFilter}
                  onChange={setPersonnelStatusFilter}
                  showAllOption={false}
                  searchPlaceholder="Durum ara..."
                  className="w-full sm:w-[160px]"
                  options={[
                    { id: "active", name: "Aktif" },
                    { id: "passive", name: "Pasif" },
                    { id: "all", name: "Tüm Durumlar" },
                  ]}
                />

                {/* Filtreleri Temizle */}
                {(personnelDisplayType !== "all" ||
                  personnelFilterRegion !== "all" ||
                  personnelFilterMY !== "all" ||
                  personnelFilterBM !== "all" ||
                  personnelFilterFormen !== "all" ||
                  personnelStatusFilter !== "active" ||
                  !!personnelSearchTerm.trim()) && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setPersonnelDisplayType("all");
                      setPersonnelFilterRegion("all");
                      setPersonnelFilterMY("all");
                      setPersonnelFilterBM("all");
                      setPersonnelFilterFormen("all");
                      setPersonnelStatusFilter("active");
                      clearPersonnelSearch();
                    }}
                    className="text-sm"
                  >
                    Filtreleri Temizle
                  </Button>
                )}
              </div>
            </div>

              {/* Personel Listesi - Kart veya Tablo Görünümü */}
              {(() => {
                const managerScopedPersonnelIds = (() => {
                  // Müdür, süper admin ve Başkan: tüm personel (backend listesi); hiyerarşi daraltması yok
                  if (!isManagerView || !personnelId || isMudur || isAdmin || isGolgeAdmin) {
                    return null;
                  }
                  const scoped = getAllSubordinates(personnelId, userRole, personnel);
                  return new Set(scoped.map((p) => p.id));
                })();

                // Personel filtreleme
                const filteredPersonnel = personnel.filter(person => {
                // Yonetici gorunumunde listeyi kartlarla ayni hiyerarsi kapsaminda tut.
                if (managerScopedPersonnelIds && !managerScopedPersonnelIds.has(person.id)) return false;
                
                // Arama filtresi
                const searchLower = normalizeSearchText(personnelSearchTerm);
                const matchesSearch = !searchLower ||
                  normalizeSearchText(person.name).includes(searchLower) ||
                  (person.custom_role_name && normalizeSearchText(person.custom_role_name).includes(searchLower));

                // Gösterim türü filtresi — "Özel Rol": müdürün atadığı, açıklamalı özel rol
                // (custom_role_name eski import etiketleriyle de dolu; gerçek özel roller açıklama içerir).
                const matchesDisplayType =
                  personnelDisplayType === "all"
                    ? true
                    : personnelDisplayType === "ozel_rol"
                      ? Boolean(
                          person.custom_role_name && String(person.custom_role_name).trim() &&
                          person.custom_role_description && String(person.custom_role_description).trim()
                        )
                      : personnelDisplayType.startsWith("team:")
                        ? String(person.primary_team_id || "") === personnelDisplayType.slice(5)
                        : personnelDisplayType === "park_gorevlisi"
                          ? isPureParkGorevlisi(person)
                          : person.role === personnelDisplayType;
                
                // Bölge filtresi
                const matchesRegion = personnelFilterRegion === "all" || (() => {
                  // 1. Bölge Mühendisi kontrolü - region_ids'ine veya assigned_bolge_muhendisi_id'sine bak
                  if (person.role === 'bolge_muhendisi') {
                    if (personnelFilterRegion === "none") {
                      // Hiçbir bölgeye atanmamış mı?
                      const isAssignedToAnyRegion = regions.some(r => r.assigned_bolge_muhendisi_id === person.id);
                      const hasRegionIds = person.region_ids && person.region_ids.length > 0;
                      const hasOldRegionId = person.region_id;
                      return !hasRegionIds && !hasOldRegionId && !isAssignedToAnyRegion;
                    }
                    
                    // Kendi region_ids array'inde bu bölge var mı?
                    if (person.region_ids && person.region_ids.includes(personnelFilterRegion)) return true;
                    // Eski region_id sistemini de kontrol et (backward compatibility)
                    if (person.region_id === personnelFilterRegion) return true;
                    // Bu bölgenin assigned_bolge_muhendisi mi?
                    const region = regions.find(r => r.id === personnelFilterRegion);
                    return region && region.assigned_bolge_muhendisi_id === person.id;
                  }
                  
                  // 2. Personnel'in YENİ region_ids array'i varsa (ÇOKLU BÖLGE DESTEĞİ)
                  if (person.region_ids && person.region_ids.length > 0) {
                    if (personnelFilterRegion === "none") return false;
                    return person.region_ids.includes(personnelFilterRegion);
                  }
                  
                  // 3. ESKİ region_id sistemi (backward compatibility)
                  if (person.region_id) {
                    if (personnelFilterRegion === "none") return false;
                    return person.region_id === personnelFilterRegion;
                  }
                  
                  // 4. Atandığı parkların bölgelerine bak (ÖNCELIK: Gerçek park atamaları)
                  const assignedParks = parks.filter(p => 
                    p.assigned_personnel_ids && p.assigned_personnel_ids.includes(person.id)
                  );
                  
                  if (personnelFilterRegion === "none") {
                    // Bölge atanmamış: Hem kendisinin region_ids yok, hem de tüm parkları bölgesiz
                    const hasRegionIds = person.region_ids && person.region_ids.length > 0;
                    return !hasRegionIds && !person.region_id && assignedParks.every(p => !p.region_id);
                  }
                  
                  // Seçili bölgedeki parklara atanmış mı?
                  const hasParksInRegion = assignedParks.some(p => p.region_id === personnelFilterRegion);
                  if (hasParksInRegion) return true;
                  
                  // 4. Hiyerarşik kontrol - SADECE park ataması yoksa (Formen gibi)
                  // Eğer bu personel hiç parka atanmamışsa, üst yöneticisinin bölgesine bak
                  if (assignedParks.length === 0 && person.parent_id) {
                    const parentPerson = personnel.find(p => p.id === person.parent_id);
                    if (parentPerson) {
                      // Parent Bölge Mühendisi mi ve o bölgeye atanmış mı?
                      if (parentPerson.role === 'bolge_muhendisi') {
                        const region = regions.find(r => r.id === personnelFilterRegion);
                        if (region && region.assigned_bolge_muhendisi_id === parentPerson.id) {
                          return true;
                        }
                      }
                      // Parent'ın parent'ını da kontrol et
                      if (parentPerson.parent_id) {
                        const grandParent = personnel.find(p => p.id === parentPerson.parent_id);
                        if (grandParent && grandParent.role === 'bolge_muhendisi') {
                          const region = regions.find(r => r.id === personnelFilterRegion);
                          if (region && region.assigned_bolge_muhendisi_id === grandParent.id) {
                            return true;
                          }
                        }
                      }
                    }
                  }
                  
                  return false;
                })();
                
                // Hiyerarşi filtresi (MY -> BM -> Formen)
                const { myId, bmId, formenId } = resolveHierarchyIds(person);
                const matchesHierarchy =
                  (personnelFilterMY === "all" || myId === personnelFilterMY) &&
                  (personnelFilterBM === "all" || bmId === personnelFilterBM) &&
                  (personnelFilterFormen === "all" || formenId === personnelFilterFormen);

                // Durum filtresi (pasif/ayrılmış personel varsayılanda gizli)
                const isPassivePerson = ['passive', 'inactive', 'ayrildi', 'deleted']
                  .includes(String(person.status || '').toLowerCase());
                const matchesStatus =
                  personnelStatusFilter === "all" ||
                  (personnelStatusFilter === "passive" ? isPassivePerson : !isPassivePerson);

                return matchesSearch && matchesDisplayType && matchesRegion && matchesHierarchy && matchesStatus;
              });

              // Sıralama
              let sortedPersonnel = filteredPersonnel;
              
              // Kullanıcı A-Z seçtiyse her zaman sadece alfabetik sırala (rol grubu gösterme)
              if (personnelSortOrder === "alphabetical") {
                sortedPersonnel = [...filteredPersonnel].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
              } else if (personnelSortOrder === "hierarchy") {
                // Hiyerarşik sıralama: Rol önceliğine göre
                const roleOrder = {
                  'mudur': 1,
                  'mudur_yardimcisi': 2,
                  'bolge_muhendisi': 3,
                  'formen': 4,
                  'park_gorevlisi': 5,
                };
                sortedPersonnel = [...filteredPersonnel].sort((a, b) => {
                  const aOrder = roleOrder[a.role] || 999;
                  const bOrder = roleOrder[b.role] || 999;
                  if (aOrder !== bOrder) return aOrder - bOrder;
                  return (a.name || '').localeCompare(b.name || '', 'tr');
                });
              } else if (personnelDisplayType === "park_gorevlisi") {
                // Önce formenleri bul ve alfabetik sırala
                const formenMap = new Map();
                filteredPersonnel.forEach(p => {
                  if (p.formen_id) {
                    const formen = personnel.find(f => f.id === p.formen_id);
                    if (formen) {
                      if (!formenMap.has(p.formen_id)) {
                        formenMap.set(p.formen_id, {
                          formenName: formen.name,
                          formenId: p.formen_id,
                          members: []
                        });
                      }
                      formenMap.get(p.formen_id).members.push(p);
                    }
                  }
                });
                
                // Formenleri alfabetik sırala
                const sortedGroups = Array.from(formenMap.values())
                  .sort((a, b) => a.formenName.localeCompare(b.formenName, 'tr'));
                
                // Her grubun içindeki üyeleri alfabetik sırala ve birleştir
                sortedPersonnel = [];
                sortedGroups.forEach(group => {
                  const sortedMembers = group.members.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
                  sortedPersonnel.push(...sortedMembers);
                });
                
                // Formeni olmayan personelleri en sona ekle (alfabetik)
                const noFormen = filteredPersonnel.filter(p => !p.formen_id)
                  .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
                sortedPersonnel.push(...noFormen);
              } else if (personnelDisplayType === "formen") {
                // Formen filtrelendiğinde bölge mühendisine göre grupla
                const muhendisMap = new Map();
                filteredPersonnel.forEach(p => {
                  if (p.bolge_muhendisi_id) {
                    const muhendis = personnel.find(m => m.id === p.bolge_muhendisi_id);
                    if (muhendis) {
                      if (!muhendisMap.has(p.bolge_muhendisi_id)) {
                        muhendisMap.set(p.bolge_muhendisi_id, {
                          muhendisName: muhendis.name,
                          muhendisId: p.bolge_muhendisi_id,
                          members: []
                        });
                      }
                      muhendisMap.get(p.bolge_muhendisi_id).members.push(p);
                    }
                  }
                });
                
                // Bölge mühendislerini alfabetik sırala
                const sortedGroups = Array.from(muhendisMap.values())
                  .sort((a, b) => a.muhendisName.localeCompare(b.muhendisName, 'tr'));
                
                // Her grubun içindeki formenleri alfabetik sırala ve birleştir
                sortedPersonnel = [];
                sortedGroups.forEach(group => {
                  const sortedMembers = group.members.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
                  sortedPersonnel.push(...sortedMembers);
                });
                
                // Bölge mühendisi olmayan formenleri en sona ekle
                const noMuhendis = filteredPersonnel.filter(p => !p.bolge_muhendisi_id)
                  .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
                sortedPersonnel.push(...noMuhendis);
              } else if (personnelDisplayType === "bolge_muhendisi") {
                // Bölge mühendisi filtrelendiğinde müdür yardımcısına göre grupla
                const yardimciMap = new Map();
                filteredPersonnel.forEach(p => {
                  if (p.mudur_yardimcisi_id) {
                    const yardimci = personnel.find(m => m.id === p.mudur_yardimcisi_id);
                    if (yardimci) {
                      if (!yardimciMap.has(p.mudur_yardimcisi_id)) {
                        yardimciMap.set(p.mudur_yardimcisi_id, {
                          yardimciName: yardimci.name,
                          yardimciId: p.mudur_yardimcisi_id,
                          members: []
                        });
                      }
                      yardimciMap.get(p.mudur_yardimcisi_id).members.push(p);
                    }
                  }
                });
                
                // Müdür yardımcılarını alfabetik sırala
                const sortedGroups = Array.from(yardimciMap.values())
                  .sort((a, b) => a.yardimciName.localeCompare(b.yardimciName, 'tr'));
                
                // Her grubun içindeki mühendisleri alfabetik sırala ve birleştir
                sortedPersonnel = [];
                sortedGroups.forEach(group => {
                  const sortedMembers = group.members.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
                  sortedPersonnel.push(...sortedMembers);
                });
                
                // Müdür yardımcısı olmayan mühendisleri en sona ekle
                const noYardimci = filteredPersonnel.filter(p => !p.mudur_yardimcisi_id)
                  .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
                sortedPersonnel.push(...noYardimci);
              } else {
                // Varsayılan: Alfabetik sıralama (Sırala = varsayılan)
                sortedPersonnel = [...filteredPersonnel].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
              }

              const hasActivePersonnelFilters =
                !!personnelSearchTerm?.trim() ||
                personnelDisplayType !== "all" ||
                personnelFilterRegion !== "all" ||
                personnelFilterMY !== "all" ||
                personnelFilterBM !== "all" ||
                personnelFilterFormen !== "all";

              // Koruma: veri var ama beklenmedik filtre akışında liste tamamen boş kalırsa
              // kullanıcıya hiç personel yokmuş gibi görünmesin.
              if (
                sortedPersonnel.length === 0 &&
                Array.isArray(personnel) &&
                personnel.length > 0 &&
                !hasActivePersonnelFilters
              ) {
                sortedPersonnel = [...personnel].sort((a, b) => (a?.name || '').localeCompare(b?.name || '', 'tr'));
              }

              if (loadingPersonnel) {
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                      <Card key={i} className="animate-pulse">
                        <CardHeader>
                          <div className="h-6 bg-gray-200 rounded w-2/3 mb-3"></div>
                          <div className="flex gap-2">
                            <div className="h-5 bg-gray-100 rounded w-20"></div>
                            <div className="h-5 bg-gray-100 rounded w-16"></div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div className="h-4 bg-gray-100 rounded"></div>
                            <div className="h-4 bg-gray-100 rounded w-5/6"></div>
                            <div className="h-4 bg-gray-100 rounded w-4/6"></div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                );
              }

              if (sortedPersonnel.length === 0) {
                return (
                  <div className="text-center py-16 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-dashed border-purple-200">
                    <Users className="h-20 w-20 text-purple-400 mx-auto mb-4 animate-pulse" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      {personnel.length === 0 ? "Henüz Personel Yok" : "Sonuç Bulunamadı"}
                    </h3>
                    <p className="text-gray-500 mb-4">
                      {personnel.length === 0 ? "Sisteme henüz personel eklenmemiş" : "Filtreye uygun personel bulunamadı"}
                    </p>
                    {personnel.length === 0 && (
                      <Button onClick={() => setIsPersonnelDialogOpen(true)} className="mt-2">
                        <Plus className="h-4 w-4 mr-2" />
                        İlk Personeli Ekle
                      </Button>
                    )}
                  </div>
                );
              }

              return personnelViewMode === "cards" ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {sortedPersonnel.map(person => {
                    const assignedParks = parks.filter(p => p.assigned_personnel_ids && p.assigned_personnel_ids.includes(person.id));
                    // Rozet sayımı: formen için cache (kirli/tutarsız) yerine sorumlu
                    // olduğu bölgelerdeki park sayısı — aşağıdaki "Toplam" ile birebir aynı.
                    const displayParkCount = person.role === 'formen'
                      ? getFormenRegionParks(person, parks, personnel).length
                      : assignedParks.length;

                    const isPassivePerson = ['passive', 'inactive', 'ayrildi', 'deleted']
                      .includes(String(person.status || '').toLowerCase());

                    return (
                      <Card key={person.id} className={`hover:shadow-lg transition-shadow ${isPassivePerson ? 'opacity-70 grayscale' : ''}`} data-testid={`personnel-card-${person.id}`}>
                        <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-lg mb-1">{person.name}</CardTitle>
                              <CardDescription className="flex items-center gap-2 flex-wrap">
                                {isPassivePerson && (
                                  <Badge className="text-xs font-semibold bg-gray-500 text-white border-gray-600">
                                    Pasif
                                  </Badge>
                                )}
                                <Badge className={`text-xs font-semibold ${getRoleBadge(person.role).className}`}>
                                  {getRoleBadge(person.role).label}
                                </Badge>
                                {getNonDefaultTeamLabel(person.primary_team_id) && (
                                  <Badge className="text-xs font-semibold bg-teal-500 text-white border-teal-600">
                                    {getNonDefaultTeamLabel(person.primary_team_id)}
                                  </Badge>
                                )}
                                {person.custom_role_name && String(person.custom_role_name).trim() &&
                                 person.custom_role_description && String(person.custom_role_description).trim() && (
                                  <Badge className="text-xs font-semibold bg-cyan-500 text-white border-cyan-600">
                                    Özel Rol: {person.custom_role_name}
                                  </Badge>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  <TreePine className="h-3 w-3 mr-1" />
                                  {displayParkCount} Park
                                </Badge>
                                {person.status === 'on_leave' && (
                                  <Badge variant="outline" className="text-xs bg-orange-50 border-orange-300 text-orange-700">
                                    İzinli
                                  </Badge>
                                )}
                                {person.status === 'on_report' && (
                                  <Badge variant="outline" className="text-xs bg-red-50 border-red-300 text-red-700">
                                    Raporlu
                                  </Badge>
                                )}
                                {/* Vekalet Bilgisi - Bu kişi başkasının yerine bakıyorsa */}
                                {(() => {
                                  const personSubstitutions = allActiveSubstitutions.filter(sub => sub.substitute_personnel_id === person.id);
                                  if (personSubstitutions.length > 0) {
                                    return (
                                      <Badge 
                                        variant="outline" 
                                        className="text-xs bg-blue-50 border-blue-300 text-blue-700 cursor-pointer hover:bg-blue-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedSubstitution(personSubstitutions[0]);
                                          setVekaletDialogOpen(true);
                                        }}
                                      >
                                        🔄 Vekalet Var ({personSubstitutions.length})
                                      </Badge>
                                    );
                                  }
                                  return null;
                                })()}
                                {/* İzinde bekleyen talepler badge'leri */}
                                {(() => {
                                  const pendingLeaves = leaveRequests.filter(lr => lr.personnel_id === person.id && lr.status === 'pending').length;
                                  const pendingMaterials = materialRequests.filter(mr => mr.personnel_id === person.id && mr.status === 'pending').length;
                                  return (
                                    <>
                                      {pendingLeaves > 0 && (
                                        <Badge variant="outline" className="text-xs bg-blue-50 border-blue-300 text-blue-700">
                                          <Calendar className="h-3 w-3 mr-1" />
                                          {pendingLeaves} İzin
                                        </Badge>
                                      )}
                                      {pendingMaterials > 0 && (
                                        <Badge variant="outline" className="text-xs bg-yellow-50 border-yellow-300 text-yellow-700">
                                          📦 {pendingMaterials} Malzeme
                                        </Badge>
                                      )}
                                    </>
                                  );
                                })()}
                              </CardDescription>
                            </div>
                            <div className="flex gap-1">
                              {isManagerEdit && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePersonnelEdit(person)}
                                className="hover:bg-blue-100"
                                data-testid={`edit-personnel-${person.id}`}
                              >
                                <Edit className="h-4 w-4 text-blue-600" />
                              </Button>
                              )}
                              {/* Silme butonu — personel.sil yetkisi gerekir */}
                              {canDeletePersonnel && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPersonnelToDelete(person);
                                  }}
                                  className="hover:bg-red-100"
                                  data-testid={`delete-personnel-${person.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-4 space-y-3">
                          {/* İletişim Bilgileri */}
                          <div className="space-y-2 text-sm">
                            {person.phone && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <span className="font-semibold">📞</span>
                                <span>{formatTrCepStoredDisplay(person.phone)}</span>
                              </div>
                            )}
                            {person.email && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <span className="font-semibold">✉️</span>
                                <span className="truncate">{person.email}</span>
                              </div>
                            )}
                          </div>

                          {/* Hiyerarşi - Bağlı Olduğu Yöneticiler */}
                          {(() => {
                            // Hiyerarşiyi bul - YENİ fieldlar varsa kullan, yoksa ESKİ parent_id ile bul
                            let formen = null;
                            let bolgeMuhendisi = null;
                            
                            if (person.formen_id) {
                              formen = personnel.find(p => p.id === person.formen_id);
                            } else if (person.parent_id) {
                              const parent = personnel.find(p => p.id === person.parent_id);
                              if (parent?.role === 'formen') {
                                formen = parent;
                              }
                            }
                            
                            if (person.bolge_muhendisi_id) {
                              bolgeMuhendisi = personnel.find(p => p.id === person.bolge_muhendisi_id);
                            } else if (formen?.bolge_muhendisi_id) {
                              bolgeMuhendisi = personnel.find(p => p.id === formen.bolge_muhendisi_id);
                            } else if (formen?.parent_id) {
                              const formenParent = personnel.find(p => p.id === formen.parent_id);
                              if (formenParent?.role === 'bolge_muhendisi') {
                                bolgeMuhendisi = formenParent;
                              }
                            } else if (person.parent_id && !formen) {
                              const parent = personnel.find(p => p.id === person.parent_id);
                              if (parent?.role === 'bolge_muhendisi') {
                                bolgeMuhendisi = parent;
                              }
                            }
                            
                            // Eğer hiyerarşi varsa göster - Parklar sekmesindeki gibi
                            // Formen için bölge mühendisi yoksa placeholder göster
                            const shouldShowHierarchy = formen || bolgeMuhendisi || person.role === 'formen';
                            
                            if (shouldShowHierarchy) {
                              return (
                                <div className="border-t pt-3 space-y-2">
                                  {formen && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="text-xs text-gray-600">Bağlı:</span>
                                      <Badge className="text-xs bg-yellow-500 text-white border border-yellow-600 px-2 py-1">
                                        Formen
                                      </Badge>
                                      <span className="font-medium text-gray-800">{formen.name}</span>
                                    </div>
                                  )}
                                  
                                  {/* Formen için Bölge Mühendisi gösterimi */}
                                  {person.role === 'formen' && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="text-xs text-gray-600">Bağlı:</span>
                                      <Badge className="text-xs bg-blue-500 text-white border border-blue-600 px-2 py-1">
                                        Bölge Mühendisi
                                      </Badge>
                                      {bolgeMuhendisi ? (
                                        <span className="font-medium text-gray-800">{bolgeMuhendisi.name}</span>
                                      ) : (
                                        <span className="text-gray-400 italic">(atanmamış)</span>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Formen olmayan roller için Bölge Mühendisi gösterimi */}
                                  {person.role !== 'formen' && bolgeMuhendisi && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="text-xs text-gray-600">Bağlı:</span>
                                      <Badge className="text-xs bg-blue-500 text-white border border-blue-600 px-2 py-1">
                                        Bölge Mühendisi
                                      </Badge>
                                      <span className="font-medium text-gray-800">{bolgeMuhendisi.name}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Atanmış Parklar veya Bölgeler (Formen ve Bölge Mühendisi için) */}
                          <div className="border-t pt-3">
                            {person.role === 'formen' ? (
                              // FORMEN için: Sorumlu Olduğu Bölgeler göster
                              <>
                                <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase flex items-center gap-1">
                                  <Home className="h-3 w-3" />
                                  Sorumlu Olduğu Bölgeler
                                </h4>
                                {(() => {
                                  // FORMEN için basit gösterim
                                  if (person.role === 'formen') {
                                    const subordinates = personnel.filter(p =>
                                      p.formen_id === person.id ||
                                      (p.parent_id === person.id && (p.role === 'park_gorevlisi'))
                                    );

                                    // Formen'in resmi bölgeleri — yalnızca bunlar listede gösterilir.
                                    // Altındaki personel başka bölgedeki bir parka atanmış olabilir
                                    // (vekalet/yardım); o park bu formen'in bölge listesine eklenmemeli.
                                    const formenOwnRegionIds = person.region_ids || [];
                                    const formenRegions = regions.filter(r => formenOwnRegionIds.includes(r.id));

                                    const subordinateParks = parks.filter(park =>
                                      park.assigned_personnel_ids &&
                                      subordinates.some(sub => park.assigned_personnel_ids.includes(sub.id))
                                    );
                                    const formenOwnParks = assignedParks;
                                    const candidateParks = [...formenOwnParks, ...subordinateParks.filter(p => !formenOwnParks.some(fp => fp.id === p.id))];
                                    // Parklar da sadece formen'in resmi bölgelerinde olanlara filtrelenir
                                    // (FormenRegionDetails içindeki "Toplam" satırı allFormenParks üzerinden hesaplanıyor).
                                    const allFormenParks = candidateParks.filter(p => p.region_id && formenOwnRegionIds.includes(p.region_id));

                                    return (
                                      <FormenRegionDetails
                                        formenRegions={formenRegions}
                                        allFormenParks={allFormenParks}
                                        subordinates={subordinates}
                                        personnel={personnel}
                                        expandedRegions={expandedRegions}
                                        setExpandedRegions={setExpandedRegions}
                                        personId={person.id}
                                      />
                                    );
                                  }
                                  
                                  // BÖLGE MÜHENDİSİ için - Formenleri AYRI AYRI göster (ESKİ KOD BLOĞU)
                                  if (false && person.role === 'bolge_muhendisi') {
                                    const formens = personnel.filter(p => 
                                      p.bolge_muhendisi_id === person.id ||
                                      (p.parent_id === person.id && p.role === 'formen')
                                    );
                                    
                                    if (formens.length === 0) {
                                      return <p className="text-xs text-gray-400 italic">Altında formen bulunmuyor</p>;
                                    }
                                    
                                    // Her Formen için bilgileri topla
                                    let totalPersonnel = 0;
                                    let totalParks = 0;
                                    let totalArea = 0;
                                    let totalGrass = 0;
                                    let allSubordinates = []; // Tüm alt personeli topla
                                    
                                    // Formen bölge detayları FormenRegionDetails component'ine taşındı - burayı temizlendi
                                  }
                                  
                                  // BÖLGE MÜHENDİSİ için - Formenleri AYRI AYRI göster
                                  if (person.role === 'bolge_muhendisi') {
                                    const formens = personnel.filter(p => 
                                      p.bolge_muhendisi_id === person.id ||
                                      (p.parent_id === person.id && p.role === 'formen')
                                    );
                                    
                                    if (formens.length === 0) {
                                      return <p className="text-xs text-gray-400 italic">Altında formen bulunmuyor</p>;
                                    }
                                    
                                    // Her Formen için bilgileri topla
                                    let totalPersonnel = 0;
                                    let totalParks = 0;
                                    let totalArea = 0;
                                    let totalGrass = 0;
                                    let allSubordinates = []; // Tüm alt personeli topla
                                    
                                    return (
                                      <div className="space-y-3">
                                        <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase flex items-center gap-1">
                                          <Home className="h-3 w-3" />
                                          Sorumlu Olduğu Bölgeler ve Formenler
                                        </h4>
                                        {formens.map(formen => {
                                          // Bu Formen'in altındaki park görevlileri
                                          const formenSubordinates = personnel.filter(p => 
                                            p.formen_id === formen.id || 
                                            (p.parent_id === formen.id && p.role === 'park_gorevlisi')
                                          );
                                          
                                          // Bu Formen ve altındakilerin parkları
                                          const formenOwnParks = parks.filter(park => 
                                            park.assigned_personnel_ids && park.assigned_personnel_ids.includes(formen.id)
                                          );
                                          
                                          const subordinateParks = parks.filter(park => 
                                            park.assigned_personnel_ids && 
                                            formenSubordinates.some(sub => park.assigned_personnel_ids.includes(sub.id))
                                          );
                                          
                                          const allFormenParks = [...formenOwnParks, ...subordinateParks.filter(p => !formenOwnParks.some(fp => fp.id === p.id))];
                                          
                                          // Bölgelere göre grupla
                                          const parkRegionIds = [...new Set(allFormenParks.map(p => p.region_id).filter(Boolean))];
                                          const formenOwnRegionIds = formen.region_ids || [];
                                          const allRegionIds = [...new Set([...parkRegionIds, ...formenOwnRegionIds])];
                                          const formenRegions = regions.filter(r => allRegionIds.includes(r.id));
                                          
                                          // Toplam istatistiklere ekle
                                          totalPersonnel += formenSubordinates.length;
                                          totalParks += allFormenParks.length;
                                          totalArea += allFormenParks.reduce((sum, p) => sum + (p.area || 0), 0);
                                          totalGrass += allFormenParks.reduce((sum, p) => sum + (p.grass_area || 0), 0);
                                          allSubordinates.push(...formenSubordinates); // Alt personeli topla
                                          
                                          return (
                                            <div key={formen.id} className="border-2 border-yellow-300 rounded-lg overflow-hidden">
                                              {/* Formen Başlığı */}
                                              <div className="bg-yellow-100 px-3 py-2 border-b border-yellow-300">
                                                <div className="flex items-center justify-between">
                                                  <div className="flex items-center gap-2">
                                                    <Badge className="text-xs bg-yellow-600 text-white">Formen</Badge>
                                                    <span className="font-bold text-sm text-yellow-900">{formen.name}</span>
                                                  </div>
                                                  <div className="flex flex-col gap-1 items-end">
                                                    <div className="flex gap-3 text-xs text-yellow-800 font-semibold">
                                                      {(() => {
                                                        const parkGorevlileri = formenSubordinates.filter(p => p.role === 'park_gorevlisi');
                                                        const cimEkibi = formenSubordinates.filter(p => p.role === 'çim_ekibi');
                                                        return (
                                                          <>
                                                            {parkGorevlileri.length > 0 && (
                                                              <button
                                                                onClick={() => setExpandedPersonnelDetails(prev => ({ ...prev, [`formen_park_${formen.id}`]: !prev[`formen_park_${formen.id}`] }))}
                                                                className="hover:underline cursor-pointer"
                                                              >
                                                                {parkGorevlileri.length} Park Görevlisi
                                                              </button>
                                                            )}
                                                            {cimEkibi.length > 0 && (
                                                              <button
                                                                onClick={() => setExpandedPersonnelDetails(prev => ({ ...prev, [`formen_cim_${formen.id}`]: !prev[`formen_cim_${formen.id}`] }))}
                                                                className="hover:underline cursor-pointer"
                                                              >
                                                                {cimEkibi.length} Çim Ekibi
                                                              </button>
                                                            )}
                                                            {parkGorevlileri.length === 0 && cimEkibi.length === 0 && <span>0 Personel</span>}
                                                          </>
                                                        );
                                                      })()}
                                                      <span>{allFormenParks.length} Park</span>
                                                    </div>
                                                    {expandedPersonnelDetails[`formen_park_${formen.id}`] && (
                                                      <div className="text-xs text-yellow-700 bg-white px-2 py-1 rounded border border-yellow-300 mt-1">
                                                        {formenSubordinates.filter(p => p.role === 'park_gorevlisi').map(p => (
                                                          <div key={p.id} className="text-xs">
                                                            • {p.name} (Park Görevlisi)
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                    {expandedPersonnelDetails[`formen_cim_${formen.id}`] && (
                                                      <div className="text-xs text-yellow-700 bg-white px-2 py-1 rounded border border-yellow-300 mt-1">
                                                        {formenSubordinates.filter(p => p.role === 'çim_ekibi').map(p => (
                                                          <div key={p.id} className="text-xs">
                                                            • {p.name} (Çim Ekibi)
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                              
                                              {/* Formen'in Bölgeleri */}
                                              {formenRegions.length > 0 ? (
                                                <div className="p-2 space-y-2 bg-white">
                                                  {formenRegions.map(region => {
                                                    const regionParks = allFormenParks.filter(p => p.region_id === region.id);
                                                    // Bu bölgedeki parkların tüm personelini bul
                                                    const regionPersonnelIds = new Set();
                                                    regionParks.forEach(park => {
                                                      if (park.assigned_personnel_ids) {
                                                        park.assigned_personnel_ids.forEach(pid => regionPersonnelIds.add(pid));
                                                      }
                                                    });
                                                    const regionPersonnelCount = Array.from(regionPersonnelIds).filter(pid => {
                                                      const p = personnel.find(per => per.id === pid);
                                                      return p && (p.role === 'park_gorevlisi');
                                                    }).length;
                                                    
                                                    const expandKey = `${person.id}_${formen.id}_${region.id}`;
                                                    const isExpanded = expandedRegions[expandKey] || false;
                                                    
                                                    return (
                                                      <div key={region.id} className="border border-rose-200 rounded-lg overflow-hidden">
                                                        <button
                                                          onClick={() => setExpandedRegions(prev => ({ ...prev, [expandKey]: !isExpanded }))}
                                                          className="w-full flex items-center justify-between p-2 bg-rose-50 hover:bg-rose-100 transition-colors"
                                                        >
                                                          <div className="flex items-center gap-2">
                                                            <ChevronDown className={`h-3 w-3 text-rose-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                            <span className="font-semibold text-xs text-rose-800">{region.name}</span>
                                                          </div>
                                                          <Badge variant="secondary" className="text-xs bg-rose-200 text-rose-800">
                                                            {regionParks.length} park, {regionPersonnelCount} görevli
                                                          </Badge>
                                                        </button>
                                                        
                                                        {isExpanded && (
                                                          <div className="p-2 space-y-1 bg-white">
                                                            {regionParks.map(park => {
                                                              // Bu parkta görevli personeli bul
                                                              const parkPersonnel = personnel.filter(p => 
                                                                park.assigned_personnel_ids && 
                                                                park.assigned_personnel_ids.includes(p.id) &&
                                                                (p.role === 'park_gorevlisi')
                                                              );
                                                              
                                                              return (
                                                                <div key={park.id} className="flex items-center justify-between text-xs p-1.5 bg-green-50 rounded border border-green-200">
                                                                  <div className="flex flex-col gap-0.5">
                                                                    <span className="font-medium text-green-700 text-xs">{park.name}</span>
                                                                    {parkPersonnel.length > 0 && (
                                                                      <span className="text-xs text-purple-600">
                                                                        👤 {parkPersonnel.map(p => p.name).join(', ')}
                                                                      </span>
                                                                    )}
                                                                  </div>
                                                                  <div className="flex gap-2 text-gray-600 text-xs">
                                                                    <span>Alan: {park.area ? `${park.area.toLocaleString('tr-TR')} m²` : '-'}</span>
                                                                    <span>Çim: {park.grass_area ? `${park.grass_area.toLocaleString('tr-TR')} m²` : '-'}</span>
                                                                  </div>
                                                                </div>
                                                              );
                                                            })}
                                                          </div>
                                                        )}
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              ) : (
                                                <div className="p-2 bg-white">
                                                  <p className="text-xs text-gray-400 italic">Henüz bölge atanmamış</p>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                        
                                        {/* Genel Toplam İstatistikler */}
                                        <div className="bg-blue-50 border border-blue-300 rounded-lg p-3">
                                          <div className="flex flex-col gap-1 text-xs">
                                            <div className="flex items-center justify-between">
                                              <span className="font-bold text-blue-700 uppercase">Genel Toplam</span>
                                              <div className="flex gap-4 text-blue-700 font-bold">
                                                <span>{totalParks} Park</span>
                                                <span>Alan: {totalArea.toLocaleString('tr-TR')} m²</span>
                                                <span>Çim: {totalGrass.toLocaleString('tr-TR')} m²</span>
                                              </div>
                                            </div>
                                            <div className="flex items-center justify-between">
                                              <span className="font-semibold text-blue-600">Personel:</span>
                                              <div className="flex gap-3 text-blue-600 font-semibold">
                                                <span>{formens.length} Formen</span>
                                                {(() => {
                                                  const parkGorevlileri = allSubordinates.filter(p => p.role === 'park_gorevlisi').length;
                                                  const cimEkibi = allSubordinates.filter(p => p.role === 'çim_ekibi').length;
                                                  return (
                                                    <>
                                                      {parkGorevlileri > 0 && <span>{parkGorevlileri} Park Görevlisi</span>}
                                                      {cimEkibi > 0 && <span>{cimEkibi} Çim Ekibi</span>}
                                                    </>
                                                  );
                                                })()}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }
                                  
                                  return null;
                                })()}
                              </>
                            ) : person.role === 'bolge_muhendisi' ? (
                              // BÖLGE MÜHENDİSİ için: Formenleri ve onların bölgelerini göster
                              <>
                                <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase flex items-center gap-1">
                                  <Home className="h-3 w-3" />
                                  Sorumlu Olduğu Bölgeler ve Formenler
                                </h4>
                                {(() => {
                                  console.log('🔵 Bölge Mühendisi:', person.name, 'ID:', person.id);
                                  const formens = personnel.filter(p => 
                                    p.role === 'formen' && (
                                      p.bolge_muhendisi_id === person.id ||
                                      p.parent_id === person.id
                                    )
                                  );
                                  console.log('🟢 Bulunan Formenler:', formens.map(f => f.name));
                                  
                                  if (formens.length === 0) {
                                    return <p className="text-xs text-gray-400 italic">Altında formen bulunmuyor</p>;
                                  }
                                  
                                  let totalParks = 0;
                                  let totalArea = 0;
                                  let totalGrass = 0;
                                  let allSubordinates = []; // Tüm alt personeli topla
                                  
                                  return (
                                    <div className="space-y-3">
                                      {formens.map(formen => {
                                        const formenSubordinates = personnel.filter(p =>
                                          p.formen_id === formen.id ||
                                          (p.parent_id === formen.id && (p.role === 'park_gorevlisi'))
                                        );

                                        // Formen'in resmi bölgeleri — listede yalnızca bunlar gösterilir.
                                        const formenOwnRegionIds = formen.region_ids || [];
                                        const formenRegions = regions.filter(r => formenOwnRegionIds.includes(r.id));

                                        const formenOwnParks = parks.filter(park =>
                                          park.assigned_personnel_ids && park.assigned_personnel_ids.includes(formen.id)
                                        );

                                        const subordinateParks = parks.filter(park =>
                                          park.assigned_personnel_ids &&
                                          formenSubordinates.some(sub => park.assigned_personnel_ids.includes(sub.id))
                                        );

                                        const candidateParks = [...formenOwnParks, ...subordinateParks.filter(p => !formenOwnParks.some(fp => fp.id === p.id))];
                                        // Çapraz atanmış (başka bölge) parkları toplamlara katma.
                                        const allFormenParks = candidateParks.filter(p => p.region_id && formenOwnRegionIds.includes(p.region_id));

                                        allSubordinates.push(...formenSubordinates); // Alt personeli topla
                                        totalParks += allFormenParks.length;
                                        totalArea += allFormenParks.reduce((sum, p) => sum + (p.area || 0), 0);
                                        totalGrass += allFormenParks.reduce((sum, p) => sum + (p.grass_area || 0), 0);
                                        
                                        return (
                                          <div key={formen.id} className="border-2 border-yellow-300 rounded-lg overflow-hidden">
                                            <div className="bg-yellow-100 px-3 py-2 border-b border-yellow-300">
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                  <Badge className="text-xs bg-yellow-600 text-white">Formen</Badge>
                                                  <span className="font-bold text-sm text-yellow-900">{formen.name}</span>
                                                </div>
                                                <div className="flex flex-col gap-1 items-end">
                                                  <div className="flex gap-3 text-xs text-yellow-800 font-semibold">
                                                    {(() => {
                                                      const parkGorevlileri = formenSubordinates.filter(p => p.role === 'park_gorevlisi');
                                                      const cimEkibi = formenSubordinates.filter(p => p.role === 'çim_ekibi');
                                                      return (
                                                        <>
                                                          {parkGorevlileri.length > 0 && (
                                                            <button
                                                              onClick={() => setExpandedPersonnelDetails(prev => ({ ...prev, [`bolge_formen_park_${formen.id}`]: !prev[`bolge_formen_park_${formen.id}`] }))}
                                                              className="hover:underline cursor-pointer"
                                                            >
                                                              {parkGorevlileri.length} Park Görevlisi
                                                            </button>
                                                          )}
                                                          {cimEkibi.length > 0 && (
                                                            <button
                                                              onClick={() => setExpandedPersonnelDetails(prev => ({ ...prev, [`bolge_formen_cim_${formen.id}`]: !prev[`bolge_formen_cim_${formen.id}`] }))}
                                                              className="hover:underline cursor-pointer"
                                                            >
                                                              {cimEkibi.length} Çim Ekibi
                                                            </button>
                                                          )}
                                                          {parkGorevlileri.length === 0 && cimEkibi.length === 0 && <span>0 Personel</span>}
                                                        </>
                                                      );
                                                    })()}
                                                    <span>{allFormenParks.length} Park</span>
                                                  </div>
                                                  {expandedPersonnelDetails[`bolge_formen_park_${formen.id}`] && (
                                                    <div className="text-xs text-yellow-700 bg-white px-2 py-1 rounded border border-yellow-300 mt-1">
                                                      {formenSubordinates.filter(p => p.role === 'park_gorevlisi').map(p => (
                                                        <div key={p.id} className="text-xs">
                                                          • {p.name} (Park Görevlisi)
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                  {expandedPersonnelDetails[`bolge_formen_cim_${formen.id}`] && (
                                                    <div className="text-xs text-yellow-700 bg-white px-2 py-1 rounded border border-yellow-300 mt-1">
                                                      {formenSubordinates.filter(p => p.role === 'çim_ekibi').map(p => (
                                                        <div key={p.id} className="text-xs">
                                                          • {p.name} (Çim Ekibi)
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            
                                            {formenRegions.length > 0 ? (
                                              <div className="p-2 space-y-2 bg-white">
                                                {formenRegions.map(region => {
                                                  const regionParks = allFormenParks.filter(p => p.region_id === region.id);
                                                  // Bu bölgedeki parkların tüm personelini bul
                                                  const regionPersonnelIds = new Set();
                                                  regionParks.forEach(park => {
                                                    if (park.assigned_personnel_ids) {
                                                      park.assigned_personnel_ids.forEach(pid => regionPersonnelIds.add(pid));
                                                    }
                                                  });
                                                  const regionPersonnelCount = Array.from(regionPersonnelIds).filter(pid => {
                                                    const p = personnel.find(per => per.id === pid);
                                                    return p && (p.role === 'park_gorevlisi');
                                                  }).length;
                                                  
                                                  const expandKey = `${person.id}_${formen.id}_${region.id}`;
                                                  const isExpanded = expandedRegions[expandKey] || false;
                                                  
                                                  return (
                                                    <div key={region.id} className="border border-rose-200 rounded-lg overflow-hidden">
                                                      <button
                                                        onClick={() => setExpandedRegions(prev => ({ ...prev, [expandKey]: !isExpanded }))}
                                                        className="w-full flex items-center justify-between p-2 bg-rose-50 hover:bg-rose-100 transition-colors"
                                                      >
                                                        <div className="flex items-center gap-2">
                                                          <ChevronDown className={`h-3 w-3 text-rose-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                          <span className="font-semibold text-xs text-rose-800">{region.name}</span>
                                                        </div>
                                                        <Badge variant="secondary" className="text-xs bg-rose-200 text-rose-800">
                                                          {regionParks.length} park, {regionPersonnelCount} görevli
                                                        </Badge>
                                                      </button>
                                                      
                                                      {isExpanded && (
                                                        <div className="p-2 space-y-1 bg-white">
                                                          {regionParks.map(park => {
                                                            // Bu parkta görevli personeli bul
                                                            const parkPersonnel = personnel.filter(p => 
                                                              park.assigned_personnel_ids && 
                                                              park.assigned_personnel_ids.includes(p.id) &&
                                                              (p.role === 'park_gorevlisi')
                                                            );
                                                            
                                                            return (
                                                              <div key={park.id} className="flex items-center justify-between text-xs p-1.5 bg-green-50 rounded border border-green-200">
                                                                <div className="flex flex-col gap-0.5">
                                                                  <span className="font-medium text-green-700 text-xs">{park.name}</span>
                                                                  {parkPersonnel.length > 0 && (
                                                                    <span className="text-xs text-purple-600">
                                                                      👤 {parkPersonnel.map(p => p.name).join(', ')}
                                                                    </span>
                                                                  )}
                                                                </div>
                                                                <div className="flex gap-2 text-gray-600 text-xs">
                                                                  <span>Alan: {park.area ? `${park.area.toLocaleString('tr-TR')} m²` : '-'}</span>
                                                                  <span>Çim: {park.grass_area ? `${park.grass_area.toLocaleString('tr-TR')} m²` : '-'}</span>
                                                                </div>
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              <div className="p-2 bg-white">
                                                <p className="text-xs text-gray-400 italic">Henüz bölge atanmamış</p>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                      
                                      <div className="bg-blue-50 border border-blue-300 rounded-lg p-3">
                                        <div className="flex flex-col gap-1 text-xs">
                                          <div className="flex items-center justify-between">
                                            <span className="font-bold text-blue-700 uppercase">Genel Toplam</span>
                                            <div className="flex gap-4 text-blue-700 font-bold">
                                              <span>{totalParks} Park</span>
                                              <span>Alan: {totalArea.toLocaleString('tr-TR')} m²</span>
                                              <span>Çim: {totalGrass.toLocaleString('tr-TR')} m²</span>
                                            </div>
                                          </div>
                                          <div className="flex items-center justify-between">
                                            <span className="font-semibold text-blue-600">Personel:</span>
                                            <div className="flex gap-3 text-blue-600 font-semibold">
                                              <span>{formens.length} Formen</span>
                                              {(() => {
                                                const parkGorevlileri = allSubordinates.filter(p => p.role === 'park_gorevlisi').length;
                                                const cimEkibi = allSubordinates.filter(p => p.role === 'çim_ekibi').length;
                                                return (
                                                  <>
                                                    {parkGorevlileri > 0 && <span>{parkGorevlileri} Park Görevlisi</span>}
                                                    {cimEkibi > 0 && <span>{cimEkibi} Çim Ekibi</span>}
                                                  </>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </>
                            ) : (
                              // DİĞER ROLLER için: Sorumlu Olduğu Parklar göster
                              <>
                                <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase flex items-center gap-1">
                                  <TreePine className="h-3 w-3" />
                                  Sorumlu Olduğu Parklar
                                </h4>
                                {assignedParks.length === 0 ? (
                                  <p className="text-xs text-gray-400 italic">Henüz park atanmamış</p>
                                ) : (
                                  (() => {
                                    const isSubstitutionRecordActiveToday = (sub) => {
                                      if (!sub?.start_date) return false;
                                      const relatedLeave = findRelatedLeaveForSubstitution(sub);
                                      const effectiveEnd = getEffectiveSubstitutionEndDate(relatedLeave, sub?.end_date);
                                      return isSubstitutionActiveToday(sub?.start_date, effectiveEnd);
                                    };
                                    const getSubstitutionEffectiveEnd = (sub) => {
                                      const relatedLeave = findRelatedLeaveForSubstitution(sub);
                                      return getEffectiveSubstitutionEndDate(relatedLeave, sub?.end_date);
                                    };

                                    // Parkları kategorize et: Vekalet olarak baktığı parklar vs Kendi parkları
                                    // Ayrıca izindeyken başkasının baktığı parkları da kontrol et
                                    const parksWithVekaletInfo = assignedParks.map(park => {
                                      const parkSubMatch = (sub) =>
                                        Array.isArray(sub?.parks) &&
                                        sub.parks.some((p) => (p?.id || p?.park_id) === park.id);
                                      // Bu park vekalet mi kontrol et (bu kişi başkasının yerine bakıyor)
                                      const vekaletSub = allActiveSubstitutions.find(sub => 
                                        sub.substitute_personnel_id === person.id &&
                                        (sub?.status || "active") === "active" &&
                                        isSubstitutionRecordActiveToday(sub) &&
                                        parkSubMatch(sub)
                                      );
                                      const isVekalet = !!vekaletSub;
                                      const vekaletEndDate = vekaletSub ? getSubstitutionEffectiveEnd(vekaletSub) : null;
                                      const vekaletOriginalPerson = vekaletSub?.original_personnel_name;
                                      
                                      // Bu park için başkası mı bakıyor (bu kişi izinde)
                                      let onLeaveSub = allActiveSubstitutions.find(sub => 
                                        sub.original_personnel_id === person.id &&
                                        (sub?.status || "active") === "active" &&
                                        isSubstitutionRecordActiveToday(sub) &&
                                        parkSubMatch(sub)
                                      );
                                      // Fallback: active_substitutions'ta id yoksa (örn. ÇİM EKİBİ),
                                      // onaylı izin talebindeki park_substitutes'tan vekili bul.
                                      if (!onLeaveSub) {
                                        const leaveFallback = (leaveRequests || []).find((lr) =>
                                          lr?.status === "approved" &&
                                          lr?.personnel_id === person.id &&
                                          isSubstitutionActiveToday(
                                            lr?.start_date,
                                            getEffectiveSubstitutionEndDate(lr, lr?.end_date || lr?.return_date)
                                          ) &&
                                          Array.isArray(lr?.park_substitutes) &&
                                          lr.park_substitutes.some((ps) => (ps?.park_id || ps?.id) === park.id)
                                        );
                                        if (leaveFallback) {
                                          const ps = leaveFallback.park_substitutes.find((item) => (item?.park_id || item?.id) === park.id);
                                          onLeaveSub = {
                                            start_date: leaveFallback?.start_date,
                                            end_date: getEffectiveSubstitutionEndDate(leaveFallback, leaveFallback?.end_date || leaveFallback?.return_date),
                                            substitute_personnel_name:
                                              ps?.substitute_name ||
                                              ps?.substitute_personnel_name ||
                                              leaveFallback?.substitute_personnel_name ||
                                              "Vekil"
                                          };
                                        }
                                      }
                                      const hasSubstitute = !!onLeaveSub;
                                      const substituteStartDate = onLeaveSub?.start_date;
                                      const substituteEndDate = onLeaveSub?.end_date;
                                      // Çöktürülmüş vekalet kaydı tüm parkları TEK kayıtta birleştirir;
                                      // üst-seviye substitute_personnel_name yalnız "ilk dolu vekil"dir.
                                      // Bu parkın gerçek vekilini per-park kırılımdan (substitute_breakdown)
                                      // çöz; yoksa (tek-vekil senaryo / fallback kaydı) üst-seviye alana düş.
                                      const breakdownEntry = Array.isArray(onLeaveSub?.substitute_breakdown)
                                        ? onLeaveSub.substitute_breakdown.find(
                                            (b) => (b?.id || b?.park_id) === park.id
                                          )
                                        : null;
                                      const substituteName =
                                        breakdownEntry?.substitute_personnel_name ||
                                        onLeaveSub?.substitute_personnel_name;
                                      
                                      return {
                                        ...park,
                                        isVekalet,
                                        vekaletEndDate,
                                        vekaletOriginalPerson,
                                        hasSubstitute,
                                        substituteStartDate,
                                        substituteEndDate,
                                        substituteName
                                      };
                                    });
                                    
                                    // Vekalet parkları (başkasının yerine bakıyor) - alfabetik sırala
                                    const vekaletParks = parksWithVekaletInfo
                                      .filter(p => p.isVekalet)
                                      .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
                                    
                                    // İzindeyken başkasının baktığı parklar - alfabetik sırala
                                    const parksWithSubstitute = parksWithVekaletInfo
                                      .filter(p => p.hasSubstitute && !p.isVekalet)
                                      .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
                                    
                                    // Kendi parkları (vekalet değil, başkası da bakmıyor) - alfabetik sırala
                                    const ownParks = parksWithVekaletInfo
                                      .filter(p => !p.isVekalet && !p.hasSubstitute)
                                      .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
                                    
                                    return (
                                      <div className="space-y-3">
                                        {/* İzindeyken başkasının baktığı parklar */}
                                        {parksWithSubstitute.length > 0 && (
                                          <div className="space-y-2">
                                            <p className="text-xs font-semibold text-green-700 flex items-center gap-1">
                                              🏖️ İzin Süresince Vekil Bakıyor ({parksWithSubstitute.length})
                                            </p>
                                            {parksWithSubstitute.map(park => (
                                              <div 
                                                key={park.id} 
                                                className="flex items-center justify-between text-xs p-2 rounded border bg-green-50 border-green-200"
                                              >
                                                <div className="flex flex-col gap-1">
                                                  <span className="font-medium text-green-700">{park.name}</span>
                                                  <span className="text-[10px] bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded font-semibold inline-block w-fit">
                                                    VEKALET - {park.substituteStartDate ? new Date(park.substituteStartDate).toLocaleDateString('tr-TR') : ''} - {park.substituteEndDate ? new Date(park.substituteEndDate).toLocaleDateString('tr-TR') : ''} → {park.substituteName}'DE
                                                  </span>
                                                </div>
                                                <div className="flex gap-3 text-gray-600">
                                                  <span className="flex items-center gap-1">
                                                    <span className="font-semibold">Alan:</span>
                                                    {park.area ? `${park.area.toLocaleString('tr-TR')} m²` : '-'}
                                                  </span>
                                                  <span className="flex items-center gap-1">
                                                    <span className="font-semibold">Çim:</span>
                                                    {park.grass_area ? `${park.grass_area.toLocaleString('tr-TR')} m²` : '-'}
                                                  </span>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        
                                        {/* Vekalet parkları (başkasının yerine bakıyor) */}
                                        {vekaletParks.length > 0 && (
                                          <div className="space-y-2">
                                            <p className="text-xs font-semibold text-orange-700 flex items-center gap-1">
                                              🔄 Vekalet Olarak Bakıyor ({vekaletParks.length})
                                            </p>
                                            {vekaletParks.map(park => (
                                              <div 
                                                key={park.id} 
                                                className="flex items-center justify-between text-xs p-2 rounded border bg-orange-50 border-orange-300"
                                              >
                                                <div className="flex items-center gap-2">
                                                  <span className="font-medium text-orange-700">{park.name}</span>
                                                  <span className="text-[10px] bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded font-semibold">
                                                    VEKALETEN - {park.vekaletEndDate ? new Date(park.vekaletEndDate).toLocaleDateString('tr-TR') : ''}'e kadar bakacak
                                                  </span>
                                                </div>
                                                <div className="flex gap-3 text-gray-600">
                                                  <span className="flex items-center gap-1">
                                                    <span className="font-semibold">Alan:</span>
                                                    {park.area ? `${park.area.toLocaleString('tr-TR')} m²` : '-'}
                                                  </span>
                                                  <span className="flex items-center gap-1">
                                                    <span className="font-semibold">Çim:</span>
                                                    {park.grass_area ? `${park.grass_area.toLocaleString('tr-TR')} m²` : '-'}
                                                  </span>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        
                                        {/* Kendi parkları */}
                                        {ownParks.length > 0 && (
                                          <div className="space-y-2">
                                            {(vekaletParks.length > 0 || parksWithSubstitute.length > 0) && (
                                              <p className="text-xs font-semibold text-green-700 flex items-center gap-1">
                                                🌳 Kendi Parkları ({ownParks.length})
                                              </p>
                                            )}
                                            {ownParks.map(park => {
                                              // Paylaşımlı park: bu parka bakan diğer park görevlileri
                                              const coWorkers = (park.assigned_personnel_ids || [])
                                                .map(pid => personnel.find(pp => pp.id === pid))
                                                .filter(Boolean)
                                                .filter(pp => pp.role === 'park_gorevlisi' && pp.id !== person.id && pp.name !== person.name)
                                                .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "tr"));
                                              const isShared = coWorkers.length > 0;
                                              return (
                                              <div
                                                key={park.id}
                                                className="flex items-center justify-between text-xs p-2 rounded border bg-green-50 border-green-200"
                                              >
                                                <div className="flex flex-col gap-1">
                                                  <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-medium text-green-700">{park.name}</span>
                                                    {isShared && (
                                                      <span className="text-[10px] bg-purple-500 text-white px-1.5 py-0.5 rounded font-semibold">
                                                        👥 Paylaşımlı · {coWorkers.length + 1} görevli
                                                      </span>
                                                    )}
                                                  </div>
                                                  {isShared && (
                                                    <span className="text-[10px] text-purple-700">
                                                      Birlikte: {coWorkers.map(pp => pp.name).join(', ')}
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="flex gap-3 text-gray-600">
                                                  <span className="flex items-center gap-1">
                                                    <span className="font-semibold">Alan:</span>
                                                    {park.area ? `${park.area.toLocaleString('tr-TR')} m²` : '-'}
                                                  </span>
                                                  <span className="flex items-center gap-1">
                                                    <span className="font-semibold">Çim:</span>
                                                    {park.grass_area ? `${park.grass_area.toLocaleString('tr-TR')} m²` : '-'}
                                                  </span>
                                                </div>
                                              </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                        
                                        <div className="bg-blue-50 border border-blue-300 rounded p-2">
                                          <div className="flex items-center justify-between text-xs">
                                            <span className="font-bold text-blue-700">TOPLAM</span>
                                            <div className="flex gap-3 text-blue-700 font-bold">
                                              <span>
                                                Alan: {assignedParks.reduce((sum, p) => sum + (p.area || 0), 0).toLocaleString('tr-TR')} m²
                                              </span>
                                              <span>
                                                Çim: {assignedParks.reduce((sum, p) => sum + (p.grass_area || 0), 0).toLocaleString('tr-TR')} m²
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })()
                                )}
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
              ) : (
                /* Tablo Görünümü */
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">Ad Soyad</TableHead>
                          <TableHead>Rol</TableHead>
                          <TableHead>Bağlı Olduğu</TableHead>
                        <TableHead>Telefon</TableHead>
                        <TableHead>E-posta</TableHead>
                        <TableHead>Sorumlu Olduğu Parklar</TableHead>
                        <TableHead className="text-right w-[100px]">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedPersonnel.map(person => {
                          const assignedParks = parks.filter(p => p.assigned_personnel_ids && p.assigned_personnel_ids.includes(person.id));
                          
                          return (
                            <TableRow key={person.id} data-testid={`personnel-row-${person.id}`}>
                              <TableCell className="font-medium">
                                <div className="space-y-1">
                                  <div>{person.name}</div>
                                  {['passive', 'inactive', 'ayrildi', 'deleted'].includes(String(person.status || '').toLowerCase()) && (
                                    <Badge className="text-xs font-semibold bg-gray-500 text-white border-gray-600">
                                      Pasif
                                    </Badge>
                                  )}
                                  {person.status === 'on_leave' && (
                                    <Badge variant="outline" className="text-xs bg-orange-50 border-orange-300 text-orange-700">
                                      İzinli
                                    </Badge>
                                  )}
                                  {person.status === 'on_report' && (
                                    <Badge variant="outline" className="text-xs bg-red-50 border-red-300 text-red-700">
                                      Raporlu
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1 items-start">
                                  <Badge className={`text-xs font-semibold ${getRoleBadge(person.role).className}`}>
                                    {getRoleBadge(person.role).label}
                                  </Badge>
                                  {getNonDefaultTeamLabel(person.primary_team_id) && (
                                    <Badge className="text-xs font-semibold bg-teal-500 text-white border-teal-600">
                                      {getNonDefaultTeamLabel(person.primary_team_id)}
                                    </Badge>
                                  )}
                                  {person.custom_role_name && String(person.custom_role_name).trim() &&
                                   person.custom_role_description && String(person.custom_role_description).trim() && (
                                    <Badge className="text-xs font-semibold bg-cyan-500 text-white border-cyan-600">
                                      Özel Rol: {person.custom_role_name}
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  // Hiyerarşiyi bul - YENİ fieldlar varsa kullan, yoksa ESKİ parent_id ile bul
                                  let formen = null;
                                  let bolgeMuhendisi = null;
                                  let mudurYardimcisi = null;
                                  
                                  if (person.formen_id) {
                                    formen = personnel.find(p => p.id === person.formen_id);
                                  } else if (person.parent_id) {
                                    const parent = personnel.find(p => p.id === person.parent_id);
                                    if (parent?.role === 'formen') {
                                      formen = parent;
                                    }
                                  }
                                  
                                  if (person.bolge_muhendisi_id) {
                                    bolgeMuhendisi = personnel.find(p => p.id === person.bolge_muhendisi_id);
                                  } else if (formen?.bolge_muhendisi_id) {
                                    bolgeMuhendisi = personnel.find(p => p.id === formen.bolge_muhendisi_id);
                                  } else if (formen?.parent_id) {
                                    const formenParent = personnel.find(p => p.id === formen.parent_id);
                                    if (formenParent?.role === 'bolge_muhendisi') {
                                      bolgeMuhendisi = formenParent;
                                    }
                                  } else if (person.parent_id && !formen) {
                                    const parent = personnel.find(p => p.id === person.parent_id);
                                    if (parent?.role === 'bolge_muhendisi') {
                                      bolgeMuhendisi = parent;
                                    }
                                  }
                                  
                                  if (person.mudur_yardimcisi_id) {
                                    mudurYardimcisi = personnel.find(p => p.id === person.mudur_yardimcisi_id);
                                  }
                                  
                                  // Renkli badge'ler ile göster
                                  if (!formen && !bolgeMuhendisi && !mudurYardimcisi) {
                                    return <span className="text-xs text-gray-400">-</span>;
                                  }
                                  
                                  return (
                                    <div className="flex flex-wrap gap-1">
                                      {formen && (
                                        <Badge className="text-xs bg-yellow-500 text-white border border-yellow-600">
                                          {formen.name}
                                        </Badge>
                                      )}
                                      {bolgeMuhendisi && (
                                        <Badge className="text-xs bg-blue-500 text-white border border-blue-600">
                                          {bolgeMuhendisi.name}
                                        </Badge>
                                      )}
                                      {mudurYardimcisi && (
                                        <Badge className="text-xs bg-purple-100 text-purple-800 border border-purple-300">
                                          {mudurYardimcisi.name}
                                        </Badge>
                                      )}
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              <TableCell className="text-sm text-gray-600">
                                {person.phone ? formatTrCepStoredDisplay(person.phone) : "-"}
                              </TableCell>
                              <TableCell className="text-sm text-gray-600">{person.email || "-"}</TableCell>
                              <TableCell>
                                {assignedParks.length === 0 ? (
                                  <span className="text-xs text-gray-400 italic">Park atanmamış</span>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="space-y-1">
                                      {assignedParks.map(park => (
                                        <div key={park.id} className="flex items-center justify-between text-xs p-1.5 bg-green-50 rounded border border-green-200">
                                          <span className="font-medium text-green-700">{park.name}</span>
                                          <div className="flex gap-2 text-gray-600 text-xs">
                                            <span>{park.area ? `${park.area.toLocaleString('tr-TR')} m²` : '-'}</span>
                                            <span className="text-gray-400">|</span>
                                            <span>Çim: {park.grass_area ? `${park.grass_area.toLocaleString('tr-TR')} m²` : '-'}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="bg-blue-50 border border-blue-300 rounded p-1.5 text-xs font-bold text-blue-700">
                                      TOPLAM: {assignedParks.reduce((sum, p) => sum + (p.area || 0), 0).toLocaleString('tr-TR')} m² 
                                      (Çim: {assignedParks.reduce((sum, p) => sum + (p.grass_area || 0), 0).toLocaleString('tr-TR')} m²)
                                    </div>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex gap-2 justify-end">
                                  {isManagerEdit && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handlePersonnelEdit(person)}
                                    className="hover:bg-blue-100"
                                    data-testid={`edit-personnel-${person.id}`}
                                  >
                                    <Edit className="h-4 w-4 text-blue-600" />
                                  </Button>
                                  )}
                                  {/* Silme butonu — personel.sil yetkisi gerekir */}
                                  {canDeletePersonnel && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPersonnelToDelete(person);
                                      }}
                                      className="hover:bg-red-100"
                                      data-testid={`delete-personnel-${person.id}`}
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
              })()}
            </>)}
          </TabsContent>
          )}

          {/* İZİN YÖNETİMİ TAB (golge_admin sadece görüntüleme) */}
          {(isAdmin || isMudur || isFormen || isBolgeMuhendisi || isMudurYardimcisi || isParkGorevlisi || isGolgeAdmin) && activeTab === "approvals" && (
            <TabsContent value="approvals" className="space-y-6">
              {/* BENİM İZİNLERİM - Tüm Personel İçin */}
              {(isParkGorevlisi || isFormen || isBolgeMuhendisi || isMudurYardimcisi) && (
                <div className="space-y-6">
                  {/* Başlık ve Yeni İzin Talebi Butonu */}
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-800">📋 İzin Taleplerim</h2>
                    <Button 
                      onClick={() => {
                        if (isDelegatorReadOnly && !isGolgeAdmin) {
                          toast.warning(READ_ONLY_MESSAGE);
                          return;
                        }
                        if (isGolgeAdmin) return;
                        setIsLeaveRequestDialogOpen(true);
                      }}
                      disabled={isDelegatorReadOnly}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Yeni İzin Talebi
                    </Button>
                  </div>

                  {/* Tüm İzin Durumları Kartı */}
                  {(() => {
                    const currentPerson = personnel.find(p => p.id === personnelId);
                    const annualLeave = currentPerson?.annual_leave_days || 20;
                    const hireDate = currentPerson?.hire_date;
                    
                    // Sosyal izin bilgileri
                    const usedSocialLeave = currentPerson?.used_social_leave_days || 0;
                    const remainingSocialLeave = 12 - usedSocialLeave;
                    const socialLeaveByMonth = currentPerson?.social_leave_by_month || {};
                    
                    // Rapor bilgileri
                    const usedReportDays = currentPerson?.used_report_days || 0;
                    const remainingReportDays = 40 - usedReportDays;
                    
                    // Onaylanmış tüm izin taleplerini bul (izin geçmişi için)
                    const approvedLeaves = leaveRequests.filter(lr => 
                      lr.personnel_id === personnelId && 
                      lr.status === 'approved'
                    ).sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

                    const toPositiveNumber = (value) => {
                      const n = Number(value);
                      return Number.isFinite(n) && n > 0 ? n : null;
                    };

                    const getUsedDaysFromRequest = (leave) => {
                      const earlyReturnDate = leave?.actual_return_date || leave?.return_request?.actual_return_date;
                      const returnRequestStatus = String(leave?.return_request?.status || '').trim().toLowerCase();
                      const hasApprovedEarlyReturn =
                        leave?.status === 'approved' &&
                        Boolean(earlyReturnDate) &&
                        returnRequestStatus === 'approved';

                      const usedFromEarlyReturn = hasApprovedEarlyReturn ? toPositiveNumber(leave?.leave_days_used_actual) : null;
                      const usedFromPlanned = toPositiveNumber(leave?.annual_days_planned) ?? toPositiveNumber(leave?.workdays_used);
                      const fallbackTotal = toPositiveNumber(leave?.total_days);
                      return usedFromEarlyReturn ?? usedFromPlanned ?? fallbackTotal ?? 0;
                    };

                    // Kullanıcı panelinde sayaçlar anlık kalsın:
                    // personel kaydı henüz refresh olmasa bile onaylı taleplerden kullanılan günü türet.
                    const approvedAnnualUsed = approvedLeaves
                      .filter((l) => ['Yıllık İzin', 'Diğer'].includes(l.reason_type))
                      .reduce((sum, l) => sum + getUsedDaysFromRequest(l), 0);
                    const usedLeave = Math.max(currentPerson?.used_leave_days || 0, approvedAnnualUsed);
                    const remainingLeave = Math.max(0, annualLeave - usedLeave);
                    
                    // İzin türlerine göre grupla
                    const leavesByType = {
                      'Yıllık İzin': approvedLeaves.filter(l => l.reason_type === 'Yıllık İzin'),
                      'Sosyal İzin': approvedLeaves.filter(l => l.reason_type === 'Sosyal İzin'),
                      'Rapor': approvedLeaves.filter(l => l.reason_type === 'Rapor'),
                      'Diğer': approvedLeaves.filter(l => l.reason_type === 'Diğer')
                    };
                    
                    return (
                      <div className="space-y-4">
                        {/* Yıllık İzin Kartı */}
                        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <div className="bg-blue-500 rounded-full p-2">
                                  <Calendar className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-blue-900">🏖️ Yıllık İzin Durumum</h3>
                                  {hireDate && (
                                    <p className="text-xs text-blue-600">
                                      İşe Başlama: {new Date(hireDate).toLocaleDateString('tr-TR')}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-4 text-center">
                                <div>
                                  <p className="text-xl font-bold text-blue-700">{annualLeave}</p>
                                  <p className="text-xs text-blue-600">Toplam</p>
                                </div>
                                <div>
                                  <p className="text-xl font-bold text-orange-600">{usedLeave}</p>
                                  <p className="text-xs text-orange-600">Kullanılan</p>
                                </div>
                                <div>
                                  <p className="text-xl font-bold text-green-600">{remainingLeave}</p>
                                  <p className="text-xs text-green-600">Kalan</p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Yıllık İzin Geçmişi */}
                            {leavesByType['Yıllık İzin'].length > 0 && (
                              <details className="mt-2">
                                <summary className="cursor-pointer text-sm font-semibold text-blue-800 hover:text-blue-600">
                                  📅 Yıllık İzin Geçmişi ({leavesByType['Yıllık İzin'].length} kayıt)
                                </summary>
                                <div className="space-y-1 mt-2 max-h-32 overflow-y-auto">
                                  {leavesByType['Yıllık İzin'].map((leave, idx) => {
                                    const startDate = new Date(leave.start_date);
                                    const endDate = new Date(leave.end_date);
                                    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                                    
                                    // Hafta içi ve hafta sonu hesapla
                                    let weekdays = 0;
                                    let weekends = 0;
                                    let current = new Date(startDate);
                                    while (current <= endDate) {
                                      const dayOfWeek = current.getDay();
                                      if (dayOfWeek === 0 || dayOfWeek === 6) {
                                        weekends++;
                                      } else {
                                        weekdays++;
                                      }
                                      current.setDate(current.getDate() + 1);
                                    }
                                    const toValidNumber = (value) => {
                                      const n = Number(value);
                                      return Number.isFinite(n) ? n : null;
                                    };
                                    const toPositiveNumber = (value) => {
                                      const n = Number(value);
                                      return Number.isFinite(n) && n > 0 ? n : null;
                                    };
                                    const plannedAnnualDays = toPositiveNumber(leave?.annual_days_planned) ?? toPositiveNumber(leave?.workdays_used) ?? weekdays;
                                    const earlyReturnDate = leave?.actual_return_date || leave?.return_request?.actual_return_date;
                                    const returnRequestStatus = String(leave?.return_request?.status || '').trim().toLowerCase();
                                    const hasApprovedEarlyReturn = leave.status === 'approved' && Boolean(earlyReturnDate) && returnRequestStatus === 'approved';
                                    const effectiveAnnualDays = hasApprovedEarlyReturn
                                      ? (toPositiveNumber(leave?.leave_days_used_actual) ?? plannedAnnualDays)
                                      : plannedAnnualDays;
                                    const holidayWeekdayCount = Math.max(0, weekdays - plannedAnnualDays);
                                    const isEarlyReturnAdjusted =
                                      hasApprovedEarlyReturn &&
                                      effectiveAnnualDays < plannedAnnualDays;
                                    const dateRangeLabel = `${startDate.toLocaleDateString('tr-TR').replaceAll('.', ',')}-${endDate.toLocaleDateString('tr-TR').replaceAll('.', ',')}`;
                                    
                                    return (
                                      <div key={leave.id} className="bg-blue-100 rounded px-2 py-1 text-xs border border-blue-300">
                                        <div className="flex items-center justify-between">
                                          <span className="text-gray-700">
                                            {startDate.toLocaleDateString('tr-TR')} - {endDate.toLocaleDateString('tr-TR')}
                                          </span>
                                          <span className="text-blue-700 font-medium">{leave.status === 'approved' ? plannedAnnualDays : totalDays} gün</span>
                                        </div>
                                        <div className="text-[10px] text-gray-600 mt-0.5">
                                          📅 {weekdays} hafta içi{weekends > 0 ? `, ${weekends} gün hafta sonu` : ''}
                                        </div>
                                        {leave.status === 'approved' && (
                                          <div className="text-[10px] text-green-700 mt-0.5">
                                            ✅ Yıllık izinden {effectiveAnnualDays} gün düşüldü{holidayWeekdayCount > 0 ? ` • ${holidayWeekdayCount} gün bayram tatili` : ''}
                                          </div>
                                        )}
                                        {isEarlyReturnAdjusted && (
                                          <div className="text-[10px] text-amber-800 mt-1 bg-amber-50 border border-amber-200 rounded px-1.5 py-1">
                                            {`${dateRangeLabel} tarihleri arası ${plannedAnnualDays} gün yıllık${holidayWeekdayCount > 0 ? ` ${holidayWeekdayCount} gün bayram tatili` : ''} olan izin talebiniz ${(earlyReturnDate ? new Date(earlyReturnDate).toLocaleDateString('tr-TR').replaceAll('.', ',') : '-')} tarihinde erken işbaşı yaptığınız için ${effectiveAnnualDays} gün olarak yıllık izin talebinden düşülmüştür.`}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </details>
                            )}
                          </CardContent>
                        </Card>
                        
                        {/* Sosyal İzin Kartı - Tıklanınca detay göster */}
                        <details className="group">
                          <summary className="cursor-pointer list-none">
                            <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200 hover:shadow-md transition-shadow">
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="bg-purple-500 rounded-full p-2">
                                      <Users className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h3 className="font-semibold text-purple-900">👨‍👩‍👧‍👦 Sosyal İzin Durumum</h3>
                                      <p className="text-xs text-purple-600">
                                        {leavesByType['Sosyal İzin'].length > 0 
                                          ? `${leavesByType['Sosyal İzin'].length} kayıt - detay için tıklayın`
                                          : 'Henüz sosyal izin kullanılmadı'}
                                      </p>
                                    </div>
                                  </div>
                                  <ChevronDown className="h-5 w-5 text-purple-500 group-open:rotate-180 transition-transform" />
                                </div>
                              </CardContent>
                            </Card>
                          </summary>
                          {/* Sosyal İzin Detayları */}
                          <div className="mt-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <p className="text-sm font-semibold text-purple-800 mb-2">📅 Yıl İçinde Kullanılan Sosyal İzinler</p>
                            {leavesByType['Sosyal İzin'].length > 0 ? (
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {leavesByType['Sosyal İzin'].map((leave) => {
                                  const startDate = new Date(leave.start_date);
                                  const endDate = new Date(leave.end_date);
                                  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                                  return (
                                    <div key={leave.id} className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm border border-purple-200">
                                      <span className="text-gray-700">
                                        {startDate.toLocaleDateString('tr-TR')} - {endDate.toLocaleDateString('tr-TR')}
                                      </span>
                                      <span className="text-purple-700 font-medium bg-purple-100 px-2 py-0.5 rounded">{days} gün</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">Bu yıl henüz sosyal izin kullanılmadı.</p>
                            )}
                          </div>
                        </details>
                        
                        {/* Rapor Kartı - Tıklanınca detay göster */}
                        <details className="group">
                          <summary className="cursor-pointer list-none">
                            <Card className="bg-gradient-to-r from-pink-50 to-red-50 border-pink-200 hover:shadow-md transition-shadow">
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="bg-pink-500 rounded-full p-2">
                                      <FileText className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h3 className="font-semibold text-pink-900">🏥 Rapor Durumum</h3>
                                      <p className="text-xs text-pink-600">
                                        {leavesByType['Rapor'].length > 0 
                                          ? `${leavesByType['Rapor'].length} kayıt - detay için tıklayın`
                                          : 'Henüz rapor kullanılmadı'}
                                      </p>
                                    </div>
                                  </div>
                                  <ChevronDown className="h-5 w-5 text-pink-500 group-open:rotate-180 transition-transform" />
                                </div>
                              </CardContent>
                            </Card>
                          </summary>
                          {/* Rapor Detayları */}
                          <div className="mt-2 p-3 bg-pink-50 rounded-lg border border-pink-200">
                            <p className="text-sm font-semibold text-pink-800 mb-2">📅 Yıl İçinde Alınan Raporlar</p>
                            {leavesByType['Rapor'].length > 0 ? (
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {leavesByType['Rapor'].map((leave) => {
                                  const startDate = new Date(leave.start_date);
                                  const endDate = new Date(leave.end_date);
                                  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                                  return (
                                    <div key={leave.id} className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm border border-pink-200">
                                      <span className="text-gray-700">
                                        {startDate.toLocaleDateString('tr-TR')} - {endDate.toLocaleDateString('tr-TR')}
                                      </span>
                                      <span className="text-pink-700 font-medium bg-pink-100 px-2 py-0.5 rounded">{days} gün</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">Bu yıl henüz rapor alınmadı.</p>
                            )}
                          </div>
                        </details>
                        
                        {/* Diğer İzin Kartı - Tıklanınca detay göster */}
                        <details className="group">
                          <summary className="cursor-pointer list-none">
                            <Card className="bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200 hover:shadow-md transition-shadow">
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="bg-gray-500 rounded-full p-2">
                                      <FileText className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h3 className="font-semibold text-gray-900">📋 Diğer İzin Durumum</h3>
                                      <p className="text-xs text-gray-600">
                                        {leavesByType['Diğer'].length > 0 
                                          ? `${leavesByType['Diğer'].length} kayıt - detay için tıklayın`
                                          : 'Henüz diğer izin kullanılmadı'}
                                      </p>
                                    </div>
                                  </div>
                                  <ChevronDown className="h-5 w-5 text-gray-500 group-open:rotate-180 transition-transform" />
                                </div>
                              </CardContent>
                            </Card>
                          </summary>
                          {/* Diğer İzin Detayları */}
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm font-semibold text-gray-800 mb-2">📅 Yıl İçinde Kullanılan Diğer İzinler</p>
                            {leavesByType['Diğer'].length > 0 ? (
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {leavesByType['Diğer'].map((leave) => {
                                  const startDate = new Date(leave.start_date);
                                  const endDate = new Date(leave.end_date);
                                  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                                  return (
                                    <div key={leave.id} className="bg-white rounded px-3 py-2 text-sm border border-gray-200">
                                      <div className="flex items-center justify-between">
                                        <span className="text-gray-700">
                                          {startDate.toLocaleDateString('tr-TR')} - {endDate.toLocaleDateString('tr-TR')}
                                        </span>
                                        <span className="text-gray-700 font-medium bg-gray-100 px-2 py-0.5 rounded">{days} gün</span>
                                      </div>
                                      {leave.reason_detail && (
                                        <p className="text-xs text-gray-500 mt-1 italic">"{leave.reason_detail}"</p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">Bu yıl henüz diğer izin kullanılmadı.</p>
                            )}
                          </div>
                        </details>
                      </div>
                    );
                  })()}

                  {/* Vekalet Envanteri (Açılır) */}
                  {(() => {
                    const todayStr = new Date().toISOString().slice(0, 10);
                    const formatDateWithComma = (value) => {
                      if (!value) return "-";
                      const d = new Date(value);
                      if (Number.isNaN(d.getTime())) return "-";
                      return d.toLocaleDateString("tr-TR").replaceAll(".", ",");
                    };
                    const getEffectiveDelegationEnd = (leaveReq) => {
                      if (!leaveReq) return null;
                      const rr = leaveReq.return_request || {};
                      const rrStatus = String(rr.status || "").trim().toLowerCase();
                      const actualReturn = leaveReq.actual_return_date || rr.actual_return_date;
                      const handoverMode = String(leaveReq.handover_mode || rr.handover_mode || "immediate").trim().toLowerCase();
                      const handoverUntil = leaveReq.handover_until || rr.handover_until;
                      if (rrStatus === "approved" && actualReturn) {
                        if (handoverMode === "end_of_day" || handoverMode === "custom") {
                          return handoverUntil || actualReturn;
                        }
                        // immediate modda vekalet fiili dönüş gününde değil, bir önceki gün sona erer.
                        const actualDate = new Date(actualReturn);
                        if (Number.isNaN(actualDate.getTime())) return actualReturn;
                        actualDate.setDate(actualDate.getDate() - 1);
                        return actualDate.toISOString().slice(0, 10);
                      }
                      return leaveReq.end_date || leaveReq.return_date || leaveReq.start_date || null;
                    };

                    const currentPersonnel = (personnel || []).find((p) => p.id === personnelId);
                    const currentPersonnelName = String(currentPersonnel?.name || "").trim().toLowerCase();
                    const asSubstituteFromLeavesAll = (leaveRequests || [])
                      .filter((lr) => {
                        if (lr.status !== "approved" || !lr.start_date || !lr.end_date) return false;
                        const directMatch = lr.substitute_personnel_id === personnelId;
                        const planMatch = lr.delegationPlan?.singleDelegateUserId === personnelId;
                        const parkMatch = Array.isArray(lr.park_substitutes) && lr.park_substitutes.some((ps) => {
                          const subId = ps?.substitute_id || ps?.substitute_personnel_id;
                          const subName = String(ps?.substitute_name || ps?.substitute_personnel_name || "").trim().toLowerCase();
                          return subId === personnelId || (currentPersonnelName && subName === currentPersonnelName);
                        });
                        return Boolean(directMatch || parkMatch || planMatch);
                      })
                      .map((lr) => {
                        const owner = (personnel || []).find((p) => p.id === lr.personnel_id);
                        let parkItems = [];
                        if (Array.isArray(lr.park_substitutes) && lr.park_substitutes.length > 0) {
                          parkItems = lr.park_substitutes
                            .filter((ps) => {
                              const subId = ps?.substitute_id || ps?.substitute_personnel_id;
                              const subName = String(ps?.substitute_name || ps?.substitute_personnel_name || "").trim().toLowerCase();
                              return subId === personnelId || (currentPersonnelName && subName === currentPersonnelName);
                            })
                            .map((ps) => ps?.park_name || ps?.park_id)
                            .filter(Boolean);
                        }
                        if (
                          parkItems.length === 0 &&
                          (lr.substitute_personnel_id === personnelId || lr.delegationPlan?.singleDelegateUserId === personnelId) &&
                          Array.isArray(lr.substitute_parks)
                        ) {
                          parkItems = lr.substitute_parks
                            .map((p) => (typeof p === "string" ? p : (p?.name || p?.park_name || p?.id)))
                            .filter(Boolean);
                        }
                        return {
                          id: `leave_as_sub_${lr.id}_${personnelId}`,
                          original_personnel_name: owner?.name || lr.personnel_name || "Personel",
                          start_date: (lr.start_date || "").slice(0, 10),
                          end_date: (getEffectiveDelegationEnd(lr) || "").slice(0, 10),
                          parks: parkItems,
                          reason_type: lr.reason_type,
                        };
                      })
                      .sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));
                    const asSubstituteFromLeaves = asSubstituteFromLeavesAll.filter((sub) => {
                      const start = String(sub.start_date || "").slice(0, 10);
                      const end = String(sub.end_date || "").slice(0, 10);
                      return Boolean(start && end && end >= todayStr);
                    });
                    const asSubstituteHistory = asSubstituteFromLeavesAll.filter((sub) => {
                      const end = String(sub.end_date || "").slice(0, 10);
                      return Boolean(end && end < todayStr);
                    });

                    const asSubstituteFromActiveSubstitutions = (visibleAsSubstitute || [])
                      .map((sub) => {
                        const relatedLeave = (leaveRequests || []).find((lr) => lr.id === (sub.leave_request_id || sub.leave_id));
                        const start = (relatedLeave?.start_date || sub.start_date || "").slice(0, 10);
                        const effectiveEnd = (getEffectiveDelegationEnd(relatedLeave) || sub.end_date || "").slice(0, 10);
                        return {
                          ...sub,
                          start_date: start || sub.start_date,
                          end_date: effectiveEnd || sub.end_date,
                        };
                      })
                      .filter((sub) => {
                        const start = String(sub.start_date || "").slice(0, 10);
                        const end = String(sub.end_date || "").slice(0, 10);
                        return Boolean(start && end && end >= todayStr);
                      });

                    const asSubstitute = asSubstituteFromLeaves.length > 0 ? asSubstituteFromLeaves : asSubstituteFromActiveSubstitutions;
                    const leaveEnd = (lr) => (lr.end_date || lr.return_date || lr.start_date || '').slice(0, 10);
                    const vekaletVerdigimFromLeaves = (leaveRequests || [])
                      .filter(
                        (lr) => lr.personnel_id === personnelId && lr.status === 'approved' && (lr.substitute_personnel_id || (lr.park_substitutes || []).length > 0) && lr.start_date && lr.end_date && leaveEnd(lr) >= todayStr
                      )
                      .sort((a, b) => ((a.start_date || '').localeCompare(b.start_date || '')));
                    const onLeave = vekaletVerdigimFromLeaves.length > 0 ? vekaletVerdigimFromLeaves.map((lr) => ({
                      id: lr.id,
                      leave_request_id: lr.id,
                      start_date: (lr.start_date || '').slice(0, 10),
                      end_date: (lr.end_date || lr.return_date || lr.start_date || '').slice(0, 10),
                      reason_type: lr.reason_type,
                      parks: lr.substitute_parks || lr.park_substitutes || [],
                      substitute_personnel_id: lr.substitute_personnel_id,
                      substitute_personnel_name: lr.substitute_personnel_name,
                      original_personnel_id: lr.personnel_id,
                    })) : (mySubstitutions.on_leave || []);
                    return (
                      <div className="space-y-3">
                        <details className="group">
                          <summary className="cursor-pointer list-none">
                            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 hover:shadow-md transition-shadow">
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="bg-amber-500 rounded-full p-2">
                                      <Users className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h3 className="font-semibold text-amber-900">🤝 Vekalet Aldığım</h3>
                                      <p className="text-xs text-amber-700">
                                        {`Aktif: ${asSubstitute.length} • Geçmiş: ${asSubstituteHistory.length}`}
                                      </p>
                                    </div>
                                  </div>
                                  <ChevronDown className="h-5 w-5 text-amber-600 group-open:rotate-180 transition-transform" />
                                </div>
                              </CardContent>
                            </Card>
                          </summary>
                          <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            {asSubstitute.length === 0 ? (
                              <p className="text-sm text-amber-800">Aktif vekalet yok.</p>
                            ) : (
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {asSubstitute.map((sub) => (
                                  <div key={sub.id} className="bg-white rounded border border-amber-200 p-2 text-sm">
                                    <div className="font-semibold text-gray-800">{sub.original_personnel_name}</div>
                                    <div className="text-xs text-gray-600">
                                      {new Date(sub.start_date).toLocaleDateString('tr-TR')} - {new Date(sub.end_date).toLocaleDateString('tr-TR')}
                                    </div>
                                    {sub.parks?.length > 0 && (
                                      <div className="text-[11px] text-gray-500 mt-1">
                                        {sub.parks.length} park
                                        <div className="mt-1 text-[11px] text-amber-800 leading-relaxed space-y-0.5">
                                          {(sub.parks || [])
                                            .map((p) => (typeof p === 'string' ? p : p?.name))
                                            .filter(Boolean)
                                            .map((pName, idx) => (
                                              <div key={`${sub.id}_park_${idx}`}>🌳 {pName}</div>
                                            ))}
                                        </div>
                                      </div>
                                    )}
                                    {sub.reason_type && (
                                      <div className="text-[11px] text-amber-700 mt-1">{sub.reason_type}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {asSubstituteHistory.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-amber-300">
                                <p className="text-xs font-semibold text-amber-900 mb-2">
                                  📚 Geçmiş Vekaletler ({asSubstituteHistory.length})
                                </p>
                                <div className="space-y-2 max-h-44 overflow-y-auto">
                                  {asSubstituteHistory.map((sub) => (
                                    <div key={`${sub.id}_history`} className="bg-amber-50/70 rounded border border-amber-200 p-2 text-sm">
                                      <div className="font-semibold text-gray-800">{sub.original_personnel_name}</div>
                                      <div className="text-xs text-gray-600">
                                        {new Date(sub.start_date).toLocaleDateString('tr-TR')} - {new Date(sub.end_date).toLocaleDateString('tr-TR')}
                                      </div>
                                      {sub.parks?.length > 0 && (
                                        <div className="mt-1 text-[11px] text-amber-800 leading-relaxed space-y-0.5">
                                          {(sub.parks || [])
                                            .map((p) => (typeof p === 'string' ? p : p?.name))
                                            .filter(Boolean)
                                            .map((pName, idx) => (
                                              <div key={`${sub.id}_history_park_${idx}`}>🌳 {pName}</div>
                                            ))}
                                        </div>
                                      )}
                                      {sub.reason_type && (
                                        <div className="text-[11px] text-amber-700 mt-1">{sub.reason_type}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </details>

                        <details className="group">
                          <summary className="cursor-pointer list-none">
                            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 hover:shadow-md transition-shadow">
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="bg-green-500 rounded-full p-2">
                                      <UserCheck className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h3 className="font-semibold text-green-900">🧾 Vekalet Verdiğim</h3>
                                      <p className="text-xs text-green-700">
                                        {onLeave.length > 0 ? `${onLeave.length} kayıt` : 'Aktif vekalet yok'}
                                      </p>
                                    </div>
                                  </div>
                                  <ChevronDown className="h-5 w-5 text-green-600 group-open:rotate-180 transition-transform" />
                                </div>
                              </CardContent>
                            </Card>
                          </summary>
                          <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                            {onLeave.length === 0 ? (
                              <p className="text-sm text-green-800">Aktif vekalet yok.</p>
                            ) : (
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {onLeave.map((sub) => {
                                  const leave = (leaveRequests || []).find((lr) => lr.id === (sub.leave_request_id || sub.id));
                                  let parkVekilLines = [];
                                  if (leave) {
                                    const psList = leave.park_substitutes || [];
                                    if (psList.length > 0) {
                                      parkVekilLines = psList.map((p) => {
                                        const parkName = (p.park_name || p.park_id || '').trim();
                                        const vekil = (p.substitute_name || p.substitute_personnel_name || leave.substitute_personnel_name || 'Vekil').trim();
                                        return parkName ? `${parkName} → ${vekil}` : null;
                                      }).filter(Boolean);
                                    }
                                    if (parkVekilLines.length === 0 && (leave.substitute_parks || []).length > 0 && leave.substitute_personnel_name) {
                                      const vekilAdi = (leave.substitute_personnel_name || 'Vekil').trim();
                                      parkVekilLines = (leave.substitute_parks || []).map((p) => {
                                        const parkName = (typeof p === 'string' ? p : (p?.name || p?.park_name || p?.id || '')).trim();
                                        return parkName ? `${parkName} → ${vekilAdi}` : null;
                                      }).filter(Boolean);
                                    }
                                  }
                                  if (parkVekilLines.length === 0 && (sub.parks || []).length > 0) {
                                    parkVekilLines = (sub.parks || []).map((p) => {
                                      const parkName = typeof p === 'string' ? p : (p?.name || p?.park_name || '');
                                      const vekil = (p?.substitute_name || p?.substitute_personnel_name || sub?.substitute_personnel_name || '').trim();
                                      return parkName ? `${parkName} → ${vekil || 'Vekil'}` : null;
                                    }).filter(Boolean);
                                  }
                                  if (parkVekilLines.length === 0 && (leave?.substitute_personnel_id || leave?.substitute_personnel_name || sub.substitute_personnel_name)) {
                                    const vekilAdi = (leave?.substitute_personnel_name || sub.substitute_personnel_name || (personnel || []).find((p) => p.id === (leave?.substitute_personnel_id || sub.substitute_personnel_id))?.name || 'Vekil').trim();
                                    const currentUserParks = (parks || []).filter((p) => (p.assigned_personnel_ids || []).includes(personnelId));
                                    if (currentUserParks.length > 0) {
                                      parkVekilLines = currentUserParks.map((p) => `${p.name || p.park_name || p.id || ''} → ${vekilAdi}`).filter(Boolean);
                                    }
                                    if (parkVekilLines.length === 0) parkVekilLines.push(`Vekil: ${vekilAdi}`);
                                  }
                                  const isFormenOwner = isFormen;
                                  const vekilDisplayName = (
                                    leave?.substitute_personnel_name ||
                                    sub?.substitute_personnel_name ||
                                    (personnel || []).find((p) => p.id === (leave?.substitute_personnel_id || sub?.substitute_personnel_id))?.name ||
                                    "Vekil"
                                  ).trim();
                                  const formenRegionNames = isFormenOwner
                                    ? getFormenDelegationRegionNames({
                                        ...sub,
                                        original_personnel_id: sub?.original_personnel_id || personnelId,
                                        parks: Array.isArray(sub?.parks) ? sub.parks : [],
                                      })
                                    : [];
                                  const formenRegionLine = isFormenOwner && formenRegionNames.length > 0
                                    ? `${formenRegionNames.join(", ")} → ${vekilDisplayName}`
                                    : null;
                                  const effectiveDelegationEnd = getEffectiveDelegationEnd(leave);
                                  const hasApprovedEarlyReturn = String(leave?.return_request?.status || "").trim().toLowerCase() === "approved" && Boolean(leave?.actual_return_date || leave?.return_request?.actual_return_date);
                                  const hasDelegationSummary = Boolean(leave && hasApprovedEarlyReturn && effectiveDelegationEnd);
                                  const delegationSummaryText = hasDelegationSummary
                                    ? `${formatDateWithComma(leave?.start_date)}-${formatDateWithComma(leave?.end_date)} tarihleri arası yıllık izniniz vardı ${formatDateWithComma(effectiveDelegationEnd)} tarihine kadar parklarınıza bakılan vekalet :`
                                    : null;
                                  return (
                                    <div key={sub.id} className="bg-white rounded border border-green-200 p-2 text-sm">
                                      <div className="text-xs text-gray-600">
                                        {new Date(sub.start_date).toLocaleDateString('tr-TR')} - {new Date(sub.end_date).toLocaleDateString('tr-TR')}
                                      </div>
                                      {delegationSummaryText && (
                                        <div className="text-[11px] text-green-800 mt-1 bg-green-50 border border-green-200 rounded px-2 py-1 leading-relaxed">
                                          {delegationSummaryText}
                                        </div>
                                      )}
                                      {!isFormenOwner && parkVekilLines.length > 0 && (
                                        <div className="text-[11px] text-green-700 mt-1 leading-relaxed space-y-0.5">
                                          {parkVekilLines.map((line, idx) => (
                                            <div key={idx}>🌳 {line}</div>
                                          ))}
                                        </div>
                                      )}
                                      {isFormenOwner && (
                                        <div className="text-[11px] text-green-700 mt-1 leading-relaxed">
                                          {formenRegionLine ? (
                                            <div>🗺️ {formenRegionLine}</div>
                                          ) : (
                                            <div>🗺️ Bölgeler → {vekilDisplayName}</div>
                                          )}
                                        </div>
                                      )}
                                      {sub.reason_type && (
                                        <div className="text-[11px] text-gray-500 mt-1">{sub.reason_type}</div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </details>
                      </div>
                    );
                  })()}

                  {/* Mevcut İzin Talepleri */}
                  {(() => {
                    const myRequests = leaveRequests.filter(lr => lr.personnel_id === personnelId);
                    const pendingRequests = myRequests.filter(lr => lr.status === "in_progress");
                    const approvedRequests = myRequests.filter(lr => lr.status === "approved");
                    const rejectedRequests = myRequests.filter(lr => lr.status === "rejected");
                    
                    // Filtrelenmiş talepler
                    const filteredRequests = leaveStatusFilter === "all" ? myRequests :
                      leaveStatusFilter === "pending" ? pendingRequests :
                      leaveStatusFilter === "approved" ? approvedRequests :
                      rejectedRequests;

                    if (myRequests.length === 0) {
                      return (
                        <Card className="p-8 text-center">
                          <div className="text-gray-400 mb-4">
                            <Calendar className="h-16 w-16 mx-auto opacity-50" />
                          </div>
                          <p className="text-gray-500 mb-4">Henüz izin talebiniz bulunmuyor.</p>
                          <Button 
                            onClick={() => {
                              if (isDelegatorReadOnly && !isGolgeAdmin) {
                                toast.warning(READ_ONLY_MESSAGE);
                                return;
                              }
                              if (isGolgeAdmin) return;
                              setIsLeaveRequestDialogOpen(true);
                            }}
                            disabled={isDelegatorReadOnly}
                            variant="outline"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            İlk İzin Talebinizi Oluşturun
                          </Button>
                        </Card>
                      );
                    }

                    return (
                      <div className="space-y-4">
                        {/* İstatistik Kartları - Tıklanabilir */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <Card 
                            className={`bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 cursor-pointer transition-all hover:shadow-md ${leaveStatusFilter === "all" ? "ring-2 ring-blue-500" : ""}`}
                            onClick={() => setLeaveStatusFilter("all")}
                          >
                            <CardContent className="p-4 text-center">
                              <Calendar className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                              <p className="text-2xl font-bold text-blue-700">{myRequests.length}</p>
                              <p className="text-sm text-blue-600">Tümü</p>
                            </CardContent>
                          </Card>
                          <Card 
                            className={`bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 cursor-pointer transition-all hover:shadow-md ${leaveStatusFilter === "pending" ? "ring-2 ring-orange-500" : ""}`}
                            onClick={() => setLeaveStatusFilter("pending")}
                          >
                            <CardContent className="p-4 text-center">
                              <Clock className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                              <p className="text-2xl font-bold text-orange-700">{pendingRequests.length}</p>
                              <p className="text-sm text-orange-600">Bekleyen</p>
                            </CardContent>
                          </Card>
                          <Card 
                            className={`bg-gradient-to-br from-green-50 to-green-100 border-green-200 cursor-pointer transition-all hover:shadow-md ${leaveStatusFilter === "approved" ? "ring-2 ring-green-500" : ""}`}
                            onClick={() => setLeaveStatusFilter("approved")}
                          >
                            <CardContent className="p-4 text-center">
                              <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
                              <p className="text-2xl font-bold text-green-700">{approvedRequests.length}</p>
                              <p className="text-sm text-green-600">Onaylanan</p>
                            </CardContent>
                          </Card>
                          <Card 
                            className={`bg-gradient-to-br from-red-50 to-red-100 border-red-200 cursor-pointer transition-all hover:shadow-md ${leaveStatusFilter === "rejected" ? "ring-2 ring-red-500" : ""}`}
                            onClick={() => setLeaveStatusFilter("rejected")}
                          >
                            <CardContent className="p-4 text-center">
                              <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                              <p className="text-2xl font-bold text-red-700">{rejectedRequests.length}</p>
                              <p className="text-sm text-red-600">Reddedilen</p>
                            </CardContent>
                          </Card>
                        </div>

                        {/* İzin Talepleri Listesi - Filtrelenmiş (Açılır) */}
                        {(() => {
                          const hasPending = filteredRequests.some(r => r.status === "in_progress");
                          return (
                            <details className="group" open={hasPending}>
                              <summary className="cursor-pointer list-none">
                                <Card className="border-gray-200 hover:shadow-md transition-shadow">
                                  <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                      <CardTitle className="text-lg">
                                        {leaveStatusFilter === "all" ? "Tüm İzin Taleplerim" : 
                                         leaveStatusFilter === "pending" ? "Bekleyen Taleplerim" :
                                         leaveStatusFilter === "approved" ? "Onaylanan Taleplerim" :
                                         "Reddedilen Taleplerim"} ({filteredRequests.length})
                                      </CardTitle>
                                      <ChevronDown className="h-5 w-5 text-gray-500 group-open:rotate-180 transition-transform" />
                                    </div>
                                  </CardContent>
                                </Card>
                              </summary>
                              <div className="mt-2">
                                <Card>
                                  <CardContent className="pt-4">
                                    <div className="flex justify-end mb-3">
                                      {leaveStatusFilter !== "all" && (
                                        <Button 
                                          variant="outline" 
                                          size="sm"
                                          onClick={() => setLeaveStatusFilter("all")}
                                        >
                                          Filtreyi Temizle
                                        </Button>
                                      )}
                                    </div>
                                    {filteredRequests.length === 0 ? (
                                      <div className="text-center py-8 text-gray-500">
                                        <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                        <p>Bu kategoride izin talebi bulunmuyor.</p>
                                      </div>
                                    ) : (
                                    <div className="space-y-3">
                                      {filteredRequests
                                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                        .map(request => {
                                  const statusColors = {
                                    'in_progress': 'bg-orange-100 text-orange-800 border-orange-300',
                                    'approved': 'bg-green-100 text-green-800 border-green-300',
                                    'rejected': 'bg-red-100 text-red-800 border-red-300'
                                  };
                                  const statusLabels = {
                                    'in_progress': '⏳ Bekliyor',
                                    'approved': '✅ Onaylandı',
                                    'rejected': '❌ Reddedildi'
                                  };
                                  const reviewModeLabels = {
                                    special_review: 'Özel Değerlendirme',
                                    exception_review: 'İstisna Gerektiren Acil',
                                    auto_reject: 'Kural Dışı',
                                  };
                                  const reviewModeClasses = {
                                    special_review: 'bg-blue-50 text-blue-700 border-blue-300',
                                    exception_review: 'bg-amber-50 text-amber-700 border-amber-300',
                                    auto_reject: 'bg-red-50 text-red-700 border-red-300',
                                  };
                                  const typeIcons = {
                                    'Yıllık İzin': '🏖️',
                                    'Sosyal İzin': '👨‍👩‍👧‍👦',
                                    'Rapor': '🏥',
                                    'Diğer': '📋'
                                  };
                                  const effectiveReturnDate = request.return_date || getNextWorkday(request.end_date);
                                  const returnDateText = effectiveReturnDate
                                    ? new Date(effectiveReturnDate).toLocaleDateString('tr-TR')
                                    : '-';
                                  const showReviewBadge = request.review_mode && request.review_mode !== 'normal';

                                  return (
                                    <div 
                                      key={request.id} 
                                      className={`p-4 rounded-lg border-2 ${statusColors[request.status] || 'bg-gray-100 border-gray-300'}`}
                                    >
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xl">{typeIcons[request.reason_type || request.leave_type] || '📋'}</span>
                                            <span className="font-semibold">{request.reason_type || request.leave_type || 'İzin'}</span>
                                            <Badge variant="outline" className={statusColors[request.status]}>
                                              {statusLabels[request.status]}
                                            </Badge>
                                            {showReviewBadge && (
                                              <Badge variant="outline" className={reviewModeClasses[request.review_mode] || 'bg-gray-50 text-gray-700 border-gray-300'}>
                                                {reviewModeLabels[request.review_mode] || request.review_mode}
                                              </Badge>
                                            )}
                                          </div>
                                          <p className="text-sm text-gray-600">
                                            📅 {new Date(request.start_date).toLocaleDateString('tr-TR')} - {new Date(request.end_date).toLocaleDateString('tr-TR')}
                                            {/* Netlik: "(8 gün)" takvim aralığıdır; yıllık izinden yalnız hafta sonu/
                                                tatil hariç İŞ GÜNLERİ düşer. Personel 8 gün düşecek sanmasın. Yıllık
                                                izinde takvim günü ile iş gününü BİRLİKTE ve açıkça göster; diğer
                                                türlerde/eşitlikte sade "(N gün)". Karar mantığı tek noktada:
                                                leaveUtils.shouldShowAnnualWorkdayHint (slug kanonikleştirme + memur
                                                eşitlik gizlemesi, test edilebilir). */}
                                            {shouldShowAnnualWorkdayHint(request) ? (
                                              <span className="ml-2 font-medium">
                                                ({request.total_days} takvim günü ·{' '}
                                                <span className="text-emerald-700">yıllık izinden {request.workdays_used} iş günü düşer</span>
                                                {' '}· hafta sonu/tatil hariç)
                                              </span>
                                            ) : (
                                              <span className="ml-2 font-medium">({request.total_days} gün)</span>
                                            )}
                                            {request.status === 'approved' && (
                                              <span className="ml-2 text-green-700 font-medium">| İşbaşı: {returnDateText}</span>
                                            )}
                                          </p>
                                          {request.status === 'in_progress' && (() => {
                                            // Talep sahibi, kartı açmadan izninin ŞU AN kimde beklediğini görsün
                                            // (Formen→BM→MY→Müdür akışında sıradaki onaycı). İsim önce kendi
                                            // personel listesinden, yoksa backend'in denormalize ettiği
                                            // current_approver_name'den çözülür.
                                            const appr = personnel.find(p => p.id === request.current_approver_id);
                                            const apprName = appr?.name || request.current_approver_name || '';
                                            if (!apprName) return null;
                                            const roleMap = { mudur: 'Müdür', mudur_yardimcisi: 'MY', bolge_muhendisi: 'BM', formen: 'Formen' };
                                            const roleLbl = roleMap[appr?.role || request.current_approver_role] || '';
                                            return (
                                              <p className="text-sm text-orange-700 font-medium mt-1">
                                                ⏳ Şu an: {roleLbl ? `${roleLbl} ` : ''}{apprName}'da
                                              </p>
                                            );
                                          })()}
                                          {request.reason && (
                                            <p className="text-sm text-gray-500 mt-1">📝 {request.reason}</p>
                                          )}
                                          {request.rejection_reason && (
                                            <p className="text-sm text-red-600 mt-1">❌ Red sebebi: {request.rejection_reason}</p>
                                          )}
                                          {request.status === 'approved' && request.reason_type === 'Yıllık İzin' && (() => {
                                            const startDate = new Date(request.start_date);
                                            const endDate = new Date(request.end_date);
                                            let weekdays = 0;
                                            let current = new Date(startDate);
                                            while (current <= endDate) {
                                              const dayOfWeek = current.getDay();
                                              if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                                                weekdays++;
                                              }
                                              current.setDate(current.getDate() + 1);
                                            }
                                            const toValidNumber = (value) => {
                                              const n = Number(value);
                                              return Number.isFinite(n) ? n : null;
                                            };
                                            const toPositiveNumber = (value) => {
                                              const n = Number(value);
                                              return Number.isFinite(n) && n > 0 ? n : null;
                                            };
                                            const plannedAnnualDays = toPositiveNumber(request?.annual_days_planned) ?? toPositiveNumber(request?.workdays_used) ?? weekdays;
                                            const earlyReturnDate = request?.actual_return_date || request?.return_request?.actual_return_date;
                                            const returnRequestStatus = String(request?.return_request?.status || '').trim().toLowerCase();
                                            const hasApprovedEarlyReturn = Boolean(earlyReturnDate) && returnRequestStatus === 'approved';
                                            const effectiveAnnualDays = hasApprovedEarlyReturn
                                              ? (toPositiveNumber(request?.leave_days_used_actual) ?? plannedAnnualDays)
                                              : plannedAnnualDays;
                                            const holidayWeekdayCount = Math.max(0, weekdays - plannedAnnualDays);
                                            const isEarlyReturnAdjusted =
                                              hasApprovedEarlyReturn &&
                                              effectiveAnnualDays < plannedAnnualDays;
                                            if (!isEarlyReturnAdjusted) return null;
                                            const dateRangeLabel = `${startDate.toLocaleDateString('tr-TR').replaceAll('.', ',')}-${endDate.toLocaleDateString('tr-TR').replaceAll('.', ',')}`;
                                            return (
                                              <p className="text-xs text-amber-800 mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-1 leading-relaxed">
                                                {`${dateRangeLabel} tarihleri arası ${plannedAnnualDays} gün yıllık${holidayWeekdayCount > 0 ? ` ${holidayWeekdayCount} gün bayram tatili` : ''} olan izin talebiniz ${(earlyReturnDate ? new Date(earlyReturnDate).toLocaleDateString('tr-TR').replaceAll('.', ',') : '-')} tarihinde erken işbaşı yaptığınız için ${effectiveAnnualDays} gün olarak yıllık izin talebinden düşülmüştür.`}
                                              </p>
                                            );
                                          })()}
                                          {request.review_notice && showReviewBadge && request.status !== 'approved' && (
                                            <p className="text-xs text-blue-700 mt-2 whitespace-pre-line">
                                              {request.review_notice}
                                            </p>
                                          )}
                                          {showReviewBadge && request.status === 'in_progress' && (() => {
                                            // Özel değerlendirmeyi kimin yaptığını çalışana göster.
                                            // İsim önce kendi personel listesinden, yoksa backend'in
                                            // denormalize ettiği current_approver_name'den çözülür.
                                            const reviewer = personnel.find(p => p.id === request.current_approver_id);
                                            const reviewerName = reviewer?.name || request.current_approver_name || '';
                                            if (!reviewerName) return null;
                                            const roleMap = {
                                              mudur: 'Müdür',
                                              mudur_yardimcisi: 'MY',
                                              bolge_muhendisi: 'Bölge Mühendisi',
                                              formen: 'Formen',
                                            };
                                            const roleLbl = roleMap[reviewer?.role || request.current_approver_role] || '';
                                            return (
                                              <p className="text-xs text-blue-800 mt-1 font-medium">
                                                🔎 Değerlendiren: {roleLbl ? `${roleLbl} ` : ''}{reviewerName}
                                              </p>
                                            );
                                          })()}
                                          {request.status === 'approved' && (() => {
                                            const parkBasedDelegates = (Array.isArray(request.park_substitutes) ? request.park_substitutes : [])
                                              .map((ps) => {
                                                const delegateName =
                                                  ps?.substitute_name ||
                                                  ps?.substitute_personnel_name ||
                                                  personnel.find((p) => p.id === ps?.substitute_id)?.name ||
                                                  personnel.find((p) => p.id === ps?.substitute_personnel_id)?.name ||
                                                  null;
                                                const parkName = ps?.park_name || 'Park';
                                                return delegateName ? { parkName, delegateName } : null;
                                              })
                                              .filter(Boolean);

                                            const singleDelegateName =
                                              request.substitute_personnel_name ||
                                              personnel.find((p) => p.id === request.substitute_personnel_id)?.name ||
                                              null;

                                            if (!singleDelegateName && parkBasedDelegates.length === 0) return null;

                                            return (
                                              <div className="mt-2 rounded-lg border-l-4 border-emerald-500 bg-emerald-50/80 px-3 py-2">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-[11px]">
                                                    👤
                                                  </span>
                                                  <p className="text-xs font-semibold tracking-wide text-emerald-900 uppercase">
                                                    Kesinlesen Vekil Bilgisi
                                                  </p>
                                                </div>
                                                {parkBasedDelegates.length > 0 ? (
                                                  <div className="space-y-1 pl-7">
                                                    {parkBasedDelegates.map((d, idx) => (
                                                      <p key={`${d.parkName}_${idx}`} className="text-xs text-emerald-800">
                                                        {d.parkName}: <span className="font-semibold">{d.delegateName}</span>
                                                      </p>
                                                    ))}
                                                  </div>
                                                ) : (
                                                  <p className="text-xs text-emerald-800 pl-7">
                                                    Vekil: <span className="font-semibold">{singleDelegateName}</span>
                                                  </p>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                        <div className="text-right text-xs text-gray-500">
                                          {new Date(request.created_at).toLocaleDateString('tr-TR')}
                                        </div>
                                      </div>
                                      
                                      {/* Onay Geçmişi (Talep sahibi: tüm geçmiş + mevcut aşama) */}
                                      {request.approval_history && request.approval_history.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                          {(() => {
                                            const isOwner = personnelId === request.personnel_id;
                                            const historyToShow = isOwner
                                              ? request.approval_history
                                              : filterApprovalHistory(
                                                  request.approval_history,
                                                  userRole,
                                                  request.status,
                                                  personnelId,
                                                  request.personnel_id
                                                );
                                            const last = historyToShow[historyToShow.length - 1];
                                            const cleanComment = last?.comment
                                              ? last.comment.replace(/^RED ÖNERİSİ:\s*/, '')
                                              : '';
                                            const currentApprover = personnel.find(p => p.id === request.current_approver_id);
                                            return (
                                              <>
                                                <p className="text-xs text-gray-500 mb-2">İşlem Geçmişi:</p>
                                                <div className="flex flex-wrap gap-2">
                                                  {historyToShow.map((history, idx) => (
                                                    <Badge
                                                      key={idx}
                                                      variant="outline"
                                                      className={history.action === 'approved' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}
                                                    >
                                                      {history.approver_name}
                                                    </Badge>
                                                  ))}
                                                </div>
                                                {/* "Şu an incelemede" bilgisi kartın üstünde ("⏳ Şu an: …")
                                                    zaten gösteriliyor; tekrar önlemek için burada kaldırıldı. */}
                                                {['approved', 'rejected', 'cancelled'].includes(request.status) && (
                                                  <div className="mt-3">
                                                    <p className="text-xs text-gray-500 mb-2">
                                                      Son Onay Notu{last?.approver_name ? ` (${last.approver_name})` : ''}:
                                                    </p>
                                                    {cleanComment ? (
                                                      <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-2">
                                                        {cleanComment}
                                                      </div>
                                                    ) : (
                                                      <div className="text-xs text-gray-400">Not girilmedi.</div>
                                                    )}
                                                  </div>
                                                )}
                                              </>
                                            );
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  );
                                        })}
                                    </div>
                                    )}
                                  </CardContent>
                                </Card>
                              </div>
                            </details>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* İZİN YÖNETİMİ - Yeni Geliştirilmiş Panel - Yöneticiler + gölge admin (görüntüleme) */}
              {(isAdmin || isMudur || isFormen || isBolgeMuhendisi || isMudurYardimcisi || isGolgeAdmin) && (
                <LeaveManagementPanel
                  leaveRequests={leaveRequests}
                  personnel={personnel}
                  regions={regions}
                  parks={parks}
                  personnelId={personnelId}
                  userRole={userRole}
                  filterApprovalHistory={filterApprovalHistory}
                  mudurSocialReportOverrideEnabled={systemSettings?.mudur_social_report_override === true}
                  isReadOnly={isDelegatorReadOnly || isGolgeAdmin}
                  onActionClick={(req, action) => {
                    const canBypassReadOnly =
                      String(req?.current_approver_id || "") === String(personnelId || "");
                    if ((isDelegatorReadOnly || isGolgeAdmin) && !canBypassReadOnly) {
                      if (!isGolgeAdmin) toast.warning(READ_ONLY_MESSAGE);
                      return;
                    }
                    setSelectedLeaveRequest(req);
                    setLeaveAction(action);
                    setIsLeaveActionDialogOpen(true);
                  }}
                  onUpdate={async () => {
                    await fetchLeaveRequests();
                    await fetchPersonnel({ force: true, silent: true });
                    await fetchAllActiveSubstitutions();
                  }}
                  requestViewMode={leaveRequestViewMode}
                  onRequestViewModeChange={setLeaveRequestViewMode}
                  leaveTypeTabs={leaveTypeTabs}
                  setLeaveTypeTabs={setLeaveTypeTabs}
                  lowLeavePersonnel={stats.lowLeavePersonnel || []}
                  onOpenPersonnelDetails={(person, defaultTab) => {
                    setSelectedPersonnelForDetail(person);
                    setPersonnelDetailDefaultTab(defaultTab || 'leaves');
                    setPersonnelDetailDialogOpen(true);
                  }}
                  onEditPersonnel={(person) => {
                    // İzinler sekmesinde açılması için
                    setPersonnelFormInitialTab("leaves");
                    handlePersonnelEdit(person);
                  }}
                  onClearLeaveTestData={handleClearLeaveTestData}
                />
              )}
            </TabsContent>
          )}

          {/* BÖLGELER TAB */}
          {activeTab === "regions" && (
          <TabsContent value="regions" className="space-y-6">
            <RegionsTab 
              userRole={userRole}
              personnelId={personnelId}
              regions={(() => {
                const currentPersonnel = personnel.find(p => p.id === personnelId);
                const myRegionIds = currentPersonnel?.region_ids || [];
                
                if (isMudurYardimcisi) {
                  // Müdür Yardımcısı: kendi region_ids + kendine bağlı BM/Formen atamalı bölgeler
                  const mySubordinates = personnel.filter(
                    p =>
                      p.mudur_yardimcisi_id === personnelId &&
                      p.role !== "mudur_yardimcisi" &&
                      p.role !== "mudur"
                  );

                  const myBolgeMuhIds = mySubordinates
                    .filter(p => p.role === "bolge_muhendisi")
                    .map(p => p.id);

                  const myFormenIds = mySubordinates
                    .filter(p => p.role === "formen")
                    .map(p => p.id);

                  return regions.filter(r => {
                    if (myRegionIds.includes(r.id)) return true;
                    if (r.assigned_bolge_muhendisi_id && myBolgeMuhIds.includes(r.assigned_bolge_muhendisi_id)) return true;
                    if (Array.isArray(r.assigned_formen_ids) && r.assigned_formen_ids.some(fid => myFormenIds.includes(fid))) return true;
                    return false;
                  });
                } else if (isBolgeMuhendisi) {
                  // Bölge Mühendisi sadece kendi atandığı bölgeleri görür
                  return regions.filter(r => r.assigned_bolge_muhendisi_id === personnelId);
                } else if (isFormen) {
                  // Formen sadece kendi atandığı bölgeleri görür
                  return regions.filter(r => r.assigned_formen_ids && r.assigned_formen_ids.includes(personnelId));
                }
                // Admin tüm bölgeleri görür
                return regions;
              })()}
              personnel={personnel}
              parks={(() => {
                const currentPersonnel = personnel.find(p => p.id === personnelId);
                let myRegionIds = currentPersonnel?.region_ids || [];
                
                if (isMudurYardimcisi || isBolgeMuhendisi || isFormen) {
                  if (isMudurYardimcisi) {
                    const mySubordinates = personnel.filter(
                      p =>
                        p.mudur_yardimcisi_id === personnelId &&
                        p.role !== "mudur_yardimcisi" &&
                        p.role !== "mudur"
                    );
                    const myBolgeMuhIds = mySubordinates
                      .filter(p => p.role === "bolge_muhendisi")
                      .map(p => p.id);
                    const myFormenIds = mySubordinates
                      .filter(p => p.role === "formen")
                      .map(p => p.id);
                    const managedRegionIds = regions
                      .filter(r =>
                        (r.assigned_bolge_muhendisi_id && myBolgeMuhIds.includes(r.assigned_bolge_muhendisi_id)) ||
                        (Array.isArray(r.assigned_formen_ids) && r.assigned_formen_ids.some(fid => myFormenIds.includes(fid)))
                      )
                      .map(r => r.id);
                    myRegionIds = [...new Set([...myRegionIds, ...managedRegionIds])];
                  }

                  // Bölge Mühendisi için Formen'lerin bölgelerini de ekle
                  if (isBolgeMuhendisi) {
                    const myFormenler = personnel.filter(p => p.bolge_muhendisi_id === personnelId && p.role === "formen");
                    const formenRegionIds = myFormenler.flatMap(f => f.region_ids || []);
                    const managedRegionIds = regions
                      .filter(r => r.assigned_bolge_muhendisi_id === personnelId)
                      .map(r => r.id);
                    const regionFormenIds = regions
                      .filter(r => r.assigned_bolge_muhendisi_id === personnelId)
                      .flatMap(r => Array.isArray(r.assigned_formen_ids) ? r.assigned_formen_ids : []);
                    const regionFormenManagedRegionIds = regions
                      .filter(r => Array.isArray(r.assigned_formen_ids) && r.assigned_formen_ids.some(fid => regionFormenIds.includes(fid)))
                      .map(r => r.id);
                    myRegionIds = [...new Set([...myRegionIds, ...formenRegionIds, ...managedRegionIds, ...regionFormenManagedRegionIds])];
                  }

                  if (isFormen) {
                    const managedRegionIds = regions
                      .filter(r => Array.isArray(r.assigned_formen_ids) && r.assigned_formen_ids.includes(personnelId))
                      .map(r => r.id);
                    myRegionIds = [...new Set([...myRegionIds, ...managedRegionIds])];
                  }
                  return parks.filter(park => myRegionIds.includes(park.region_id));
                }
                return parks;
              })()}
              onDataChange={() => {
                // Bölge CRUD sonrası: force ile TTL cache'i atla. Bölge düzenleme
                // personel FK'lerini (bolge_muhendisi_region_id / formen_region_id)
                // de değiştirdiği için personel de tazelenir. Promise döndür ki
                // RegionsTab await edebilsin.
                return Promise.all([
                  fetchPersonnel({ force: true, silent: true }),
                  fetchParks({ force: true, silent: true }),
                  fetchRegions({ force: true }),
                ]);
              }}
            />
          </TabsContent>
          )}

          {/* Aşama C: Veri Yönetimi paneli artık yalnızca Yönetici Paneli'nde (/admin/veri-yonetimi).
              Kullanıcı panelinden tamamen kaldırıldı. */}

          {/* KONUM TAKİBİ TAB - Yöneticiler için */}
          {isManagerView && activeTab === "location-tracking" && (
            <TabsContent value="location-tracking" className="space-y-6">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-3xl font-bold">Görev Alanı Yönetimi</h2>
                <div className="flex gap-2 items-start">
                  <div className="flex flex-col items-center">
                    <Button 
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      variant={autoRefresh ? "default" : "outline"}
                      className={autoRefresh ? "bg-blue-600 hover:bg-blue-700" : ""}
                    >
                      {autoRefresh ? "🔄 Otomatik Yenileme: Açık" : "⏸️ Otomatik Yenileme: Kapalı"}
                    </Button>
                    <span className="text-xs text-gray-500 mt-1">
                      {autoRefresh ? "Her 30 sn güncellenir" : "Her 30 sn günceller"}
                    </span>
                  </div>
                  <Button 
                    onClick={() => setShowHeatMap(!showHeatMap)}
                    variant={showHeatMap ? "default" : "outline"}
                    className={showHeatMap ? "bg-orange-600 hover:bg-orange-700" : ""}
                  >
                    {showHeatMap ? "🔥 Isı Haritası: Açık" : "🌡️ Isı Haritası: Kapalı"}
                  </Button>
                  <Button 
                    onClick={() => setReportDialogOpen(true)} 
                    variant="outline"
                    className="bg-green-50 hover:bg-green-100"
                  >
                    📊 Rapor Al
                  </Button>
                  <Button onClick={() => fetchLocationLogs(selectedDate)} disabled={loadingLocations}>
                    {loadingLocations ? "Yükleniyor..." : "🔄 Manuel Yenile"}
                  </Button>
                </div>
              </div>

              {/* Tarih Seçici ve Bildirim Ayarları */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Tarih Seçimi</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2 items-start">
                      <div className="flex-1">
                        <DatePicker
                          value={tempSelectedDate}
                          onChange={setTempSelectedDate}
                          max={todayYmd()}
                          placeholder="Tarih seç"
                        />
                      </div>
                      <Button 
                        onClick={() => {
                          setSelectedDate(tempSelectedDate);
                          fetchLocationLogs(tempSelectedDate);
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        Ara
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Bildirim Ayarları */}
                <Card className="border-2 border-blue-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
                      Bildirim Ayarları
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Ana Anahtar */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">Bildirimler</p>
                        <p className="text-xs text-gray-600">Tüm bildirimleri aç/kapat</p>
                      </div>
                      <Button
                        size="sm"
                        variant={notificationPrefs.enabled ? "default" : "outline"}
                        onClick={() => updateNotificationPreferences({
                          ...notificationPrefs,
                          enabled: !notificationPrefs.enabled
                        })}
                        disabled={loadingPrefs}
                      >
                        {notificationPrefs.enabled ? "🔔 Açık" : "🔕 Kapalı"}
                      </Button>
                    </div>

                    {notificationPrefs.enabled && (
                      <>
                        {/* Uygulama İçi Bildirimler */}
                        <div className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={notificationPrefs.in_app_notifications}
                              onChange={(e) => updateNotificationPreferences({
                                ...notificationPrefs,
                                in_app_notifications: e.target.checked
                              })}
                              className="w-4 h-4"
                              disabled={loadingPrefs}
                            />
                            <span className="text-sm font-semibold">🔔 Uygulama İçi Bildirimler</span>
                          </div>
                        </div>
                        
                        {/* Email Bildirimleri */}
                        <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={notificationPrefs.email_notifications}
                              onChange={(e) => updateNotificationPreferences({
                                ...notificationPrefs,
                                email_notifications: e.target.checked
                              })}
                              className="w-4 h-4"
                              disabled={loadingPrefs}
                            />
                            <span className="text-sm">📧 Email Bildirimleri</span>
                          </div>
                        </div>

                        {/* SMS Bildirimleri */}
                        <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={notificationPrefs.sms_notifications}
                              onChange={(e) => updateNotificationPreferences({
                                ...notificationPrefs,
                                sms_notifications: e.target.checked
                              })}
                              className="w-4 h-4"
                              disabled={loadingPrefs}
                            />
                            <span className="text-sm">📱 SMS Bildirimleri</span>
                          </div>
                        </div>

                        {/* Bildirim Tipleri */}
                        <div className="pt-2 border-t">
                          <p className="text-xs font-semibold text-gray-600 mb-2">Bildirim Türleri:</p>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={notificationPrefs.notification_types.includes("out_of_park")}
                                onChange={(e) => {
                                  const types = e.target.checked
                                    ? [...notificationPrefs.notification_types, "out_of_park"]
                                    : notificationPrefs.notification_types.filter(t => t !== "out_of_park");
                                  updateNotificationPreferences({
                                    ...notificationPrefs,
                                    notification_types: types
                                  });
                                }}
                                className="w-4 h-4"
                                disabled={loadingPrefs}
                              />
                              <span className="text-xs">🚨 Park Dışı Uyarısı</span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Hızlı Filtre ve Ölçeklenebilir Liste Araçları */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Hızlı Filtreler</CardTitle>
                  <CardDescription>
                    Varsayılan görünüm aksiyon gerektiren personelleri öne çıkarır.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: "action_required", label: "Aksiyon Gerekenler" },
                      { id: "all", label: "Tümü" },
                      { id: "temporary", label: "Geçici Görev" },
                      { id: "out_of_park", label: "Park Dışı" },
                      { id: "no_location", label: "Konum Yok" },
                      { id: "stale", label: "Gecikmeli" }
                    ].map((f) => (
                      <Button
                        key={f.id}
                        size="sm"
                        variant={locationQuickFilter === f.id ? "default" : "outline"}
                        onClick={() => setLocationQuickFilter(f.id)}
                      >
                        {f.label}
                      </Button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-6 gap-2">
                    <Input
                      value={locationSearchQuery}
                      onChange={(e) => setLocationSearchQuery(e.target.value)}
                      placeholder="Ad, park, görev, formen, BM, MY ara..."
                      className="lg:col-span-2"
                    />
                    <SearchableSelect
                      value={locationFilterMY}
                      onChange={setLocationFilterMY}
                      options={locationFilterOptions.my}
                      allLabel="Tüm MY"
                      searchPlaceholder="Müdür yardımcısı ara..."
                      emptyText="Kayıt bulunamadı."
                    />
                    <SearchableSelect
                      value={locationFilterBM}
                      onChange={setLocationFilterBM}
                      options={locationFilterOptions.bm}
                      allLabel="Tüm BM"
                      searchPlaceholder="Bölge mühendisi ara..."
                      emptyText="Kayıt bulunamadı."
                    />
                    <SearchableSelect
                      value={locationFilterFormen}
                      onChange={setLocationFilterFormen}
                      options={locationFilterOptions.formen}
                      allLabel="Tüm Formen"
                      searchPlaceholder="Formen ara..."
                      emptyText="Kayıt bulunamadı."
                    />
                    <SearchableSelect
                      value={locationFilterPark}
                      onChange={setLocationFilterPark}
                      options={locationFilterOptions.park}
                      allLabel="Tüm Parklar"
                      searchPlaceholder="Park ara..."
                      emptyText="Park bulunamadı."
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 items-center">
                    <SearchableSelect
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                      value={locationSortBy}
                      onChange={(v) => setLocationSortBy(v)}
                      showAllOption={false}
                      placeholder="Sırala"
                      searchPlaceholder="Sıralama ara..."
                      options={[
                        { id: "status", name: "Duruma göre sırala" },
                        { id: "name", name: "İsme göre sırala" },
                        { id: "updated_desc", name: "Son güncellenen önce" },
                        { id: "updated_asc", name: "Eski güncellenen önce" },
                        { id: "park", name: "Park adına göre" },
                      ]}
                    />
                    <SearchableSelect
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                      value={String(locationStaleMinutes)}
                      onChange={(v) => setLocationStaleMinutes(Number(v) || 10)}
                      showAllOption={false}
                      placeholder="Gecikme"
                      searchPlaceholder="Gecikme ara..."
                      options={[
                        { id: "5", name: "Gecikmeli: 5 dk" },
                        { id: "10", name: "Gecikmeli: 10 dk" },
                        { id: "15", name: "Gecikmeli: 15 dk" },
                        { id: "30", name: "Gecikmeli: 30 dk" },
                      ]}
                    />
                    <Button size="sm" variant="outline" onClick={exportFilteredLocationCsv}>
                      CSV Dışa Aktar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setLocationQuickFilter("action_required");
                        setLocationSearchQuery("");
                        // Hiyerarşi filtreleri role-uygun varsayılana döner:
                        // giriş yapan kişi kendi kapsamında kalır (Formen→kendisi,
                        // BM→kendisi, MY→kendisi; Müdür/Başkan→Tümü).
                        setLocationFilterMY(isMudurYardimcisi ? personnelId : "all");
                        setLocationFilterBM(isBolgeMuhendisi ? personnelId : "all");
                        setLocationFilterFormen(isFormen ? personnelId : "all");
                        setLocationFilterPark("all");
                        setLocationSortBy("status");
                        setLocationStaleMinutes(10);
                      }}
                    >
                      Filtreyi Temizle
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {filteredLocationLogs.length} kayıt listeleniyor
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Harita */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                        Gerçek Zamanlı Konum Haritası
                      </CardTitle>
                      <CardDescription>
                        {filteredLocationLogs.length} park görevlisi görüntüleniyor
                        {showRouteFor && ` • 🛤️ ${showRouteFor.name} rotası gösteriliyor`}
                      </CardDescription>
                    </div>
                    {showRouteFor && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleHideRoute}
                      >
                        ✕ Rotayı Kapat
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {filteredLocationLogs.length === 0 && mapVisibleParks.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 max-w-md mx-auto">
                      <p className="font-medium">📍 Uygulanan filtrelerde kayıt bulunamadı</p>
                      <p className="text-sm mt-2">Park görevlileri mesai saatleri içinde otomatik konum güncellemesi yapar.</p>
                      <ul className="text-sm mt-4 text-left list-disc list-inside space-y-1">
                        <li><strong>Bugün</strong> seçiliyse: Park görevlisi olarak giriş yapılmış bir cihaz açık olmalı ve konum izni verilmiş olmalı.</li>
                        <li>Geçmiş tarih seçiliyse: O gün hiç konum gönderilmemiş olabilir.</li>
                        <li>Altınızda <strong>park görevlisi</strong> tanımlı mı kontrol edin (personel hiyerarşisi).</li>
                      </ul>
                    </div>
                  ) : (
                    <Suspense fallback={
                      <div style={{ height: '600px', width: '100%' }} className="flex items-center justify-center bg-gray-50 rounded">
                        <Loader2 className="h-12 w-12 animate-spin text-green-600" />
                      </div>
                    }>
                      <MapDisplay
                        filteredLocationLogs={filteredLocationLogs}
                        parks={mapVisibleParks}
                        parkStaffInfo={parkStaffInfoById}
                        fitSignature={mapFitSignature}
                        showRouteFor={showRouteFor}
                        routeData={routeData}
                        showHeatMap={showHeatMap}
                        formatTurkeyTime={formatTurkeyTime}
                      />
                    </Suspense>
                  )}
                </CardContent>
              </Card>

              {/* İstatistikler */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Toplam Görevli</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold">{locationSummary.total}</p>
                  </CardContent>
                </Card>
                
                <Card className="cursor-pointer" onClick={() => setLocationQuickFilter("all")}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Parkta Olanlar</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-green-600">
                      {locationSummary.inPark}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="cursor-pointer" onClick={() => setLocationQuickFilter("temporary")}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Geçici Görevde</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-orange-600">
                      {locationSummary.temporary}
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="cursor-pointer" onClick={() => setLocationQuickFilter("out_of_park")}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Park Dışı</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-red-600">
                      {locationSummary.out}
                    </p>
                  </CardContent>
                </Card>

                <Card className="cursor-pointer" onClick={() => setLocationQuickFilter("no_location")}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Konum Yok</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-slate-600">
                      {locationSummary.noLocation}
                    </p>
                  </CardContent>
                </Card>

                <Card className="cursor-pointer" onClick={() => setLocationQuickFilter("stale")}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Gecikmeli</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-amber-600">
                      {locationSummary.stale}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Geçici Görev Yönetimi - Tüm Yöneticiler için */}
              {(isFormen || isBolgeMuhendisi || isMudurYardimcisi || isMudur) && (
                <Card className="border-2 border-orange-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <span>🔶</span>
                          Geçici Görev Yönetimi
                        </CardTitle>
                        <CardDescription>
                          Park görevlilerinize geçici görev atayın (bu sürede &quot;park dışı&quot; bildirimi gitmez)
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => openTempAssignmentDialog(null)}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        ➕ Geçici Görev Ata
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      // Sadece kendi personeline ait geçici görevleri filtrele
                      const myAssignments = activeAssignments.filter(assignment => {
                        const assignmentPersonnel = personnel.find(p => p.id === assignment.personnel_id);
                        if (!assignmentPersonnel) return false;
                        
                        // Formen: sadece formen_id kendisine eşit olanlar
                        if (isFormen) {
                          return assignmentPersonnel.formen_id === personnelId;
                        } else if (isBolgeMuhendisi) {
                          return assignmentPersonnel.bolge_muhendisi_id === personnelId;
                        } else if (isMudurYardimcisi) {
                          return assignmentPersonnel.mudur_yardimcisi_id === personnelId;
                        } else if (isMudur) {
                          return true;
                        }
                        return false;
                      });
                      
                      return myAssignments.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500 mb-3">Aktif geçici görev yok</p>
                          <p className="text-sm text-gray-400">Yukarıdaki butona tıklayarak park görevlilerinize geçici görev atayabilirsiniz</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {myAssignments.map((assignment) => (
                          <div key={assignment.id} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h5 className="font-semibold">{assignment.personnel_name}</h5>
                                <p className="text-sm mt-1"><strong>Görev:</strong> {assignment.task_type}</p>
                                {assignment.task_description && (
                                  <p className="text-sm"><strong>Açıklama:</strong> {assignment.task_description}</p>
                                )}
                                {assignment.location && (
                                  <p className="text-sm"><strong>Konum:</strong> {assignment.location}</p>
                                )}
                                <p className="text-sm"><strong>Süre:</strong> {assignment.duration_hours} saat</p>
                                <p className="text-xs text-gray-600 mt-1">
                                  <strong>Başlangıç:</strong> {formatTurkeyTime(assignment.start_time)}
                                </p>
                                <p className="text-xs text-gray-600">
                                  <strong>Bitiş:</strong> {formatTurkeyTime(assignment.end_time)}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleCancelTempAssignment(assignment.id)}
                              >
                                İptal Et
                              </Button>
                            </div>
                          </div>
                          ))}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Detaylı Liste */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle>Park Görevlileri Detay Listesi</CardTitle>
                    <span className="text-xs text-muted-foreground">
                      Sayfa {Math.min(locationPage, locationTotalPages)} / {locationTotalPages} • Toplam {filteredLocationLogs.length}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {filteredLocationLogs.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Veri bulunamadı</p>
                  ) : (
                    <div className="space-y-3">
                      {paginatedLocationLogs.map((log, index) => {
                        const hasAssignment = log.temporary_assignment?.is_active;
                        const stale = isLocationStale(log) && !hasAssignment;
                        // Faz B-8 / Madde 2: konum alınamıyor / son konum ne kadar önce
                        const noLocationData = !hasAssignment && (log.no_location_data === true || (log.in_park !== true && log.in_park !== false));
                        const lastSeenMins = log.timestamp
                          ? Math.max(0, Math.round((Date.now() - new Date(log.timestamp).getTime()) / 60000))
                          : null;
                        // Faz B-8 / Madde 3: istemcinin bildirdiği konum izni/GPS durumu
                        const cstat = log.client_status;
                        const clientStatusLabel =
                          cstat === "permission_denied" ? "Konum izni kapalı olabilir"
                          : cstat === "gps_off" ? "GPS kapalı olabilir"
                          : (cstat === "unavailable") ? "Konum alınamıyor olabilir"
                          : null;
                        return (
                          <div key={index} className={`p-4 border rounded-lg hover:bg-gray-50 ${hasAssignment ? 'border-orange-300 bg-orange-50' : ''}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold">{log.personnel_name}</h4>
                                  {hasAssignment && (
                                    <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs font-semibold rounded">
                                      GEÇİCİ GÖREV
                                    </span>
                                  )}
                                  {stale && (
                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded">
                                      GECİKMELİ
                                    </span>
                                  )}
                                  {clientStatusLabel && (
                                    <span
                                      className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded"
                                      title={log.client_status_at ? `Bildirim: ${formatTurkeyTime(log.client_status_at)}` : undefined}
                                    >
                                      ⚠ {clientStatusLabel}
                                    </span>
                                  )}
                                </div>
                                {hasAssignment ? (
                                  <div className="mt-1">
                                    <p className="text-sm text-orange-700 font-medium">
                                      🟠 {log.temporary_assignment.task_type}
                                    </p>
                                    <p className="text-xs text-gray-600 mt-0.5">
                                      Süre: {log.temporary_assignment.duration_hours} saat | Onaylayan: {log.temporary_assignment.assigned_by_name || 'Formen'}
                                    </p>
                                    <p className="text-xs text-gray-600 mt-0.5">
                                      Bitiş: {formatTurkeyTime(log.temporary_assignment.end_time)}
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-600">
                                    {log.in_park === true ? '🟢 Parkta' : (log.in_park === false ? '🔴 Park Dışı' : '⚪ Konum verisi yok')}
                                    {log.park_name && ` - ${log.park_name}`}
                                  </p>
                                )}
                                {!hasAssignment && log.in_park === false && log.distance_from_nearest_park && (
                                  <p className="text-xs text-gray-500">
                                    En yakın parka {log.distance_from_nearest_park.toFixed(0)}m uzaklıkta
                                  </p>
                                )}
                                {/* Faz B-8 / Madde 2: konum alınamıyor / son konum bilgisi */}
                                {!hasAssignment && noLocationData && (
                                  <p className="text-xs text-red-600 mt-0.5">
                                    Konum alınamıyor — bugün konum verisi yok
                                    {clientStatusLabel ? ` (${clientStatusLabel.toLowerCase()})` : " (GPS/izin kapalı, internet yok veya uygulama kapalı olabilir)"}
                                  </p>
                                )}
                                {!hasAssignment && !noLocationData && stale && lastSeenMins != null && (
                                  <p className="text-xs text-amber-600 mt-0.5">
                                    Konum gecikmeli — son konum {lastSeenMins} dk önce alındı
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openLocationDetail(log)}
                                >
                                  Detay
                                </Button>
                                {/* Rotayı Göster/Gizle Butonu */}
                                {log.latitude && log.longitude && (
                                  <Button
                                    size="sm"
                                    variant={showRouteFor?.name === log.personnel_name ? "default" : "outline"}
                                    onClick={() => {
                                      if (showRouteFor?.name === log.personnel_name) {
                                        handleHideRoute();
                                      } else {
                                        handleShowRoute(log.personnel_id, log.personnel_name);
                                      }
                                    }}
                                    className={showRouteFor?.name === log.personnel_name ? "bg-blue-600 hover:bg-blue-700" : ""}
                                  >
                                    {showRouteFor?.name === log.personnel_name ? "🛤️ Rotayı Gizle" : "📍 Rotayı Göster"}
                                  </Button>
                                )}
                                {isFormen && !hasAssignment && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const foundPersonnel = personnel.find(p => p.name === log.personnel_name);
                                      if (foundPersonnel) openTempAssignmentDialog(foundPersonnel);
                                    }}
                                  >
                                    📋 Geçici Görev Ver
                                  </Button>
                                )}
                                <div className="text-right text-sm text-gray-500">
                                  <p className="text-xs">{formatTurkeyTime(log.timestamp)}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="pt-3 border-t flex items-center justify-between">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={locationPage <= 1}
                          onClick={() => setLocationPage((p) => Math.max(1, p - 1))}
                        >
                          Önceki
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          {(Math.min(locationPage, locationTotalPages) - 1) * LOCATION_PAGE_SIZE + 1}
                          {" - "}
                          {Math.min(Math.min(locationPage, locationTotalPages) * LOCATION_PAGE_SIZE, filteredLocationLogs.length)}
                          {" / "}
                          {filteredLocationLogs.length}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={locationPage >= locationTotalPages}
                          onClick={() => setLocationPage((p) => Math.min(locationTotalPages, p + 1))}
                        >
                          Sonraki
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <DraggableModal
                isOpen={locationDetailOpen}
                onClose={() => setLocationDetailOpen(false)}
                title={locationDetailData?.personnel_name ? `${locationDetailData.personnel_name} - Detay` : "Personel Detayı"}
                maxWidth="max-w-2xl"
              >
                <div className="space-y-4">
                  {locationDetailData && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="p-3 border rounded-md bg-slate-50">
                        <p className="text-xs text-muted-foreground">Durum</p>
                        <p className="font-semibold">
                          {locationDetailData?.temporary_assignment?.is_active
                            ? "🟠 Geçici Görevde"
                            : locationDetailData?.in_park === true
                              ? "🟢 Parkta"
                              : locationDetailData?.in_park === false
                                ? "🔴 Park Dışı"
                                : "⚪ Konum verisi yok"}
                        </p>
                      </div>
                      <div className="p-3 border rounded-md bg-slate-50">
                        <p className="text-xs text-muted-foreground">Son Güncelleme</p>
                        <p className="font-semibold">{formatTurkeyTime(locationDetailData?.timestamp)}</p>
                      </div>
                      <div className="p-3 border rounded-md">
                        <p className="text-xs text-muted-foreground">Formen</p>
                        <p className="font-medium">
                          {locationPersonNameById[locationPersonnelById[locationDetailData?.personnel_id]?.formen_id] || "-"}
                        </p>
                      </div>
                      <div className="p-3 border rounded-md">
                        <p className="text-xs text-muted-foreground">Bölge Mühendisi</p>
                        <p className="font-medium">
                          {locationPersonNameById[locationPersonnelById[locationDetailData?.personnel_id]?.bolge_muhendisi_id] || "-"}
                        </p>
                      </div>
                      <div className="p-3 border rounded-md">
                        <p className="text-xs text-muted-foreground">Müdür Yardımcısı</p>
                        <p className="font-medium">
                          {locationPersonNameById[locationPersonnelById[locationDetailData?.personnel_id]?.mudur_yardimcisi_id] || "-"}
                        </p>
                      </div>
                      <div className="p-3 border rounded-md">
                        <p className="text-xs text-muted-foreground">Ana Park</p>
                        <p className="font-medium">{locationDetailData?.park_name || "-"}</p>
                      </div>
                    </div>
                  )}

                  {locationDetailData?.temporary_assignment?.is_active && (
                    <div className="p-3 border border-orange-200 bg-orange-50 rounded-md">
                      <p className="font-semibold text-orange-800">Aktif Geçici Görev</p>
                      <p className="text-sm mt-1">{locationDetailData?.temporary_assignment?.task_type || "-"}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Bitiş: {formatTurkeyTime(locationDetailData?.temporary_assignment?.end_time)}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-semibold mb-2">Son 5 Konum Kaydı</p>
                    {loadingLocationDetail ? (
                      <p className="text-sm text-muted-foreground">Yükleniyor...</p>
                    ) : locationDetailLogs.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Kayıt bulunamadı.</p>
                    ) : (
                      <div className="space-y-2 max-h-72 overflow-auto pr-1">
                        {locationDetailLogs.map((item, idx) => (
                          <div key={`${item?.timestamp || idx}-${idx}`} className="p-2 border rounded-md text-sm">
                            <div className="flex items-center justify-between">
                              <span>{item?.in_park ? "🟢 Parkta" : "🔴 Park Dışı"}</span>
                              <span className="text-xs text-muted-foreground">{formatTurkeyTime(item?.timestamp)}</span>
                            </div>
                            {item?.park_name && (
                              <p className="text-xs text-muted-foreground mt-1">{item.park_name}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </DraggableModal>
            </TabsContent>
          )}

          {/* REVİZE YÖNETİMİ TAB */}
          {/* Revisions Tab - Refactored */}
          <RevisionsTab
            revisions={revisions}
            loadingRevisions={loadingRevisions}
            setRevisionDialogOpen={setRevisionDialogOpen}
            setSelectedRevision={setSelectedRevision}
            onDeleteRevision={(revision) => setRevisionToDelete(revision)}
            onDeleteCompletedRevisionHistory={handleDeleteCompletedRevisionHistory}
            userRole={userRole}
            parks={parks}
            personnel={personnel}
            personnelId={personnelId}
            regions={regions}
          />

          {/* MALZEME YÖNETİMİ TAB */}
          {activeTab === "materials" && (
          <TabsContent value="materials">
            <MaterialManagementTab
              userRole={userRole}
              personnelId={personnelId}
              materialRequests={materialRequests}
              loadingMaterials={loadingMaterials}
              personnel={personnel}
              regions={regions}
              parks={parks}
              isReadOnly={isGolgeAdmin}
              onCreateRequest={() => setShowMaterialRequestDialog(true)}
              onDeliverRequest={(request) => {
                setSelectedMaterialRequest(request);
                setShowMaterialDeliveryDialog(true);
              }}
              onRejectRequest={handleRejectMaterialRequest}
              onDeleteRequest={async (requestId) => {
                await axios.delete(`${API}/api/material-requests/${requestId}`);
                fetchMaterialRequests(true); // Sessiz yenile: sayfa yukarı kaymasın, kaldığı yerden devam etsin
              }}
              onRefresh={fetchMaterialRequests}
            />
          </TabsContent>
          )}

          {activeTab === "profile" && (
          <TabsContent value="profile" className="space-y-6">
            <ProfileTab personnelName={personnelName} />
          </TabsContent>
          )}

          {/* İŞLEM GEÇMİŞİ TAB - sadece müdür */}
          {(isMudur || isAdmin) && activeTab === "audit-logs" && (
          <TabsContent value="audit-logs" className="space-y-6">
            <AuditLogTab apiBase={API} isVisible={activeTab === "audit-logs"} />
          </TabsContent>
          )}

          {(isMudur || isAdmin) && activeTab === "login-management" && (
          <TabsContent value="login-management" className="space-y-6">
            <LoginManagementTab isVisible={activeTab === "login-management"} />
          </TabsContent>
          )}

          {/* Aşama C (migration 0061): Rol Yönetimi sekmesi (Dinamik Rol & Yetki + Organizasyon Ağacı + Klasik Hiyerarşi) yalnızca Yönetici Paneli'nde (/admin/roller, /admin/organizasyon). Kullanıcı panelinden tamamen kaldırıldı. */}

          {/* MESAJLAR SEKMESİ */}
          {activeTab === "messages" && (
          <TabsContent value="messages" className="space-y-6">
            <MessagesTab
              userRole={userRole}
              personnelId={personnelId}
              personnelName={personnelName}
              regions={regions}
              parks={parks}
              personnel={personnel}
              onUnreadCountRefresh={fetchUnreadMessages}
            />
          </TabsContent>
          )}

          {/* MESAİ YÖNETİMİ SEKMESİ */}
          {activeTab === "overtime-management" && (
          <TabsContent value="overtime-management" className="space-y-6">
            <MesaiYonetimiTab
              userRole={userRole}
              personnelId={personnelId}
            />
          </TabsContent>
          )}
            </Tabs>
          </div>
        </div>
      </div>

      {/* REVİZE TALEBİ DETAY DIALOG */}
      <DraggableModal
        isOpen={selectedRevision !== null}
        onClose={() => setSelectedRevision(null)}
        title="Revize Talebi Detayı"
        maxWidth="max-w-4xl"
        closeOnOverlayClick={false}
      >
          {selectedRevision && (
            <div className="space-y-6 py-1">
              <div className="flex items-center justify-end">
                <Badge variant={
                  selectedRevision.status === "Onaylandı" ? "default" :
                  selectedRevision.status === "Reddedildi" ? "destructive" :
                  selectedRevision.status === "Tamamlandı" ? "secondary" : "outline"
                }>
                  {selectedRevision.status}
                </Badge>
              </div>
              {/* Talep Bilgileri */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold mb-3">Talep Bilgileri</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="font-medium">Park:</span> {selectedRevision.park_name}</div>
                  <div><span className="font-medium">Kategori:</span> {selectedRevision.category}</div>
                  <div><span className="font-medium">Başlık:</span> {selectedRevision.title}</div>
                  <div><span className="font-medium">Talep Eden:</span> {selectedRevision.created_by_name}</div>
                  <div><span className="font-medium">Tarih:</span> {new Date(selectedRevision.created_at).toLocaleString('tr-TR')}</div>
                  {selectedRevision.urgency && (
                    <div className="col-span-2">
                      <span className="font-medium">Aciliyet:</span> 
                      {isMudur ? (
                        // Müdür aciliyet değiştirebilir
                        <SearchableSelect
                          value={selectedRevision.urgency}
                          onChange={async (newUrgency) => {
                            try {
                              await axios.put(`${API}/api/revisions/${selectedRevision.id}/urgency`, {
                                urgency: newUrgency
                              });
                              toast.success(`Aciliyet güncellendi: ${newUrgency}`);
                              fetchRevisions();
                              // Seçili revizyonu güncelle
                              setSelectedRevision({...selectedRevision, urgency: newUrgency});
                            } catch (error) {
                              toast.error("Aciliyet güncellenemedi");
                            }
                          }}
                          className="w-[200px] ml-2 inline-flex"
                          showAllOption={false}
                          placeholder="Aciliyet seç"
                          searchPlaceholder="Aciliyet ara..."
                          options={[
                            { id: "ÇOK ACİL", name: "🔴 ÇOK ACİL" },
                            { id: "ACİL", name: "🟠 ACİL" },
                            { id: "ORTA", name: "🟡 ORTA" },
                            { id: "DÜŞÜK", name: "🟢 DÜŞÜK" },
                          ]}
                        />
                      ) : (
                        // Diğerleri sadece görür
                        <Badge className="ml-2" variant={
                          selectedRevision.urgency === "ÇOK ACİL" ? "destructive" :
                          selectedRevision.urgency === "ACİL" ? "default" : 
                          selectedRevision.urgency === "ORTA" ? "secondary" : "outline"
                        } style={{
                          backgroundColor: 
                            selectedRevision.urgency === "ÇOK ACİL" ? "#dc2626" :
                            selectedRevision.urgency === "ACİL" ? "#f97316" :
                            selectedRevision.urgency === "ORTA" ? "#eab308" : "#22c55e",
                          color: "white"
                        }}>
                          {selectedRevision.urgency === "ÇOK ACİL" ? "🔴" :
                           selectedRevision.urgency === "ACİL" ? "🟠" :
                           selectedRevision.urgency === "ORTA" ? "🟡" : "🟢"} {selectedRevision.urgency}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                {selectedRevision.description && (
                  <div className="mt-3">
                    <span className="font-medium">Açıklama:</span>
                    <p className="text-gray-700 mt-1">{selectedRevision.description}</p>
                  </div>
                )}
                
                {/* Tamamlanmış Bilgisi - Description'dan hemen sonra göster */}
                {selectedRevision.status === "Tamamlandı" && selectedRevision.completed_at && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-800">✅ Revize Tamamlandı</span>
                    </div>
                    <div className="text-sm text-green-700 space-y-1">
                      <p>
                        <span className="font-medium">📅 Tamamlanma Tarihi:</span>{' '}
                        {new Date(selectedRevision.completed_at).toLocaleDateString('tr-TR', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      {selectedRevision.completed_by_name && (
                        <p>
                          <span className="font-medium">👤 Tamamlayan:</span>{' '}
                          {selectedRevision.completed_by_name}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Reddedildi Bilgisi */}
                {selectedRevision.status === "Reddedildi" && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-red-600 text-lg">❌</span>
                      <span className="font-semibold text-red-800">Red Bilgisi</span>
                    </div>
                    <div className="text-sm text-red-700 space-y-1">
                      {selectedRevision.decided_by_name && (
                        <p>
                          <span className="font-medium">👤 Reddeden:</span>{' '}
                          {selectedRevision.decided_by_name}
                        </p>
                      )}
                      {selectedRevision.decided_at && (
                        <p>
                          <span className="font-medium">🕐 Red Zamanı:</span>{' '}
                          {new Date(selectedRevision.decided_at).toLocaleString('tr-TR')}
                        </p>
                      )}
                      {selectedRevision.decision_comment && (
                        <p className="mt-2 pt-2 border-t border-red-200">
                          <span className="font-medium">💬 Red Nedeni:</span>{' '}
                          {selectedRevision.decision_comment}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Erteleme Bilgisi */}
                {selectedRevision.status === "Ertelendi" && (
                  <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-orange-600 text-lg">⏰</span>
                      <span className="font-semibold text-orange-800">Erteleme Bilgisi</span>
                    </div>
                    <div className="text-sm text-orange-700 space-y-1">
                      {selectedRevision.postponed_until && (
                        <p>
                          <span className="font-medium">📅 Erteleme Tarihi:</span>{' '}
                          {new Date(selectedRevision.postponed_until).toLocaleDateString('tr-TR', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      )}
                      {selectedRevision.decided_by_name && (
                        <p>
                          <span className="font-medium">👤 Erteleyen:</span>{' '}
                          {selectedRevision.decided_by_name}
                        </p>
                      )}
                      {selectedRevision.decided_at && (
                        <p>
                          <span className="font-medium">🕐 Erteleme Zamanı:</span>{' '}
                          {new Date(selectedRevision.decided_at).toLocaleString('tr-TR')}
                        </p>
                      )}
                      {selectedRevision.decision_comment && (
                        <p className="mt-2 pt-2 border-t border-orange-200">
                          <span className="font-medium">💬 Açıklama:</span>{' '}
                          {selectedRevision.decision_comment}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Karar Geçmişi - En son karar en üstte */}
                {selectedRevision.decision_history && selectedRevision.decision_history.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      📜 Karar Geçmişi ({selectedRevision.decision_history.length} karar)
                    </h4>
                    <div className="space-y-2">
                      {[...selectedRevision.decision_history].reverse().map((hist, idx) => (
                        <div key={idx} className="text-sm p-2 bg-white rounded border border-gray-100">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className={
                              hist.status === "Onaylandı" ? "bg-green-50 text-green-700 border-green-300" :
                              hist.status === "Reddedildi" ? "bg-red-50 text-red-700 border-red-300" :
                              hist.status === "Ertelendi" ? "bg-orange-50 text-orange-700 border-orange-300" : ""
                            }>
                              {hist.status}
                            </Badge>
                            {hist.urgency && (
                              <Badge variant="secondary" className="text-xs">
                                {hist.urgency}
                              </Badge>
                            )}
                            <span className="text-xs text-gray-400 ml-auto">
                              {hist.decided_at ? new Date(hist.decided_at).toLocaleString('tr-TR') : ''}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">
                            <span className="font-medium">{hist.decided_by_name}</span> tarafından karar verildi
                            {hist.postponed_until && (
                              <span className="text-orange-600 ml-1">
                                (📅 {new Date(hist.postponed_until).toLocaleDateString('tr-TR')}'e ertelenmişti)
                              </span>
                            )}
                          </p>
                          {hist.comment && (
                            <p className="text-xs text-gray-500 mt-1 italic">"{hist.comment}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Kararı Değiştir Butonları - Sadece Müdür ve Tamamlanmamış talepler için */}
                {isMudur && 
                 selectedRevision.decision && 
                 !selectedRevision.completed &&
                 selectedRevision.status !== "Onay Bekliyor" && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                      🔄 Kararı Değiştir
                    </h4>
                    <p className="text-xs text-blue-600 mb-3">
                      Mevcut karar: <strong>{selectedRevision.status}</strong>. Yeni bir karar vererek değiştirebilirsiniz.
                    </p>
                    
                    {/* Aciliyet Seçimi - Onay için */}
                    {selectedRevision.status !== "Onaylandı" && (
                      <div className="mb-3 p-2 bg-white rounded border">
                        <label className="text-xs font-medium text-gray-600 block mb-1">Onay için Aciliyet Seçin:</label>
                        <SearchableSelect
                          value={selectedUrgency}
                          onChange={setSelectedUrgency}
                          className="w-full"
                          showAllOption={false}
                          placeholder="Aciliyet seçin"
                          searchPlaceholder="Aciliyet ara..."
                          options={[
                            { id: "ÇOK ACİL", name: "🔴 ÇOK ACİL" },
                            { id: "ACİL", name: "🟠 ACİL" },
                            { id: "ORTA", name: "🟡 ORTA" },
                            { id: "DÜŞÜK", name: "🟢 DÜŞÜK" },
                          ]}
                        />
                      </div>
                    )}

                    {/* Red Nedeni - Red için */}
                    {selectedRevision.status !== "Reddedildi" && (
                      <div className="mb-3 p-2 bg-white rounded border">
                        <label className="text-xs font-medium text-gray-600 block mb-1">Red Nedeni (opsiyonel):</label>
                        <input 
                          type="text"
                          id="reject-reason-change"
                          className="w-full border rounded p-1 text-sm"
                          placeholder="Neden reddedildi..."
                        />
                      </div>
                    )}
                    
                    <div className="flex gap-2 flex-wrap">
                      {selectedRevision.status !== "Onaylandı" && (
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          disabled={!selectedUrgency}
                          onClick={async () => {
                            if (!selectedUrgency) {
                              toast.error("Lütfen aciliyet seviyesi seçin!");
                              return;
                            }
                            try {
                              await axios.post(`${API}/api/revisions/${selectedRevision.id}/decide`, {
                                decision: "approved",
                                urgency: selectedUrgency,
                                decided_by_id: personnelId,
                                decided_by_name: personnel.find(p => p.id === personnelId)?.name
                              });
                              toast.success("Karar güncellendi: Onaylandı");
                              fetchRevisions();
                              setSelectedRevision(null);
                            } catch (error) {
                              console.error("Onay hatası:", error);
                              toast.error("Karar güncellenemedi");
                            }
                          }}
                        >
                          ✅ Onayla
                        </Button>
                      )}
                      {selectedRevision.status !== "Reddedildi" && (
                        <Button 
                          size="sm"
                          variant="destructive"
                          onClick={async () => {
                            const reason = document.getElementById('reject-reason-change')?.value || "";
                            try {
                              await axios.post(`${API}/api/revisions/${selectedRevision.id}/decide`, {
                                decision: "rejected",
                                comment: reason,
                                decided_by_id: personnelId,
                                decided_by_name: personnel.find(p => p.id === personnelId)?.name
                              });
                              toast.success("Karar güncellendi: Reddedildi");
                              fetchRevisions();
                              setSelectedRevision(null);
                            } catch (error) {
                              toast.error("Karar güncellenemedi");
                            }
                          }}
                        >
                          ❌ Reddet
                        </Button>
                      )}
                      {selectedRevision.status !== "Ertelendi" && (
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setPostponingRevision(selectedRevision);
                            setPostponeDate("");
                            setShowPostponeDialog(true);
                          }}
                        >
                          ⏰ Ertele
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Hiyerarşik Görüşler */}
              <div>
                <h3 className="font-semibold mb-3">Görüşler ve Fotoğraflar</h3>
                <div className="space-y-4">
                  {selectedRevision.entries
                    ?.filter(entry => {
                      // HİYERARŞİK GÖRÜNÜRLÜK KURALLARI:
                      
                      // Park Görevlisi: HİÇBİR CEVAP/GÖRÜŞ GÖREMEMELİ
                      if (isParkGorevlisi) {
                        return false;
                      }
                      
                      // Formen: Sadece kendi görüşü + park görevlisinin talebi + müdürün cevabı
                      if (isFormen) {
                        return entry.level === 'park_gorevlisi' || 
                               entry.level === 'formen' || 
                               entry.level === 'mudur';
                      }
                      
                      // Bölge Müh: Formen + park görevlisi + müdür
                      if (isBolgeMuhendisi) {
                        return entry.level === 'park_gorevlisi' || 
                               entry.level === 'formen' || 
                               entry.level === 'bolge_muhendisi' ||
                               entry.level === 'mudur';
                      }
                      
                      // Müdür Yardımcısı ve Müdür: Tüm görüşler
                      if (isMudurYardimcisi || isMudur) {
                        return true;
                      }
                      
                      return false;
                    })
                    .map((entry, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-medium">{entry.person_name}</span>
                          <Badge className="ml-2" variant="outline">{entry.level}</Badge>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(entry.timestamp).toLocaleString('tr-TR')}
                        </span>
                      </div>
                      {entry.comment && (
                        <p className="text-sm text-gray-700 mb-2">{entry.comment}</p>
                      )}
                      {entry.photos && entry.photos.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-2">
                            📸 {entry.photos.length} fotoğraf
                          </p>
                          <div className="grid grid-cols-3 gap-2">
                            {entry.photos.map((photo, photoIdx) => {
                              // /uploads/ yolunu /api/uploads/ olarak değiştir (Kubernetes Ingress için)
                              const photoPath = photo.startsWith('/uploads/') ? photo.replace('/uploads/', '/api/uploads/') : photo;
                              const fullUrl = `${BACKEND_URL}${photoPath}`;
                              const galleryPhotos = entry.photos
                                .map((entryPhoto) => (
                                  entryPhoto.startsWith('/uploads/')
                                    ? `${BACKEND_URL}${entryPhoto.replace('/uploads/', '/api/uploads/')}`
                                    : `${BACKEND_URL}${entryPhoto}`
                                ));
                              return (
                                <img 
                                  key={photoIdx}
                                  src={fullUrl}
                                  alt={`Fotoğraf ${photoIdx + 1}`}
                                  className="w-full h-24 object-cover rounded border cursor-pointer hover:opacity-80"
                                  onClick={() =>
                                    openRevisionPhotoGallery(
                                      galleryPhotos,
                                      photoIdx,
                                      `${entry.person_name} - Görüş Fotoğrafları`
                                    )
                                  }
                                  onError={(e) => {
                                    console.error('Image load error:', fullUrl);
                                    e.target.style.border = '2px solid red';
                                    e.target.alt = `HATA: ${fullUrl}`;
                                  }}
                                />
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Görüş Ekleme (Hiyerarşiye göre) */}
              {selectedRevision.status !== "Tamamlandı" && 
               selectedRevision.status !== "Reddedildi" && (
                revisionEntryGate.canSubmit ? (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Görüş Ekle</h3>
                  <textarea
                    className="w-full border rounded-md p-2"
                    rows="3"
                    placeholder="Görüşünüzü yazın..."
                    id="comment-input"
                  />
                  
                  {/* Fotoğraf Yükleme */}
                  <div className="mt-3">
                    <label className="text-sm font-medium">Fotoğraflar (Opsiyonel - Max 5)</label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="w-full border rounded-md p-2 mt-1"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        const remainingSlots = 5 - addEntryPhotos.length;
                        const filesToUpload = files.slice(0, remainingSlots);
                        
                        if (filesToUpload.length === 0) {
                          toast.error("Maksimum 5 fotoğraf yükleyebilirsiniz");
                          return;
                        }
                        
                        toast.info(`${filesToUpload.length} fotoğraf yükleniyor...`);
                        
                        try {
                          const uploadedUrls = [];
                          for (const file of filesToUpload) {
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            const response = await axios.post(`${API}/api/upload-revision-photo`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            
                            uploadedUrls.push(response.data.url);
                          }
                          
                          setAddEntryPhotos([...addEntryPhotos, ...uploadedUrls]);
                          toast.success(`${uploadedUrls.length} fotoğraf yüklendi!`);
                          e.target.value = ''; // Reset input
                        } catch (error) {
                          console.error("Fotoğraf yükleme hatası:", error);
                          toast.error("Fotoğraf yüklenemedi");
                        }
                      }}
                      disabled={addEntryPhotos.length >= 5}
                    />
                    
                    {/* Fotoğraf Önizleme */}
                    {addEntryPhotos.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-green-600 mb-2">
                          ✓ {addEntryPhotos.length} fotoğraf yüklendi
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {addEntryPhotos.map((photo, idx) => {
                            return (
                            <div key={idx} className="relative">
                              <img 
                                src={`${BACKEND_URL}${photo}`}
                                alt={`Fotoğraf ${idx + 1}`}
                                className="w-full h-20 object-cover rounded border"
                                onError={(e) => {
                                  console.error('Add entry photo error:', photo, 'BACKEND_URL:', BACKEND_URL);
                                  e.target.style.border = '2px solid red';
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newPhotos = addEntryPhotos.filter((_, i) => i !== idx);
                                  setAddEntryPhotos(newPhotos);
                                  toast.success("Fotoğraf silindi");
                                }}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    type="button"
                    className="mt-2"
                    disabled={sendingRevisionEntry}
                    onClick={async (e) => {
                      if (e?.preventDefault) e.preventDefault();
                      if (e?.stopPropagation) e.stopPropagation();
                      if (sendingRevisionEntry || revisionEntrySubmitLockRef.current) return;
                      const comment = document.getElementById('comment-input').value;
                      if (!comment.trim()) {
                        toast.error("Lütfen bir görüş yazın");
                        return;
                      }

                      const revisionId = selectedRevision?.id;
                      if (!revisionId) {
                        toast.error("Revize talebi bulunamadı");
                        return;
                      }

                      if (isDelegatorReadOnly || isGolgeAdmin) {
                        if (!isGolgeAdmin) toast.warning(READ_ONLY_MESSAGE);
                        return;
                      }

                      revisionEntrySubmitLockRef.current = true;
                      setSendingRevisionEntry(true);

                      const actor = personnel.find((p) => p.id === personnelId);
                      const actorName = actor?.name || personnelName || "Bilinmiyor";
                      const actorRole = normalizeRevisionRole(userRole);
                      const photosToSend = [...addEntryPhotos];

                      try {
                        const controller = new AbortController();
                        const qs = new URLSearchParams();
                        qs.set("person_id", String(personnelId || ""));
                        qs.set("person_name", String(actorName || ""));
                        qs.set("person_role", String(actorRole || ""));
                        qs.set("comment", String(comment || ""));
                        qs.set("photos_json", JSON.stringify(photosToSend || []));
                        let hardTimeoutId = null;
                        const fetchPromise = fetch(`${API}/api/revisions/${revisionId}/add-entry-and-forward?${qs.toString()}`, {
                          method: "POST",
                          signal: controller.signal,
                        });
                        const timeoutPromise = new Promise((_, reject) => {
                          hardTimeoutId = setTimeout(() => {
                            controller.abort();
                            reject(new Error("__REV_ENTRY_TIMEOUT__"));
                          }, 15000);
                        });
                        const response = await Promise.race([fetchPromise, timeoutPromise]);
                        if (hardTimeoutId) clearTimeout(hardTimeoutId);

                        if (!response.ok) {
                          let detail = "Bilinmeyen hata";
                          try {
                            const errorData = await response.json();
                            detail = errorData?.detail || errorData?.message || detail;
                          } catch {
                            // ignore parse error
                          }
                          throw new Error(String(detail));
                        }
                        setSelectedRevision(null);
                        setAddEntryPhotos([]);
                        const commentEl = document.getElementById('comment-input');
                        if (commentEl) commentEl.value = "";
                        toast.success("Görüş bir üst makama iletildi.");
                        Promise.resolve(fetchRevisions()).catch((refreshError) => {
                          console.warn("Revize listesi yenilenemedi:", refreshError);
                        });
                      } catch (error) {
                        console.error("Görüş ekleme hatası:", error);
                        if (error?.isDelegationReadonly || error?.message === "DELEGATION_READ_ONLY") {
                          if (!isGolgeAdmin) toast.warning(READ_ONLY_MESSAGE);
                        } else if (error?.message === "__REV_ENTRY_TIMEOUT__") {
                          toast.error("İstek zaman aşımına uğradı. Lütfen tekrar deneyin.");
                        } else if (error?.name === "AbortError") {
                          toast.error("Görüş gönderilemedi: Sunucu geç yanıt verdi, lütfen tekrar deneyin.");
                        } else {
                          toast.error("Görüş eklenemedi: " + (error?.message || "Bilinmeyen hata"));
                        }
                      } finally {
                        revisionEntrySubmitLockRef.current = false;
                        setSendingRevisionEntry(false);
                      }
                    }}
                  >
                    Görüş Gönder ve İlerlet
                  </Button>
                </div>
                ) : (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Görüş Ekle</h3>
                  <div className="rounded-lg border bg-gray-50 p-3 text-sm text-gray-700">
                    {revisionEntryGate.message || "Bu aşamada işlem yapamazsınız."}
                  </div>
                </div>
                )
              )}

              {/* Müdür Karar Paneli - Park Görevlisi hariç herkes görür */}
              {isMudur && selectedRevision.status === "Onay Bekliyor" && (
                <div className="border-t pt-4 bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-4">Müdür Kararı</h3>
                  
                  {/* Aciliyet Seçimi */}
                  <div className="mb-4">
                    <label className="text-sm font-medium mb-2 block">Aciliyet Seviyesi Belirleyin *</label>
                    <SearchableSelect
                      value={selectedUrgency}
                      onChange={setSelectedUrgency}
                      className="w-full"
                      showAllOption={false}
                      placeholder="Aciliyet seçin"
                      searchPlaceholder="Aciliyet ara..."
                      options={[
                        { id: "ÇOK ACİL", name: "🔴 ÇOK ACİL" },
                        { id: "ACİL", name: "🟠 ACİL" },
                        { id: "ORTA", name: "🟡 ORTA" },
                        { id: "DÜŞÜK", name: "🟢 DÜŞÜK" },
                      ]}
                    />
                  </div>

                  {/* Karar Butonları */}
                  <div className="grid grid-cols-3 gap-3">
                    <Button 
                      type="button"
                      variant="default"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={async (e) => {
                        if (e?.preventDefault) e.preventDefault();
                        if (e?.stopPropagation) e.stopPropagation();
                        if (!selectedUrgency) {
                          toast.error("Lütfen aciliyet seviyesi seçin!");
                          return;
                        }
                        
                        try {
                          await axios.post(`${API}/api/revisions/${selectedRevision.id}/decide`, {
                            decision: "approved",
                            urgency: selectedUrgency,
                            decided_by_id: personnelId,
                            decided_by_name: personnel.find(p => p.id === personnelId)?.name
                          });
                          toast.success(`Talep onaylandı! (${selectedUrgency})`);
                          setSelectedUrgency("ORTA"); // Reset
                          fetchRevisions();
                          setSelectedRevision(null);
                        } catch (error) {
                          console.error("Onaylama hatası:", error);
                          toast.error("Onaylanamadı");
                        }
                      }}
                    >
                      ✅ Onayla
                    </Button>
                    <Button 
                      type="button"
                      variant="destructive"
                      onClick={(e) => {
                        if (e?.preventDefault) e.preventDefault();
                        if (e?.stopPropagation) e.stopPropagation();
                        setRejectingRevision(selectedRevision);
                        setRejectReason("");
                        setShowRejectDialog(true);
                      }}
                    >
                      ❌ Reddet
                    </Button>
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={(e) => {
                        if (e?.preventDefault) e.preventDefault();
                        if (e?.stopPropagation) e.stopPropagation();
                        setPostponingRevision(selectedRevision);
                        setPostponeDate("");
                        setShowPostponeDialog(true);
                      }}
                    >
                      ⏰ Ertele
                    </Button>
                  </div>
                </div>
              )}

              {/* Tamamlama Butonu (Müdür ve Müdür Y.) */}
              {(isMudur || isMudurYardimcisi) && 
               selectedRevision.status === "Onaylandı" && 
               !selectedRevision.completed && (
                <div className="border-t pt-4">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="secondary"
                        className="w-full bg-green-100 hover:bg-green-200 text-green-800 border border-green-300"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Tamamlandı Olarak İşaretle
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>⚠️ Dikkat - Geri Dönüşü Yoktur!</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                          <p>
                            <strong>"{selectedRevision.park_name}"</strong> parkındaki revize işini 
                            <strong> TAMAMLANDI</strong> olarak işaretlemek istediğinize emin misiniz?
                          </p>
                          <p className="text-red-600 font-medium">
                            Bu işlem geri alınamaz! Tamamlandı olarak işaretlenen talepler düzenlenemez.
                          </p>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Hayır, Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-green-600 hover:bg-green-700"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/api/revisions/${selectedRevision.id}/complete`, {
                                completed_by_id: personnelId,
                                completed_by_name: personnel.find(p => p.id === personnelId)?.name
                              });
                              toast.success("Talep tamamlandı olarak işaretlendi!");
                              fetchRevisions();
                              setSelectedRevision(null);
                            } catch (error) {
                              console.error("Tamamlama hatası:", error);
                              toast.error("Tamamlanamadı");
                            }
                          }}
                        >
                          Evet, Tamamlandı İşaretle
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}

              {/* Silme Butonu (Sadece Müdür) */}
              {isMudur && (
                <div className="border-t pt-4">
                  <Button 
                    variant="destructive"
                    className="w-full"
                    onClick={() => {
                      setRevisionToDelete(selectedRevision);
                    }}
                    disabled={deletingRevision}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {deletingRevision ? "Siliniyor..." : "Talebi Sil"}
                  </Button>
                </div>
              )}
            </div>
          )}
      </DraggableModal>

      {/* REVİZE TALEBİ OLUŞTURMA DIALOG */}
      <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni Revize Talebi</DialogTitle>
            <DialogDescription>
              Park bakım/onarım talebi oluşturun. Talep hiyerarşik olarak incelenecektir.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRevisionSubmit}>
            <div className="space-y-4 py-4">
              {/* Park Seçimi */}
              <div>
                <label className="text-sm font-medium">Park *</label>
                <SearchableSelect
                  className="w-full border rounded-md p-2 mt-1"
                  value={revisionForm.park_id}
                  onChange={(v) => {
                    const selectedPark = parks.find(p => p.id === v);
                    setRevisionForm({
                      ...revisionForm,
                      park_id: v,
                      park_name: selectedPark?.name || ""
                    });
                  }}
                  allLabel="Park Seçiniz"
                  allValue=""
                  searchPlaceholder="Park ara..."
                  options={parks
                    .filter(park => {
                      // Müdür tüm parkları görebilir
                      if (isMudur) return true;

                      // Müdür Yardımcısı: Sadece bölge müh/formen/park görevlilerinin sorumlu olduğu parkları görebilir
                      if (isMudurYardimcisi) {
                        // Altındaki personeli bul
                        const subordinates = personnel.filter(p => p.mudur_yardimcisi_id === personnelId);
                        const subordinateIds = subordinates.map(s => s.id);
                        // Park'ta bu subordinate'lerden biri varsa göster
                        return park.assigned_personnel_ids?.some(id => subordinateIds.includes(id));
                      }

                      // Bölge Mühendisi: Formen ve park görevlilerinin sorumlu olduğu parkları görebilir
                      if (isBolgeMuhendisi) {
                        const subordinates = personnel.filter(p => p.bolge_muhendisi_id === personnelId);
                        const subordinateIds = subordinates.map(s => s.id);
                        return park.assigned_personnel_ids?.some(id => subordinateIds.includes(id));
                      }

                      // Formen: Kendi ve park görevlilerinin sorumlu olduğu parkları görebilir
                      if (isFormen) {
                        const subordinates = personnel.filter(p => p.formen_id === personnelId);
                        const subordinateIds = [personnelId, ...subordinates.map(s => s.id)];
                        return park.assigned_personnel_ids?.some(id => subordinateIds.includes(id));
                      }

                      // Park görevlisi: Sadece kendi sorumlu olduğu parkları görebilir
                      return park.assigned_personnel_ids && park.assigned_personnel_ids.includes(personnelId);
                    })
                    .map(park => ({ id: park.id, name: park.name }))
                  }
                />
              </div>

              {/* Kategori */}
              <div>
                <label className="text-sm font-medium">Kategori *</label>
                <SearchableSelect
                  className="w-full border rounded-md p-2 mt-1"
                  value={revisionForm.category}
                  onChange={(v) => setRevisionForm({...revisionForm, category: v})}
                  showAllOption={false}
                  placeholder="Kategori seçin"
                  searchPlaceholder="Kategori ara..."
                  options={REVISION_TYPES.map((type) => ({ id: type, name: type }))}
                />
              </div>

              {/* Başlık */}
              <div>
                <label className="text-sm font-medium">Başlık *</label>
                <input
                  type="text"
                  className="w-full border rounded-md p-2 mt-1"
                  placeholder="Örn: Salıncak kırık, değiştirilmeli"
                  value={revisionForm.title}
                  onChange={(e) => setRevisionForm({...revisionForm, title: e.target.value})}
                  required
                />
              </div>

              {/* Açıklama */}
              <div>
                <label className="text-sm font-medium">Açıklama</label>
                <textarea
                  className="w-full border rounded-md p-2 mt-1"
                  rows="3"
                  placeholder="Detaylı açıklama"
                  value={revisionForm.description}
                  onChange={(e) => setRevisionForm({...revisionForm, description: e.target.value})}
                />
              </div>

              {/* İlk Yorum */}
              <div>
                <label className="text-sm font-medium">İlk Görüşünüz</label>
                <textarea
                  className="w-full border rounded-md p-2 mt-1"
                  rows="3"
                  placeholder="İlk gözlemleriniz ve önerileriniz"
                  value={revisionForm.initial_comment}
                  onChange={(e) => setRevisionForm({...revisionForm, initial_comment: e.target.value})}
                />
              </div>

              {/* Fotoğraf Upload */}
              <div>
                <label className="text-sm font-medium">Fotoğraflar (Opsiyonel - Max 5)</label>
                <p className="text-xs text-gray-500 mb-2">Birden fazla fotoğraf seçebilirsiniz (Max 5)</p>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="w-full border rounded-md p-2 mt-1"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    const remainingSlots = 5 - revisionForm.initial_photos.length;
                    const filesToUpload = files.slice(0, remainingSlots);
                    
                    if (filesToUpload.length === 0) {
                      toast.error("Maksimum 5 fotoğraf yükleyebilirsiniz");
                      return;
                    }
                    
                    toast.info(`${filesToUpload.length} fotoğraf yükleniyor...`);
                    
                    try {
                      const uploadedUrls = [];
                      for (const file of filesToUpload) {
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        const response = await axios.post(`${API}/api/upload-revision-photo`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        uploadedUrls.push(response.data.url);
                      }
                      
                      setRevisionForm({
                        ...revisionForm, 
                        initial_photos: [...revisionForm.initial_photos, ...uploadedUrls]
                      });
                      toast.success(`${uploadedUrls.length} fotoğraf yüklendi!`);
                      e.target.value = ''; // Reset input
                    } catch (error) {
                      console.error("Fotoğraf yükleme hatası:", error);
                      toast.error("Fotoğraf yüklenemedi");
                    }
                  }}
                  disabled={revisionForm.initial_photos.length >= 5}
                />
                
                {/* Fotoğraf Önizleme */}
                {revisionForm.initial_photos.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-green-600 mb-2">
                      ✓ {revisionForm.initial_photos.length} fotoğraf yüklendi
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {revisionForm.initial_photos.map((photo, idx) => {
                        return (
                        <div key={idx} className="relative">
                          <img 
                            src={`${BACKEND_URL}${photo}`}
                            alt={`Fotoğraf ${idx + 1}`}
                            className="w-full h-20 object-cover rounded border"
                            onError={(e) => {
                              console.error('Form photo error:', photo, 'BACKEND_URL:', BACKEND_URL);
                              e.target.style.border = '2px solid red';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newPhotos = revisionForm.initial_photos.filter((_, i) => i !== idx);
                              setRevisionForm({...revisionForm, initial_photos: newPhotos});
                              toast.success("Fotoğraf silindi");
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setRevisionDialogOpen(false)}
              >
                İptal
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Oluşturuluyor..." : "Talep Oluştur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Park Silme Onay Dialog */}
      <AlertDialog open={!!parkToDelete} onOpenChange={(open) => !open && setParkToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Parkı Silmek İstediğinize Emin Misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-gray-900">{parkToDelete?.name}</span> adlı parkı silmek üzeresiniz.
              Bu işlem geri alınamaz; parkın envanteri, görevli atamaları ve geçmişi de listelerden düşer.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <DeleteNameConfirm
            name={parkToDelete?.name}
            value={parkDeleteConfirmText}
            onChange={setParkDeleteConfirmText}
            label="parkın adını"
          />

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setParkToDelete(null)}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleParkDelete}
              disabled={!parkDeleteConfirmed}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
            >
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Personel Silme Onay Dialog */}
      <AlertDialog open={!!personnelToDelete} onOpenChange={(open) => !open && setPersonnelToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Personeli Silmek İstediğinize Emin Misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-gray-900">{personnelToDelete?.name}</span> adlı personeli silmek üzeresiniz.
              Bu işlem geri alınamaz; personelin park atamaları, izin ve mesai geçmişi de etkilenir.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <DeleteNameConfirm
            name={personnelToDelete?.name}
            value={personnelDeleteConfirmText}
            onChange={setPersonnelDeleteConfirmText}
            label="personelin adını"
          />

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPersonnelToDelete(null)}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (!personnelDeleteConfirmed) return;
                handlePersonnelDelete(personnelToDelete?.id);
              }}
              disabled={!personnelDeleteConfirmed}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
            >
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revize Talebi Silme Onay Dialog */}
      <AlertDialog open={!!revisionToDelete} onOpenChange={(open) => !open && setRevisionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Revize Talebini Silmek İstediğinize Emin Misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2">
                <p>
                  <span className="font-semibold text-gray-900">{revisionToDelete?.park_name}</span> parkı için oluşturulan
                </p>
                <p className="font-semibold text-gray-900">&ldquo;{revisionToDelete?.title}&rdquo;</p>
                <p>başlıklı revize talebini silmek üzeresiniz.</p>
                <p className="text-red-600 font-semibold mt-3">Bu işlem geri alınamaz!</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <DeleteNameConfirm
            name={revisionToDelete?.title}
            value={revisionDeleteConfirmText}
            onChange={setRevisionDeleteConfirmText}
            label="talebin başlığını"
          />

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRevisionToDelete(null)} disabled={deletingRevision}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevisionDelete}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
              disabled={deletingRevision || !revisionDeleteConfirmed}
            >
              {deletingRevision ? "Siliniyor..." : "Evet, Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Geçici Görev Atama Dialog - Sürüklenebilir, sağ üstte X ile kapatma */}
      <DraggableModal
        isOpen={tempAssignmentDialogOpen}
        onClose={() => {
          setTempAssignmentDialogOpen(false);
          setSelectedPersonnelForAssignment(null);
        }}
        title="🔶 Geçici Görev Ver"
        subtitle={selectedPersonnelForAssignment ? `${selectedPersonnelForAssignment.name} için geçici görev` : 'Park görevlisi seçin'}
        maxWidth="max-w-md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setTempAssignmentDialogOpen(false);
                setSelectedPersonnelForAssignment(null);
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleCreateTempAssignment}
              disabled={loadingAssignments || !selectedPersonnelForAssignment}
            >
              {loadingAssignments ? "Oluşturuluyor..." : "Geçici Görev Ver"}
            </Button>
          </div>
        }
      >
          <div className="space-y-4">
            {/* Park Görevlisi Seçimi - Sadece panelden açıldıysa göster */}
            {!selectedPersonnelForAssignment && (
              <div>
                <label className="text-sm font-medium mb-2 block">Park Görevlisi *</label>
                <SearchableSelect
                  className="w-full p-2 border rounded"
                  value={selectedPersonnelForAssignment?.id || ''}
                  onChange={(v) => {
                    const selected = personnel.find(p => p.id === v);
                    setSelectedPersonnelForAssignment(selected || null);
                  }}
                  allLabel="Park görevlisi seçin..."
                  allValue=""
                  searchPlaceholder="Park görevlisi ara..."
                  options={personnel
                    .filter(p => {
                      if (p.role !== 'park_gorevlisi') return false;

                      if (isMudur) {
                        return true;
                      } else if (isMudurYardimcisi) {
                        return p.mudur_yardimcisi_id === personnelId;
                      } else if (isBolgeMuhendisi) {
                        return p.bolge_muhendisi_id === personnelId;
                      } else if (isFormen) {
                        return p.formen_id === personnelId;
                      }
                      return false;
                    })
                    .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                    .map(p => ({ id: p.id, name: p.name }))
                  }
                />
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium mb-2 block">Görev Türü</label>
              <SearchableSelect
                className="w-full p-2 border rounded"
                value={tempAssignmentForm.task_type}
                onChange={(v) => setTempAssignmentForm({
                  ...tempAssignmentForm,
                  task_type: v
                })}
                showAllOption={false}
                placeholder="Görev türü seçin"
                searchPlaceholder="Görev türü ara..."
                options={[
                  { id: "Başka Parkta Yardım", name: "Başka Parkta Yardım" },
                  { id: "Bakım İşi", name: "Bakım İşi" },
                  { id: "Ekipman Taşıma", name: "Ekipman Taşıma" },
                  { id: "Toplantı", name: "Toplantı" },
                  { id: "Diğer", name: "Diğer" },
                ]}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Görev Açıklaması (Opsiyonel)</label>
              <textarea
                className="w-full p-2 border rounded"
                rows="2"
                placeholder="Görev hakkında detay..."
                value={tempAssignmentForm.task_description}
                onChange={(e) => setTempAssignmentForm({
                  ...tempAssignmentForm,
                  task_description: e.target.value
                })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Konum (Opsiyonel)</label>
              <Input
                placeholder="Örn: Atatürk Parkı, Merkez İlçe"
                value={tempAssignmentForm.location}
                onChange={(e) => setTempAssignmentForm({
                  ...tempAssignmentForm,
                  location: e.target.value
                })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Süre (Saat)</label>
              <SearchableSelect
                className="w-full p-2 border rounded"
                value={String(tempAssignmentForm.duration_hours)}
                onChange={(v) => setTempAssignmentForm({
                  ...tempAssignmentForm,
                  duration_hours: parseFloat(v)
                })}
                showAllOption={false}
                placeholder="Süre seçin"
                searchPlaceholder="Süre ara..."
                options={[
                  { id: "0.5", name: "30 dakika" },
                  { id: "1", name: "1 saat" },
                  { id: "2", name: "2 saat" },
                  { id: "3", name: "3 saat" },
                  { id: "4", name: "4 saat" },
                  { id: "6", name: "6 saat" },
                  { id: "8", name: "8 saat (Tam Gün)" },
                ]}
              />
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <p className="font-semibold text-blue-800">ℹ️ Önemli Bilgi</p>
              <p className="text-blue-700 mt-1">
                Bu görev süresi boyunca {selectedPersonnelForAssignment ? <strong>{selectedPersonnelForAssignment.name}</strong> : 'seçilen görevli'} için &quot;park dışı&quot; bildirimi GİTMEZ. 
                Üst yönetim tüm detayları görebilir.
              </p>
            </div>
          </div>
      </DraggableModal>

      {/* Personel Ekle/Düzenle Dialog */}
      <PersonnelForm
        isOpen={isPersonnelDialogOpen}
        onClose={(open) => {
          setIsPersonnelDialogOpen(open);
          if (!open) {
            setPersonnelFormInitialTab("general"); // Dialog kapanınca sıfırla
            setPersonnelTaskFilters({ startDate: null, endDate: null, parkId: null, parkName: null }); // Filtreleri sıfırla
          }
        }}
        isEditing={isEditingPersonnel}
        isFormReadOnly={isFormReadOnly}
        formData={personnelForm}
        setFormData={setPersonnelForm}
        onSubmit={handlePersonnelSubmit}
        loading={isPersonnelSaving}
        personnel={personnel}
        regions={regions}
        parks={parks}
        setIsQuickRegionDialogOpen={setIsQuickRegionDialogOpen}
        setIsQuickParkDialogOpen={setIsQuickParkDialogOpen}
        leaveRequests={leaveRequests}
        materialRequests={materialRequests}
        getRoleBadge={getRoleBadge}
        initialTab={personnelFormInitialTab}
        taskFilters={personnelTaskFilters}
        userRole={userRole}
        currentPersonnelId={personnelId}
      />

      {/* ESKİ FORM KODU BURADAN SİLİNDİ - PersonnelForm component'ine taşındı */}

      {/* Park Görevlisi Olmayan Parklar Seçim Dialogu */}
      <Dialog open={unassignedParksDialogOpen} onOpenChange={setUnassignedParksDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TreePine className="h-5 w-5 text-amber-600" />
              Park Görevlisi Olmayan Parklar
            </DialogTitle>
            <DialogDescription>
              Aşağıdaki parklarda park görevlisi bulunmamaktadır. 
              {(isMudur || isMudurYardimcisi) 
                ? ' Personel atamak istediğiniz parkı seçin.'
                : ' Görüntülemek istediğiniz parkı seçin.'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {unassignedParksList.map((park) => {
              // Bu parka atanmış formeni bul
              const assignedFormen = park.assigned_personnel_ids
                ?.map(pid => personnel.find(p => p.id === pid))
                .filter(p => p && p.role === 'formen');
              const parkRegion = regions.find(r => r.id === park.region_id);
              
              return (
                <div
                  key={park.id}
                  className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => {
                    setUnassignedParksDialogOpen(false);
                    if (isMudur || isMudurYardimcisi) {
                      setActiveTab("parks");
                      setTimeout(() => handleParkEdit(park), 100);
                    } else {
                      setSearchTerm(park.name);
                      setActiveTab("parks");
                    }
                  }}
                >
                  <div className="font-medium text-gray-900">{park.name}</div>
                  {parkRegion && (
                    <div className="text-sm text-gray-500 mt-1">
                      📍 {parkRegion.name}
                    </div>
                  )}
                  {assignedFormen && assignedFormen.length > 0 && (
                    <div className="text-sm text-amber-600 mt-1">
                      👷 Formen: {assignedFormen.map(f => f.name).join(', ')}
                    </div>
                  )}
                  <div className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <span>⚠️</span>
                    <span>Park Görevlisi Atanmamış</span>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnassignedParksDialogOpen(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İzin Talep Dialog */}
      <LeaveRequestFormDialog
        open={isLeaveRequestDialogOpen}
        onOpenChange={setIsLeaveRequestDialogOpen}
        leaveRequestForm={leaveRequestForm}
        setLeaveRequestForm={setLeaveRequestForm}
        handleSubmit={handleLeaveRequestSubmit}
        loading={leaveRequestLoading}
        isReadOnly={isDelegatorReadOnly}
        calculateLeaveDays={calculateLeaveDays}
        currentPerson={personnel.find(p => p.id === personnelId)}
        personnel={personnel}
        parks={parks}
        errorMessage={leaveRequestError}
        asSubstitutePeriods={substituteBannerItems}
      />

      {/* İzin Onay/Red/Sevk Dialog */}
      <LeaveActionDialog
        open={isLeaveActionDialogOpen}
        onOpenChange={setIsLeaveActionDialogOpen}
        selectedLeaveRequest={selectedLeaveRequest}
        leaveAction={leaveAction}
        userRole={userRole}
        personnelId={personnelId}
        filterApprovalHistory={filterApprovalHistory}
        onAction={async (commentText) => {
          const canBypassReadOnly =
            String(selectedLeaveRequest?.current_approver_id || "") === String(personnelId || "");
          if ((isDelegatorReadOnly || isGolgeAdmin) && !canBypassReadOnly) {
            if (!isGolgeAdmin) toast.warning(READ_ONLY_MESSAGE);
            return;
          }
          // Önce dialog'u kapat - duplicate tıklama önleme
          setIsLeaveActionDialogOpen(false);
          
          try {
            const isDelegatedActor =
              String(selectedLeaveRequest?.current_approver_id || "") === String(personnelId || "") &&
              String(selectedLeaveRequest?.current_approver_delegated_from_id || "").trim().length > 0;
            const delegationContext = isDelegatedActor
              ? {
                  acting_as_delegate: true,
                  delegated_from_user_id: selectedLeaveRequest?.current_approver_delegated_from_id || null,
                  delegated_from_name: selectedLeaveRequest?.current_approver_delegated_from_name || null
                }
              : null;
            const payload = {
              action: leaveAction,
              approver_id: personnelId,
              actor_id: personnelId,
              actor_role: userRole,
              comment: commentText || null,
              acting_as_delegate: Boolean(delegationContext?.acting_as_delegate),
              delegated_from_user_id: delegationContext?.delegated_from_user_id || null
            };
            const url = `/leave-requests/${selectedLeaveRequest.id}/action`;
            console.log("[leave-action] request", {
              action: payload.action,
              requestId: selectedLeaveRequest?.id || null,
              actor_id: payload.actor_id,
              actor_role: payload.actor_role,
              comment: payload.comment,
              delegationContext,
              url,
            });
            // Onay ucu ağır bir işlem (vekalet yeniden hesaplama, materializasyon,
            // bildirim, SSE). 5s timeout çok agresifti: işlem backend'de başarıyla
            // sürerken istemci timeout'a düşüp "HTTP ?: timeout of 5000ms" hatası
            // atıyor, kullanıcı tekrar deneyince ikinci kez başarı toast'ı çıkıyor
            // (aynı anda hata + "onaylandı" görünmesinin kök nedeni). Diğer yazma
            // işlemleriyle hizalı 30s'e çekildi.
            const opts = { timeout: 30000, headers: { "Content-Type": "application/json" } };
            let actionError = null;
            let completed = false;
            try {
              await apiClient.post(url, payload, opts);
              completed = true;
            } catch (postError) {
              actionError = postError;
              // Timeout/ağ hatası olsa bile işlem backend'de tamamlanmış olabilir.
              // Tek seferlik doğrulama commit yarışını kaçırıyordu; kısa aralıklarla
              // birkaç kez dene ve approver/status/geçmiş değişiminden herhangi birini
              // tamamlanma kanıtı say (böylece yanlış hata toast'ı atılmaz).
              const priorApproverId = String(selectedLeaveRequest?.current_approver_id || "");
              const priorStatus = String(selectedLeaveRequest?.status || "");
              const priorHistoryLen = Array.isArray(selectedLeaveRequest?.approval_history)
                ? selectedLeaveRequest.approval_history.length
                : 0;
              for (let attempt = 0; attempt < 3 && !completed; attempt++) {
                if (attempt > 0) {
                  await new Promise((resolve) => setTimeout(resolve, 1500));
                }
                try {
                  const checkResp = await apiClient.get(`/leave-requests`, {
                    params: getLeaveRequestParams(),
                    timeout: 5000
                  });
                  const all = Array.isArray(checkResp?.data) ? checkResp.data : [];
                  const updated = all.find((lr) => String(lr?.id || "") === String(selectedLeaveRequest?.id || ""));
                  if (updated) {
                    const approverChanged = String(updated?.current_approver_id || "") !== priorApproverId;
                    const statusChanged = String(updated?.status || "") !== priorStatus;
                    const updatedHistoryLen = Array.isArray(updated?.approval_history)
                      ? updated.approval_history.length
                      : 0;
                    const historyGrew = updatedHistoryLen > priorHistoryLen;
                    if (approverChanged || statusChanged || historyGrew) {
                      completed = true;
                    }
                  }
                } catch {
                  // doğrulama da başarısızsa sonraki denemeye/asıl hataya düş
                }
              }
            }
            if (!completed && actionError) throw actionError;
            const actionText = 
              leaveAction === "approve" ? "onaylandı" :
              leaveAction === "reject" ? "reddedildi" :
              "üst makama sevk edildi";
            toast.success(`İzin talebi ${actionText}`);
            const refreshed = await fetchLeaveRequests();
            await fetchPersonnel({ force: true, silent: true });
            await fetchAllActiveSubstitutions();
            if (!refreshed) {
              toast.info("İşlem kaydedildi, liste yenilenemedi. Sayfa yenileniyor...");
              setTimeout(() => window.location.reload(), 300);
            }
          } catch (error) {
            console.error("[leave-action] failed", {
              status: error?.response?.status || null,
              data: error?.response?.data || null,
              headers: error?.response?.headers || null,
              message: error?.message || null,
              code: error?.code || null,
            });
            const statusCode = Number(error?.response?.status || 0);
            const responseData = error?.response?.data;
            const rawMsg = responseData?.detail || responseData?.error || responseData?.message || error?.message || "İşlem başarısız";
            const msg = typeof rawMsg === "string" ? rawMsg : JSON.stringify(rawMsg);
            toast.error(`HTTP ${statusCode || "?"}: ${msg}`);
            // Hata durumunda dialog'u tekrar aç
            setIsLeaveActionDialogOpen(true);
          }
        }}
      />

      {/* İşbaşı akışı - tarih/not/devir tercihi */}
      <Dialog open={isReturnFlowOpen} onOpenChange={setIsReturnFlowOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>İşbaşı Kaydı Oluştur</DialogTitle>
            <DialogDescription>
              Fiili dönüş tarihini girin ve vekaletin nasıl devam edeceğini seçin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
              <div><strong>Personel:</strong> {selectedReturnSub?.original_personnel_name || "-"}</div>
              <div><strong>Vekil:</strong> {selectedReturnSub?.substitute_personnel_name || "-"}</div>
              <div>
                <strong>Planlanan aralık:</strong>{" "}
                {selectedReturnSub?.start_date ? new Date(selectedReturnSub.start_date).toLocaleDateString("tr-TR") : "-"}
                {" - "}
                {selectedReturnSub?.end_date ? new Date(selectedReturnSub.end_date).toLocaleDateString("tr-TR") : "-"}
              </div>
            </div>

            <DatePicker
              id="actual-return-date"
              label="Fiili işbaşı tarihi"
              value={returnFlowForm.actual_return_date}
              onChange={(value) => setReturnFlowForm(prev => ({ ...prev, actual_return_date: value }))}
            />

            <div>
              <Label htmlFor="return-note">Açıklama (opsiyonel)</Label>
              <Textarea
                id="return-note"
                placeholder="Örn: Sağlık sebebi düzeldi, erken işbaşı yapıldı."
                value={returnFlowForm.return_note}
                onChange={(e) => setReturnFlowForm(prev => ({ ...prev, return_note: e.target.value }))}
                rows={3}
              />
            </div>

            <div>
              <Label>Vekalet planı</Label>
              <SearchableSelect
                value={returnFlowForm.handover_mode}
                onChange={(value) => setReturnFlowForm(prev => ({ ...prev, handover_mode: value }))}
                showAllOption={false}
                placeholder="Vekalet planı seçin"
                searchPlaceholder="Plan ara..."
                options={[
                  { id: "immediate", name: "Hemen bitir" },
                  { id: "end_of_day", name: "Gün sonuna kadar devam etsin" },
                  { id: "custom", name: "Belirli tarihe kadar devam etsin" },
                ]}
              />
            </div>

            {returnFlowForm.handover_mode === "custom" && (
              <DatePicker
                id="handover-until"
                label="Vekalet bitiş tarihi"
                value={returnFlowForm.handover_until}
                onChange={(value) => setReturnFlowForm(prev => ({ ...prev, handover_until: value }))}
              />
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsReturnFlowOpen(false)}
              disabled={returnFlowSaving}
            >
              İptal
            </Button>
            <Button
              type="button"
              disabled={returnFlowSaving || !returnFlowForm.actual_return_date}
              onClick={async () => {
                if (!selectedReturnSub?.leave_request_id) {
                  toast.error("İzin talebi bulunamadı");
                  return;
                }
                if (returnFlowForm.handover_mode === "custom" && !returnFlowForm.handover_until) {
                  toast.error("Özel devir için bitiş tarihi seçin");
                  return;
                }
                setReturnFlowSaving(true);
                try {
                  await handleMarkReturn({
                    leaveRequestId: selectedReturnSub.leave_request_id,
                    actualReturnDate: returnFlowForm.actual_return_date,
                    returnNote: returnFlowForm.return_note,
                    handoverMode: returnFlowForm.handover_mode,
                    handoverUntil: returnFlowForm.handover_until,
                  });
                  setIsReturnFlowOpen(false);
                } finally {
                  setReturnFlowSaving(false);
                }
              }}
            >
              {returnFlowSaving ? "Kaydediliyor..." : "İşbaşını Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İşbaşı talebi onay/red dialogu (tarayıcı prompt yerine) */}
      <Dialog
        open={isReturnActionDialogOpen}
        onOpenChange={(open) => {
          setIsReturnActionDialogOpen(open);
          if (!open) {
            setSelectedReturnApprovalRequest(null);
            setReturnApprovalComment("");
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {returnApprovalAction === "approve" ? "İşbaşı Talebini Onayla" : "İşbaşı Talebini Reddet"}
            </DialogTitle>
            <DialogDescription>
              {selectedReturnApprovalRequest
                ? `${selectedReturnApprovalRequest.personnel_name || "Personel"} için işbaşı talebini değerlendiriyorsunuz.`
                : "İşbaşı talebini değerlendiriyorsunuz."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {selectedReturnApprovalRequest && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <p>
                  <span className="font-semibold">İzin türü:</span>{" "}
                  {selectedReturnApprovalRequest.reason_type || "Belirtilmemiş"}
                </p>
                <p>
                  <span className="font-semibold">Planlanan dönüş:</span>{" "}
                  {selectedReturnApprovalRequest.return_date
                    ? new Date(selectedReturnApprovalRequest.return_date).toLocaleDateString("tr-TR")
                    : "-"}
                </p>
                <p>
                  <span className="font-semibold">Fiili dönüş:</span>{" "}
                  {selectedReturnApprovalRequest.return_request?.actual_return_date
                    ? new Date(selectedReturnApprovalRequest.return_request.actual_return_date).toLocaleDateString("tr-TR")
                    : "-"}
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="return-approval-comment">Açıklama (opsiyonel)</Label>
              <Textarea
                id="return-approval-comment"
                value={returnApprovalComment}
                onChange={(e) => setReturnApprovalComment(e.target.value)}
                placeholder={returnApprovalAction === "approve"
                  ? "Örn: İşbaşı talebi uygun görüldü."
                  : "Örn: Tarih uyumsuzluğu nedeniyle reddedildi."}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsReturnActionDialogOpen(false)}
              disabled={returnApprovalSaving}
            >
              İptal
            </Button>
            <Button
              onClick={handleSubmitReturnRequestAction}
              disabled={returnApprovalSaving}
              className={returnApprovalAction === "approve" ? "bg-green-600 hover:bg-green-700" : ""}
              variant={returnApprovalAction === "approve" ? "default" : "destructive"}
            >
              {returnApprovalSaving ? "Gönderiliyor..." : (returnApprovalAction === "approve" ? "Onayla" : "Reddet")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hızlı Bölge Ekleme Dialog */}
      <Dialog open={isQuickRegionDialogOpen} onOpenChange={setIsQuickRegionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader className="flex flex-row items-start justify-between">
            <div>
              <DialogTitle>Yeni Bölge Ekle</DialogTitle>
              <DialogDescription>
                Hızlıca yeni bir bölge ekleyin ve otomatik olarak seçilsin
              </DialogDescription>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 rounded-full hover:bg-red-100 hover:text-red-600 -mt-2 -mr-2"
              onClick={() => {
                setQuickRegionName("");
                setIsQuickRegionDialogOpen(false);
              }}
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogHeader>
          <form onSubmit={handleQuickRegionAdd} className="space-y-4">
            <div>
              <Label htmlFor="quick-region-name">Bölge Adı *</Label>
              <Input
                id="quick-region-name"
                value={quickRegionName}
                onChange={(e) => setQuickRegionName(e.target.value)}
                placeholder="Örn: ÇANKAYA"
                required
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setQuickRegionName("");
                setIsQuickRegionDialogOpen(false);
              }}>
                İptal
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Ekleniyor..." : "Ekle ve Seç"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hızlı Park Ekleme Dialog - Ayrı Bileşen (Performans için) */}
      <QuickParkDialog
        isOpen={isQuickParkDialogOpen}
        onClose={() => setIsQuickParkDialogOpen(false)}
        onSubmit={handleQuickParkAdd}
        loading={loading}
      />


      {/* Rapor İndirme Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📊 Rapor İndir
            </DialogTitle>
            <DialogDescription>
              Excel formatında detaylı raporlar oluşturun
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Rapor Türü</label>
              <SearchableSelect
                className="w-full p-2 border rounded"
                value={reportType}
                onChange={(v) => setReportType(v)}
                showAllOption={false}
                placeholder="Rapor türü seçin"
                searchPlaceholder="Rapor türü ara..."
                options={[
                  { id: "location-logs", name: "📍 Konum Logları" },
                  { id: "work-hours", name: "⏰ Çalışma Saatleri" },
                  { id: "temporary-assignments", name: "🔶 Geçici Görevler" },
                  { id: "out-of-park-analysis", name: "🔴 Park Dışı Analiz" },
                ]}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Tarih Aralığı</label>
              <DateRangePicker
                startDate={reportStartDate}
                endDate={reportEndDate}
                onChange={({ start, end }) => {
                  setReportStartDate(start);
                  setReportEndDate(end);
                }}
                max={todayYmd()}
                placeholder="Rapor tarih aralığı"
              />
            </div>

            {/* Rapor Açıklamaları */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs">
              <p className="font-semibold text-blue-800 mb-2">
                {reportType === 'location-logs' && '📍 Konum Logları: Park görevlilerinin gün içi konum kayıtları, parkta/park dışı süreleri'}
                {reportType === 'work-hours' && '⏰ Çalışma Saatleri: Günlük/haftalık toplam çalışma saatleri ve verimlilik analizi'}
                {reportType === 'temporary-assignments' && '🔶 Geçici Görevler: Tüm geçici görev kayıtları ve detayları'}
                {reportType === 'out-of-park-analysis' && '🔴 Park Dışı Analiz: Park dışında kalma süreleri ve nedenleri'}
              </p>
              <p className="text-blue-700">
                Rapor özet istatistikler ve detaylı verilerle Excel formatında indirilecek.
              </p>
            </div>

            {/* Hızlı Tarih Seçenekleri */}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setReportStartDate(today);
                  setReportEndDate(today);
                }}
              >
                Bugün
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  const dateStr = yesterday.toISOString().split('T')[0];
                  setReportStartDate(dateStr);
                  setReportEndDate(dateStr);
                }}
              >
                Dün
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  const today = new Date();
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  setReportStartDate(weekAgo.toISOString().split('T')[0]);
                  setReportEndDate(today.toISOString().split('T')[0]);
                }}
              >
                Son 7 Gün
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  const today = new Date();
                  const monthAgo = new Date();
                  monthAgo.setDate(monthAgo.getDate() - 30);
                  setReportStartDate(monthAgo.toISOString().split('T')[0]);
                  setReportEndDate(today.toISOString().split('T')[0]);
                }}
              >
                Son 30 Gün
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReportDialogOpen(false)}
            >
              İptal
            </Button>
            <Button
              onClick={handleDownloadReport}
              disabled={loadingReport || !reportStartDate || !reportEndDate}
              className="bg-green-600 hover:bg-green-700"
            >
              {loadingReport ? "İndiriliyor..." : "📥 Excel İndir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* İzinli Personel Dialog - DraggableModal */}
      <DraggableModal
        isOpen={onLeaveDialogOpen}
        onClose={() => {
          setOnLeaveDialogOpen(false);
          setLeaveTypeFilter('all');
          setLeaveOrgFilters({ ...EMPTY_DETAIL_FILTERS });
          setLeaveDayOffset(0);
        }}
        // Başlık seçili günü İZLER: sabit "Bugün" kalsaydı yarına bakarken
        // yanıltırdı.
        title={`📅 ${leaveDayLabel || 'Seçili Gün'} İzinli Personeller`}
        subtitle={leaveViewDate.toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        maxWidth="max-w-4xl"
      >
        {/* İzin Türü Filtreleri */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button 
            size="sm" 
            variant={leaveTypeFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setLeaveTypeFilter('all')}
            className={leaveTypeFilter === 'all' ? 'bg-gray-700' : ''}
          >
            Hepsi
          </Button>
          <Button 
            size="sm" 
            variant={leaveTypeFilter === 'Yıllık İzin' ? 'default' : 'outline'}
            onClick={() => setLeaveTypeFilter('Yıllık İzin')}
            className={leaveTypeFilter === 'Yıllık İzin' ? 'bg-blue-500 hover:bg-blue-600' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}
          >
            🔵 Yıllık İzin
          </Button>
          <Button 
            size="sm" 
            variant={leaveTypeFilter === 'Sosyal İzin' ? 'default' : 'outline'}
            onClick={() => setLeaveTypeFilter('Sosyal İzin')}
            className={leaveTypeFilter === 'Sosyal İzin' ? 'bg-purple-500 hover:bg-purple-600' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}
          >
            🟣 Sosyal İzin
          </Button>
          <Button 
            size="sm" 
            variant={leaveTypeFilter === 'Diğer' ? 'default' : 'outline'}
            onClick={() => setLeaveTypeFilter('Diğer')}
            className={leaveTypeFilter === 'Diğer' ? 'bg-gray-500 hover:bg-gray-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}
          >
            ⚪ Diğer
          </Button>

          {/* Gün seçici — tür düğmelerinin hemen yanında (sağ köşeye itilmez),
              ince bir ayraçla ayrılmış. Ayrı bir eksen (gün) olduğu için tür
              grubuna dahil DEĞİL: aynı grupta olsaydı "Yarın"a basınca
              "Sosyal İzin" seçimi düşerdi.
              Bugün dışı gün seçiliyken tüm kontrol dolgulu amber olur — soluk
              bir yazı rengi yeterince dikkat çekmiyordu ve kullanıcı hangi güne
              baktığını fark etmeden karar verebilirdi. */}
          {(() => {
            const bugun = leaveDayOffset === 0;
            const okBtn =
              "flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:opacity-35 disabled:cursor-not-allowed";
            return (
              <div className="ml-2 flex items-center gap-2 border-l border-gray-200 pl-3">
                <div
                  className={`flex items-center gap-0.5 rounded-lg border px-1 py-0.5 transition-colors ${
                    bugun
                      ? "border-gray-200 bg-gray-50"
                      : "border-amber-500 bg-amber-100 ring-2 ring-amber-300/60"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setLeaveDayOffset((o) => Math.max(0, o - 1))}
                    disabled={bugun}
                    title="Önceki gün"
                    aria-label="Önceki gün"
                    className={`${okBtn} ${bugun ? "text-gray-500 hover:bg-gray-200" : "text-amber-900 hover:bg-amber-200"}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span
                    className={`flex min-w-[124px] items-center justify-center gap-1.5 text-center text-sm ${
                      bugun ? "text-gray-700" : "font-bold text-amber-900"
                    }`}
                  >
                    {!bugun && <CalendarClock className="h-4 w-4 shrink-0" />}
                    {leaveDayLabel
                      ? `${leaveDayLabel} · ${leaveViewDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}`
                      : leaveViewDate.toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                  <button
                    type="button"
                    onClick={() => setLeaveDayOffset((o) => Math.min(LEAVE_DAY_MAX_OFFSET, o + 1))}
                    disabled={leaveDayOffset >= LEAVE_DAY_MAX_OFFSET}
                    title="Sonraki gün"
                    aria-label="Sonraki gün"
                    className={`${okBtn} ${bugun ? "text-gray-500 hover:bg-gray-200" : "text-amber-900 hover:bg-amber-200"}`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                {!bugun && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setLeaveDayOffset(0)}
                    className="h-8 border-amber-500 text-xs font-medium text-amber-800 hover:bg-amber-50"
                  >
                    Bugüne dön
                  </Button>
                )}
              </div>
            );
          })()}
        </div>

        {/* Bugün DIŞINDA bir güne bakılırken şerit. Kontrolün rengi tek başına
            gözden kaçabiliyordu; yönetici hangi güne baktığını fark etmeden
            "bugün kimse yok" diye karar verebilirdi. */}
        {leaveDayOffset !== 0 && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-400 bg-amber-50 px-3 py-2">
            <CalendarClock className="h-4 w-4 shrink-0 text-amber-700" />
            <p className="text-sm text-amber-900">
              Bugüne değil,{" "}
              <span className="font-semibold">
                {leaveViewDate.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>{" "}
              gününe bakıyorsunuz.
            </p>
          </div>
        )}

        <div className="max-h-[60vh] overflow-y-auto">
          {(() => {
            // Seçili GÜNDE izinli personeller (Rapor hariç). Gün seçicisi
            // kapalıyken (offset 0) bu değer bugündür → eski davranış.
            const today = leaveViewDate;

            let todayOnLeave = leaveRequests.filter(lr => {
              if (lr.status !== 'approved' || lr.reason_type === 'Rapor') return false;
              const startDate = new Date(lr.start_date);
              const endDate = new Date(lr.end_date);
              startDate.setHours(0, 0, 0, 0);
              endDate.setHours(0, 0, 0, 0);
              return today >= startDate && today <= endDate;
            });
            
            // Rol bazlı filtreleme
            if (isMudurYardimcisi) {
              const myPersonnelIds = personnel.filter(p => 
                p.mudur_yardimcisi_id === personnelId && 
                p.role !== "mudur" && 
                p.role !== "mudur_yardimcisi"
              ).map(p => p.id);
              todayOnLeave = todayOnLeave.filter(lr => myPersonnelIds.includes(lr.personnel_id));
            } else if (isBolgeMuhendisi) {
              const myPersonnelIds = personnel.filter(p => 
                p.bolge_muhendisi_id === personnelId && 
                p.role !== "mudur" && 
                p.role !== "mudur_yardimcisi" &&
                p.role !== "bolge_muhendisi"
              ).map(p => p.id);
              todayOnLeave = todayOnLeave.filter(lr => myPersonnelIds.includes(lr.personnel_id));
            } else if (isFormen) {
              const myPersonnelIds = personnel.filter(p => 
                p.formen_id === personnelId &&
                (p.role === "park_gorevlisi")
              ).map(p => p.id);
              todayOnLeave = todayOnLeave.filter(lr => myPersonnelIds.includes(lr.personnel_id));
            }
            
            // İzin türü filtrelemesi
            if (leaveTypeFilter !== 'all') {
              todayOnLeave = todayOnLeave.filter(lr => lr.reason_type === leaveTypeFilter);
            }

            // Tür filtresinden geçenler, henüz organizasyon filtresi uygulanmadan.
            // Dropdown seçenekleri bu kümeden türetilir (kademeli filtre).
            const leaveWithPerson = todayOnLeave
              .map((lr) => ({ lr, person: personnel.find((p) => p.id === lr.personnel_id) }))
              .filter((x) => !!x.person);

            const lf = leaveOrgFilters;
            const leaveOrgOpts = buildPersonnelOrgFilterOptions(
              leaveWithPerson.map((x) => x.person), lf
            );
            const visibleLeaves = leaveWithPerson.filter(
              (x) => personnelPassesOrgFilter(x.person, lf, null)
            );

            const leaveFilterBar = (
              <div className="mb-4">
                <DetailOrgFilterBar
                  filters={lf}
                  setFilters={setLeaveOrgFilters}
                  regionOptions={leaveOrgOpts.regionOptions}
                  formenOptions={leaveOrgOpts.formenOptions}
                  bolgeMuhOptions={leaveOrgOpts.bolgeMuhOptions}
                  mudurYrdOptions={leaveOrgOpts.mudurYrdOptions}
                  searchPlaceholder="Personel adı ara..."
                  searchAriaLabel="Personel adı ara"
                />
              </div>
            );

            // Bugün hiç izinli yoksa filtre çubuğu YİNE gösterilir; gizlemek
            // "filtre nerede?" sorusuna yol açıyordu. Bu durumda filtrelenecek
            // bir şey olmadığı için sade mesaj yeter.
            if (leaveWithPerson.length === 0) {
              return (
                <>
                  {leaveFilterBar}
                  <p className="text-center text-gray-500 py-8">
                    {leaveTypeFilter === 'all'
                      ? `${leaveDayLabel || 'Seçili gün'} izinli personel bulunmamaktadır.`
                      : `${leaveDayLabel || 'Seçili gün'} ${leaveTypeFilter} kullanan personel bulunmamaktadır.`}
                  </p>
                </>
              );
            }

            // İzinli var ama filtreler hepsini eledi → hangi filtrenin eledigini yaz.
            if (visibleLeaves.length === 0) {
              return (
                <>
                  {leaveFilterBar}
                  <DetailFilterEmptyState
                    filters={lf}
                    setFilters={setLeaveOrgFilters}
                    {...leaveOrgOpts}
                    noun="personel"
                    searchChipLabel="Personel adı"
                  />
                </>
              );
            }

            // İzin türüne göre renk şeması
            const getLeaveTypeColors = (reasonType) => {
              switch(reasonType) {
                case 'Yıllık İzin':
                  return { border: 'border-blue-400', bg: 'bg-blue-50', hover: 'hover:bg-blue-100', badgeBg: 'bg-blue-100', badgeText: 'text-blue-700', badgeBorder: 'border border-blue-300' };
                case 'Sosyal İzin':
                  return { border: 'border-purple-400', bg: 'bg-purple-50', hover: 'hover:bg-purple-100', badgeBg: 'bg-purple-100', badgeText: 'text-purple-700', badgeBorder: 'border border-purple-300' };
                case 'Diğer':
                  return { border: 'border-gray-400', bg: 'bg-gray-50', hover: 'hover:bg-gray-100', badgeBg: 'bg-gray-100', badgeText: 'text-gray-700', badgeBorder: 'border border-gray-300' };
                default:
                  return { border: 'border-blue-400', bg: 'bg-blue-50', hover: 'hover:bg-blue-100', badgeBg: 'bg-blue-100', badgeText: 'text-blue-700', badgeBorder: 'border border-blue-300' };
              }
            };
            
            return (
              <>
              {leaveFilterBar}
              <div className="space-y-3">
                {visibleLeaves.map(({ lr: leave, person }) => {
                  const isParkWorker = person.role === 'park_gorevlisi';
                  const assignedParks = isParkWorker ? parks.filter(park =>
                    park.assigned_personnel_ids && park.assigned_personnel_ids.includes(person.id)
                  ) : [];
                  // Park + park-başına vekil: yetkili kaynak izin/vekalet kaydının
                  // per-park kırılımıdır (Aktif Vekaletler kartıyla aynı). park tablosu
                  // `assigned_personnel_ids` JSON kolonu ile park_assignment tablosu
                  // senkron dışı kalabildiğinden (kimi personelin ataması yalnız
                  // park_assignment'ta olur) breakdown'ı önceliklendir; assigned parkları
                  // da birleştir ki senkron olan kayıtlarda kapsanmayan park da görünsün.
                  const parkVekilList = isParkWorker
                    ? buildLeaveParkVekilList(
                        person, assignedParks, allActiveSubstitutions,
                        // Bugün için null → bugünkü çıktı bit bit aynı kalır.
                        // İleri gün seçiliyse planı talebin kendisinden oku.
                        leaveDayOffset === 0 ? null : leave
                      )
                    : [];

                  const formenName = person.formen_name || (person.formen_id ? personnel.find(p => p.id === person.formen_id)?.name : null) || null;
                  const bolgeMuhName = person.bolge_muhendisi_name || (person.bolge_muhendisi_id ? personnel.find(p => p.id === person.bolge_muhendisi_id)?.name : null) || null;
                  const mudurYrdName = person.mudur_yardimcisi_name || (person.mudur_yardimcisi_id ? personnel.find(p => p.id === person.mudur_yardimcisi_id)?.name : null) || null;

                  const colors = getLeaveTypeColors(leave.reason_type);
                  
                  return (
                    <div key={leave.id} className={`border-l-4 ${colors.border} rounded-lg p-3 ${colors.bg} ${colors.hover} transition-colors`}>
                      {/* Üst satır: İsim, Rol, İzin Türü, Tarih */}
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        <h4 className="font-semibold text-lg">{person.name}</h4>
                        <Badge className={`${getRoleBadge(person.role).className} text-xs`}>
                          {getRoleBadge(person.role).label}
                        </Badge>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.badgeBg} ${colors.badgeText} ${colors.badgeBorder}`}>
                          {leave.reason_type}
                        </span>
                        <LeaveDateRangePopover
                          startDate={leave.start_date}
                          endDate={leave.end_date}
                          returnDate={leave.return_date}
                          totalDays={leave.total_days}
                          weekdays={leave.weekdays}
                          weekends={leave.weekends}
                        />
                        {renderReturnDateChip(leave.return_date)}
                      </div>

                      {/* Açıklama — izin TÜRÜ tek başına "neden 2 gün sosyal izin?"
                          sorusunu cevaplamıyordu; talep sırasında girilen detay
                          (ör. "Sendika Seçimi (Delege)") burada da gösterilir.
                          reason_detail seçilen alt-sebep, reason ise serbest not. */}
                      {(leave.reason_detail || leave.reason) && (
                        <div className="mb-3 -mt-1 flex flex-wrap items-baseline gap-x-1.5 text-xs">
                          <span className="text-gray-500">📝 Açıklama:</span>
                          {leave.reason_detail && (
                            <span className="font-medium text-gray-800">{leave.reason_detail}</span>
                          )}
                          {leave.reason && leave.reason !== leave.reason_detail && (
                            <span className="italic text-gray-600">
                              {leave.reason_detail ? `— ${leave.reason}` : leave.reason}
                            </span>
                          )}
                        </div>
                      )}


                      {/* Hiyerarşi ve Park bilgileri - Grid kutu kutu - 4 sütun yan yana */}
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        {/* Park ve Vekalet - sadece park görevlisi için */}
                        {isParkWorker && (
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500 font-medium mb-1">🏞️ Parklar ve Vekalet</p>
                            {parkVekilList.length > 0 ? (
                              <div className="space-y-1">
                                {parkVekilList.map((pv, idx) => (
                                  <div key={pv.id || idx} className="flex items-center gap-1 flex-wrap">
                                    <span className="font-semibold text-gray-800">{idx + 1}. {pv.name}</span>
                                    {pv.substituteName && (
                                      <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded border border-green-300">
                                        → {pv.substituteName}&apos;DE
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">-</span>
                            )}
                          </div>
                        )}
                        {/* Park görevlisi değilse boş alan */}
                        {!isParkWorker && <div></div>}
                        {/* Formen */}
                        {person.role !== 'formen' && person.role !== 'bolge_muhendisi' && person.role !== 'mudur_yardimcisi' && person.role !== 'mudur' ? (
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500 font-medium">👷 Formen</p>
                            <p className="font-semibold text-gray-800 truncate">
                              {formenName ? formenName : <span className="text-gray-400 italic">-</span>}
                            </p>
                          </div>
                        ) : <div></div>}
                        {/* Bölge Mühendisi */}
                        {person.role !== 'bolge_muhendisi' && person.role !== 'mudur_yardimcisi' && person.role !== 'mudur' ? (
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500 font-medium">👨‍💼 Bölge Müh.</p>
                            <p className="font-semibold text-gray-800 truncate">
                              {bolgeMuhName ? bolgeMuhName : <span className="text-gray-400 italic">-</span>}
                            </p>
                          </div>
                        ) : <div></div>}
                        {/* Müdür Yardımcısı */}
                        {person.role !== 'mudur_yardimcisi' && person.role !== 'mudur' ? (
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500 font-medium">👔 Müdür Yrd.</p>
                            <p className="font-semibold text-gray-800 truncate">
                              {mudurYrdName ? mudurYrdName : <span className="text-gray-400 italic">-</span>}
                            </p>
                          </div>
                        ) : <div></div>}
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            );
          })()}
        </div>
      </DraggableModal>

      {/* Raporlu Personel Dialog - DraggableModal */}
      <DraggableModal
        isOpen={onReportDialogOpen}
        onClose={() => {
          setOnReportDialogOpen(false);
          setReportOrgFilters({ ...EMPTY_DETAIL_FILTERS });
        }}
        title="🩺 Bugün Raporlu Personeller"
        subtitle={new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        maxWidth="max-w-4xl"
      >
        <div className="max-h-[60vh] overflow-y-auto">
          {(() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            let todayOnReport = leaveRequests.filter(lr => {
              if (lr.status !== 'approved' || lr.reason_type !== 'Rapor') return false;
              const startDate = new Date(lr.start_date);
              const endDate = new Date(lr.end_date);
              startDate.setHours(0, 0, 0, 0);
              endDate.setHours(0, 0, 0, 0);
              return today >= startDate && today <= endDate;
            });
            
            // Rol bazlı filtreleme
            if (isMudurYardimcisi) {
              const myPersonnelIds = personnel.filter(p => 
                p.mudur_yardimcisi_id === personnelId && 
                p.role !== "mudur" && 
                p.role !== "mudur_yardimcisi"
              ).map(p => p.id);
              todayOnReport = todayOnReport.filter(lr => myPersonnelIds.includes(lr.personnel_id));
            } else if (isBolgeMuhendisi) {
              const myPersonnelIds = personnel.filter(p => 
                p.bolge_muhendisi_id === personnelId && 
                p.role !== "mudur" && 
                p.role !== "mudur_yardimcisi" &&
                p.role !== "bolge_muhendisi"
              ).map(p => p.id);
              todayOnReport = todayOnReport.filter(lr => myPersonnelIds.includes(lr.personnel_id));
            } else if (isFormen) {
              const myPersonnelIds = personnel.filter(p => 
                p.formen_id === personnelId &&
                (p.role === "park_gorevlisi")
              ).map(p => p.id);
              todayOnReport = todayOnReport.filter(lr => myPersonnelIds.includes(lr.personnel_id));
            }
            
            // İzinli modalıyla aynı kademeli filtre: kişi → bölge çözümü
            // getPersonnelOrgMeta üzerinden yapılır.
            const reportWithPerson = todayOnReport
              .map((lr) => ({ lr, person: personnel.find((p) => p.id === lr.personnel_id) }))
              .filter((x) => !!x.person);

            const rf = reportOrgFilters;
            const reportOrgOpts = buildPersonnelOrgFilterOptions(
              reportWithPerson.map((x) => x.person), rf
            );
            const visibleReports = reportWithPerson.filter(
              (x) => personnelPassesOrgFilter(x.person, rf, null)
            );

            const reportFilterBar = (
              <div className="mb-4">
                <DetailOrgFilterBar
                  filters={rf}
                  setFilters={setReportOrgFilters}
                  regionOptions={reportOrgOpts.regionOptions}
                  formenOptions={reportOrgOpts.formenOptions}
                  bolgeMuhOptions={reportOrgOpts.bolgeMuhOptions}
                  mudurYrdOptions={reportOrgOpts.mudurYrdOptions}
                  searchPlaceholder="Personel adı ara..."
                  searchAriaLabel="Personel adı ara"
                />
              </div>
            );

            // Hiç raporlu yoksa da çubuk gösterilir (izinli modalıyla aynı kural).
            if (reportWithPerson.length === 0) {
              return (
                <>
                  {reportFilterBar}
                  <p className="text-center text-gray-500 py-8">Bugün raporlu personel bulunmamaktadır.</p>
                </>
              );
            }

            if (visibleReports.length === 0) {
              return (
                <>
                  {reportFilterBar}
                  <DetailFilterEmptyState
                    filters={rf}
                    setFilters={setReportOrgFilters}
                    {...reportOrgOpts}
                    noun="personel"
                    searchChipLabel="Personel adı"
                  />
                </>
              );
            }

            return (
              <>
              {reportFilterBar}
              <div className="space-y-3">
                {visibleReports.map(({ lr: leave, person }) => {
                  const isParkGorevlisi = person.role === 'park_gorevlisi';
                  const assignedParks = isParkGorevlisi
                    ? parks.filter(park => park.assigned_personnel_ids && park.assigned_personnel_ids.includes(person.id))
                    : [];
                  const parkVekilList = isParkGorevlisi
                    ? buildLeaveParkVekilList(person, assignedParks, allActiveSubstitutions)
                    : [];

                  const formenName = person.formen_name || (person.formen_id ? personnel.find(p => p.id === person.formen_id)?.name : null) || null;
                  const bolgeMuhName = person.bolge_muhendisi_name || (person.bolge_muhendisi_id ? personnel.find(p => p.id === person.bolge_muhendisi_id)?.name : null) || null;
                  const mudurYrdName = person.mudur_yardimcisi_name || (person.mudur_yardimcisi_id ? personnel.find(p => p.id === person.mudur_yardimcisi_id)?.name : null) || null;

                  return (
                    <div key={leave.id} className="border-l-4 border-pink-400 rounded-lg p-3 bg-pink-50 hover:bg-pink-100 transition-colors">
                      {/* Üst satır: İsim, Rol, Rapor, Tarih */}
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        <h4 className="font-semibold text-lg">{person.name}</h4>
                        <Badge className={`${getRoleBadge(person.role).className} text-xs`}>
                          {getRoleBadge(person.role).label}
                        </Badge>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-700 border border-pink-300">
                          Rapor
                        </span>
                        <LeaveDateRangePopover
                          startDate={leave.start_date}
                          endDate={leave.end_date}
                          returnDate={leave.return_date}
                          totalDays={leave.total_days}
                          weekdays={leave.weekdays}
                          weekends={leave.weekends}
                        />
                        {renderReturnDateChip(leave.return_date)}
                      </div>

                      {/* Rapor fotoğrafı önizlemesi — İzin Yönetimi kartındaki ile
                          aynı davranış: küçük görsele tıklayınca uygulama içi
                          görüntüleyici açılır (yakınlaştır / döndür / kaydır).
                          Önizleme olmadan yönetici raporun okunur olup olmadığını
                          ancak indirerek anlayabiliyordu. */}
                      {leave.report_file && (() => {
                        const openReport = () => setReportViewer({
                          src: leave.report_file,
                          filename: leave.report_filename,
                          alt: `Rapor — ${person.name}`,
                        });
                        return (
                          <div className="mb-3 flex items-center gap-3 rounded-lg border border-pink-300 bg-white/70 p-2">
                            <button
                              type="button"
                              onClick={openReport}
                              title="Raporu büyüt"
                              className="shrink-0 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500"
                            >
                              <img
                                src={leave.report_file}
                                alt="Rapor"
                                className="h-20 w-16 cursor-zoom-in rounded border border-pink-300 bg-white object-contain transition-opacity hover:opacity-80"
                              />
                            </button>
                            <div className="min-w-0 text-xs">
                              <p className="font-semibold text-pink-900">📄 Rapor Fotoğrafı</p>
                              {leave.report_filename && (
                                <p className="truncate text-gray-600" title={leave.report_filename}>
                                  {leave.report_filename}
                                </p>
                              )}
                              <div className="mt-1 flex flex-wrap gap-3">
                                <button
                                  type="button"
                                  onClick={openReport}
                                  className="font-medium text-blue-600 hover:underline"
                                >
                                  🔍 Büyüt
                                </button>
                                <a
                                  href={leave.report_file}
                                  download={leave.report_filename || 'rapor.jpg'}
                                  className="text-blue-600 hover:underline"
                                >
                                  📎 İndir
                                </a>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Hiyerarşi ve Park bilgileri - Grid kutu kutu - 4 sütun yan yana */}
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        {/* Park ve Vekalet - sadece park görevlisi için */}
                        {isParkGorevlisi && (
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500 font-medium mb-1">🏞️ Parklar ve Vekalet</p>
                            {parkVekilList.length > 0 ? (
                              <div className="space-y-1">
                                {parkVekilList.map((pv, idx) => (
                                  <div key={pv.id || idx} className="flex items-center gap-1 flex-wrap">
                                    <span className="font-semibold text-gray-800">{idx + 1}. {pv.name}</span>
                                    {pv.substituteName && (
                                      <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded border border-green-300">
                                        → {pv.substituteName}&apos;DE
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">-</span>
                            )}
                          </div>
                        )}
                        {/* Park görevlisi değilse boş alan */}
                        {!isParkGorevlisi && <div></div>}
                        {/* Formen */}
                        {person.role !== 'formen' && person.role !== 'bolge_muhendisi' && person.role !== 'mudur_yardimcisi' && person.role !== 'mudur' ? (
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500 font-medium">👷 Formen</p>
                            <p className="font-semibold text-gray-800 truncate">
                              {formenName ? formenName : <span className="text-gray-400 italic">-</span>}
                            </p>
                          </div>
                        ) : <div></div>}
                        {/* Bölge Mühendisi */}
                        {person.role !== 'bolge_muhendisi' && person.role !== 'mudur_yardimcisi' && person.role !== 'mudur' ? (
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500 font-medium">👨‍💼 Bölge Müh.</p>
                            <p className="font-semibold text-gray-800 truncate">
                              {bolgeMuhName ? bolgeMuhName : <span className="text-gray-400 italic">-</span>}
                            </p>
                          </div>
                        ) : <div></div>}
                        {/* Müdür Yardımcısı */}
                        {person.role !== 'mudur_yardimcisi' && person.role !== 'mudur' ? (
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500 font-medium">👔 Müdür Yrd.</p>
                            <p className="font-semibold text-gray-800 truncate">
                              {mudurYrdName ? mudurYrdName : <span className="text-gray-400 italic">-</span>}
                            </p>
                          </div>
                        ) : <div></div>}
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            );
          })()}
        </div>
      </DraggableModal>

      {/* Rapor fotoğrafı görüntüleyicisi — raporlu personel kartından açılır.
          Portal ile <body> altına basıldığı için üstteki modalın içinde kalmaz. */}
      <ImageLightbox
        open={!!reportViewer}
        onClose={() => setReportViewer(null)}
        src={reportViewer?.src}
        alt={reportViewer?.alt || 'Rapor'}
        filename={reportViewer?.filename}
      />

      {/* İzni Azalan Personeller Dialog */}
      <Dialog open={lowLeaveDialogOpen} onOpenChange={setLowLeaveDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              İzni Azalan Personeller
            </DialogTitle>
            <DialogDescription>
              Yıllık izin hakkı 5 gün veya daha az kalan park görevlileri
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {(() => {
              const lowLeaveList = stats.lowLeavePersonnel || [];
              
              if (lowLeaveList.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-2 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                    <p>Tüm personelin yeterli izin hakkı var</p>
                  </div>
                );
              }
              
              // Kalan izne göre sırala (azdan çoğa)
              const sortedList = [...lowLeaveList].sort((a, b) => {
                const remainingA = (a.annual_leave_days || 20) - (a.used_leave_days || 0);
                const remainingB = (b.annual_leave_days || 20) - (b.used_leave_days || 0);
                return remainingA - remainingB;
              });
              
              return (
                <div className="space-y-3">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-orange-800">
                      ⚠️ <strong>{sortedList.length}</strong> personelin yıllık izin hakkı 5 gün veya daha az kalmış
                    </p>
                  </div>
                  
                  {sortedList.map(person => {
                    const remaining = (person.annual_leave_days || 20) - (person.used_leave_days || 0);
                    const assignedParks = parks.filter(p => 
                      p.assigned_personnel_ids && p.assigned_personnel_ids.includes(person.id)
                    );
                    const formen = personnel.find(p => p.id === person.formen_id);
                    
                    return (
                      <div 
                        key={person.id}
                        className={`rounded-lg border p-4 ${
                          remaining <= 0 ? 'bg-red-50 border-red-300' :
                          remaining <= 2 ? 'bg-orange-50 border-orange-300' :
                          'bg-yellow-50 border-yellow-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                              remaining <= 0 ? 'bg-red-500' :
                              remaining <= 2 ? 'bg-orange-500' :
                              'bg-yellow-500'
                            }`}>
                              {person.name?.charAt(0)}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{person.name}</p>
                              <p className="text-xs text-gray-500">
                                {assignedParks.length > 0 ? assignedParks.map(p => p.name).join(', ') : 'Park atanmamış'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-2xl font-bold ${
                              remaining <= 0 ? 'text-red-600' :
                              remaining <= 2 ? 'text-orange-600' :
                              'text-yellow-600'
                            }`}>
                              {remaining} gün
                            </p>
                            <p className="text-xs text-gray-500">kalan izin</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 text-xs mt-3">
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500">Toplam Hak</p>
                            <p className="font-semibold">{person.annual_leave_days || 20} gün</p>
                          </div>
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500">Kullanılan</p>
                            <p className="font-semibold">{person.used_leave_days || 0} gün</p>
                          </div>
                          <div className="bg-white/60 rounded p-2">
                            <p className="text-gray-500">Formen</p>
                            <p className="font-semibold truncate">{formen?.name || '-'}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button onClick={() => setLowLeaveDialogOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Envanter/Tesis/Spor/Sulama Detay - Sürüklenebilir, sağ üst X ile kapatılır */}
      <DraggableModal
        isOpen={detailDialogOpen}
        onClose={() => {
          setRunningTrackSurfaceFilters({
            suniCim: false,
            belirtilmemis: false,
            kum: false,
            kaucuk: false,
          });
          setPlaygroundSurfaceFilters({
            ahsap: false,
            belirtilmemis: false,
            dere_cakili: false,
            kaucuk: false,
            kum: false,
            sentetik_cim: false,
          });
          setSportsEquipmentSurfaceFilters({
            asfalt: false,
            belirtilmemis: false,
            diger: false,
            kaucuk: false,
            kilit_tasi: false,
            sentetik_cim: false,
          });
          setDetailDialogOpen(false);
        }}
        title={detailDialogTitle}
        subtitle={
          detailDialogTitle === "Yürüme/Koşu Yolu Detayları"
            ? "Parkların yürüme/koşu yolu özellikleri, boyutları ve tipleri"
            : detailDialogTitle === "Çocuk Oyun Grubu Detayları"
            ? "Parkların çocuk oyun grubu alanları ve zemin tipleri"
            : detailDialogTitle === "Spor Aleti Detayları"
            ? "Parkların spor aleti alanları ve zemin tipleri"
            : detailDialogTitle === "Havuz Detayları"
            ? "Parklardaki havuzların çalışma durumları - Çalışan ve Arızalı havuzlar"
            : `Bu ${(detailDialogTitle || "").toLowerCase()} bulunan parklar ve adetleri`
        }
        maxWidth="max-w-4xl"
        footer={
          <Button
            onClick={() => {
              setRunningTrackSurfaceFilters({
                suniCim: false,
                belirtilmemis: false,
                kum: false,
                kaucuk: false,
              });
              setPlaygroundSurfaceFilters({
                ahsap: false,
                belirtilmemis: false,
                dere_cakili: false,
                kaucuk: false,
                kum: false,
                sentetik_cim: false,
              });
              setSportsEquipmentSurfaceFilters({
                asfalt: false,
                belirtilmemis: false,
                diger: false,
                kaucuk: false,
                kilit_tasi: false,
                sentetik_cim: false,
              });
              setDetailDialogOpen(false);
            }}
          >
            Kapat
          </Button>
        }
      >
          <div className="mt-2">
            {detailDialogParks.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Bu tesise/envantere sahip park bulunamadı.</p>
            ) : detailDialogTitle === "Spor Aleti Detayları" ? (
              // Özel görünüm: Zemin Tipi → Bölge → Park (+ zemin / bölge / formen / BM / MY filtreleri)
              <div className="space-y-4">
                {(() => {
                  const sf = sportsEquipmentSurfaceFilters;
                  const selectedSurfaceIds = surfaceObjToIds(sf);
                  const anySf = selectedSurfaceIds.length > 0;
                  const matchesSurface = (t) => {
                    if (sf.asfalt && t === "asfalt") return true;
                    if (sf.belirtilmemis && t === "yok") return true;
                    if (sf.diger && t === "diger") return true;
                    if (sf.kaucuk && t === "kaucuk") return true;
                    if (sf.kilit_tasi && t === "kilit_tasi") return true;
                    if (sf.sentetik_cim && t === "sentetik_cim") return true;
                    return false;
                  };

                  // Önce zemin filtresi, sonra organizasyon (bölge/formen/BM/MY) filtresi
                  const surfaceParks = anySf
                    ? detailDialogParks.filter((p) => matchesSurface(p.type || "yok"))
                    : detailDialogParks;
                  const f = invDetailFilters;
                  const orgOpts = buildOrgFilterOptions(surfaceParks, f);
                  const parksForView = surfaceParks.filter((p) => parkPassesOrgFilter(p, f, null));

                  const filterBar = (
                    <DetailOrgFilterBar
                      filters={f}
                      setFilters={setInvDetailFilters}
                      regionOptions={orgOpts.regionOptions}
                      formenOptions={orgOpts.formenOptions}
                      bolgeMuhOptions={orgOpts.bolgeMuhOptions}
                      mudurYrdOptions={orgOpts.mudurYrdOptions}
                      extraActive={anySf}
                      onClearExtra={() =>
                        setSportsEquipmentSurfaceFilters(surfaceIdsToObj(SPORTS_SURFACE_OPTIONS, []))
                      }
                    >
                      <MultiSelectSearch
                        value={selectedSurfaceIds}
                        onChange={(ids) =>
                          setSportsEquipmentSurfaceFilters(surfaceIdsToObj(SPORTS_SURFACE_OPTIONS, ids))
                        }
                        options={SPORTS_SURFACE_OPTIONS}
                        allLabel="Tüm Zeminler"
                        searchPlaceholder="Zemin ara..."
                        className="h-8 text-xs flex-1 min-w-[150px]"
                      />
                    </DetailOrgFilterBar>
                  );

                  if (parksForView.length === 0) {
                    return (
                      <>
                        {filterBar}
                        <DetailFilterEmptyState
                          filters={f}
                          setFilters={setInvDetailFilters}
                          {...orgOpts}
                          extraActive={anySf}
                          extraLabel="Zemin filtresi açık"
                          onClearExtra={() =>
                            setSportsEquipmentSurfaceFilters(surfaceIdsToObj(SPORTS_SURFACE_OPTIONS, []))
                          }
                        />
                      </>
                    );
                  }

                  // 1. ÖNCE ZEMİN TİPİNE göre grupla
                  const groupedByType = parksForView.reduce((acc, park) => {
                    const type = park.type || 'yok';
                    if (!acc[type]) acc[type] = [];
                    acc[type].push(park);
                    return acc;
                  }, {});

                  const typeLabels = {
                    'kaucuk': 'Kauçuk',
                    'asfalt': 'Asfalt',
                    'sentetik_cim': 'Suni Çim',
                    'kilit_tasi': 'Kilit Taşı',
                    'diger': 'Diğer',
                    'yok': 'Belirtilmemiş'
                  };

                  const typeColors = {
                    'kaucuk': { bg: 'from-red-50 to-pink-50', border: 'border-red-300' },
                    'asfalt': { bg: 'from-gray-50 to-slate-50', border: 'border-gray-400' },
                    'sentetik_cim': { bg: 'from-green-50 to-emerald-50', border: 'border-green-400' },
                    'kilit_tasi': { bg: 'from-stone-50 to-zinc-100', border: 'border-stone-400' },
                    'diger': { bg: 'from-orange-50 to-amber-50', border: 'border-orange-300' },
                    'yok': { bg: 'from-orange-50 to-yellow-50', border: 'border-orange-300' }
                  };

                  // Zemin tiplerini sırala (en çok olan önce)
                  return (
                    <>
                      {filterBar}
                      {Object.keys(groupedByType)
                        .sort((a, b) => groupedByType[b].length - groupedByType[a].length)
                        .map(surfaceType => {
                          const parksWithType = groupedByType[surfaceType];

                          // 2. Her zemin tipi içinde BÖLGEYE göre grupla
                          const groupedByRegion = parksWithType.reduce((acc, park) => {
                            const region = park.regionName || 'Bölge Atanmamış';
                            if (!acc[region]) acc[region] = [];
                            acc[region].push(park);
                            return acc;
                          }, {});

                          const totalTypeArea = parksWithType.reduce((sum, p) => sum + p.area, 0);
                          const colors = typeColors[surfaceType] || typeColors['yok'];

                          return (
                            <Card key={surfaceType} className={`bg-gradient-to-br ${colors.bg} ${colors.border} border-2`}>
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-xl flex items-center gap-2">
                                    <Dumbbell className="h-6 w-6" />
                                    {typeLabels[surfaceType] || surfaceType}
                                  </CardTitle>
                                  <div className="text-right">
                                    <Badge className="text-sm">{parksWithType.length} Park</Badge>
                                    <p className="text-xs text-gray-600 mt-1">Toplam: {totalTypeArea.toFixed(2)} m²</p>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                {/* 3. Bölgeleri alfabetik sırala */}
                                {Object.keys(groupedByRegion)
                                  .sort((a, b) => a.localeCompare(b, 'tr'))
                                  .map(regionName => {
                                    const parksInRegion = groupedByRegion[regionName];
                                    const regionArea = parksInRegion.reduce((sum, p) => sum + p.area, 0);

                                    return (
                                      <div key={regionName} className="bg-white rounded-lg p-3 border-2 border-gray-200">
                                        <div className="flex items-center justify-between mb-2 pb-2 border-b">
                                          <h4 className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                                            <TreePine className="h-4 w-4 text-blue-600" />
                                            {regionName}
                                            {renderRegionForemenLabel(parksInRegion[0]?.regionId)}
                                          </h4>
                                          <div className="text-xs text-gray-600">
                                            {parksInRegion.length} Park • {regionArea.toFixed(2)} m²
                                          </div>
                                        </div>
                                        <div className="space-y-1.5">
                                          {/* 4. Parkları alfabetik sırala */}
                                          {parksInRegion
                                            .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                                            .map((park, idx) => (
                                              <div key={idx} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded">
                                                <span className="font-medium text-gray-800">{park.name}</span>
                                                <span className="text-xs font-semibold text-blue-600">📐 {park.area.toFixed(2)} m²</span>
                                              </div>
                                            ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </CardContent>
                            </Card>
                          );
                        })}
                    </>
                  );
                })()}
              </div>
            ) : detailDialogTitle === "Havuz Detayları" ? (
              // Özel görünüm: Havuz Durumu → Bölge → Park (+ durum / bölge / formen / BM / MY filtreleri)
              <div className="space-y-4">
                {(() => {
                  const psf = poolStatusFilters;
                  const selectedStatusIds = surfaceObjToIds(psf);
                  const anyStatus = selectedStatusIds.length > 0;

                  // Önce havuz durumu filtresi, sonra organizasyon (bölge/formen/BM/MY)
                  const statusParks = anyStatus
                    ? detailDialogParks.filter((p) => psf[p.status || "yok"])
                    : detailDialogParks;
                  const f = invDetailFilters;
                  const orgOpts = buildOrgFilterOptions(statusParks, f);
                  const parksForView = statusParks.filter((p) => parkPassesOrgFilter(p, f, null));

                  const filterBar = (
                    <DetailOrgFilterBar
                      filters={f}
                      setFilters={setInvDetailFilters}
                      regionOptions={orgOpts.regionOptions}
                      formenOptions={orgOpts.formenOptions}
                      bolgeMuhOptions={orgOpts.bolgeMuhOptions}
                      mudurYrdOptions={orgOpts.mudurYrdOptions}
                      extraActive={anyStatus}
                      onClearExtra={() =>
                        setPoolStatusFilters(surfaceIdsToObj(POOL_STATUS_OPTIONS, []))
                      }
                    >
                      <MultiSelectSearch
                        value={selectedStatusIds}
                        onChange={(ids) =>
                          setPoolStatusFilters(surfaceIdsToObj(POOL_STATUS_OPTIONS, ids))
                        }
                        options={POOL_STATUS_OPTIONS}
                        allLabel="Tüm Durumlar"
                        searchPlaceholder="Durum ara..."
                        className="h-8 text-xs flex-1 min-w-[150px]"
                      />
                    </DetailOrgFilterBar>
                  );

                  // DURUMA göre grupla
                  const groupedByStatus = parksForView.reduce((acc, park) => {
                    const status = park.status || 'yok';
                    if (!acc[status]) acc[status] = [];
                    acc[status].push(park);
                    return acc;
                  }, {});

                  const statusLabels = {
                    'calisiyor': 'Çalışıyor',
                    'arizali': 'Arızalı',
                    'yok': 'Belirtilmemiş'
                  };

                  const statusColors = {
                    'calisiyor': { bg: 'from-green-50 to-emerald-50', border: 'border-green-400', icon: '✅' },
                    'arizali': { bg: 'from-red-50 to-pink-50', border: 'border-red-400', icon: '❌' },
                    'yok': { bg: 'from-gray-50 to-slate-50', border: 'border-gray-300', icon: '❓' }
                  };

                  const statusOrder = ['calisiyor', 'arizali', 'yok'];
                  const visibleStatuses = statusOrder.filter(
                    (status) => groupedByStatus[status] && groupedByStatus[status].length > 0
                  );

                  return (
                    <>
                      {filterBar}
                      {visibleStatuses.length === 0 ? (
                        <DetailFilterEmptyState
                          filters={f}
                          setFilters={setInvDetailFilters}
                          {...orgOpts}
                          extraActive={anyStatus}
                          extraLabel="Havuz durumu filtresi açık"
                          onClearExtra={() =>
                            setPoolStatusFilters(surfaceIdsToObj(POOL_STATUS_OPTIONS, []))
                          }
                        />
                      ) : (
                        visibleStatuses.map(status => {
                          const parksWithStatus = groupedByStatus[status];

                          // Her durum içinde BÖLGEYE göre grupla
                          const groupedByRegion = parksWithStatus.reduce((acc, park) => {
                            const region = park.regionName || 'Bölge Atanmamış';
                            if (!acc[region]) acc[region] = [];
                            acc[region].push(park);
                            return acc;
                          }, {});

                          const colors = statusColors[status] || statusColors['yok'];

                          return (
                            <Card key={status} className={`bg-gradient-to-br ${colors.bg} ${colors.border} border-2`}>
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-xl flex items-center gap-2">
                                    <span className="text-2xl">{colors.icon}</span>
                                    {statusLabels[status]}
                                  </CardTitle>
                                  <div className="text-right">
                                    <Badge className="text-sm">{parksWithStatus.length} Park</Badge>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                {/* Bölgeleri alfabetik sırala */}
                                {Object.keys(groupedByRegion)
                                  .sort((a, b) => a.localeCompare(b, 'tr'))
                                  .map(regionName => {
                                    const parksInRegion = groupedByRegion[regionName];

                                    return (
                                      <div key={regionName} className="bg-white rounded-lg p-3 border-2 border-gray-200">
                                        <div className="flex items-center justify-between mb-2 pb-2 border-b">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <Badge variant="outline" className="text-xs">{regionName}</Badge>
                                            {renderRegionForemenLabel(parksInRegion[0]?.regionId)}
                                          </div>
                                          <span className="text-xs text-gray-500">{parksInRegion.length} park</span>
                                        </div>

                                        {/* Parkları alfabetik sırala */}
                                        <div className="flex flex-wrap gap-2">
                                          {parksInRegion
                                            .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                                            .map(park => (
                                              <Badge key={park.name} variant="secondary" className="text-xs py-1 px-2 bg-white border">
                                                🌳 {park.name}
                                              </Badge>
                                            ))
                                          }
                                        </div>
                                      </div>
                                    );
                                  })}
                              </CardContent>
                            </Card>
                          );
                        })
                      )}
                    </>
                  );
                })()}
              </div>
            ) : detailDialogTitle === "Çocuk Oyun Grubu Detayları" ? (
              // Özel görünüm: Zemin Tipi → Bölge → Park (+ zemin / bölge / formen / BM / MY filtreleri)
              <div className="space-y-4">
                {(() => {
                  const pf = playgroundSurfaceFilters;
                  const selectedSurfaceIds = surfaceObjToIds(pf);
                  const anyPf = selectedSurfaceIds.length > 0;
                  const matchesSurface = (t) => {
                    if (pf.ahsap && t === "ahsap") return true;
                    if (pf.belirtilmemis && t === "yok") return true;
                    if (pf.dere_cakili && t === "dere_cakili") return true;
                    if (pf.kaucuk && t === "kaucuk") return true;
                    if (pf.kum && t === "kum") return true;
                    if (pf.sentetik_cim && t === "sentetik_cim") return true;
                    return false;
                  };

                  // Önce zemin filtresi, sonra organizasyon (bölge/formen/BM/MY) filtresi
                  const surfaceParks = anyPf
                    ? detailDialogParks.filter((p) => matchesSurface(p.type || "yok"))
                    : detailDialogParks;
                  const f = invDetailFilters;
                  const orgOpts = buildOrgFilterOptions(surfaceParks, f);
                  const parksForView = surfaceParks.filter((p) => parkPassesOrgFilter(p, f, null));

                  const filterBar = (
                    <DetailOrgFilterBar
                      filters={f}
                      setFilters={setInvDetailFilters}
                      regionOptions={orgOpts.regionOptions}
                      formenOptions={orgOpts.formenOptions}
                      bolgeMuhOptions={orgOpts.bolgeMuhOptions}
                      mudurYrdOptions={orgOpts.mudurYrdOptions}
                      extraActive={anyPf}
                      onClearExtra={() =>
                        setPlaygroundSurfaceFilters(surfaceIdsToObj(PLAYGROUND_SURFACE_OPTIONS, []))
                      }
                    >
                      <MultiSelectSearch
                        value={selectedSurfaceIds}
                        onChange={(ids) =>
                          setPlaygroundSurfaceFilters(surfaceIdsToObj(PLAYGROUND_SURFACE_OPTIONS, ids))
                        }
                        options={PLAYGROUND_SURFACE_OPTIONS}
                        allLabel="Tüm Zeminler"
                        searchPlaceholder="Zemin ara..."
                        className="h-8 text-xs flex-1 min-w-[150px]"
                      />
                    </DetailOrgFilterBar>
                  );

                  if (parksForView.length === 0) {
                    return (
                      <>
                        {filterBar}
                        <DetailFilterEmptyState
                          filters={f}
                          setFilters={setInvDetailFilters}
                          {...orgOpts}
                          extraActive={anyPf}
                          extraLabel="Zemin filtresi açık"
                          onClearExtra={() =>
                            setPlaygroundSurfaceFilters(surfaceIdsToObj(PLAYGROUND_SURFACE_OPTIONS, []))
                          }
                        />
                      </>
                    );
                  }

                  // 1. ÖNCE ZEMİN TİPİNE göre grupla
                  const groupedByType = parksForView.reduce((acc, park) => {
                    const type = park.type || 'yok';
                    if (!acc[type]) acc[type] = [];
                    acc[type].push(park);
                    return acc;
                  }, {});

                  const typeLabels = {
                    'dere_cakili': 'Dere Çakılı',
                    'kaucuk': 'Kauçuk',
                    'kum': 'Kum',
                    'sentetik_cim': 'Suni Çim',
                    'ahsap': 'Ahşap',
                    'kilit_tasi': 'Kilit Taşı',
                    'yok': 'Belirtilmemiş'
                  };

                  const typeColors = {
                    'dere_cakili': { bg: 'from-gray-50 to-slate-50', border: 'border-gray-400' },
                    'kaucuk': { bg: 'from-red-50 to-pink-50', border: 'border-red-300' },
                    'kum': { bg: 'from-yellow-50 to-amber-50', border: 'border-yellow-400' },
                    'sentetik_cim': { bg: 'from-green-50 to-emerald-50', border: 'border-green-400' },
                    'ahsap': { bg: 'from-orange-50 to-amber-50', border: 'border-orange-400' },
                    'kilit_tasi': { bg: 'from-stone-50 to-zinc-100', border: 'border-stone-400' },
                    'yok': { bg: 'from-purple-50 to-pink-50', border: 'border-purple-300' }
                  };

                  // Zemin tiplerini sırala (en çok olan önce)
                  return (
                    <>
                      {filterBar}
                      {Object.keys(groupedByType)
                        .sort((a, b) => groupedByType[b].length - groupedByType[a].length)
                        .map(surfaceType => {
                          const parksWithType = groupedByType[surfaceType];

                          // 2. Her zemin tipi içinde BÖLGEYE göre grupla
                          const groupedByRegion = parksWithType.reduce((acc, park) => {
                            const region = park.regionName || 'Bölge Atanmamış';
                            if (!acc[region]) acc[region] = [];
                            acc[region].push(park);
                            return acc;
                          }, {});

                          const totalTypeArea = parksWithType.reduce((sum, p) => sum + p.area, 0);
                          const colors = typeColors[surfaceType] || typeColors['yok'];

                          return (
                            <Card key={surfaceType} className={`bg-gradient-to-br ${colors.bg} ${colors.border} border-2`}>
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-xl flex items-center gap-2">
                                    <Baby className="h-6 w-6" />
                                    {typeLabels[surfaceType] || surfaceType}
                                  </CardTitle>
                                  <div className="text-right">
                                    <Badge className="text-sm">{parksWithType.length} Park</Badge>
                                    <p className="text-xs text-gray-600 mt-1">Toplam: {totalTypeArea.toFixed(2)} m²</p>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                {/* 3. Bölgeleri alfabetik sırala */}
                                {Object.keys(groupedByRegion)
                                  .sort((a, b) => a.localeCompare(b, 'tr'))
                                  .map(regionName => {
                                    const parksInRegion = groupedByRegion[regionName];
                                    const regionArea = parksInRegion.reduce((sum, p) => sum + p.area, 0);

                                    return (
                                      <div key={regionName} className="bg-white rounded-lg p-3 border-2 border-gray-200">
                                        <div className="flex items-center justify-between mb-2 pb-2 border-b">
                                          <h4 className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                                            <TreePine className="h-4 w-4 text-blue-600" />
                                            {regionName}
                                            {renderRegionForemenLabel(parksInRegion[0]?.regionId)}
                                          </h4>
                                          <div className="text-xs text-gray-600">
                                            {parksInRegion.length} Park • {regionArea.toFixed(2)} m²
                                          </div>
                                        </div>
                                        <div className="space-y-1.5">
                                          {/* 4. Parkları alfabetik sırala */}
                                          {parksInRegion
                                            .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                                            .map((park, idx) => (
                                              <div key={idx} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded">
                                                <span className="font-medium text-gray-800">{park.name}</span>
                                                <span className="text-xs font-semibold text-blue-600">📐 {park.area.toFixed(2)} m²</span>
                                              </div>
                                            ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </CardContent>
                            </Card>
                          );
                        })}
                    </>
                  );
                })()}
              </div>
            ) : detailDialogTitle === "Yürüme/Koşu Yolu Detayları" ? (
              // Özel görünüm: Zemin Tipi → Bölge → Park (+ zemin / bölge / formen / BM / MY filtreleri)
              <div className="space-y-4">
                {(() => {
                  const MIXED = "sentetik_cim_ve_kaucuk";
                  const rf = runningTrackSurfaceFilters;
                  const selectedSurfaceIds = surfaceObjToIds(rf);
                  const anyFilter = selectedSurfaceIds.length > 0;
                  const matchesSurface = (t) => {
                    if (rf.suniCim && (t === "sentetik_cim" || t === MIXED)) return true;
                    if (rf.belirtilmemis && t === "yok") return true;
                    if (rf.kum && t === "kum") return true;
                    if (rf.kaucuk && (t === "kaucuk" || t === MIXED)) return true;
                    return false;
                  };

                  // Önce zemin filtresi, sonra organizasyon (bölge/formen/BM/MY) filtresi
                  const surfaceParks = anyFilter
                    ? detailDialogParks.filter((p) => matchesSurface(p.type || "yok"))
                    : detailDialogParks;
                  const f = invDetailFilters;
                  const orgOpts = buildOrgFilterOptions(surfaceParks, f);
                  const parksForView = surfaceParks.filter((p) => parkPassesOrgFilter(p, f, null));

                  const filterBar = (
                    <DetailOrgFilterBar
                      filters={f}
                      setFilters={setInvDetailFilters}
                      regionOptions={orgOpts.regionOptions}
                      formenOptions={orgOpts.formenOptions}
                      bolgeMuhOptions={orgOpts.bolgeMuhOptions}
                      mudurYrdOptions={orgOpts.mudurYrdOptions}
                      extraActive={anyFilter}
                      onClearExtra={() =>
                        setRunningTrackSurfaceFilters(surfaceIdsToObj(RUNNING_TRACK_SURFACE_OPTIONS, []))
                      }
                    >
                      <MultiSelectSearch
                        value={selectedSurfaceIds}
                        onChange={(ids) =>
                          setRunningTrackSurfaceFilters(surfaceIdsToObj(RUNNING_TRACK_SURFACE_OPTIONS, ids))
                        }
                        options={RUNNING_TRACK_SURFACE_OPTIONS}
                        allLabel="Tüm Zeminler"
                        searchPlaceholder="Zemin ara..."
                        className="h-8 text-xs flex-1 min-w-[150px]"
                      />
                    </DetailOrgFilterBar>
                  );

                  if (parksForView.length === 0) {
                    return (
                      <>
                        {filterBar}
                        <DetailFilterEmptyState
                          filters={f}
                          setFilters={setInvDetailFilters}
                          {...orgOpts}
                          extraActive={anyFilter}
                          extraLabel="Zemin filtresi açık"
                          onClearExtra={() =>
                            setRunningTrackSurfaceFilters(surfaceIdsToObj(RUNNING_TRACK_SURFACE_OPTIONS, []))
                          }
                        />
                      </>
                    );
                  }

                  // 1. ÖNCE ZEMİN TİPİNE göre grupla
                  const groupedByType = parksForView.reduce((acc, park) => {
                    const type = park.type || 'yok';
                    if (!acc[type]) acc[type] = [];
                    acc[type].push(park);
                    return acc;
                  }, {});

                  const typeLabels = {
                    'kum': 'Kum',
                    'sentetik_cim': 'Suni Çim',
                    'sentetik_cim_ve_kaucuk': 'Suni Çim + Kauçuk',
                    'kaucuk': 'Kauçuk',
                    'asfalt': 'Asfalt',
                    'parke': 'Parke',
                    'kilit_tasi': 'Kilit Taşı',
                    'yok': 'Belirtilmemiş'
                  };

                  const typeColors = {
                    'kum': { bg: 'from-yellow-50 to-amber-50', border: 'border-yellow-400' },
                    'sentetik_cim': { bg: 'from-green-50 to-emerald-50', border: 'border-green-400' },
                    'sentetik_cim_ve_kaucuk': { bg: 'from-emerald-50 to-rose-50', border: 'border-emerald-500' },
                    'kaucuk': { bg: 'from-rose-50 to-pink-50', border: 'border-rose-400' },
                    'asfalt': { bg: 'from-gray-50 to-slate-50', border: 'border-gray-400' },
                    'parke': { bg: 'from-orange-50 to-red-50', border: 'border-orange-400' },
                    'kilit_tasi': { bg: 'from-stone-50 to-zinc-100', border: 'border-stone-400' },
                    'yok': { bg: 'from-blue-50 to-indigo-50', border: 'border-blue-300' }
                  };

                  // Zemin tiplerini sırala (en çok olan önce)
                  return (
                    <>
                      {filterBar}
                      {Object.keys(groupedByType)
                        .sort((a, b) => groupedByType[b].length - groupedByType[a].length)
                        .map(surfaceType => {
                          const parksWithType = groupedByType[surfaceType];

                          // 2. Her zemin tipi içinde BÖLGEYE göre grupla
                          const groupedByRegion = parksWithType.reduce((acc, park) => {
                            const region = park.regionName || 'Bölge Atanmamış';
                            if (!acc[region]) acc[region] = [];
                            acc[region].push(park);
                            return acc;
                          }, {});

                          const totalTypeArea = parksWithType.reduce((sum, p) => sum + p.area, 0);
                          const colors = typeColors[surfaceType] || typeColors['yok'];

                          return (
                            <Card key={surfaceType} className={`bg-gradient-to-br ${colors.bg} ${colors.border} border-2`}>
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-xl flex items-center gap-2">
                                    <Footprints className="h-6 w-6" />
                                    {typeLabels[surfaceType]}
                                  </CardTitle>
                                  <div className="text-right">
                                    <Badge className="text-sm">{parksWithType.length} Park</Badge>
                                    <p className="text-xs text-gray-600 mt-1">Toplam: {totalTypeArea.toFixed(2)} m²</p>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                {/* 3. Bölgeleri alfabetik sırala */}
                                {Object.keys(groupedByRegion)
                                  .sort((a, b) => a.localeCompare(b, 'tr'))
                                  .map(regionName => {
                                    const parksInRegion = groupedByRegion[regionName];
                                    const regionArea = parksInRegion.reduce((sum, p) => sum + p.area, 0);

                                    return (
                                      <div key={regionName} className="bg-white rounded-lg p-3 border-2 border-gray-200">
                                        <div className="flex items-center justify-between mb-2 pb-2 border-b">
                                          <h4 className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                                            <TreePine className="h-4 w-4 text-blue-600" />
                                            {regionName}
                                            {renderRegionForemenLabel(parksInRegion[0]?.regionId)}
                                          </h4>
                                          <div className="text-xs text-gray-600">
                                            {parksInRegion.length} Park • {regionArea.toFixed(2)} m²
                                          </div>
                                        </div>
                                        <div className="space-y-1.5">
                                          {/* 4. Parkları alfabetik sırala */}
                                          {parksInRegion
                                            .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                                            .map((park, idx) => (
                                              <div key={idx} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded">
                                                <span className="font-medium text-gray-800">{park.name}</span>
                                                <div className="flex items-center gap-3 text-xs text-gray-600">
                                                  <span>📏 {park.length}m</span>
                                                  <span>↔️ {park.width}m</span>
                                                  <span className="font-semibold text-blue-600">📐 {park.area.toFixed(2)} m²</span>
                                                </div>
                                              </div>
                                            ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </CardContent>
                            </Card>
                          );
                        })}
                    </>
                  );
                })()}
              </div>
            ) : (
              // Bölgelere göre gruplandırılmış görünüm (+ bölge / formen / bölge müh. / müdür yrd. filtresi)
              <div className="space-y-4">
                {(() => {
                  const f = invDetailFilters;
                  const { regionOptions, formenOptions, bolgeMuhOptions, mudurYrdOptions } =
                    buildOrgFilterOptions(detailDialogParks, f);

                  // --- Tüm filtreleri uygula ---
                  const filteredParks = detailDialogParks.filter((park) =>
                    parkPassesOrgFilter(park, f, null)
                  );

                  // --- Bölgelere göre grupla + alfabetik sırala ---
                  const groupedByRegion = filteredParks.reduce((acc, park) => {
                    const region = park.regionName || "Bölge Atanmamış";
                    if (!acc[region]) acc[region] = [];
                    acc[region].push(park);
                    return acc;
                  }, {});
                  const sortedRegions = Object.keys(groupedByRegion).sort((a, b) =>
                    a.localeCompare(b, "tr")
                  );

                  return (
                    <>
                      <DetailOrgFilterBar
                        filters={f}
                        setFilters={setInvDetailFilters}
                        regionOptions={regionOptions}
                        formenOptions={formenOptions}
                        bolgeMuhOptions={bolgeMuhOptions}
                        mudurYrdOptions={mudurYrdOptions}
                      />

                      {sortedRegions.length === 0 ? (
                        <DetailFilterEmptyState
                          filters={f}
                          setFilters={setInvDetailFilters}
                          regionOptions={regionOptions}
                          formenOptions={formenOptions}
                          bolgeMuhOptions={bolgeMuhOptions}
                          mudurYrdOptions={mudurYrdOptions}
                        />
                      ) : (
                        sortedRegions.map((regionName) => {
                          const parksInRegion = groupedByRegion[regionName];
                          const totalCount = parksInRegion.reduce(
                            (sum, p) => sum + (p.count || 0),
                            0
                          );
                          const hasCount = parksInRegion.some((p) => p.count !== null);
                          const meta = getRegionMeta(parksInRegion[0]?.regionId);
                          const formenLabel = meta.formenNames.join(", ");

                          return (
                            <Card
                              key={regionName}
                              className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50"
                            >
                              <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                                    <TreePine className="h-5 w-5 text-blue-600" />
                                    {regionName}
                                    {formenLabel && (
                                      <span
                                        className="inline-flex items-center gap-1 text-xs font-normal text-slate-400"
                                        title={`Formen: ${formenLabel}`}
                                      >
                                        <HardHat className="h-3 w-3" />
                                        {formenLabel}
                                      </span>
                                    )}
                                  </CardTitle>
                                  <div className="flex items-center gap-2">
                                    <Badge className="bg-blue-600">{parksInRegion.length} Park</Badge>
                                    {hasCount && totalCount > 0 && (
                                      <Badge variant="outline" className="border-blue-400 text-blue-700">
                                        Toplam: {totalCount} adet
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent className="pt-2">
                                <div className="flex flex-wrap gap-2">
                                  {parksInRegion
                                    .sort((a, b) => a.name.localeCompare(b.name, "tr"))
                                    .map((park, idx) => (
                                      <Badge
                                        key={idx}
                                        variant="secondary"
                                        className="text-xs py-1.5 px-3 bg-white border border-gray-200 hover:bg-gray-50"
                                      >
                                        🌳 {park.name}
                                        {park.count !== null && park.count > 0 && (
                                          <span className="ml-1 text-blue-600 font-semibold">
                                            ({park.count})
                                          </span>
                                        )}
                                      </Badge>
                                    ))}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
      </DraggableModal>

      {/* Malzeme Talebi Oluşturma Dialog */}
      <DraggableModal
        isOpen={showMaterialRequestDialog}
        onClose={() => setShowMaterialRequestDialog(false)}
        title="Yeni Malzeme Talebi"
        subtitle={isFormen ? "Kendiniz veya ekibinizdeki personel için malzeme talep edin" : "İhtiyacınız olan malzemeyi talep edin"}
        maxWidth="max-w-md"
      >
          <form onSubmit={handleMaterialRequestSubmit} className="space-y-4">
              {/* Formen için personel seçimi */}
              {isFormen && (
                <div>
                  <label className="text-sm font-medium">Talep Kimin İçin? *</label>
                  <SearchableSelect
                    className="w-full border rounded-md p-2 mt-1"
                    value={materialForm.for_personnel_id || ""}
                    onChange={(v) => setMaterialForm({...materialForm, for_personnel_id: v || null})}
                    allLabel="Kendim için"
                    allValue=""
                    searchPlaceholder="Personel ara..."
                    options={personnel
                      .filter(p => p.formen_id === personnelId && p.role === "park_gorevlisi")
                      .map(p => ({ id: p.id, name: p.name }))
                    }
                  />
                  <p className="text-xs text-gray-500 mt-1">Ekibinizdeki bir personel için de talep oluşturabilirsiniz</p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium">Malzeme Türü *</label>
                <SearchableSelect
                  className="w-full border rounded-md p-2 mt-1"
                  value={materialForm.material_type}
                  onChange={(v) => setMaterialForm({...materialForm, material_type: v})}
                  allLabel="Seçiniz"
                  allValue=""
                  searchPlaceholder="Malzeme türü ara..."
                  options={["Bahçe Hortumu","Budama Makası","Çalı Süpürge","Çöp Poşeti","Faraş","Kürek","Tırmık","Yelpaze Tırmık","Diğer"].map((type) => ({ id: type, name: type }))}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Adet *</label>
                <input
                  type="number"
                  min="1"
                  className="w-full border rounded-md p-2 mt-1"
                  value={materialForm.quantity}
                  onChange={(e) => setMaterialForm({...materialForm, quantity: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Açıklama</label>
                <textarea
                  className="w-full border rounded-md p-2 mt-1"
                  rows="3"
                  placeholder="Opsiyonel açıklama"
                  value={materialForm.description}
                  onChange={(e) => setMaterialForm({...materialForm, description: e.target.value})}
                />
              </div>
              
              <DatePicker
                label="Talep Tarihi"
                required
                value={materialForm.requested_date}
                onChange={(value) => setMaterialForm({...materialForm, requested_date: value})}
              />
            
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowMaterialRequestDialog(false);
                  setMaterialForm({
                    material_type: "",
                    quantity: "1",
                    description: "",
                    requested_date: "",
                    for_personnel_id: null
                  });
                }}
              >
                İptal
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Oluşturuluyor..." : "Talep Oluştur"}
              </Button>
            </div>
          </form>
      </DraggableModal>

      {/* Malzeme Talebi Silme Onay Dialog */}
      <AlertDialog open={!!materialToDelete} onOpenChange={(open) => !open && setMaterialToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Malzeme Talebini Silmek İstediğinize Emin Misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. <strong>{materialToDelete?.material_type}</strong> talebi kalıcı olarak silinecektir.
              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                <p className="text-gray-700"><strong>Talep Eden:</strong> {materialToDelete?.personnel_name}</p>
                <p className="text-gray-700"><strong>Adet:</strong> {materialToDelete?.quantity}</p>
                <p className="text-gray-700"><strong>Durum:</strong> {materialToDelete?.status === 'delivered' ? 'Teslim Edildi' : 'Bekliyor'}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setMaterialToDelete(null)}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteMaterialRequest}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? "Siliniyor..." : "Evet, Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Personel Detay Dialog - İzin ve Malzeme Talepleri */}
      <PersonnelDetailsDialog
        key={`${selectedPersonnelForDetail?.id}-${personnelDetailDefaultTab}`}
        isOpen={personnelDetailDialogOpen}
        onClose={setPersonnelDetailDialogOpen}
        personnel={selectedPersonnelForDetail}
        leaveRequests={leaveRequests}
        materialRequests={materialRequests}
        getRoleBadge={getRoleBadge}
        allPersonnel={personnel}
        parks={parks}
        onPersonnelUpdated={fetchPersonnel}
        onOpenAdvancedEdit={handlePersonnelEdit}
        defaultTab={personnelDetailDefaultTab}
        userRole={userRole}
        currentPersonnelId={personnelId}
      />


      {/* Malzeme Teslim Etme Dialog */}
      <Dialog open={showMaterialDeliveryDialog} onOpenChange={setShowMaterialDeliveryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Malzeme Teslimi</DialogTitle>
            <DialogDescription>
              {selectedMaterialRequest?.material_type} - {selectedMaterialRequest?.personnel_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMaterialDelivery}>
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-600">Talep Edilen Malzeme</p>
                <p className="font-semibold text-lg">{selectedMaterialRequest?.material_type}</p>
                <p className="text-sm mt-1">Adet: {selectedMaterialRequest?.quantity}</p>
                {selectedMaterialRequest?.description && (
                  <p className="text-sm mt-1 text-gray-600">{selectedMaterialRequest.description}</p>
                )}
              </div>
              
              <DatePicker
                label="Teslim Tarihi"
                required
                value={deliveryForm.delivered_date}
                onChange={(value) => setDeliveryForm({...deliveryForm, delivered_date: value})}
              />
              
              <div>
                <label className="text-sm font-medium">Teslim Notu</label>
                <textarea
                  className="w-full border rounded-md p-2 mt-1"
                  rows="3"
                  placeholder="Opsiyonel not"
                  value={deliveryForm.delivery_note}
                  onChange={(e) => setDeliveryForm({...deliveryForm, delivery_note: e.target.value})}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowMaterialDeliveryDialog(false);
                  setSelectedMaterialRequest(null);
                  setDeliveryForm({
                    delivered_date: "",
                    delivery_note: ""
                  });
                }}
              >
                İptal
              </Button>
              <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
                {loading ? "Teslim Ediliyor..." : "Teslim Et"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Senkronizasyon Dialog */}
      <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CloudUpload className="h-5 w-5 text-blue-600" />
              Production'a Senkronize Et
            </DialogTitle>
            <DialogDescription>
              Preview ortamındaki tüm verileri Production ortamına aktarın.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>⚠️ Dikkat:</strong> Bu işlem Preview'daki verileri Production'a aktaracak. 
                Production'da aynı ID'ye sahip veriler güncellenecektir.
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-medium mb-2">Aktarılacak Veriler:</p>
              <ul className="text-xs text-blue-700 grid grid-cols-2 gap-1">
                <li>• Pasif Alanlar</li>
                <li>• Pasif Alan Planları</li>
                <li>• Pasif Alan Geçmişi</li>
                <li>• Parklar</li>
                <li>• Personel</li>
                <li>• Bölgeler</li>
                <li>• İzin Talepleri</li>
                <li>• Malzeme Talepleri</li>
                <li>• Revizeler</li>
                <li>• Geçici Görevler</li>
                <li>• Faaliyet Planları</li>
                <li>• Konum Logları</li>
              </ul>
            </div>
            
            {syncProgress && (
              <div className="bg-gray-50 border rounded-lg p-4">
                <div className="flex items-center gap-2">
                  {syncLoading && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                  <span className="text-sm">{syncProgress}</span>
                </div>
              </div>
            )}
            
            {syncResults && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-h-48 overflow-auto">
                <p className="text-sm text-green-800 font-medium mb-2">✅ Aktarım Sonuçları:</p>
                <div className="text-xs text-green-700 space-y-1">
                  {Object.entries(syncResults).map(([collection, result]) => (
                    <div key={collection} className="flex justify-between">
                      <span>{collection}:</span>
                      <span>{result.imported} yeni, {result.updated} güncellendi</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncDialogOpen(false)} disabled={syncLoading}>
              İptal
            </Button>
            <Button 
              onClick={handleSyncToProduction} 
              disabled={syncLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {syncLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Aktarılıyor...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Senkronize Et
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Erteleme Dialog */}
      <Dialog open={showPostponeDialog} onOpenChange={setShowPostponeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Revize Talebini Ertele</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Bu revize talebini hangi tarihe ertelemek istiyorsunuz?
            </p>
            <DatePicker
              label="Erteleme Tarihi"
              required
              value={postponeDate}
              onChange={setPostponeDate}
              min={todayYmd()}
              highlightHolidays
            />
            <div>
              <label className="text-sm font-medium mb-2 block">Açıklama (Opsiyonel)</label>
              <textarea
                className="w-full border rounded-md p-2"
                rows="3"
                placeholder="Erteleme nedeni veya notunuz..."
                id="postpone-comment"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPostponeDialog(false)}>
              İptal
            </Button>
            <Button 
              onClick={async () => {
                if (!postponeDate) {
                  toast.error("Lütfen erteleme tarihi seçin!");
                  return;
                }
                const comment = document.getElementById('postpone-comment')?.value || "";
                try {
                  await axios.post(`${API}/api/revisions/${postponingRevision.id}/decide`, {
                    decision: "postponed",
                    postponed_until: postponeDate,
                    comment: comment,
                    decided_by_id: personnelId,
                    decided_by_name: personnel.find(p => p.id === personnelId)?.name
                  });
                  toast.success("Talep ertelendi!");
                  setShowPostponeDialog(false);
                  setPostponingRevision(null);
                  setPostponeDate("");
                  setSelectedUrgency("ORTA");
                  fetchRevisions();
                  setSelectedRevision(null);
                } catch (error) {
                  console.error("Erteleme hatası:", error);
                  toast.error("Ertelenemedi");
                }
              }}
              disabled={!postponeDate}
            >
              ⏰ Ertele
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reddetme Dialog */}
      <Dialog
        open={showRejectDialog}
        onOpenChange={(open) => {
          setShowRejectDialog(open);
          if (!open) {
            setRejectingRevision(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Revize Talebini Reddet</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <p className="text-sm text-gray-600">
              Reddetme nedeni yazın (opsiyonel):
            </p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Reddetme nedenini buraya yazın..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              İptal
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!rejectingRevision?.id) return;
                try {
                  await axios.post(`${API}/api/revisions/${rejectingRevision.id}/decide`, {
                    decision: "rejected",
                    comment: rejectReason?.trim() || null,
                    decided_by_id: personnelId,
                    decided_by_name: personnel.find(p => p.id === personnelId)?.name
                  });
                  toast.success("Talep reddedildi!");
                  setSelectedUrgency("ORTA");
                  setShowRejectDialog(false);
                  setRejectingRevision(null);
                  setRejectReason("");
                  fetchRevisions();
                  setSelectedRevision(null);
                } catch (error) {
                  console.error("Reddetme hatası:", error);
                  toast.error("Reddedilemedi");
                }
              }}
            >
              ❌ Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Vekalet Yönetim Dialog */}
      <DraggableModal
        isOpen={vekaletDialogOpen}
        onClose={() => {
          setVekaletDialogOpen(false);
          setSelectedSubstitution(null);
          setNewSubstituteId("");
        }}
        title="🔄 Vekalet Yönetimi"
        maxWidth="max-w-[500px]"
      >
          {selectedSubstitution && (
            <div className="space-y-4">
              {/* Vekalet Detayları */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">İzindeki Personel:</span>
                  <span className="font-semibold text-gray-800">{selectedSubstitution.original_personnel_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Vekil:</span>
                  <span className="font-semibold text-blue-700">{selectedSubstitution.substitute_personnel_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Tarih Aralığı:</span>
                  <span className="font-semibold text-gray-800">
                    {new Date(selectedSubstitution.start_date).toLocaleDateString('tr-TR')} - {new Date(selectedSubstitution.end_date).toLocaleDateString('tr-TR')}
                  </span>
                </div>
                <div className="border-t pt-3">
                  <span className="text-sm text-gray-600 block mb-2">Vekalet Parkları:</span>
                  <div className="flex flex-wrap gap-1">
                    {selectedSubstitution.parks?.map(park => (
                      <Badge key={park.id} variant="outline" className="text-xs bg-green-50 border-green-300">
                        {park.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Vekili Değiştir */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Vekaleti Başka Birine Devret:</label>
                <SearchableSelect
                  value={newSubstituteId}
                  onChange={(v) => setNewSubstituteId(v)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  allLabel="Yeni vekil seçin..."
                  allValue=""
                  searchPlaceholder="Vekil ara..."
                  options={(() => {
                    // İzindeki personelin formen_id'sini bul
                    const originalPerson = personnel.find(p => p.id === selectedSubstitution.original_personnel_id);
                    const formenId = originalPerson?.formen_id;

                    // Aynı formene bağlı park görevlileri ve çim ekibi (formen hariç)
                    return personnel
                      .filter(p =>
                        p.id !== selectedSubstitution.substitute_personnel_id &&
                        p.id !== selectedSubstitution.original_personnel_id &&
                        (p.role === 'park_gorevlisi') &&
                        p.formen_id === formenId
                      )
                      .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                      .map(p => ({ id: p.id, name: `${p.name} (${getRoleBadge(p.role).label})` }));
                  })()}
                />
                <Button
                  onClick={() => handleTransferSubstitution(selectedSubstitution.id, newSubstituteId)}
                  disabled={!newSubstituteId || vekaletLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {vekaletLoading ? "İşleniyor..." : "🔄 Vekaleti Devret"}
                </Button>
              </div>

              {/* Vekaleti Kaldır */}
              <div className="border-t pt-4">
                <Button
                  variant="destructive"
                  onClick={() => handleRemoveSubstitution(selectedSubstitution.id)}
                  disabled={vekaletLoading}
                  className="w-full"
                >
                  {vekaletLoading ? "İşleniyor..." : "🗑️ Vekaleti Tamamen Kaldır"}
                </Button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Vekaleti kaldırırsanız, parklar vekil personelden geri alınır.
                </p>
              </div>
            </div>
          )}
      </DraggableModal>
    </div>
  );
}

// Profesyonel oturum yönetimi: AuthContext bağdaştırıcısı
//
// Token artık HTTP-only cookie'de (pb_access, pb_refresh) — JavaScript'e değmez.
// Sayfa yenilendiğinde AuthContext otomatik olarak GET /api/auth/me ile state restore eder.
// localStorage sadece display info (ad, rol, id) tutar — token YOK.
function App() {
  const auth = useAuth();
  const { isLoggedIn, user, bootstrapping, login, logout } = auth;

  const userRole = user?.userRole || "";
  const userRoleRaw = user?.userRoleRaw || "";
  const personnelId = user?.personnelId || "";
  const personnelName = user?.personnelName || "";
  const credentialSetupPending = Boolean(user?.credentialSetupPending);

  // LoginScreen ile mevcut imza uyumu
  const handleLogin = (role, perId, perName, _token = null, loginOpts = {}) => {
    const payload = loginOpts.loginPayload || {
      personnel_id: perId,
      name: perName,
      role,
      credential_setup_required: Boolean(loginOpts.credentialSetupRequired),
      access_expires_at: loginOpts.accessExpiresAt || null,
      idle_settings: loginOpts.idleSettings || null,
      remember_me: loginOpts.rememberMe ?? true,
    };
    login(payload);
  };

  const handleCredentialSetupCompleted = ({ token, name, role, personnelId: pid, ...rest }) => {
    // Backend zaten yeni cookie set etti; bizim için sadece state güncellemesi.
    login({
      personnel_id: pid || personnelId,
      name: name || personnelName,
      role: role || userRole,
      // F1-D: role_raw taşınmazsa AuthContext eskiden `role`a ("admin") düşüyordu.
      role_raw: rest.role_raw || userRoleRaw,
      credential_setup_required: false,
      access_expires_at: rest.accessExpiresAt || rest.access_expires_at || null,
      idle_settings: rest.idleSettings || rest.idle_settings || null,
      remember_me: rest.rememberMe ?? rest.remember_me ?? true,
    });
  };

  const handleLogout = async () => {
    await logout();
    toast.info("Çıkış yapıldı");
  };

  // Eski parkBakimAuthRequired event'i — bazı yardımcı modüller hala bunu kullanıyor olabilir.
  useEffect(() => {
    const onAuthRequired = () => {
      logout({ silent: true });
    };
    window.addEventListener("parkBakimAuthRequired", onAuthRequired);
    return () => window.removeEventListener("parkBakimAuthRequired", onAuthRequired);
  }, [logout]);

  // İlk yükleme: AuthContext /api/auth/me ile state restore ediyor; loading göster
  if (bootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-green-700 font-medium">Oturum kontrol ediliyor...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <Toaster />
        <LoginScreen onLogin={handleLogin} />
      </>
    );
  }

  if (credentialSetupPending) {
    return (
      <>
        <Toaster />
        <FirstCredentialSetup
          personnelName={personnelName}
          onCompleted={handleCredentialSetupCompleted}
        />
      </>
    );
  }

  // Admin rolü: kullanıcı panelini hiç görmez, doğrudan /admin'e yönlendirilir.
  // userRoleRaw kullanılıyor — TB-047'de 'baskan' rolü API'de 'admin' alias'lanır;
  // ham rol ('baskan') BAŞKAN ÇANKAYA gibi kullanıcıları /admin'e göndermez,
  // onlar kullanıcı panelinde kalır (operasyonel taraf).
  if (userRoleRaw === "admin") {
    return <Navigate to="/admin" replace />;
  }

  return (
    <>
      <IdleTimeoutManager />
      <Dashboard userRole={userRole} personnelId={personnelId} onLogout={handleLogout} />
      {/* BELİ (AI) — yetkiye bağlı floating asistan; mevcut ekranları etkilemez.
          E2E/CI'da (REACT_APP_E2E=1) HİÇ RENDER EDİLMEZ → sağ-alt floating buton
          mount olmaz; aksi halde OBT "Yeniden Gönder" gibi butonların önüne gelip
          Playwright tıklamalarını bloklar (pointer-events hack/force DEĞİL — widget
          yüklenmez). Dev/prod DEĞİŞMEZ (flag yalnız e2e-live compose'unda set edilir). */}
      {process.env.REACT_APP_E2E !== "1" && <BeliLauncher />}
    </>
  );
}

export default App;
